// MagicShelf app source — compiled by esbuild via Netlify build (see netlify.toml).
// This replaced the in-browser Babel script inside index.html (Batch 3, 2026-07-05).
// Globals from CDN (index.html): React, ReactDOM, supabase (supabase-js).
// Relative imports are bundled by esbuild; nothing here comes from npm.
import { searchItems } from "./search.js";
import { normalizeText } from "./text.js";
import { recentDestinations } from "./recents.js";
import { firstScanTarget } from "./scan-target.js";
import { inwardTarget, outwardTarget } from "./scan-nav.js";
import { fuseItemResults, isSemanticWorthwhile, rankMatches } from "./semantic.js";
import { iconKeyForLocation } from "./location-icons.js";
import { isAppOwner } from "./owners.js";
import {
  DEFAULT_SCAN_CONFIG, SCAN_CONFIG_KEY, SCAN_CONFIG_KEY_LEGACY, SCAN_MODELS,
  migrateScanConfig, normalizeScanConfig,
} from "./scan-config.js";
import { draftAfterRetry, draftChanged, draftFromApprove, verifyDraft } from "./scan-draft.js";
import {
  appendHandled, canStepBack, canStepForward, findHandled,
  historyPosition, patchHandled, removeHandled, stepHistory,
} from "./scan-history.js";

const { useState, useRef, useEffect, useMemo, useCallback } = React;

const SUPABASE_URL = "https://gpeeifokzfcdwzrstbkk.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_BaPOFxEr69zXIjFk38Ed1A_81fO78TX";
const supabase = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const APP_VERSION = "2.2.0";
const MAX_IMAGE_LONG_SIDE = 2048;
// Mirrors normalizeQuantity in detect.js, which is the one that actually guards the column.
const MAX_QUANTITY = 9999;
// PostgREST reports an unknown column as PGRST204, Postgres as 42703. The column name is
// matched too, so an unrelated schema error still surfaces instead of being swallowed.
const isMissingColumn = (error, column) => {
  const code = error?.code || "";
  if (code !== "PGRST204" && code !== "42703") return false;
  return `${error.message || ""}`.includes(column);
};
const cleanQuantity = (raw) => {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, MAX_QUANTITY);
};
// One switch for the half-finished options, and it is FALSE ON EVERY MERGE.
//
// Scan config, view mode and search behaviour all landed in the settings sheet because that was the
// quickest place to put a toggle while the thing behind it was being worked on. Each is useful to
// whoever is working on it and noise to everyone else, and three panels of them turn "settings" into
// a lab bench. Flip this to true locally to get them back; do not commit it true.
//
// What it hides is only the CONTROLS. The behaviour keeps running on its defaults — list view,
// `DEFAULT_SCAN_CONFIG`, `DEFAULT_SEARCH_CONFIG` — and anything a user already chose is still in
// localStorage and still honoured, so hiding the panel cannot silently change how the app behaves for
// someone who set something months ago.
const DEV_MODE = false;
// The door the browser tier uses: a spec about icons mode cannot open a panel a constant has hidden,
// and hard-coding `true` for tests would defeat the point of the constant. Deliberately not a URL
// parameter, which would be a shareable link into the lab bench. Anyone with a console can set it,
// which is fine — these are display options, not permissions.
const devOptions = () => DEV_MODE || (typeof window !== "undefined" && window.__MAGICSHELF_DEV__ === true);

// The scan config, its defaults and the migration of a stored one all live in src/scan-config.js.

// What tapping the camera means. Set on the bottom bar of the scan screen; `item` is the default
// because it is what almost every tap is.
//
// `reference` is not a kind of thing to create — it is a way of recording where one is. The user
// photographs whatever will help them find the thing again (the room, the shelf, the box) and those
// frames ride along with the next item they scan as extra images. It was called "breadcrumbs", which
// named one use of it: the frames need not be a path at all, and a reference picture of the crate
// something is in is the commonest case. See the trail buffer in ScanLive.
const SCAN_MODES = [
  { id: "location", name: "Location" },
  { id: "item", name: "Item" },
  { id: "reference", name: "Reference" },
];
const DEFAULT_SCAN_MODE = "item";
// Matches MAX_TRAIL_IMAGES in detect.js. Enforced here too so the camera stops taking frames it
// knows the server will drop, rather than letting the user photograph a trail that silently
// truncates on save.
const MAX_TRAIL_FRAMES = 8;
// Selectable fallback behavior for testing weak-signal handling.
const SCAN_CHAIN_OPTIONS = [
  { id: "resilient", name: "Resilient (recommended)", desc: "Retries with a smaller image if a scan stalls on weak signal." },
  { id: "direct", name: "Direct", desc: "Single attempt, no weak-signal retry. For comparison." },
];
// Sub-location prediction, the one scan setting outside the dev panel.
//
// It is off by default and marked beta because it is a guess that writes rows: approving a filing
// cabinet can create Drawer 1..3 with it, which is the difference between a good guess and three
// wrong locations to delete. It is offered anyway because when it is right it is the tedious part of
// setting up a space done for free.
//
// LOCATION MODE ONLY, enforced where the scan is requested rather than here. The prediction asks
// "what compartments does this place have?", which is meaningless for a tap on a stapler, and the
// mode toggle already says which kind of thing the user is pointing at.
const SUBLOCATION_FEATURE = {
  id: "sublocation",
  name: "Sub-location prediction",
  beta: true,
  desc: "In Location mode, guesses the compartments of a place you add — Drawer 1, Drawer 2, Drawer 3 for a three-drawer cabinet — for you to approve or uncheck.",
};

// The scan config as sent for one tap. The setting says whether the user wants compartment
// predictions at all; the mode says whether this particular tap could have any. Applied at every
// call site rather than inside the labeler, so `scan_config` on the wire is the truth of what was
// asked for and the cost of the prompt matches it.
function scanConfigForMode(config, scanMode) {
  if (!config?.sublocation || scanMode === "location") return config;
  return { ...config, sublocation: false };
}
// The toggleable feature flags shown in the dev config panel.
const SCAN_FEATURES = [
  { id: "tree", name: "Send the location tree", desc: "Shows the AI how this space is organised, so new places it suggests are named the way you name yours. Costs a little more per scan." },
  { id: "choices", name: "Offer other names", desc: "Returns a few alternative labels to tap instead of typing, when the AI isn't sure." },
  { id: "styles", name: "Offer shorter and longer names", desc: "Returns the same name at three lengths — just the noun, how you'd say it, and a detailed version — to tap between." },
  { id: "tags", name: "Suggest tags", desc: "Tags each scan with what kind of thing it is, so searching \"silverware\" finds a spork. You can change, remove or add your own before saving." },
];

// Search behaviour, kept separate from scan config because it is a preference about reading
// results rather than about what the AI is asked for, and because it applies to people who never
// scan anything. Same mechanism as the scan config: client-only, so no schema and no round trip.
// Whether the location picker offers a "Recent" section above the tree. See the comment where
// it is read, in LocationTreePicker.
const SHOW_RECENT_DESTINATIONS = false;

const SEARCH_CONFIG_KEY = "magicshelf_search_config_v1";
const DEFAULT_SEARCH_CONFIG = { stableOrder: false };
const SEARCH_FEATURES = [
  { id: "stableOrder", name: "Don't reorder results", desc: "Keeps the first results where they are and adds smarter matches below, instead of rearranging the list a moment later." },
];
function normalizeSearchConfig(cfg) {
  const c = cfg && typeof cfg === "object" ? cfg : {};
  return {
    stableOrder: typeof c.stableOrder === "boolean" ? c.stableOrder : DEFAULT_SEARCH_CONFIG.stableOrder,
  };
}

// How the browse screens draw what is in a place. Same mechanism again, and for the same reason:
// it is a preference about reading, so it needs no schema and no round trip.
//
// One setting for all three screens rather than a per-screen toggle. The mode is a statement about
// how the user prefers to read their belongings, not about the shelf they happen to be looking at,
// and a control that has to be set again on every screen is a control nobody sets.
const VIEW_MODE_KEY = "magicshelf_view_mode_v1";
const DEFAULT_VIEW_MODE = "list";
const VIEW_MODES = [
  { id: "list", name: "List", desc: "Compact rows. The most things visible at once." },
  { id: "icons", name: "Icons", desc: "Photo cards for locations as well as items, so a place is recognisable rather than read." },
  { id: "tree", name: "Tree", desc: "The whole tree at once, with drag to move a location somewhere else." },
];
function normalizeViewMode(mode) {
  return VIEW_MODES.some((m) => m.id === mode) ? mode : DEFAULT_VIEW_MODE;
}
// Declared next to the modes but populated after the icon set below, since the icons are components.
const VIEW_MODE_ICONS = {};
const Ico = ({ d, size = 22, sw = 2, extra, cls }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={extra} className={cls}>{d}</svg>
);
const Camera = (p) => <Ico {...p} d={<><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></>} />;
const Search = (p) => <Ico {...p} d={<><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></>} />;
const MapPin = (p) => <Ico {...p} d={<><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>} />;
const X = (p) => <Ico {...p} d={<><path d="M18 6 6 18M6 6l12 12"/></>} />;
const Check = (p) => <Ico {...p} d={<><path d="M20 6 9 17l-5-5"/></>} />;
const Package = (p) => <Ico {...p} d={<><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></>} />;
const ChevronLeft = (p) => <Ico {...p} d={<><path d="m15 18-6-6 6-6"/></>} />;
const ChevronRight = (p) => <Ico {...p} d={<><path d="m9 18 6-6-6-6"/></>} />;
const Trash2 = (p) => <Ico {...p} d={<><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M10 11v6M14 11v6"/></>} />;
const ImageIcon = (p) => <Ico {...p} d={<><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></>} />;
const AlertCircle = (p) => <Ico {...p} d={<><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></>} />;
const Info = (p) => <Ico {...p} d={<><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></>} />;
const Mail = (p) => <Ico {...p} d={<><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>} />;
const ListIcon = (p) => <Ico {...p} d={<><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></>} />;
const GridIcon = (p) => <Ico {...p} d={<><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></>} />;
const TreeIcon = (p) => <Ico {...p} d={<><rect width="6" height="5" x="3" y="3" rx="1"/><rect width="6" height="5" x="15" y="9" rx="1"/><rect width="6" height="5" x="15" y="17" rx="1"/><path d="M6 8v9a2 2 0 0 0 2 2h7M6 11h9"/></>} />;
Object.assign(VIEW_MODE_ICONS, { list: ListIcon, icons: GridIcon, tree: TreeIcon });
// The hooked Undo and Redo arrows lived here. They went with the buttons they were drawn for — the
// approve header now has chevrons that walk the session's handled scans instead. `git log` has them if a
// reversal control is ever wanted again, along with the note that an arc with the sweep flags wrong
// renders as a comma with a tick beside it at 18px.
const Loader = (p) => <Ico {...p} cls="spin-svg" d={<path d="M21 12a9 9 0 1 1-6.219-8.56"/>} />;
const LogOut = (p) => <Ico {...p} d={<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>} />;
const Plus = (p) => <Ico {...p} d={<><path d="M12 5v14M5 12h14"/></>} />;
const Layers = (p) => <Ico {...p} d={<><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>} />;
const Edit2 = (p) => <Ico {...p} d={<><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z"/></>} />;
const Users = (p) => <Ico {...p} d={<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></>} />;
const MoreVertical = (p) => <Ico {...p} d={<><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></>} />;
const Settings = (p) => <Ico {...p} d={<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>} />;

// Default icons for a location with no photo. A neutral box for everything was legible and
// told you nothing; a kitchen that looks like a kitchen is findable at a glance down a list.
// src/location-icons.js maps a name to one of these keys — the keys here are the contract
// between that file and this one.
const House = (p) => <Ico {...p} d={<><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .7-1.5l7-6a2 2 0 0 1 2.6 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></>} />;
const Bed = (p) => <Ico {...p} d={<><path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/></>} />;
const Utensils = (p) => <Ico {...p} d={<><path d="M3 2v7a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2V2"/><path d="M5.5 11v11"/><path d="M18 2a4 4 0 0 0-4 4v5a1 1 0 0 0 1 1h3z"/><path d="M18 12v10"/></>} />;
const Fridge = (p) => <Ico {...p} d={<><path d="M5 6a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z"/><path d="M5 10h14"/><path d="M15 7v6"/></>} />;
const Droplet = (p) => <Ico {...p} d={<path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"/>} />;
const Sofa = (p) => <Ico {...p} d={<><path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"/><path d="M2 11v5a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v2H6v-2a2 2 0 0 0-4 0Z"/><path d="M4 18v2M20 18v2"/></>} />;
const Car = (p) => <Ico {...p} d={<><path d="M19 17h2a1 1 0 0 0 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4a1 1 0 0 0 1 1h2"/><path d="M9 17h6"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></>} />;
const Sprout = (p) => <Ico {...p} d={<><path d="M7 20h10"/><path d="M10 20c5.5-2.5.8-6.4 3-10"/><path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z"/><path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z"/></>} />;
const Wrench = (p) => <Ico {...p} d={<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-8 7.9l-6.9 6.9a2.1 2.1 0 0 1-3-3l6.9-6.9a6 6 0 0 1 7.9-8l-3.7 3.8z"/>} />;
const Tent = (p) => <Ico {...p} d={<><path d="M3.5 21 14 3"/><path d="M20.5 21 10 3"/><path d="M15.5 21 12 15l-3.5 6"/><path d="M2 21h20"/></>} />;
const Washer = (p) => <Ico {...p} d={<><rect width="18" height="20" x="3" y="2" rx="2"/><path d="M6 6h.01M10 6h.01"/><circle cx="12" cy="14" r="5"/><path d="M12 16.5a1.5 1.5 0 0 0 0-3 1.5 1.5 0 0 1 0-3"/></>} />;
const Briefcase = (p) => <Ico {...p} d={<><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/></>} />;
const Shirt = (p) => <Ico {...p} d={<path d="M20.4 3.5 16 2a4 4 0 0 1-8 0L3.6 3.5a2 2 0 0 0-1.3 2.2l.6 3.5a1 1 0 0 0 1 .8H6v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V10h2.1a1 1 0 0 0 1-.8l.6-3.5a2 2 0 0 0-1.3-2.2z"/>} />;
const Book = (p) => <Ico {...p} d={<path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"/>} />;
const Archive = (p) => <Ico {...p} d={<><rect width="20" height="5" x="2" y="3" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="M10 12h4"/></>} />;
// The rest of the set exists to be CHOSEN rather than guessed. A keyword list can only justify an
// icon it can recognise a word for, so the guessable set stayed small; a picker has no such limit,
// and "shelf in the garage that holds the Christmas things" is exactly the location whose name no
// rule will ever classify.
const Bike = (p) => <Ico {...p} d={<><circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><path d="M15 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/></>} />;
const Ball = (p) => <Ico {...p} d={<><circle cx="12" cy="12" r="10"/><path d="M12 2a10 10 0 0 0 0 20M2 12h20"/></>} />;
const Toy = (p) => <Ico {...p} d={<><circle cx="12" cy="7" r="3"/><path d="M9 10c-2.5 1-4 3.4-4 6.5V21h14v-4.5c0-3.1-1.5-5.5-4-6.5"/><path d="M9 21v-4M15 21v-4"/></>} />;
const Paw = (p) => <Ico {...p} d={<><circle cx="6" cy="10" r="2"/><circle cx="10" cy="6" r="2"/><circle cx="14" cy="6" r="2"/><circle cx="18" cy="10" r="2"/><path d="M8.5 15.5c1-2 2-3 3.5-3s2.5 1 3.5 3c1.2 2.4-.3 4.5-3.5 4.5s-4.7-2.1-3.5-4.5z"/></>} />;
const Music = (p) => <Ico {...p} d={<><circle cx="7" cy="18" r="3"/><circle cx="18" cy="16" r="3"/><path d="M10 18V5l11-2v13"/></>} />;
const FirstAid = (p) => <Ico {...p} d={<><rect width="20" height="14" x="2" y="6" rx="2"/><path d="M9 6V4h6v2"/><path d="M12 10v6M9 13h6"/></>} />;
const Plug = (p) => <Ico {...p} d={<><path d="M9 2v6M15 2v6"/><path d="M6 8h12v3a6 6 0 0 1-12 0z"/><path d="M12 17v5"/></>} />;
const Palette = (p) => <Ico {...p} d={<><path d="M12 2a10 10 0 1 0 0 20c1.4 0 2-1 2-2s-.7-2-2-2h-1a2 2 0 0 1 0-4h6a4 4 0 0 0 4-4 10 10 0 0 0-9-8z"/><circle cx="8" cy="9" r="1.2"/><circle cx="12" cy="7" r="1.2"/></>} />;
const Thread = (p) => <Ico {...p} d={<><path d="M9 2h6l1 20H8z"/><path d="M8 7h8M8 12h8M8 17h8"/></>} />;
const Files = (p) => <Ico {...p} d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h8M8 17h5"/></>} />;
const Gift = (p) => <Ico {...p} d={<><rect width="20" height="5" x="2" y="7" rx="1"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M12 7v15"/><path d="M12 7S10.5 3 8.5 3a2 2 0 0 0 0 4M12 7s1.5-4 3.5-4a2 2 0 0 1 0 4"/></>} />;
const Baby = (p) => <Ico {...p} d={<><circle cx="12" cy="8" r="5"/><path d="M9 7h.01M15 7h.01M10 10.5c1.2.8 2.8.8 4 0"/><path d="M6 22a6 6 0 0 1 12 0"/></>} />;
const Spray = (p) => <Ico {...p} d={<><path d="M7 8h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z"/><path d="M9 8V4h4v4"/><path d="M19 5h.01M21 8h.01M19 11h.01"/></>} />;
const Suitcase = (p) => <Ico {...p} d={<><rect width="16" height="13" x="4" y="7" rx="2"/><path d="M9 7V4h6v3"/><path d="M9 20v2M15 20v2"/></>} />;
const Boot = (p) => <Ico {...p} d={<><path d="M6 3h4l1 9h4a4 4 0 0 1 4 4v5H6z"/><path d="M6 16h13"/></>} />;
const Camera2 = (p) => <Ico {...p} d={<><rect width="20" height="14" x="2" y="6" rx="2"/><circle cx="12" cy="13" r="3.5"/><path d="M8 6l1.5-2h5L16 6"/></>} />;
const Monitor = (p) => <Ico {...p} d={<><rect width="20" height="14" x="2" y="3" rx="2"/><path d="M8 21h8M12 17v4"/></>} />;
const Basket = (p) => <Ico {...p} d={<><path d="M3 9h18l-1.7 10a2 2 0 0 1-2 1.7H6.7a2 2 0 0 1-2-1.7z"/><path d="M8 9 12 3l4 6"/><path d="M9 13v4M15 13v4"/></>} />;
const Hook = (p) => <Ico {...p} d={<><path d="M12 3v7a4 4 0 0 0 8 0"/><path d="M8 21h8"/><path d="M12 10v11"/></>} />;
const Star = (p) => <Ico {...p} d={<path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9z"/>} />;

// icon key -> component. The keys are the contract with src/location-icons.js (which can produce
// only the guessable subset) AND with `nodes.icon` (which can hold any of them). Order matters:
// this is the order the picker draws, so the guessable rooms-and-furniture ones come first.
const LOCATION_ICONS = {
  house: House, bed: Bed, kitchen: Utensils, fridge: Fridge, bath: Droplet, sofa: Sofa,
  car: Car, shed: Sprout, tools: Wrench, tent: Tent, laundry: Washer, desk: Briefcase,
  wardrobe: Shirt, books: Book, drawer: Archive, boxes: Layers, box: Package,
  bike: Bike, sport: Ball, toys: Toy, pets: Paw, music: Music, medicine: FirstAid,
  electronics: Plug, art: Palette, sewing: Thread, papers: Files, gifts: Gift, baby: Baby,
  cleaning: Spray, luggage: Suitcase, shoes: Boot, photos: Camera2, computer: Monitor,
  basket: Basket, hooks: Hook, favourite: Star,
};

// The icon a location is drawn with.
//
// The user's choice beats the guess, and the guess is still there when they have not made one — so
// `icon` being null means "keep guessing" rather than "no icon", and a name that changes goes on
// updating the drawing until someone says otherwise. An unrecognised key falls back to the guess as
// well, which is what lets a key be added to the client without a migration to match.
function locationIcon(location) {
  const chosen = typeof location === "string" ? null : location?.icon;
  if (chosen && LOCATION_ICONS[chosen]) return LOCATION_ICONS[chosen];
  const name = typeof location === "string" ? location : location?.name;
  return LOCATION_ICONS[iconKeyForLocation(name)] || Package;
}

// Whether a location is drawn as its icon or as its photograph.
//
// A CHOSEN icon beats a photo, which is the one surprising bit of this and is the only rule that makes
// the control honest: pick an icon for a place that already has a photo and, under "photo always wins",
// nothing visible would happen. It is also reversible without losing anything — the photo is still in
// `node_images`, so clearing the icon brings it straight back, which is why the sheet offers "use the
// photo instead" rather than making the user re-take it.
function locationDrawsIcon(location) {
  const chosen = location?.icon;
  if (chosen && LOCATION_ICONS[chosen]) return true;
  return !location?.image;
}

// ─────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────

// Single network choke point. Adds a per-request TIMEOUT (so a stalled request on weak
// signal fails instead of hanging forever) and auto-RETRIES transient network/timeout
// failures with backoff (weak signal is often intermittent — one or two retries recover
// a lot without the user doing anything). Non-retryable errors (auth, 4xx) fail fast.
async function callFunction(body, opts = {}) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error("Not signed in");

  const timeoutMs = opts.timeoutMs || 45000;   // hard ceiling per attempt
  const maxAttempts = opts.maxAttempts || 3;    // initial try + 2 retries
  const payload = JSON.stringify({ ...body, app_version: APP_VERSION, device_info: { ua: navigator.userAgent } });

  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const r = await fetch("/.netlify/functions/detect", {
        method: "POST",
        headers: { "content-type": "application/json", "authorization": `Bearer ${token}` },
        body: payload,
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      const text = await r.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error("Non-JSON: " + text.slice(0, 200)); }
      if (!r.ok) {
        const err = new Error(data?.error || `HTTP ${r.status}`);
        err.status = r.status; err.data = data;
        // 4xx (bad request, auth, quota) won't improve on retry — fail immediately.
        if (r.status >= 400 && r.status < 500) throw err;
        lastErr = err; // 5xx: server hiccup, worth a retry
      } else {
        return data;
      }
    } catch (e) {
      clearTimeout(timer);
      // Abort (timeout) and network errors are retryable; a thrown 4xx above is not.
      if (e.status && e.status >= 400 && e.status < 500) throw e;
      lastErr = (e.name === "AbortError")
        ? Object.assign(new Error("The connection timed out. Weak signal?"), { code: "timeout" })
        : e;
    }
    // Backoff before the next attempt (skip the wait after the final attempt).
    if (attempt < maxAttempts) await new Promise((res) => setTimeout(res, 800 * attempt));
  }
  throw lastErr || new Error("Request failed");
}

// Label a marked scan. scanConfig = { model, context, sublocation } (see SCAN_CONFIG);
// scanContext optionally carries { location_name, location_path, sibling_labels } for
// context-aware config. NOTE: `mode` is the function's dispatch field ("point"), so the
// scan config travels as `scan_config`.
// ─────────────────────────────────────────────────────────────────────
// SCAN STRATEGY CHAIN. Labeling a scan is a pluggable strategy, and the fallback is a
// linear walk down an ordered list: try each strategy; if it fails with an error the
// strategy says is "fall-through-able", try the next. This replaces the hardcoded
// "full then shrink" branch so future strategies (on-device CLIP, a cheaper server model,
// a different capture path) drop in as new list entries — the walker doesn't change.
//
// Each strategy: {
//   id, label,                         // identity (also loggable for A/B later)
//   run: async (ctx) => result,        // ctx = { image, scanConfig, scanContext, sessionId }
//   fallThroughOn: (err) => boolean,   // SEAM: per-step trigger. Today all use the same
//                                      //   "timeout" rule; later a step can fall through on
//                                      //   offline, low-confidence, quota, etc.
// }
// SEAM (dev config): activeScanChain() returns a single hardcoded order today. A dev-mode
// picker can later choose among multiple named chains (e.g. "server-only",
// "on-device-first") by swapping what it returns.
// ─────────────────────────────────────────────────────────────────────

// Shared trigger: fall through to the next strategy only on a weak-signal timeout.
// (Real 4xx like quota/auth already throw before reaching the chain walker.)
const fallThroughOnTimeout = (err) => !!(err && err.code === "timeout");

const SCAN_STRATEGIES = {
  "server-vlm-full": {
    id: "server-vlm-full",
    label: "Server VLM (full quality)",
    fallThroughOn: fallThroughOnTimeout,
    run: async ({ image, scanConfig, scanContext, sessionId }) =>
      callFunction(
        { mode: "point", image, scan_config: scanConfig || undefined, scan_context: scanContext || undefined, session_id: sessionId || undefined },
        { maxAttempts: 1, timeoutMs: 18000 } // one short attempt; the chain handles fallback
      ),
  },
  "server-vlm-shrunk": {
    id: "server-vlm-shrunk",
    label: "Server VLM (downscaled for weak signal)",
    fallThroughOn: fallThroughOnTimeout,
    run: async ({ image, scanConfig, scanContext, sessionId }) => {
      // Weak cellular uplink is the bottleneck — send a much smaller image.
      const small = await shrinkForUpload(image);
      return callFunction(
        { mode: "point", image: small, scan_config: scanConfig || undefined, scan_context: scanContext || undefined, session_id: sessionId || undefined },
        { maxAttempts: 2, timeoutMs: 25000 }
      );
    },
  },
  // FUTURE STRATEGY SLOTS (not built): "on-device-clip" (embed/label locally — privacy +
  // offline), "server-cheap-model" (cheaper fallback), "capture-retake" (prompt a new
  // photo). Add here + include in the chain below; the walker needs no changes.
};

// The active fallback order. Now DEV-SELECTABLE via scan config (scanConfig.scanChain) so
// the weak-signal fallback can be toggled on/off for testing on the same connection.
// Named chains:
//   "resilient" — full-quality attempt, then downscale-and-retry on timeout (the fix).
//   "direct"    — single server attempt, no shrink fallback (closest to old behavior).
const SCAN_CHAINS = {
  resilient: ["server-vlm-full", "server-vlm-shrunk"],
  direct: ["server-vlm-full"],
};
let _activeChainId = "resilient"; // updated from scan config on load / change
function setActiveScanChain(id) { if (SCAN_CHAINS[id]) _activeChainId = id; }
function activeScanChain() {
  return SCAN_CHAINS[_activeChainId] || SCAN_CHAINS.resilient;
}

// Walk the active strategy chain: run each in order; on a fall-through-able failure move
// to the next; otherwise throw. Throws the last error if the whole chain is exhausted.
async function labelMarked(annotatedDataUrl, scanConfig, scanContext, sessionId) {
  const ctx = { image: annotatedDataUrl, scanConfig, scanContext, sessionId };
  const chain = activeScanChain();
  let lastErr;
  for (let i = 0; i < chain.length; i++) {
    const strat = SCAN_STRATEGIES[chain[i]];
    if (!strat) continue;
    try {
      return await strat.run(ctx);
    } catch (e) {
      lastErr = e;
      const isLast = i === chain.length - 1;
      // Advance to the next strategy only if THIS one says the error is fall-through-able.
      if (isLast || !strat.fallThroughOn || !strat.fallThroughOn(e)) throw e;
    }
  }
  throw lastErr || new Error("All scan strategies failed");
}
async function labelMarkedWithRetry(dataUrl, scanConfig, scanContext, sessionId) {
  // Retry/timeout now live in callFunction (which backs off and fails fast on 4xx like
  // 402 quota). This wrapper is kept for its call sites; no extra retry needed here.
  return await labelMarked(dataUrl, scanConfig, scanContext, sessionId);
}
// Upload helper with the same weak-signal fallback as the scan chain: try the full image
// on a short timeout; if it times out (thin cellular uplink), shrink the image and retry.
// Respects the active scan chain so "Direct" mode disables the shrink fallback everywhere
// (for clean A/B testing), while "Resilient" enables it. Non-timeout errors throw through.
async function callWithImageFallback(p) {
  const resilient = _activeChainId !== "direct";
  if (!resilient) {
    // Direct: single attempt, no shrink fallback — mirrors the label path's "direct" chain.
    return await callFunction(p, { maxAttempts: 1, timeoutMs: 20000 });
  }
  try {
    return await callFunction(p, { maxAttempts: 1, timeoutMs: 20000 });
  } catch (e) {
    if (e.code === "timeout" && p.image) {
      const small = await shrinkForUpload(p.image);
      return await callFunction({ ...p, image: small }, { maxAttempts: 2, timeoutMs: 30000 });
    }
    throw e;
  }
}

async function saveItem(p) { return await callWithImageFallback({ mode: "save_item", ...p }); }

// Client-side location mutations still write through Supabase directly. Follow each create or
// rename with one bounded server call for that exact node; failures are reported by the
// read-only audit and never turn into a global repair from the browser.
//
// NEVER AWAITED at the call sites, on purpose. The user's write already succeeded by the time
// this runs, so waiting on it only delays the rename or the new row appearing — by up to the
// timeout below if the provider is slow. A browser tab stays alive after the call, unlike a
// serverless container, so letting it finish in the background loses nothing. It swallows its
// own errors for the same reason: a missing vector is a search-quality problem the audit
// reports, not something to interrupt someone mid-edit over.
async function embedNodeAfterWrite(nodeId) {
  if (!nodeId) return false;
  try {
    await callFunction({ mode: "embed_node", node_id: nodeId }, { maxAttempts: 1, timeoutMs: 15000 });
    return true;
  } catch (error) {
    console.warn("Runtime embedding failed; embedding audit will flag it:", nodeId, error.message);
    return false;
  }
}

// Produce a smaller, more-compressed version of a data-URL image — used as a weak-signal
// FALLBACK for the label call (less to push up a thin cellular uplink). Only applied
// after a full-quality attempt times out, so good connections keep full quality and only
// bad ones trade some detail for actually getting through.
function shrinkForUpload(dataUrl, maxLongSide = 1024, quality = 0.7) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxLongSide / Math.max(img.naturalWidth, img.naturalHeight));
        const w = Math.round(img.naturalWidth * scale);
        const h = Math.round(img.naturalHeight * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    } catch { resolve(dataUrl); }
  });
}
async function rejectScan(p) { return await callWithImageFallback({ mode: "reject_scan", ...p }); }
async function setNodeImage(p) { return await callWithImageFallback({ mode: "set_node_image", ...p }); }
// Tags go through the function, not a direct write: only the server knows what is in the
// vocabulary, and a slug that is not in it saves cleanly and then matches nothing. The response
// reports what was actually kept.
async function setNodeTags(nodeId, tags) {
  return await callFunction({ mode: "set_node_tags", node_id: nodeId, tags });
}

// Every image on one node, ordered, for the carousel on the item screen. Deliberately called only
// when a node is opened: the list screens sign one image per item, and a reference trail must not
// turn a shelf of twenty items into a hundred signed URLs nobody looked at.
async function loadNodeImages(nodeId) {
  try {
    const { images } = await callFunction({ mode: "node_images", node_id: nodeId });
    return Array.isArray(images) ? images : [];
  } catch (e) {
    // A carousel that cannot load is a missing extra, not a broken screen — the primary image is
    // already on the page from the list load.
    console.warn("node_images failed:", e.message);
    return [];
  }
}

// ─────────────────────────────────────────────────────────────────────
// Semantic search
//
// The half of search that understands that "utensils" and "silverware" are the same idea.
// Two steps, and the split between them is a security decision rather than an accident:
// the function embeds the query, and THIS CLIENT runs the similarity search itself, so the
// nodes RLS policies filter the candidates as the signed-in user. Searching server-side
// would mean detect.js reimplementing "visibility flows down a tree" in JavaScript.
//
// Everything here is best-effort. Lexical search has already rendered by the time any of it
// resolves, and if the key is missing, the migration has not been run, or the network is
// gone, the user simply gets the lexical results — which is the whole product, just less
// clever. Nothing in this section may ever throw into a render path.
// ─────────────────────────────────────────────────────────────────────

const SEMANTIC_DEBOUNCE_MS = 280;   // long enough that typing a word is one request, not six
// Measured on a realistic household corpus: about a third of unrelated labels clear the 0.3
// floor for a one-word query, so a window of 40 over hundreds of nodes silently drops correct
// but weakly-worded matches before fusion ever sees them — which is the failure this was. The
// cost of a larger window is ids and floats over the wire, and match_nodes caps it at 200.
const SEMANTIC_LIMIT = 150;
// How many vocabulary terms one query may match. Small because match_tags returns the best term
// plus its genuine rivals rather than everything above a floor, so more than a handful never
// arrives — measured, a query averages 1.16 terms. Where the floor and margin that decide that
// live, and why they are not passed from here, is explained on match_tags itself.
const SEMANTIC_TAG_LIMIT = 5;
// Below this, cosine similarity on short labels stops meaning anything. Without a floor a
// nearest-neighbour search returns the whole inventory ranked, and search could never say
// "nothing like that here". Mirrors the default in match_nodes.
const SEMANTIC_THRESHOLD = 0.3;
// After a failure, stop asking for a while. A provider outage should cost one request per
// minute, not one per keystroke.
const SEMANTIC_COOLDOWN_MS = 60000;

const semanticCache = new Map(); // normalised query -> ranked matches
let semanticColdUntil = 0;

// One embedding, one search, both kinds. Items and locations used to be fetched separately,
// which paid for the same query vector twice on every keystroke — and the location matches are
// needed alongside the item matches anyway, to work out which items sit inside them.
async function semanticMatchesFor(query) {
  if (Date.now() < semanticColdUntil) return null;
  try {
    const { embedding } = await callFunction({ mode: "embed_query", query }, { maxAttempts: 1, timeoutMs: 8000 });
    if (!embedding) return null;
    // Concurrent: the two searches share one query vector and neither needs the other's answer,
    // so the pair costs one round trip rather than two. Both run as the signed-in user — RLS is
    // what keeps match_nodes to this user's tree, and the vocabulary is global by design.
    const [nodeResult, tagResult] = await Promise.all([
      supabase.rpc("match_nodes", {
        query_embedding: embedding,
        match_count: SEMANTIC_LIMIT,
        match_threshold: SEMANTIC_THRESHOLD,
        match_kinds: ["item", "location"],
      }),
      supabase.rpc("match_tags", {
        query_embedding: embedding,
        match_count: SEMANTIC_TAG_LIMIT,
      }),
    ]);
    if (nodeResult.error) throw nodeResult.error;
    // Tags are the newer half, so treat their absence as "no tag matches" rather than failing
    // the whole search: an old database without match_tags should still search by name.
    if (tagResult.error) console.warn("Tag search unavailable:", tagResult.error.message);
    return rankMatches(nodeResult.data, tagResult.error ? [] : tagResult.data);
  } catch {
    // Includes "no key configured" (503) and "function does not exist" (no migration yet).
    semanticColdUntil = Date.now() + SEMANTIC_COOLDOWN_MS;
    return null;
  }
}

// The ranked matches for the current query, or null when there are none. Debounced and cached:
// the cache is module-level because the search box unmounts on every navigation, and
// re-embedding a query the user already typed would be paying twice for the same answer.
function useSemanticMatches(query) {
  const [matches, setMatches] = useState(null);
  useEffect(() => {
    const trimmed = String(query || "").trim();
    if (!isSemanticWorthwhile(trimmed)) { setMatches(null); return undefined; }
    const key = normalizeText(trimmed);
    if (semanticCache.has(key)) { setMatches(semanticCache.get(key)); return undefined; }
    // Cleared rather than left in place: keeping the previous query's matches would mix them
    // into the new query's results for as long as the request takes.
    setMatches(null);
    let cancelled = false;
    const timer = setTimeout(async () => {
      const found = await semanticMatchesFor(trimmed);
      if (found) semanticCache.set(key, found);
      if (!cancelled) setMatches(found);
    }, SEMANTIC_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);
  return matches;
}

// The tag vocabulary, for showing a tag's readable label and for letting someone add one the
// scan missed. Global reference data — the same ~120 rows for everybody — so it is fetched once
// per page load and held at module scope, like the query cache above and for the same reason:
// the approve screen mounts and unmounts constantly and must not refetch this each time.
let tagVocabularyCache = null;
let tagVocabularyInFlight = null;

async function loadTagVocabulary() {
  if (tagVocabularyCache) return tagVocabularyCache;
  // Concurrent callers share one request rather than racing to make the same one — two taps
  // approved quickly both mount the editor.
  if (!tagVocabularyInFlight) {
    tagVocabularyInFlight = (async () => {
      const { data, error } = await supabase.from("tag_vocabulary").select("slug, label").order("label");
      if (error) {
        // Tags still display as their slug and existing ones still save; only the picker is
        // unavailable. Not worth interrupting an approval over.
        console.warn("Tag vocabulary unavailable:", error.message);
        return [];
      }
      tagVocabularyCache = data || [];
      return tagVocabularyCache;
    })().finally(() => { tagVocabularyInFlight = null; });
  }
  return tagVocabularyInFlight;
}

// What a tag is CALLED, which is not what it is matched on.
//
// tag_vocabulary.label is the text that gets embedded, so it is deliberately stuffed with
// exemplars — 'cutlery, silverware, forks, knives and spoons' is what makes a query for
// "silverware" land on the cutlery term. Rendering that as a chip produced the wall of
// near-identical sentences QA reported, sorted alphabetically and unskimmable.
//
// The slug is the curated short name and every one of the 121 terms is one or two words, so
// the display name is the slug with its hyphens opened out. No lookup, which also means an
// item's tags render anywhere without the vocabulary having loaded. A tag the user typed is the
// same story: the slug is their own words, so opening the hyphens gives them back.
function tagLabel(slug) {
  const words = String(slug || "").replace(/-/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : "";
}

// Whether a tag is one of ours or one the user typed.
//
// Both live in the same `nodes.tags` array and neither is second-class to the user, but the two are
// worth telling apart because they can do different things for search. A curated term has a row in
// `tag_vocabulary` with an embedded label full of exemplars, which is what makes a query for
// "silverware" reach a spork tagged `cutlery`. A tag someone invented has no embedding — giving every
// user-typed word one would mean an OpenAI call per tag, a writable path into global reference data,
// and a term nobody else can benefit from — so it is matched on its words instead, by search.js,
// where "wrapping paper" finds the thing tagged `wrapping-paper` regardless.
//
// So: ours help the fuzzy half of search, yours help the literal half, and the chip is drawn
// differently so the item screen is honest about which is which.
function tagIsCurated(vocabulary, slug) {
  return (vocabulary || []).some((entry) => entry.slug === slug);
}

// The exemplars, for a dimmed second line in the picker: they are the reason a term is the
// right one, and they are what the filter box actually searches.
function tagExamples(vocabulary, slug) {
  const found = (vocabulary || []).find((entry) => entry.slug === slug);
  if (!found) return "";
  // Labels come in two shapes: 'worn accessories: hats, scarves' and 'cutlery, silverware,
  // forks'. Either way what is wanted is the list, minus the short name already shown above.
  const label = String(found.label || "");
  const afterColon = label.includes(":") ? label.slice(label.indexOf(":") + 1) : label;
  return afterColon.trim();
}

// Matches nodes_tags_check in the migration. Enforced here too so the UI stops offering a
// twelfth tag rather than letting the save fail on a constraint the user cannot see.
const MAX_ITEM_TAGS = 12;

// Typed words to a tag slug. Mirrors slugifyTag in detect.js, which is the authority — this copy
// exists so the chip appears the instant it is typed rather than after a round trip, and so the
// picker can tell "you already have this one" before sending anything.
const MAX_TAG_LENGTH = 40;
function slugifyTag(text) {
  return String(text || "")
    .toLowerCase()
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_TAG_LENGTH)
    .replace(/-+$/g, "");
}

// Chip captions for the label styles vlm.js returns. The style ids ARE these words now — the schema, the
// prompt and the buttons all say short/balanced/detailed — so this only supplies the capital letter, and
// a style it does not recognise (an old event replayed, a model inventing one) falls back to its own id.
const VARIANT_STYLE_NAMES = { short: "Short", balanced: "Balanced", detailed: "Detailed" };

// Pick a tag: one of ours, or one of yours.
//
// The curated list is a filter box rather than a long scroll because 121 terms is too many to skim
// and far too few to need anything cleverer than a substring match. Rows, not chips: the chip grid QA
// saw was unreadable because the chips were the long embedding text and a wrapped grid of ragged
// multi-word chips has no column to run your eye down. Each row is the short name with its examples
// dimmed underneath, and the examples are what the filter searches, so typing "silverware" finds
// Cutlery.
//
// Typing something we do not have offers to add it as it is. Both kinds are stored the same way, in
// `nodes.tags`, and the difference is what search can do with them — see the note on `tagIsCurated`.
function TagPicker({ vocabulary, chosen, onPick, onClose }) {
  const [filter, setFilter] = useState("");
  const available = useMemo(() => {
    const taken = new Set(chosen || []);
    const needle = normalizeText(filter);
    return (vocabulary || [])
      .filter((entry) => !taken.has(entry.slug))
      .filter((entry) => !needle
        || normalizeText(entry.label).includes(needle)
        || normalizeText(tagLabel(entry.slug)).includes(needle))
      .slice(0, 40);
  }, [vocabulary, chosen, filter]);

  // What typing this would add, when it is not already on the item and is not one of ours under a
  // different spelling ("Wrapping Paper" and "wrapping-paper" are the same tag).
  const typedSlug = slugifyTag(filter);
  const ours = useMemo(() => new Set((vocabulary || []).map((entry) => entry.slug)), [vocabulary]);
  const offerCustom = typedSlug && !ours.has(typedSlug) && !(chosen || []).includes(typedSlug);

  return (
    <div style={{ marginTop: 10, border: "1px solid var(--line)", borderRadius: 14, padding: 10, background: "var(--surface)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input style={{ ...S.input, fontSize: 15, padding: "11px 13px", flex: 1 }}
          placeholder="Search or type your own" value={filter} autoFocus
          aria-label="Search or type your own tag"
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && offerCustom) onPick(typedSlug); }} />
        <button type="button" style={S.iconBtn} onClick={onClose} aria-label="Close tags">
          <X size={18} />
        </button>
      </div>
      <div style={{ marginTop: 8, maxHeight: 210, overflowY: "auto" }}>
        {offerCustom && (
          <button type="button" onClick={() => onPick(typedSlug)}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 8, textAlign: "left",
              background: "none", border: "none", borderRadius: 10, padding: "9px 8px",
              cursor: "pointer", fontFamily: "inherit", color: "inherit",
            }}>
            <Plus size={14} sw={2.6} extra={{ color: "var(--accent-ink)", flexShrink: 0 }} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>
                Add &ldquo;{tagLabel(typedSlug)}&rdquo;
              </span>
              <span style={{ display: "block", fontSize: 12, color: "var(--ink-soft)", marginTop: 1 }}>
                your own tag
              </span>
            </span>
          </button>
        )}
        {available.map((entry) => (
          <button key={entry.slug} type="button" onClick={() => onPick(entry.slug)}
            style={{
              width: "100%", display: "block", textAlign: "left", background: "none",
              border: "none", borderRadius: 10, padding: "9px 8px", cursor: "pointer",
              fontFamily: "inherit", color: "inherit",
            }}>
            <span style={{ display: "block", fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>
              {tagLabel(entry.slug)}
            </span>
            <span style={{ display: "block", fontSize: 12, color: "var(--ink-soft)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {tagExamples(vocabulary, entry.slug)}
            </span>
          </button>
        ))}
        {!available.length && !offerCustom && (
          <div style={{ fontSize: 12.5, color: "var(--ink-soft)", padding: "4px 8px" }}>
            {filter ? "Nothing matches that." : "Type a word to add your own tag."}
          </div>
        )}
      </div>
    </div>
  );
}

// Flipping through a node's images: the thing itself, then the extra photos that lead to it.
//
// One carousel, not a hero plus a filmstrip. The split version put the item's own photo above and
// the extra photos in their own section below, on the reasoning that "what this is" and "where it
// is" are different questions — but to the person looking, they are all photos of one thing, and
// having two places to swipe made the second easy to miss entirely.
//
// The primary frame keeps its crosshair, drawn from its tap coordinates by MarkedImg, so slide one
// still says which object in the picture was identified.
//
// Renders nothing at all for an empty list, and no controls for a single image — a carousel with
// one slide is just an image wearing buttons, and most items have exactly one.
function ImageFlipper({ images, alt, onRemove, height = 210 }) {
  const [index, setIndex] = useState(0);
  const count = images?.length || 0;
  // Dropping the last frame, or opening a shorter list than last time, must not leave the index
  // pointing past the end.
  const at = Math.min(index, Math.max(0, count - 1));
  useEffect(() => { if (index !== at) setIndex(at); }, [index, at]);
  if (!count) return null;
  const current = images[at];
  // Which reference this is, counted among the non-primary frames only, so the badge matches the
  // numbering on the camera's REF strip rather than the slide position.
  const referenceNo = current.is_primary ? 0 : images.slice(0, at + 1).filter((img) => !img.is_primary).length;

  return (
    <div>
      <div style={{ position: "relative", borderRadius: 16, overflow: "hidden", background: "var(--bg-deep)", height }}>
        <MarkedImg src={current.url} tap={current.tap} alt={current.is_primary ? alt : `${alt} — reference ${referenceNo}`}
          style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        {!current.is_primary && (
          <span style={S.trailBadge}>Reference {referenceNo}</span>
        )}
        {count > 1 && (
          <>
            <button type="button" aria-label="Previous image" style={{ ...S.flipBtn, left: 8 }}
              disabled={at === 0} onClick={() => setIndex(at - 1)}>
              <ChevronLeft size={20} />
            </button>
            <button type="button" aria-label="Next image" style={{ ...S.flipBtn, right: 8 }}
              disabled={at >= count - 1} onClick={() => setIndex(at + 1)}>
              <ChevronRight size={20} />
            </button>
            <span style={S.flipCount}>{at + 1} / {count}</span>
          </>
        )}
        {onRemove && !current.is_primary && (
          <button type="button" aria-label="Remove this photo" style={S.trailRemoveBtn}
            onClick={() => onRemove(current.id)}>
            <X size={15} sw={2.6} />
          </button>
        )}
      </div>
      {count > 1 && (
        <div style={{ display: "flex", gap: 6, marginTop: 8, overflowX: "auto", paddingBottom: 2 }}>
          {images.map((img, i) => (
            <button key={img.id ?? i} type="button" onClick={() => setIndex(i)}
              aria-label={`Image ${i + 1}`} aria-current={i === at}
              style={{
                flexShrink: 0, width: 46, height: 46, borderRadius: 9, overflow: "hidden", padding: 0,
                cursor: "pointer", background: "var(--bg-deep)",
                border: i === at ? "2px solid var(--accent)" : "1px solid var(--line)",
              }}>
              <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Tags on a thing: the chips, the remove buttons, and the picker. One component because
// the approve screen and the item screen must not drift apart — approve was the only place they
// could be edited, so an item's tags were unreviewable ten seconds after it was saved.
//
// `onChange` receives the whole next list. The caller decides whether that means local state
// (approve, which commits on Approve) or an immediate write (item detail).
function TagEditor({ tags, vocabulary, onChange, busy, readOnly = false }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const list = Array.isArray(tags) ? tags : [];
  return (
    <div>
      {/* Same weight, size and left edge as every other field heading on these screens. It was a
          smaller bespoke size with an explanatory note after it ("helps search find this"), which
          made it read as a caption for the chips rather than as a field, and stated something the
          user cannot act on. */}
      <div style={{ ...S.fieldLabel, marginBottom: 8 }}>Tags</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, alignItems: "center" }}>
        {list.map((slug) => {
          const curated = tagIsCurated(vocabulary, slug);
          return (
          <span key={slug} style={{
            ...S.chipSuggest, paddingRight: readOnly ? undefined : 8,
            borderColor: curated ? "var(--accent-line)" : "var(--line-strong)",
            background: curated ? "var(--accent-soft)" : "transparent",
          }}>
            {tagLabel(slug)}
            {!readOnly && (
              <button type="button" aria-label={`Remove ${tagLabel(slug)}`} disabled={busy}
                onClick={() => onChange(list.filter((s) => s !== slug))}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  marginLeft: 2, padding: 2, border: "none", background: "none",
                  color: "var(--ink-soft)", cursor: "pointer", fontFamily: "inherit",
                }}>
                <X size={13} sw={2.6} />
              </button>
            )}
          </span>
          );
        })}
        {readOnly && !list.length && (
          <span style={{ fontSize: 13, color: "var(--ink-faint)" }}>None yet</span>
        )}
        {/* Deliberately not a chip. Styled as one, it read as a tag called "Add a tag" — and as
            "Close" once the picker was open, which is what QA reported as a tag having been added. It
            says what it adds, because the manual add form has an Add of its own in the footer and two
            bare ones on a screen is a coin toss. */}
        {!readOnly && !pickerOpen && list.length < MAX_ITEM_TAGS && (
          <button type="button" disabled={busy} onClick={() => setPickerOpen(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4, background: "none",
              border: "none", padding: "4px 2px", cursor: "pointer", fontFamily: "inherit",
              fontSize: 13, fontWeight: 600, color: "var(--accent-ink)",
            }}>
            <Plus size={14} sw={2.6} /> Add tag
          </button>
        )}
        {busy && <Loader size={14} extra={{ color: "var(--ink-soft)" }} />}
      </div>
      {!readOnly && pickerOpen && (
        <TagPicker
          vocabulary={vocabulary}
          chosen={list}
          onClose={() => setPickerOpen(false)}
          onPick={(slug) => {
            setPickerOpen(false);
            if (list.includes(slug) || list.length >= MAX_ITEM_TAGS) return;
            onChange([...list, slug]);
          }}
        />
      )}
    </div>
  );
}

// id -> parent id for items and locations together, which is what the location-agreement list
// walks. Built from the loaded tree rather than fetched: the client already holds both.
function parentIndex(locations, items) {
  const parentOf = new Map();
  for (const location of locations || []) {
    if (location.parent_location_id) parentOf.set(location.id, location.parent_location_id);
  }
  for (const item of items || []) {
    if (item.location_id) parentOf.set(item.id, item.location_id);
  }
  return parentOf;
}

// Read a picked/photographed file as a data URL, capped to something sensible to upload.
// A phone camera file is several megabytes and none of that detail survives being rendered
// at thumbnail size, so it is shrunk before it ever goes near the network.
function fileToUploadableDataUrl(file, maxLongSide = 1600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read that file"));
    reader.onload = () => resolve(shrinkForUpload(reader.result, maxLongSide, 0.82));
    reader.readAsDataURL(file);
  });
}

// Read-only "who can see this node" incl. inherited-vs-direct. Returns [{ user_id, email,
// display_name, role, is_direct, source_node, source_name }], access-gated server-side.
// Returns NULL on error (e.g. migration not run) so callers can surface it — an empty
// array means "genuinely nobody", which is different from "the query failed".
async function loadNodeAccess(nodeId) {
  if (!nodeId) return [];
  const { data, error } = await supabase.rpc("node_access_list", { p_node: nodeId });
  if (error) { console.error("node_access_list failed:", error); return null; }
  return data || [];
}

async function createInvitation(spaceId, email, role) {
  return await callFunction({ mode: "create_invitation", node_id: spaceId, email, role });
}
async function acceptInvitation(invitationId) {
  return await callFunction({ mode: "accept_invitation", invitation_id: invitationId });
}
async function declineInvitation(invitationId) {
  return await callFunction({ mode: "decline_invitation", invitation_id: invitationId });
}

// Get visibility-gated signed image URLs for a set of item ids.
// Returns { [itemId]: signedUrl|null }.
// Signed-URL cache: the server signs image URLs for 7 DAYS, but re-requesting fresh
// signatures every boot produced a NEW url string each time — defeating the browser's
// image cache, so every refresh re-downloaded every image. Reuse still-valid URLs from
// localStorage (24h safety margin) and only ask the server for the rest. Keyed by node
// id (a node's primary image never changes today). Nulls (no access/no image) are NOT
// cached so newly-shared images appear promptly. Fails open in private mode.
const SIGNED_URL_CACHE_KEY = "magicshelf_signed_urls_v1";
const SIGNED_URL_TTL_MS = 7 * 24 * 3600 * 1000;        // matches server createSignedUrl TTL
const SIGNED_URL_REUSE_MARGIN_MS = 24 * 3600 * 1000;   // don't reuse within 24h of expiry
function readSignedUrlCache() {
  try { return JSON.parse(localStorage.getItem(SIGNED_URL_CACHE_KEY) || "{}") || {}; } catch { return {}; }
}
function writeSignedUrlCache(cache) {
  try { localStorage.setItem(SIGNED_URL_CACHE_KEY, JSON.stringify(cache)); } catch { /* private mode */ }
}

async function signImages(itemIds) {
  if (!itemIds || !itemIds.length) return {};
  const now = Date.now();
  const cache = readSignedUrlCache();
  const out = {};
  const need = [];
  for (const id of itemIds) {
    const e = cache[id];
    if (e && e.url && (e.exp - now) > SIGNED_URL_REUSE_MARGIN_MS) out[id] = e.url;
    else need.push(id);
  }
  if (need.length) {
    try {
      const res = await callFunction({ mode: "sign_images", node_ids: need });
      const urls = res.urls || {};
      let dirty = false;
      for (const [id, url] of Object.entries(urls)) {
        out[id] = url;
        if (url) { cache[id] = { url, exp: now + SIGNED_URL_TTL_MS }; dirty = true; }
      }
      if (dirty) {
        for (const k of Object.keys(cache)) { if (!cache[k] || cache[k].exp <= now) { delete cache[k]; } }
        writeSignedUrlCache(cache);
      }
    } catch (e) {
      console.error("signImages error:", e);
    }
  }
  return out;
}

// ── Member management reads (direct queries; old RPCs were dropped) ──
async function loadSpacePendingInvites(spaceId) {
  const { data, error } = await supabase
    .from("invitations")
    .select("id, email, role, created_at, expires_at")
    .eq("node_id", spaceId)
    .is("accepted_at", null);
  if (error) { console.error("pending invites error:", error); return []; }
  return data || [];
}
async function loadIdentities(userIds) {
  const ids = Array.from(new Set((userIds || []).filter(Boolean)));
  if (!ids.length) return {};
  // SECURITY DEFINER RPC: returns identities for yourself + anyone you co-share a node with.
  const { data, error } = await supabase.rpc("identities_for", { p_user_ids: ids });
  if (error) { console.error("identities error:", error); return {}; }
  const map = {};
  for (const r of (data || [])) map[r.user_id] = { email: r.email, display_name: r.display_name };
  return map;
}

// ── Member management writes (function modes) ──
async function setMemberRole(spaceId, targetUserId, role) {
  return await callFunction({ mode: "set_member_role", node_id: spaceId, target_user_id: targetUserId, role });
}
async function removeMember(spaceId, targetUserId) {
  return await callFunction({ mode: "remove_member", node_id: spaceId, target_user_id: targetUserId });
}
async function revokeInvitation(invitationId) {
  return await callFunction({ mode: "revoke_invitation", invitation_id: invitationId });
}
async function leaveSpace(spaceId) {
  return await callFunction({ mode: "leave_node", node_id: spaceId });
}
async function setDisplayName(name) {
  return await callFunction({ mode: "set_display_name", display_name: name });
}
async function adminInviteTester(email) {
  return await callFunction({ mode: "admin_invite_tester", email });
}
async function submitFeedback({ text, screen, context }) {
  return await callFunction({ mode: "submit_feedback", text, screen, context });
}

// Friendly label for a user identity: display name if set, else email, else fallback.
function identityLabel(idObj, fallback = "someone") {
  if (!idObj) return fallback;
  return (idObj.display_name && idObj.display_name.trim()) || idObj.email || fallback;
}

// Pending invitations addressed to the current user (by email). MUST filter by the
// user's own email explicitly: the invitations RLS SELECT policy also returns invites
// the user can MANAGE (owners/managers see invites they sent) — so trusting RLS alone
// would show an owner the invites they sent to OTHER people in their own accept/decline
// inbox. We only ever want invites addressed TO this user here.
// Pending invitations addressed to the current user, WITH the location name. Uses the
// my_pending_invitations() SECURITY DEFINER RPC: the invitee isn't a member of the node
// yet, so a normal nodes join is hidden by RLS (name came back empty → "a location").
// The RPC filters to the user's own email server-side and returns the real name safely.
async function loadInvitations(myEmail) {
  const email = (myEmail || "").trim().toLowerCase();
  if (!email) return []; // no email → can't safely scope; show nothing
  const { data, error } = await supabase.rpc("my_pending_invitations");
  if (error) { console.error("loadInvitations error:", error); return []; }
  const now = new Date();
  return (data || [])
    .filter((inv) => !inv.expires_at || new Date(inv.expires_at) > now)
    .map((inv) => ({
      id: inv.id, role: inv.role,
      location_id: inv.node_id,        // UI still keys on location_id
      node_id: inv.node_id,
      space_name: inv.space_name || "a location",
      expires_at: inv.expires_at,
    }));
}

// Draws the yellow scan crosshair on a canvas context at normalized (x,y). Shared by
// annotateImage (scan time) and markImage (display time) so the marker looks identical
// everywhere. Geometry scales to the image so it reads at any size.
function drawCrosshair(ctx, x, y, w, h) {
  const cx = x * w, cy = y * h;
  const radius = Math.max(28, Math.round(Math.min(w, h) * 0.07));
  const cross = radius * 0.65;
  const lineW = Math.max(3, Math.round(Math.min(w, h) * 0.006));
  ctx.lineWidth = lineW + 4;
  ctx.strokeStyle = "rgba(0,0,0,0.85)";
  ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI * 2); ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - cross, cy); ctx.lineTo(cx + cross, cy);
  ctx.moveTo(cx, cy - cross); ctx.lineTo(cx, cy + cross);
  ctx.stroke();
  ctx.lineWidth = lineW;
  ctx.strokeStyle = "#FFD400";
  ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI * 2); ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - cross, cy); ctx.lineTo(cx + cross, cy);
  ctx.moveTo(cx, cy - cross); ctx.lineTo(cx, cy + cross);
  ctx.stroke();
  ctx.fillStyle = "#FFD400";
  ctx.beginPath(); ctx.arc(cx, cy, Math.max(2, lineW * 0.8), 0, Math.PI * 2); ctx.fill();
}

async function annotateImage(videoOrImage, x, y) {
  let srcW, srcH, draw;
  if (videoOrImage instanceof HTMLVideoElement) {
    srcW = videoOrImage.videoWidth; srcH = videoOrImage.videoHeight;
    draw = (ctx, w, h) => ctx.drawImage(videoOrImage, 0, 0, w, h);
  } else if (videoOrImage instanceof HTMLImageElement) {
    srcW = videoOrImage.naturalWidth; srcH = videoOrImage.naturalHeight;
    draw = (ctx, w, h) => ctx.drawImage(videoOrImage, 0, 0, w, h);
  } else throw new Error("Bad source");
  if (!srcW || !srcH) throw new Error("Source not ready");
  const scale = Math.min(1, MAX_IMAGE_LONG_SIDE / Math.max(srcW, srcH));
  const w = Math.round(srcW * scale);
  const h = Math.round(srcH * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  draw(ctx, w, h);
  // Capture the CLEAN (unmarked) frame first — this is what we persist. The marked
  // version is derived (frame + tap_x/tap_y) and only needed for the VLM + live UI, so
  // we never store it; it's reconstructable from the clean frame + coordinates.
  const cleanDataUrl = canvas.toDataURL("image/jpeg", 0.88);
  drawCrosshair(ctx, x, y, w, h);
  return { dataUrl: canvas.toDataURL("image/jpeg", 0.88), cleanDataUrl, width: w, height: h };
}

// Re-mark a STORED (clean) image at display time: load the image, draw the crosshair from
// its tap coordinates, return a marked data URL. Cached per (url + coords) so each item is
// only re-encoded once, not on every render. This is why the stored frame stays clean
// (good training data) while every display still shows the marker — one marked image,
// used everywhere like a normal src.
const _markCache = new Map();
function markImage(url, tapX, tapY) {
  if (!url || tapX == null || tapY == null) return Promise.resolve(url);
  const key = `${url}|${tapX.toFixed(4)}|${tapY.toFixed(4)}`;
  if (_markCache.has(key)) return _markCache.get(key);
  const p = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const w = img.naturalWidth, h = img.naturalHeight;
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        drawCrosshair(ctx, tapX, tapY, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.88));
      } catch {
        // Tainted canvas or draw failure → fall back to the clean image (still shows,
        // just without a marker). Never block the UI on marking.
        resolve(url);
      }
    };
    img.onerror = () => resolve(url);
    img.src = url;
  });
  _markCache.set(key, p);
  return p;
}

// Hook: given a clean image URL + tap coords, returns the crosshair-marked URL once ready,
// falling back to the clean image until then (and if marking fails). Keeps display sites simple.
function useMarkedImage(image, tap) {
  const [marked, setMarked] = useState(image);
  useEffect(() => {
    let alive = true;
    if (image && tap && tap.x != null && tap.y != null) {
      markImage(image, tap.x, tap.y).then((u) => { if (alive) setMarked(u); }).catch(() => { if (alive) setMarked(image); });
    } else {
      setMarked(image);
    }
    return () => { alive = false; };
  }, [image, tap && tap.x, tap && tap.y]);
  return marked;
}

// A plain <img> that shows the crosshair-marked version of a stored image. Drop-in for
// <img>: pass the clean src + tap coords, it renders the marked result (clean until ready).
function MarkedImg({ src, tap, alt = "", style, ...rest }) {
  const marked = useMarkedImage(src, tap);
  return <img src={marked} alt={alt} style={style} {...rest} />;
}

// Spaces are now ROOT locations (parent_location_id IS NULL) in the unified locations tree.
// ── NODE-MODEL LOADERS ──────────────────────────────────────────────────────
// The DB is now one `nodes` table (kind 'location'|'item', parent_id). To avoid
// rewriting the whole UI, the loaders emit the SAME shapes the UI already uses:
//   space/location rows carry `parent_location_id` (mapped from parent_id)
//   item rows carry `label` (from name), `location_id` (from parent_id),
//     `raw_image_path` (from the primary node_images row).

// Human-readable mutation errors. RLS/permission failures should read like a
// policy ("you can't"), not a bug. Everything else keeps the original message.
function friendlyError(error, action) {
  const msg = (error && (error.message || error.code)) || "";
  if (/row-level security|permission denied|not allowed|42501/i.test(String(msg))) {
    return `You don't have permission to ${action} here — ask the owner for editor access.`;
  }
  return `Couldn't ${action}: ` + (msg || "try again");
}

// The signed-in user's own membership rows (one cheap query at boot). Powers
// client-side role checks (canEdit gating) via an ancestor walk over the loaded
// location tree. Returns NULL on failure so callers can fail OPEN (server still enforces).
async function loadMyMemberships(userId) {
  if (!userId) return {};
  const { data, error } = await supabase.from("node_members")
    .select("node_id, role").eq("user_id", userId);
  if (error) { console.error("loadMyMemberships failed:", error); return null; }
  const map = {};
  (data || []).forEach((r) => { map[r.node_id] = r.role; });
  return map;
}

// Same problem the item columns have, for the same reason: dev and production share one database, so
// this code can be live before its migration is. `icon` absent means locations draw the name-based
// guess, which is what they did before the column existed — so the read drops it and carries on
// rather than failing and leaving the user with no locations at all.
const OPTIONAL_LOCATION_COLUMNS = [
  { name: "icon", warning: "chosen location icons will fall back to the guessed ones" },
];

// Run `query(columns)` with the optional columns, dropping any the database says it does not have.
// At most one retry per column and each retry has strictly fewer, so this terminates.
async function selectTolerantly(baseColumns, query) {
  let optional = OPTIONAL_LOCATION_COLUMNS.map((column) => column.name);
  for (;;) {
    const { data, error } = await query([baseColumns, ...optional].join(", "));
    if (!error) return { data, error: null };
    const absent = optional.find((name) => isMissingColumn(error, name));
    if (!absent) return { data, error };
    const { warning } = OPTIONAL_LOCATION_COLUMNS.find((column) => column.name === absent);
    console.warn(`nodes.${absent} missing — ${warning} until the migration runs.`);
    optional = optional.filter((name) => name !== absent);
  }
}

async function loadSpaces() {
  const { data, error } = await selectTolerantly(
    "id, name, created_at, last_used_at, parent_id, owner_id, permission_settings",
    (columns) => supabase
      .from("nodes")
      .select(columns)
      .eq("kind", "location")
      .is("parent_id", null)
      .is("deleted_at", null)
      .order("last_used_at", { ascending: false }),
  );
  if (error) { console.error(error); return []; }
  return (data || []).map((n) => ({
    id: n.id, name: n.name, created_at: n.created_at, last_used_at: n.last_used_at,
    parent_location_id: null, owner_id: n.owner_id, icon: n.icon || null,
    permission_settings: n.permission_settings || {},
  }));
}

// Load ALL the user's item nodes. Views filter by root-location (space) in-memory
// using the locations tree. Emits the UI's item shape (label, location_id, image).
const ITEM_COLUMNS = "id, name, parent_id, owner_id, added_by, created_at, deleted_at";
// Columns that may not exist yet, because dev and production share one database and this code
// can deploy before its migration. An explicit column list means one absent column fails the
// whole read, so each is dropped on demand and the shelf still loads — degraded in that one
// respect rather than empty.
const OPTIONAL_ITEM_COLUMNS = [
  { name: "quantity", warning: "item counts will read as 1" },
  { name: "tags", warning: "tag search will find nothing" },
];
async function loadItems() {
  const fetchItems = (columns) => supabase
    .from("nodes")
    .select(columns)
    .eq("kind", "item")
    .neq("status", "rejected")
    .is("deleted_at", null);

  let optional = OPTIONAL_ITEM_COLUMNS.map((column) => column.name);
  let rows;
  let error;
  // At most one retry per optional column, and each retry has strictly fewer of them, so this
  // cannot loop: it either succeeds, or runs out of columns to blame and reports the error.
  for (;;) {
    ({ data: rows, error } = await fetchItems([ITEM_COLUMNS, ...optional].join(", ")));
    if (!error) break;
    const absent = optional.find((name) => isMissingColumn(error, name));
    if (!absent) break;
    const { warning } = OPTIONAL_ITEM_COLUMNS.find((column) => column.name === absent);
    console.warn(`nodes.${absent} missing — ${warning} until the migration runs.`);
    optional = optional.filter((name) => name !== absent);
  }
  if (error) {
    console.error("loadItems error:", error);
    alert("Couldn't load items: " + (error.message || error.code || "see console"));
    return [];
  }
  const itemRows = rows || [];
  itemRows.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  return await hydrateItemNodes(itemRows);
}

// Given raw item nodes, attach primary-image paths + signed URLs and emit UI shape.
async function hydrateItemNodes(itemRows) {
  if (!itemRows.length) return [];
  const ids = itemRows.map((r) => r.id);
  // primary image path + tap coordinates per node (coords let us redraw the marker at
  // display time — the stored frame is unmarked now, so the crosshair is reconstructed
  // from tap_x/tap_y rather than baked into the image).
  const pathByNode = {};
  const tapByNode = {};
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    const { data: imgs } = await supabase
      .from("node_images")
      .select("node_id, image_path, is_primary, tap_x, tap_y")
      .in("node_id", chunk)
      .eq("is_primary", true);
    (imgs || []).forEach((im) => {
      pathByNode[im.node_id] = im.image_path;
      if (im.tap_x != null && im.tap_y != null) tapByNode[im.node_id] = { x: im.tap_x, y: im.tap_y };
    });
  }
  const withImg = itemRows.filter((r) => pathByNode[r.id]);
  const urlMap = await signImages(withImg.map((r) => r.id));
  // Location names for display, and the full ancestor path for search. parent_id comes
  // along so the path can be walked: without it, searching "garage drill" could never find
  // a drill on a shelf inside the garage, which was the single biggest search complaint.
  const locById = {};
  const { data: locs } = await supabase.from("nodes").select("id, name, parent_id").eq("kind", "location");
  (locs || []).forEach((l) => { locById[l.id] = l; });
  const pathCache = {};
  const pathFor = (id) => {
    if (pathCache[id] !== undefined) return pathCache[id];
    const names = [];
    const seen = new Set();
    let cur = locById[id];
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      names.unshift(cur.name);
      cur = cur.parent_id ? locById[cur.parent_id] : null;
    }
    pathCache[id] = names.join(" > ");
    return pathCache[id];
  };
  return itemRows.map((row) => ({
    id: row.id,
    label: row.name,
    location_id: row.parent_id,
    location: row.parent_id ? (locById[row.parent_id]?.name || "") : "",
    location_path: row.parent_id ? pathFor(row.parent_id) : "",
    quantity: cleanQuantity(row.quantity),
    tags: Array.isArray(row.tags) ? row.tags : [],
    owner_id: row.owner_id, added_by: row.added_by, created_at: row.created_at,
    image: urlMap[row.id] || null,
    raw_image_path: pathByNode[row.id] || null,
    tap: tapByNode[row.id] || null,
  }));
}

async function loadLocations() {
  const { data, error } = await selectTolerantly(
    "id, name, parent_id, last_used_at, owner_id",
    (columns) => supabase
      .from("nodes")
      .select(columns)
      .eq("kind", "location")
      .is("deleted_at", null)
      .order("last_used_at", { ascending: false }),
  );
  if (error) { console.error(error); return []; }
  const rows = data || [];
  // Some locations carry a photo (they were created from an item, or scanned as a
  // location). Fetch primary-image paths only for those that have one, then sign just
  // those — most locations have no image, so this stays cheap.
  const pathByNode = {};
  const ids = rows.map((r) => r.id);
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    const { data: imgs } = await supabase
      .from("node_images")
      .select("node_id, image_path, is_primary")
      .in("node_id", chunk)
      .eq("is_primary", true);
    (imgs || []).forEach((im) => { pathByNode[im.node_id] = im.image_path; });
  }
  const withImgIds = rows.filter((r) => pathByNode[r.id]).map((r) => r.id);
  const urlMap = withImgIds.length ? await signImages(withImgIds) : {};
  return rows.map((n) => ({
    id: n.id, name: n.name, parent_location_id: n.parent_id,
    last_used_at: n.last_used_at, owner_id: n.owner_id, icon: n.icon || null,
    image: urlMap[n.id] || null,
    raw_image_path: pathByNode[n.id] || null,
  }));
}

// Tree helpers: locations is a flat array; we work with parent_location_id.

// Mirrors the server's can_manage_node_members rule (migration 20260706000000):
// true if the user owns this location OR any ancestor ("your house, your rules").
function ownsNodeOrAncestor(locations, locationId, userId) {
  let cur = locationId; const seen = new Set();
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    const n = getLocationById(locations, cur);
    if (!n) return false;
    if (n.owner_id === userId) return true;
    cur = n.parent_location_id;
  }
  return false;
}

function getLocationById(locations, id) {
  return locations.find((l) => l.id === id) || null;
}

// Walk a location up to its root (the space it belongs to). Returns the root location id.
function rootLocationIdOf(locations, locationId) {
  let cur = getLocationById(locations, locationId);
  let guard = 0;
  while (cur && cur.parent_location_id && guard < 50) {
    cur = getLocationById(locations, cur.parent_location_id);
    guard++;
  }
  return cur ? cur.id : null;
}

// Build the path from root to this location, as an array of {id, name}.
function locationPath(locations, locationId) {
  const path = [];
  const seen = new Set();
  let cur = getLocationById(locations, locationId);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    path.unshift({ id: cur.id, name: cur.name });
    cur = cur.parent_location_id ? getLocationById(locations, cur.parent_location_id) : null;
  }
  return path;
}

// Display string for a path, e.g. "Basement > Workbench > Top shelf"
function pathDisplay(path) {
  return path.map((p) => p.name).join(" > ");
}

// Nearest photo up the location chain: the location's own image, or — if it has none —
// the closest ancestor that does. Lets an item in a photoless "drawer" show the parent
// "dresser" photo. Returns { image, inherited } (inherited=true when it came from an
// ancestor, not the item's own location), or null when nothing in the chain has a photo.
function nearestLocationImage(locations, locationId) {
  const seen = new Set();
  let cur = getLocationById(locations, locationId);
  let inherited = false;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    if (cur.image) return { image: cur.image, inherited };
    inherited = true; // anything found from here up is an ancestor's photo
    cur = cur.parent_location_id ? getLocationById(locations, cur.parent_location_id) : null;
  }
  return null;
}

// How the accepted name came about, recorded on the save event. "vlm_short" is what the
// server assumes, so only the other two are interesting.
function labelSourceFor(tap, finalLabel) {
  const accepted = normalizeText(finalLabel);
  if (accepted === normalizeText(tap.label)) return "vlm_short";
  const alts = Array.isArray(tap.alternative_labels) ? tap.alternative_labels : [];
  if (alts.some((a) => normalizeText(a) === accepted)) return "vlm_alternative";
  const variants = Array.isArray(tap.label_variants) ? tap.label_variants : [];
  if (variants.some((v) => normalizeText(v?.label) === accepted)) return "vlm_variant";
  return "user_edited";
}

// The queue-entry fields a scan result contributes. Shared by the first attempt and the retry
// path so a new field cannot be added to one and forgotten in the other, which is how
// `alternative_labels` came to be spelled out three times.
function tapFieldsFromScan(result) {
  return {
    status: "done",
    label: result.label,
    confidence: result.confidence,
    is_location_guess: result.is_location === true,
    predicted_sublocations: Array.isArray(result.predicted_sublocations) ? result.predicted_sublocations : [],
    alternative_labels: Array.isArray(result.alternative_labels) ? result.alternative_labels : [],
    label_variants: Array.isArray(result.label_variants) ? result.label_variants : [],
    tags: Array.isArray(result.tags) ? result.tags : [],
  };
}

// Assemble the scan context sent with context-aware prompt modes: where the scan is
// happening (name + full path) and what's already catalogued in that exact location.
// Works from any locationId (live session or a tap's capture-time stamp). Returns null
// when there's nothing useful to send.
function buildScanContext(locations, items, locationId, locationName, opts = {}) {
  if (!locationId) return locationName ? { location_name: locationName } : null;
  const loc = getLocationById(locations, locationId);
  const p = locationPath(locations, locationId);
  const sibling_labels = (items || [])
    .filter((it) => it.location_id === locationId && (it.label || "").trim())
    .slice(0, 25)
    .map((it) => it.label.trim());
  return {
    location_name: loc?.name || locationName || "",
    location_path: p.length ? pathDisplay(p) : (loc?.name || locationName || ""),
    sibling_labels,
    // Only built when the toggle is on: it goes into the prompt, so it is billed on every
    // single scan of the session, which is not worth paying for unless it was asked for.
    location_tree: opts.tree ? locationOutline(locations, locationId) : undefined,
  };
}

// The current space's locations as an indented outline, with the scan target marked. Gives
// the model the user's own vocabulary for places, which is what makes a suggested
// sub-location read like "Middle drawer" rather than "Compartment 2".
const OUTLINE_MAX_LINES = 40;
function locationOutline(locations, locationId) {
  const rootId = rootLocationIdOf(locations, locationId);
  if (!rootId) return "";
  const lines = [];
  const walk = (id, depth) => {
    if (lines.length >= OUTLINE_MAX_LINES) return;
    const node = getLocationById(locations, id);
    if (!node) return;
    const marker = id === locationId ? "> " : "- ";
    lines.push(`${"  ".repeat(depth)}${marker}${node.name}`);
    for (const child of sortedLocationChildren(locations, id)) walk(child.id, depth + 1);
  };
  walk(rootId, 0);
  return lines.join("\n");
}

// Search lives in ./search.js: it is pure, it is the thing QA called out as bad, and
// keeping it separate is what lets it be unit-tested instead of only eyeballed.

// All descendants (recursive children) of locationId, including itself.
function locationSubtree(locations, locationId) {
  const out = new Set();
  const stack = [locationId];
  while (stack.length) {
    const cur = stack.pop();
    if (out.has(cur)) continue;
    out.add(cur);
    for (const l of locations) {
      if (l.parent_location_id === cur) stack.push(l.id);
    }
  }
  return out; // Set of location ids
}

// Direct children of locationId (one level down).
function locationChildren(locations, locationId) {
  return locations.filter((l) => l.parent_location_id === locationId);
}

// Children in the order the UI lists them.
function sortedLocationChildren(locations, locationId) {
  return locationChildren(locations, locationId).sort((a, b) => a.name.localeCompare(b.name));
}

// Subtree item counts for every location at once, for the tree view.
//
// The list views each count one level at a time — filter the items by "is this location in that
// subtree" per row — which is fine for a handful of children and quadratic for a whole tree. This
// walks upward from each item's own location instead, adding one to every ancestor, so the whole
// tree costs one pass over the items times the depth.
//
// The depth guard is not defensive dressing: cyclic parent links have reached this database before
// and hung a recursive walk, which is why the SQL side has the same kind of bound.
function computeLocationItemCounts(locations, items) {
  const parentOf = new Map((locations || []).map((l) => [l.id, l.parent_location_id || null]));
  const counts = new Map();
  for (const it of items || []) {
    if (!it.location_id) continue;
    let cur = it.location_id;
    for (let hops = 0; cur && hops < 64; hops++) {
      counts.set(cur, (counts.get(cur) || 0) + 1);
      cur = parentOf.get(cur) || null;
    }
  }
  return counts;
}

// Item counts per space (root location), computed in-memory from items + locations.
// A space's count = items whose location's root ancestor is that space.
function computeSpaceStats(items, locations, spaceIds) {
  const counts = {};
  for (const sid of spaceIds) counts[sid] = 0;
  for (const it of items) {
    if (!it.location_id) continue;
    const root = rootLocationIdOf(locations, it.location_id);
    if (root && Object.prototype.hasOwnProperty.call(counts, root)) counts[root] += 1;
  }
  return counts;
}

// ─────────────────────────────────────────────────────────────────────
// Root + Auth
// ─────────────────────────────────────────────────────────────────────

function Root() {
  const [session, setSession] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [approval, setApproval] = useState("unknown"); // 'unknown' | 'approved' | 'denied'
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setAuthChecked(true); });
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession); setAuthChecked(true);
    });
    return () => subscription.subscription.unsubscribe();
  }, []);

  // App allowlist gate: once signed in, confirm the email is approved.
  // Unapproved users can't read approved_emails (RLS), so the query returns
  // nothing → treated as denied → signed out with a message.
  useEffect(() => {
    let cancelled = false;
    if (!session) { setApproval("unknown"); return; }
    const email = (session.user?.email || "").toLowerCase().trim();
    (async () => {
      const { data, error } = await supabase
        .from("approved_emails")
        .select("email")
        .eq("email", email)
        .maybeSingle();
      if (cancelled) return;
      if (!error && data) setApproval("approved");
      else setApproval("denied");
    })();
    return () => { cancelled = true; };
  }, [session]);

  if (!authChecked) return <div style={S.bootScreen}><Loader size={28} /></div>;
  if (!session) return <Login />;
  if (approval === "unknown") return <div style={S.bootScreen}><Loader size={28} /></div>;
  if (approval === "denied") return <NotApproved email={session.user?.email} />;
  return <App session={session} />;
}

function NotApproved({ email }) {
  return (
    <div className="screen" style={{ padding: "40px 22px", justifyContent: "center", textAlign: "center" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <h1 style={{ ...S.wordmark, fontSize: 32, marginBottom: 4 }}>MagicShelf</h1>
        <div style={{ width: 54, height: 54, borderRadius: 27, background: "rgba(220,53,69,0.1)", color: "var(--danger)", display: "grid", placeItems: "center", margin: "20px auto 14px" }}>
          <AlertCircle size={26} sw={2.2} />
        </div>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Not on the guest list yet</div>
        <p style={{ color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.55, maxWidth: 300 }}>
          {email ? <><strong style={{ color: "var(--ink)" }}>{email}</strong> isn't approved for access yet.</> : "This account isn't approved for access yet."}
          {" "}MagicShelf is invite-only right now.
        </p>
        <button style={{ ...S.secondaryBtn, marginTop: 22, width: "auto" }}
          onClick={async () => { await supabase.auth.signOut(); }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState(null);
  // The main way in: a CODE by email, not a magic link, so it cannot be trapped in an in-app
  // browser. Most accounts here were created for someone and have no password at all, so the
  // password field is the exception and lives behind a button — it used to be the field you met
  // first, which asked nearly everybody for something they had never been given. Signing in with a
  // code also stands in as the forgot-password path until a real reset flow exists.
  const [mode, setMode] = useState("code"); // "code" | "password" | "codeSent"
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const signIn = async () => {
    setSigningIn(true); setError(null);
    try {
      const { error: err } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      setSigningIn(false);
      if (err) {
        const msg = (err.message || "").toLowerCase();
        // Supabase returns the same "invalid login credentials" for both wrong password and
        // no-such-account (deliberately, so you can't probe which emails exist). Keep that.
        if (msg.includes("invalid login") || msg.includes("credentials")) {
          setError("Wrong email or password. If you don't have a password, use \u201cEmail me a code instead\u201d below.");
        } else if (msg.includes("email not confirmed")) {
          setError("This account isn't confirmed yet. Ask the owner to finish setting it up.");
        } else {
          setError(err.message || "Couldn't sign in. Try again.");
        }
      }
    } catch (e) {
      setSigningIn(false);
      setError(e?.message || "Couldn't sign in. Try again.");
    }
  };

  const sendCode = async () => {
    if (!email.includes("@")) { setError("Enter your email first, then request a code."); return; }
    setBusy(true); setError(null);
    const NOT_APPROVED_MSG = "This email isn't approved for access yet. MagicShelf is invite-only right now.";
    // With shouldCreateUser:true, a NON-approved email trips the allowlist trigger at
    // account creation, which GoTrue surfaces as a generic "database error saving new
    // user". Disambiguate via is_email_approved(): not approved -> honest invite-only
    // message; approved but still failing -> surface as a real error (not a fake "not
    // approved"). RPC unavailable -> safe default to the invite-only message.
    const classify = async (rawMsg) => {
      const msg = (rawMsg || "").toLowerCase();
      if (msg.includes("not approved") || msg.includes("allowlist")) return NOT_APPROVED_MSG;
      const generic =
        msg.includes("saving new user") ||
        msg.includes("database error") ||
        msg.includes("unexpected_failure") ||
        msg.includes("not allowed");
      if (generic) {
        try {
          const { data: approved, error: rpcErr } = await supabase.rpc("is_email_approved", { p_email: email.trim() });
          if (!rpcErr && approved === false) return NOT_APPROVED_MSG;
          if (!rpcErr && approved === true) {
            return "Something went wrong sending your code — this isn't an approval problem. Try again in a minute.";
          }
        } catch { /* RPC unavailable — fall through to the safe default below */ }
        return NOT_APPROVED_MSG;
      }
      return rawMsg || "Couldn't send a code. Check the email and try again.";
    };
    try {
      // shouldCreateUser:true — an approved person added through the app (allowlist) but
      // never provisioned can sign in here and have their account auto-created on first
      // code sign-in. NOT a backdoor: the allowlist trigger on auth.users insert rejects
      // any email not in approved_emails, so only approved emails can ever create one.
      const { error: err } = await supabase.auth.signInWithOtp({ email: email.trim(), options: { shouldCreateUser: true } });
      setBusy(false);
      if (err) setError(await classify(err.message));
      else { setMode("codeSent"); }
    } catch (e) { setBusy(false); setError(await classify(e?.message)); }
  };

  const verifyCode = async () => {
    setBusy(true); setError(null);
    try {
      const { error: err } = await supabase.auth.verifyOtp({ email: email.trim(), token: code.trim(), type: "email" });
      setBusy(false);
      if (err) setError(/expired|invalid|token/i.test(err.message || "")
        ? "That code is incorrect or expired. Request a new one."
        : (err.message || "Couldn't verify the code."));
    } catch (e) { setBusy(false); setError(e?.message || "Couldn't verify the code."); }
  };

  const canSubmit = email.includes("@") && password.length > 0 && !signingIn;
  const canSendCode = email.includes("@") && !busy;
  return (
    <div className="screen" style={{ padding: "40px 22px", justifyContent: "center" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginBottom: 28 }}>
        <h1 style={{ ...S.wordmark, fontSize: 36, marginBottom: 4 }}>MagicShelf</h1>
        <p style={{ ...S.tagline, fontSize: 14 }}>Sign in to continue</p>
      </div>

      {mode === "codeSent" ? (
        <>
          <div style={{ color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.5, marginBottom: 16, textAlign: "center" }}>
            We sent a code to <strong style={{ color: "var(--ink)" }}>{email}</strong>. Enter it to sign in.
          </div>
          <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={8}
            style={{ ...S.input, fontSize: 24, letterSpacing: 8, textAlign: "center", padding: "14px 18px", marginBottom: 12 }}
            placeholder="Enter code" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
            onKeyDown={(e) => { if (e.key === "Enter" && code.length >= 6) verifyCode(); }} />
          {error && (
            <div style={{ ...S.errorBox, marginBottom: 12 }}>
              <AlertCircle size={18} extra={{ color: "var(--danger)" }} />
              <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>{error}</div>
            </div>
          )}
          <button style={{ ...S.primaryBtn, opacity: (code.length >= 6 && !busy) ? 1 : 0.4 }}
            disabled={code.length < 6 || busy} onClick={verifyCode}>
            {busy ? <><Loader size={18} /> Verifying…</> : <>Sign in</>}
          </button>
          <button style={{ ...S.linkBtn, marginTop: 14 }} onClick={() => { setMode("code"); setCode(""); setError(null); }}>Use a different email</button>
        </>
      ) : (
        <>
          <input type="email" autoComplete="email" autoCapitalize="none" autoCorrect="off"
            style={{ ...S.input, fontSize: 17, padding: "16px 18px", marginBottom: 12 }}
            placeholder="your@email.com" value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && mode === "code" && canSendCode) sendCode(); }} />
          {/* Revealed, not navigated to: the email above is already typed and asking for it twice to
              reach a second field is the kind of small insult that makes people give up. autoFocus
              because reaching this field took a deliberate tap, so the keyboard is wanted. */}
          {mode === "password" && (
            <input type="password" autoComplete="current-password" autoFocus
              style={{ ...S.input, fontSize: 17, padding: "16px 18px", marginBottom: 14 }}
              placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && canSubmit) signIn(); }} />
          )}
          {error && (
            <div style={{ ...S.errorBox, marginBottom: 14 }}>
              <AlertCircle size={18} extra={{ color: "var(--danger)" }} />
              <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>{error}</div>
            </div>
          )}
          {mode === "password" ? (
            <>
              <button style={{ ...S.primaryBtn, opacity: canSubmit ? 1 : 0.4 }}
                disabled={!canSubmit} onClick={signIn}>
                {signingIn ? <><Loader size={18} /> Signing in…</> : <>Sign in</>}
              </button>
              {/* Back to the code screen rather than sending from here: the code needs a second
                  field for the code itself, and a button that silently swaps which screen you are
                  on is worse than one that says where it is taking you. */}
              <button style={{ ...S.smallBtn, marginTop: 14 }}
                onClick={() => { setMode("code"); setPassword(""); setError(null); }}>
                <Mail size={15} sw={2.2} /> Email me a code instead
              </button>
            </>
          ) : (
            <>
              <button style={{ ...S.primaryBtn, opacity: canSendCode ? 1 : 0.4 }}
                disabled={!canSendCode} onClick={sendCode}>
                {busy ? <><Loader size={18} /> Sending…</> : <><Mail size={18} sw={2.2} /> Email me a code</>}
              </button>
              <button style={{ ...S.smallBtn, marginTop: 14 }}
                onClick={() => { setMode("password"); setError(null); }}>
                Use a password instead
              </button>
            </>
          )}
          <p style={{ color: "var(--ink-soft)", fontSize: 12.5, marginTop: 16, textAlign: "center", lineHeight: 1.5 }}>
            MagicShelf is invite-only right now. Accounts are set up for you — reach out if you need access.
          </p>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// FeedbackButton — small floating pill + sheet. Available during early testing so
// testers can send a note that lands in the events trace. Captures the current screen
// automatically. Self-contained: manages its own open/submit state.
// ─────────────────────────────────────────────────────────────────────
// `open`/`onClose` make it controlled, which the home menu needs: its row has to close the menu on the
// way here, and a trigger that unmounts takes this component's own state with it. Uncontrolled
// everywhere else, where the trigger stays on screen behind the sheet.
function FeedbackButton({ screen, context, trigger, open: openProp, onClose }) {
  const controlled = openProp !== undefined;
  const [openSelf, setOpenSelf] = useState(false);
  const open = controlled ? openProp : openSelf;
  const setOpen = (next) => { if (controlled) { if (!next) onClose?.(); } else setOpenSelf(next); };
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await submitFeedback({ text: t, screen, context });
      setDone(true);
      setText("");
      setTimeout(() => { setOpen(false); setDone(false); }, 1400);
    } catch (e) {
      alert("Couldn't send feedback: " + (e.message || "try again"));
    } finally {
      setSending(false);
    }
  };

  // Default trigger is a header icon that matches the other top-row buttons (purgeBtn).
  const triggerEl = controlled ? null : trigger
    ? trigger(() => setOpen(true))
    : (
      <button onClick={() => setOpen(true)} style={S.purgeBtn} title="Send feedback">
        <Info size={16} sw={2} extra={{ color: "var(--ink-soft)" }} />
      </button>
    );

  return (
    <>
      {triggerEl}
      {open && (
        <div style={{ ...S.sheetBackdrop, zIndex: 60 }} onClick={() => !sending && setOpen(false)}>
          <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>Send feedback</h3>
              <button style={S.iconBtn} aria-label="Close" onClick={() => !sending && setOpen(false)}><X size={20} /></button>
            </div>
            {done ? (
              <div style={{ padding: "20px 0 8px", textAlign: "center" }}>
                <Check size={30} extra={{ color: "var(--success)" }} />
                <p style={{ fontSize: 15, fontWeight: 600, margin: "10px 0 0" }}>Thanks — got it!</p>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 13, color: "var(--ink-soft)", margin: "0 0 12px", lineHeight: 1.5 }}>
                  Bugs, confusion, ideas — anything helps. This is an early build.
                </p>
                <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="What's on your mind?" maxLength={4000} rows={5}
                  style={{ ...S.input, width: "100%", fontSize: 16, padding: "13px 15px", resize: "none",
                           fontFamily: "inherit", lineHeight: 1.5 }} />
                <button style={{ ...S.primaryBtn, marginTop: 12, opacity: text.trim() && !sending ? 1 : 0.4 }}
                  disabled={!text.trim() || sending} onClick={send}>
                  {sending ? <Loader size={18} /> : <><Check size={18} /> Send</>}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// App
// ─────────────────────────────────────────────────────────────────────

function App({ session }) {
  // App-level navigation
  // The scoped items overlay: null when closed, else { id, name } — a null id means everything.
  // See ScopedItemsView for why it is an overlay and not one of the views below.
  // view: 'spaces' | 'space' | 'location_view' | 'detail' | 'location' | 'scan' | 'verify' | 'review'
  const [view, setView] = useState("spaces");
  const [itemsScope, setItemsScope] = useState(null);
  const [activeSpaceId, setActiveSpaceId] = useState(null);
  const [activeLocationId, setActiveLocationId] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  // Data
  const [spaces, setSpaces] = useState([]);   // root locations
  const [myMemberships, setMyMemberships] = useState({}); // node_id -> role (null = load failed, fail open)
  const [spaceStats, setSpaceStats] = useState({});
  const [items, setItems] = useState([]);     // ALL owner items (filtered per-view in memory)
  const [locations, setLocations] = useState([]); // full locations tree
  const [invitations, setInvitations] = useState([]); // pending invites for me
  const [myName, setMyName] = useState(null); // my display name (null until loaded)
  const [dataLoading, setDataLoading] = useState(true);

  // Scan session state
  const [sessionLocation, setSessionLocation] = useState("");
  const [sessionLocationId, setSessionLocationId] = useState(null);
  // Scan target: sessionLocationId is the location (space root or a specific one) that
  // approved items get filed into. Everything a scan needs to know about where it lands.
  // VLM scan config (testing rig). Composable flags + model; persists across sessions.
  const [scanConfig, setScanConfigState] = useState(() => {
    try {
      // Two keys, because a stored config records every flag explicitly and so pins the old default
      // of any flag whose default later moves. migrateScanConfig carries a v4 config forward while
      // dropping only what changed — see the long note on it.
      const norm = migrateScanConfig(
        JSON.parse(localStorage.getItem(SCAN_CONFIG_KEY) || "null"),
        JSON.parse(localStorage.getItem(SCAN_CONFIG_KEY_LEGACY) || "null"),
      );
      setActiveScanChain(norm.scanChain); // sync the labeler's fallback chain on load
      return norm;
    } catch { return { ...DEFAULT_SCAN_CONFIG }; }
  });
  const setScanConfig = (next) => {
    const norm = normalizeScanConfig(next);
    setActiveScanChain(norm.scanChain); // keep the labeler's chain in sync with the setting
    setScanConfigState(norm);
    try { localStorage.setItem(SCAN_CONFIG_KEY, JSON.stringify(norm)); } catch { /* private mode */ }
  };
  // Search behaviour, persisted the same way.
  const [searchConfig, setSearchConfigState] = useState(() => {
    try {
      return normalizeSearchConfig(JSON.parse(localStorage.getItem(SEARCH_CONFIG_KEY) || "null"));
    } catch { return { ...DEFAULT_SEARCH_CONFIG }; }
  });
  const setSearchConfig = (next) => {
    const norm = normalizeSearchConfig(next);
    setSearchConfigState(norm);
    try { localStorage.setItem(SEARCH_CONFIG_KEY, JSON.stringify(norm)); } catch { /* private mode */ }
  };
  const [viewMode, setViewModeState] = useState(() => {
    try { return normalizeViewMode(localStorage.getItem(VIEW_MODE_KEY)); } catch { return DEFAULT_VIEW_MODE; }
  });
  const setViewMode = (next) => {
    const norm = normalizeViewMode(next);
    setViewModeState(norm);
    try { localStorage.setItem(VIEW_MODE_KEY, norm); } catch { /* private mode */ }
  };
  // Context for context-aware prompt modes: where the current scan session is pointed,
  // rebuilt whenever the target or its contents change.
  const scanContext = useMemo(
    () => buildScanContext(locations, items, sessionLocationId, sessionLocation, { tree: scanConfig.tree }),
    [locations, items, sessionLocationId, sessionLocation, scanConfig.tree]
  );
  const [queue, setQueue] = useState([]);
  const [sessionSaved, setSessionSaved] = useState([]);
  // Scan-session id: a UUID grouping all scans/approvals/rejects in one scanning session,
  // so the events data can reconstruct "everything scanned at the pantry on Tuesday".
  // Generated when a scan session starts (entering scan mode fresh), sent on every call.
  const [scanSessionId, setScanSessionId] = useState(null);
  const newScanSession = () => { const sid = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())); setScanSessionId(sid); return sid; };
  // Own the scan-session lifecycle by view: entering scan (from a non-scan/verify view)
  // starts a fresh session id; leaving scan+verify entirely ends it. This groups every
  // scan/approve/reject in one sitting under one id without editing each entry point.
  const prevViewRef = useRef(null);
  useEffect(() => {
    const prev = prevViewRef.current;
    const inScanFlow = (v) => v === "scan" || v === "verify";
    if (view === "scan" && !inScanFlow(prev)) newScanSession();
    if (!inScanFlow(view) && inScanFlow(prev)) setScanSessionId(null);
    prevViewRef.current = view;
  }, [view]);
  const [verifyingId, setVerifyingId] = useState(null);
  const [approvingIds, setApprovingIds] = useState(new Set());
  const saving = approvingIds.size > 0;
  // Tracks tap ids that left the queue via an explicit approve/decline, so the queue-loss
  // guard doesn't mistake them for silent drops and restore them.
  const queueHandledRef = useRef(new Set());
  // Intentionally clear the queue (new session, jump-into-location) WITHOUT the guard
  // restoring the taps: mark them handled, then clear.
  const resetQueue = () => {
    setQueue((cur) => {
      cur.forEach((t) => queueHandledRef.current.add(t.id));
      return [];
    });
  };
  // Opens the location picker from the scan screen's "Adding to" header (retarget without leaving scan).
  const [scanRetargetOpen, setScanRetargetOpen] = useState(false);
  // True briefly while a scan-location switch settles, so we can block captures until ready.
  const [scanSwitching, setScanSwitching] = useState(false);

  const reloadAll = async () => {
    const [sp, locs, its, invs, mems] = await Promise.all([
      loadSpaces(), loadLocations(), loadItems(), loadInvitations(session?.user?.email), loadMyMemberships(session?.user?.id),
    ]);
    setSpaces(sp);
    setMyMemberships(mems);
    setLocations(locs);
    setItems(its);
    setInvitations(invs);
    setSpaceStats(computeSpaceStats(its, locs, sp.map((s) => s.id)));
    // My own display name (own-row read is allowed by RLS).
    supabase.from("users").select("display_name").eq("id", session.user.id).maybeSingle()
      .then(({ data }) => setMyName(data?.display_name || ""));
  };

  // Reload all owner items (space stats recompute automatically via effect).
  const refreshItems = async () => {
    setItems(await loadItems());
  };

  // Recompute space stats whenever items or locations change.
  useEffect(() => {
    if (spaces.length) setSpaceStats(computeSpaceStats(items, locations, spaces.map((s) => s.id)));
  }, [items, locations, spaces]);

  // Subtree counts for every location, computed here rather than per screen: the tree view needs a
  // count on every node it draws, including nodes the current screen never had a reason to count.
  const locationItemCounts = useMemo(() => computeLocationItemCounts(locations, items), [locations, items]);

  // Opening the camera, in one place.
  //
  // Four call sites had grown their own copy of this — home, the space view, the location view, the
  // tree — and they had already drifted on whether to reset the session and whether to run the
  // destination through `firstScanTarget`, which is exactly the sort of divergence that makes the
  // scan destination wrong on one screen out of four.
  const startScanAt = (locId, locName) => {
    const target = locId ? (getLocationById(locations, firstScanTarget(locations, locId)) || null) : null;
    setSessionLocation(target?.name || locName || "");
    setSessionLocationId(target?.id || locId || null);
    resetQueue(); setSessionSaved([]);
    setView("scan");
  };

  // Where closing the camera lands: the screen it was opened from.
  const afterScanView = () => (
    activeLocationId ? "location_view" : (activeSpaceId ? "space" : "spaces")
  );

  // Which node the tree view has highlighted, held here because the button that acts on it is the
  // screen's own floating "Add from camera" rather than anything inside the tree.
  const [treeSelection, setTreeSelection] = useState(null); // { id, name } | null
  // The typed-entry form, and where it is aimed. `{ parentId, parentName } | null`.
  const [manualAdd, setManualAdd] = useState(null);

  // Where the camera aims from a browse screen: the highlighted tree node if there is one, otherwise
  // the place the screen is about.
  const scanFromScreen = (locId, locName) => (
    treeSelection
      ? startScanAt(treeSelection.id, treeSelection.name)
      : startScanAt(locId, locName)
  );

  // Same rule for the typed route, so the two add controls on a screen agree about where they aim.
  const manualFromScreen = (locId, locName) => setManualAdd(
    treeSelection
      ? { parentId: treeSelection.id, parentName: treeSelection.name }
      : { parentId: locId || null, parentName: locName || null },
  );

  // What the tree view can do to a location, as one prop.
  //
  // A bag of callbacks rather than a dozen named props, because the tree is the *same* control on
  // all three browse screens and the alternative is spelling the identical list out three times and
  // keeping the three in step. It is a bag for a second reason too: the tree draws the whole subtree,
  // so its operations are whole-tree ones and cannot come from the screen. A screen's own
  // `onRenameSpace` renames the location that screen is about; the tree needs to rename whichever
  // node was double-tapped, which may be four levels below it.
  //
  // The two wrappers exist because the home screen keeps top-level locations in a second list
  // (`spaces`, which is what the home rows read) while the tree makes no distinction between a
  // top-level location and any other. Renaming or deleting a root from the tree has to reach both
  // lists, or the change lands everywhere except the screen the user is looking at.
  const renameLocationAnywhere = async (id, newName) => {
    const ok = await renameLocation(id, newName);
    if (ok) setSpaces((p) => p.map((s) => (s.id === id ? { ...s, name: (newName || "").trim() } : s)));
    return ok;
  };
  const deleteLocationAnywhere = async (id) => {
    const ok = await deleteLocationCascade(id);
    if (!ok) return false;
    setSpaces(await loadSpaces());
    if (activeSpaceId === id) { setActiveSpaceId(null); setActiveLocationId(null); setView("spaces"); }
    return true;
  };
  const treeOps = {
    userId: session.user.id,
    onOpenLocation: (id) => {
      // A root opens its own screen; anything below it opens as a location. The space view and the
      // location view are different screens for what the tree shows as one kind of row.
      const loc = getLocationById(locations, id);
      if (!loc) return;
      const root = rootLocationIdOf(locations, id) || id;
      setActiveSpaceId(root);
      if (root === id) { setActiveLocationId(null); setView("space"); }
      else { setActiveLocationId(id); setView("location_view"); }
    },
    onReparent: async (id, newParentId) => { await reparentLocation(id, newParentId); },
    onRename: renameLocationAnywhere,
    // Wrapped rather than passed by name: the operations below are declared later in this component,
    // so naming one here reads it during render, before its `const` is initialised.
    onSetPhoto: (id, file) => setLocationPhoto(id, file),
    onSetIcon: (id, icon) => setLocationIcon(id, icon),
    onDelete: deleteLocationAnywhere,
    onCreateChild: (name, parentId) => createLocationPure(name, parentId),
    // The tree's double tap opens the same full edit sheet the location's own screen does, which
    // needs the sharing and reparent machinery — it was a cut-down name-and-photo sheet before, and
    // was reported as not being the real edit view, which it was not.
    onCreateSpace: (name) => createSpace(name),
    onInvite: (nodeId, email, role) => inviteToSpace(nodeId, email, role),
    onMembersChanged: () => reloadAll(),
    selectedId: treeSelection?.id || null,
    onSelect: (loc) => setTreeSelection(loc ? { id: loc.id, name: loc.name } : null),
    // The tree's own add rows open the same form the list and the grid do, aimed at the row's parent.
    // They used to open a one-field sheet of their own, which made "Add from text" two different
    // things depending on which mode you happened to be in.
    onAddManually: (id, name) => setManualAdd({ parentId: id || null, parentName: name || null }),
  };

  // Items shown in "All items" use the SAME predicate as the home count (computeSpaceStats):
  // an item counts only if it lives in a location whose root is a real space. This excludes
  // orphaned/location-less items and items whose location no longer resolves to a space — so
  // the All-items grid matches the home total exactly.
  const spaceIdSet = useMemo(() => new Set(spaces.map((s) => s.id)), [spaces]);
  const visibleItems = useMemo(() => items.filter((it) => {
    if (!it.location_id) return false;
    const root = rootLocationIdOf(locations, it.location_id);
    return root && spaceIdSet.has(root);
  }), [items, locations, spaceIdSet]);

  useEffect(() => {
    // Wait until we actually have a session before loading data (reloadAll reads
    // session.user.id). After a fresh magic-link login the session arrives async,
    // so keying this effect on `session` avoids running against a null session.
    if (!session) return;
    let alive = true;
    (async () => {
      try {
        await reloadAll();
      } catch (e) {
        console.error("Initial data load failed:", e);
        // Don't strand the user on the spinner — let the app render; views handle empties.
      } finally {
        if (alive) setDataLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [session]);

  // `items` holds ALL owner items (loaded in reloadAll); space and location views filter
  // in-memory by root location. There is no separate per-view load to wait on.

  // Root locations come from loadSpaces, which resolves no photos; loadLocations does resolve
  // them and includes the roots. Without the join a top-level location could be given a photo
  // that then showed up nowhere — its rows and its edit sheet both read from `spaces`.
  const spacesWithPhotos = useMemo(() => {
    if (!locations.length) return spaces;
    const byId = new Map(locations.map((l) => [l.id, l]));
    return spaces.map((s) => {
      const loc = byId.get(s.id);
      return loc && loc.image
        ? { ...s, image: loc.image, raw_image_path: loc.raw_image_path }
        : s;
    });
  }, [spaces, locations]);

  const activeSpace = useMemo(
    () => spacesWithPhotos.find((s) => s.id === activeSpaceId),
    [spacesWithPhotos, activeSpaceId]
  );
  // During a scan-location switch, activeSpaceId changes; if the new space object isn't
  // resolvable for a beat, hold the last valid one so ScanLive doesn't unmount (which
  // would drop in-flight taps). Cleared when a real space resolves.
  const lastGoodSpaceRef = useRef(null);
  useEffect(() => { if (activeSpace) lastGoodSpaceRef.current = activeSpace; }, [activeSpace]);
  const activeSpaceStable = activeSpace || (view === "scan" || view === "verify" ? lastGoodSpaceRef.current : null);

  // If we're viewing a space that no longer exists for us (e.g. we just left it),
  // return home.
  useEffect(() => {
    if (view === "space" && activeSpaceId && !spaces.some((s) => s.id === activeSpaceId)) {
      setActiveSpaceId(null);
      setView("spaces");
    }
  }, [spaces, activeSpaceId, view]);
  const signOut = async () => { if (window.confirm("Sign out?")) await supabase.auth.signOut(); };

  // ── Invitation ops ───────────────────────────────────────────────
  // Owner invites someone to a space. Returns { ok, emailed } or throws with friendly message.
  // Client-side role check: can the user EDIT (rename/move/delete/scan-into) this
  // location? True if they own any node on the ancestor chain, or hold an
  // owner/editor membership on the node or any ancestor. Viewers and non-members
  // get read-only UI. Membership load failed (null) -> fail OPEN (server enforces).
  const canEditLocationId = (locId) => {
    const uid = session?.user?.id;
    if (!locId || !uid) return false;
    if (myMemberships === null) return true;
    let cur = locId; const seen = new Set();
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      const n = getLocationById(locations, cur);
      if (!n) break;
      if (n.owner_id === uid) return true;
      const r = myMemberships[n.id];
      if (r === "owner" || r === "editor") return true;
      cur = n.parent_location_id;
    }
    return false;
  };
  // Items: your own item is always editable; otherwise editability follows its location.
  const canEditItemNode = (it) => !!it && (it.owner_id === session?.user?.id || canEditLocationId(it.location_id));

  const inviteToSpace = async (spaceId, email, role = "editor") => {
    try {
      const res = await createInvitation(spaceId, email, role);
      return { ok: true, emailed: !!res.emailed, alreadyInvited: !!res.already_invited };
    } catch (e) {
      // Surface friendly messages for the known cases. `not_approved` is kept although the server
      // stopped sending it: a client older than the deploy is still out there, and its message was
      // the accurate one at the time.
      const data = e.data || {};
      if (data.error === "not_approved") return { ok: false, reason: "not_approved", message: data.message };
      if (data.error === "already_member") return { ok: false, reason: "already_member", message: data.message };
      return { ok: false, reason: "error", message: e.message || "Couldn't send the invitation." };
    }
  };

  const acceptInvite = async (invitationId) => {
    try {
      await acceptInvitation(invitationId);
      setInvitations((p) => p.filter((i) => i.id !== invitationId));
      await reloadAll(); // the newly-joined space now appears
    } catch (e) {
      alert(e.message || "Couldn't accept the invitation.");
    }
  };

  const declineInvite = async (invitationId) => {
    try {
      await declineInvitation(invitationId);
      setInvitations((p) => p.filter((i) => i.id !== invitationId));
    } catch (e) {
      alert(e.message || "Couldn't decline the invitation.");
    }
  };

  // ── Space ops (spaces are ROOT location nodes) ───────────────────
  const createSpace = async (name) => {
    const { data, error } = await supabase
      .from("nodes")
      .insert({ owner_id: session.user.id, name: name.trim(), kind: "location", parent_id: null, added_by: session.user.id })
      .select("id, name, created_at, last_used_at, parent_id, owner_id")
      .single();
    if (error) { alert(friendlyError(error, "create location")); return null; }
    embedNodeAfterWrite(data.id);
    // The DB trigger auto-creates the owner membership for root nodes.
    const mapped = { ...data, parent_location_id: null };
    setSpaces((p) => [mapped, ...p]);
    setLocations((p) => [mapped, ...p]);
    return mapped;
  };

  const renameSpace = async (id, newName) => {
    const { error } = await supabase.from("nodes").update({ name: newName.trim() }).eq("id", id);
    if (error) { alert(friendlyError(error, "rename")); return; }
    embedNodeAfterWrite(id);
    setSpaces((p) => p.map((s) => s.id === id ? { ...s, name: newName.trim() } : s));
    setLocations((p) => p.map((l) => l.id === id ? { ...l, name: newName.trim() } : l));
  };

  // Deleting a top-level location. Confirmation is the caller's job, through
  // ConfirmDeleteLocationSheet — the same sheet a nested location uses, because a top-level location
  // is not a different kind of thing to delete, and the native dialog that used to guard this one
  // made it look like it was.
  //
  // It is now the same *code* as well, and that is the fix rather than the tidy-up it looks like.
  // This used to soft-delete that single row and nothing else, while the sheet in front of it
  // promised to delete "everything inside it — N sub-locations and M items". The contents stayed live
  // under a deleted parent, where no screen can reach them: every list walks DOWN from a root, so a
  // subtree hanging off a deleted root is invisible rather than gone. An audit of production found
  // 116 items in that state across sixteen deleted locations, sixty-eight under one "House".
  //
  // The test covering this delete asserted only that the root's own name had left the live rows,
  // which is why it passed throughout. See tests/app/delete-location.spec.mjs.
  const deleteSpace = deleteLocationAnywhere;

  // ── Item ops ─────────────────────────────────────────────────────
  const renameItem = async (id, newLabel) => {
    const trimmed = (newLabel || "").trim();
    if (!trimmed) return false;
    const item = items.find((i) => i.id === id);
    if (item && item.label === trimmed) return true; // no-op
    const { error } = await supabase.from("nodes").update({ name: trimmed }).eq("id", id);
    if (error) { alert(friendlyError(error, "rename item")); return false; }
    embedNodeAfterWrite(id);
    supabase.from("events").insert({
      user_id: session.user.id, node_id: id,
      event_type: "label_edited",
      payload: { from: item?.label || "", to: trimmed },
      app_version: APP_VERSION,
    }).then(({ error }) => { if (error) console.warn("event log skipped:", error.message); });
    setItems((p) => p.map((i) => i.id === id ? { ...i, label: trimmed } : i));
    if (activeItem?.id === id) setActiveItem((cur) => cur ? { ...cur, label: trimmed } : cur);
    return true;
  };

  const setItemQuantity = async (id, next) => {
    const quantity = cleanQuantity(next);
    const { error } = await supabase.from("nodes").update({ quantity }).eq("id", id);
    if (error) {
      if (isMissingColumn(error, "quantity")) alert("Counts aren't switched on yet — this needs a database update.");
      else alert(friendlyError(error, "change the count"));
      return false;
    }
    setItems((p) => p.map((i) => i.id === id ? { ...i, quantity } : i));
    if (activeItem?.id === id) setActiveItem((cur) => cur ? { ...cur, quantity } : cur);
    return true;
  };

  const deleteItem = async (id) => {
    const item = items.find((i) => i.id === id);
    await supabase.from("nodes").update({ deleted_at: new Date().toISOString() }).eq("id", id);
    supabase.from("events").insert({
      user_id: session.user.id, node_id: id,
      event_type: "node_deleted",
      payload: { name_at_deletion: item?.label || "" },
      app_version: APP_VERSION,
    }).then(({ error }) => { if (error) console.warn("event log skipped:", error.message); });
    setItems((p) => p.filter((i) => i.id !== id));
    // Refresh stats since count changed
    setView(activeSpaceId ? "space" : "spaces");
    setActiveItem(null);
  };

  // Batch delete (soft) without navigating away — used by multi-select.
  const deleteItems = async (ids) => {
    if (!ids || !ids.length) return;
    const nowIso = new Date().toISOString();
    await supabase.from("nodes").update({ deleted_at: nowIso }).in("id", ids);
    supabase.from("events").insert(ids.map((id) => {
      const item = items.find((i) => i.id === id);
      return {
        user_id: session.user.id, node_id: id,
        event_type: "node_deleted",
        payload: { name_at_deletion: item?.label || "", batch: true },
        app_version: APP_VERSION,
      };
    })).then(({ error }) => { if (error) console.warn("event log skipped:", error.message); });
    setItems((p) => p.filter((i) => !ids.includes(i.id)));
  };

  // Convert an ITEM into a LOCATION in place. The node keeps its id, name, and parent,
  // so it sits exactly where the item was — it just becomes a location. The node_images
  // row is kept so a location created from an item can display its photo. Returns the
  // root (space) id so the caller can navigate into the new location.
  const convertItemToLocation = async (id) => {
    const item = items.find((i) => i.id === id) || (activeItem?.id === id ? activeItem : null);
    const { error } = await supabase.from("nodes")
      .update({ kind: "location", updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) { alert(friendlyError(error, "convert to location")); return null; }
    // Keep the node_images row — a location created from an item shows its photo.
    supabase.from("events").insert({
      user_id: session.user.id, node_id: id,
      event_type: "kind_changed",
      payload: { from: "item", to: "location", name: item?.label || "" },
      app_version: APP_VERSION,
    }).then(({ error }) => { if (error) console.warn("event log skipped:", error.message); });
    // Reload so the new location appears in the tree and the item leaves item lists.
    const [locs, its] = await Promise.all([loadLocations(), loadItems()]);
    setLocations(locs);
    setItems(its);
    // The converted node's space = its root ancestor (itself if it was top-level).
    return rootLocationIdOf(locs, id);
  };

  // Give a location (or any node) a photo. Reloads locations so the new image shows up
  // everywhere it's inherited, not just on the location itself.
  // Takes a File from a picker, or a data URL from somewhere that already read one — the manual add
  // form holds its photo as a data URL because it has no node to attach it to until Add is pressed.
  const setLocationPhoto = async (id, file) => {
    if (!id || !file) return false;
    try {
      const image = typeof file === "string" ? file : await fileToUploadableDataUrl(file);
      await setNodeImage({ node_id: id, image, app_version: APP_VERSION });
      setLocations(await loadLocations());
      // A top-level location is a row on the home screen too, and that list comes from
      // `spaces` rather than `locations` — without this the new photo appears everywhere
      // except the screen the user was looking at when they set it.
      setSpaces(await loadSpaces());
      return true;
    } catch (e) {
      alert(friendlyError(e, "save the photo"));
      return false;
    }
  };

  // An item's photo. Same server call as a location's, which does not care about `kind` — what
  // differs is that an item has no icon to fall back to, so before this there was no photo at all
  // for anything typed rather than scanned.
  const setItemPhoto = async (id, file) => {
    if (!id || !file) return false;
    try {
      const image = typeof file === "string" ? file : await fileToUploadableDataUrl(file);
      const res = await setNodeImage({ node_id: id, image, app_version: APP_VERSION });
      const url = res?.image_url || null;
      if (url) {
        setItems((p) => p.map((i) => (i.id === id ? { ...i, image: url } : i)));
        if (activeItem?.id === id) setActiveItem((cur) => (cur && cur.id === id ? { ...cur, image: url } : cur));
      }
      await refreshItems();
      return true;
    } catch (e) {
      alert(friendlyError(e, "save the photo"));
      return false;
    }
  };

  // The icon a location is drawn with, or null to go back to the guessed one.
  //
  // A direct update rather than a trip through `detect.js`, on the same grounds as renaming: it is one
  // column on `nodes`, and the update policy — can_edit_node, walking ancestors — is already the
  // answer to who may set it. Nothing here needs service role and nothing needs deriving.
  const setLocationIcon = async (id, icon) => {
    if (!id) return false;
    const { error } = await supabase.from("nodes").update({ icon: icon || null }).eq("id", id);
    if (error) {
      if (isMissingColumn(error, "icon")) {
        alert("Choosing an icon isn't available yet — the database needs its migration first.");
        return false;
      }
      alert(friendlyError(error, "save the icon"));
      return false;
    }
    // Both lists, for the reason setLocationPhoto reloads both: a top-level location is a row on the
    // home screen, which reads `spaces`, so updating only `locations` changes it everywhere except
    // the screen the user is looking at.
    setLocations((p) => p.map((l) => (l.id === id ? { ...l, icon: icon || null } : l)));
    setSpaces((p) => p.map((sp) => (sp.id === id ? { ...sp, icon: icon || null } : sp)));
    return true;
  };

  // Convert a LOCATION back into an ITEM. An item anywhere inside blocks it — a container
  // with contents isn't one thing. Empty sub-locations do NOT block it: a shelf someone
  // sketched out and never filled is scaffolding, and making them delete it by hand first
  // was pure busywork. Those get soft-deleted as part of the conversion. The node keeps
  // its id/name/parent; kind flips to 'item'. Returns the new item, or false.
  const convertLocationToItem = async (id) => {
    // Ask the database rather than trusting the in-memory copy: this decides whether it is
    // safe to delete the sub-locations, so it needs to see anything a stale client doesn't.
    const subtreeIds = Array.from(locationSubtree(locations, id));
    const { data: contained, error: kidErr } = await supabase
      .from("nodes").select("id").eq("kind", "item").is("deleted_at", null)
      .in("parent_id", subtreeIds).limit(1);
    if (kidErr) { alert(friendlyError(kidErr, "check contents")); return false; }
    if (contained && contained.length > 0) {
      alert("This location has things inside it. Move or remove them first, then you can turn it into an item.");
      return false;
    }
    const loc = getLocationById(locations, id);
    const nowIso = new Date().toISOString();
    const emptyChildIds = subtreeIds.filter((sid) => sid !== id);
    if (emptyChildIds.length) {
      const { error: subErr } = await supabase.from("nodes")
        .update({ deleted_at: nowIso }).in("id", emptyChildIds);
      if (subErr) { alert(friendlyError(subErr, "remove empty sub-locations")); return false; }
    }
    const { error } = await supabase.from("nodes")
      .update({ kind: "item", updated_at: nowIso })
      .eq("id", id);
    if (error) { alert(friendlyError(error, "convert to item")); return false; }
    supabase.from("events").insert({
      user_id: session.user.id, node_id: id,
      event_type: "kind_changed",
      payload: { from: "location", to: "item", name: loc?.name || "", removed_sublocations: emptyChildIds.length },
      app_version: APP_VERSION,
    }).then(({ error }) => { if (error) console.warn("event log skipped:", error.message); });
    const [locs, its] = await Promise.all([loadLocations(), loadItems()]);
    const nowItem = its.find((i) => i.id === id) || null;
    // Switch to the item detail FIRST (synchronously batched with the data updates)
    // so LocationView never renders against a location that no longer exists (avoids
    // a flash to top level). Then apply the reloaded lists.
    if (nowItem) {
      setActiveItem(nowItem);
      setActiveLocationId(null);
      setView("detail");
    }
    setLocations(locs);
    setItems(its);
    return nowItem;
  };

  // Move an item to a specific location. In the new model the item's space is implied
  // by the location's root, so we just set location_id. targetLocationId may be a space
  // (root location) for "directly in the space", or a nested location.
  const moveItemToDestination = async (id, targetSpaceRootId, targetLocationId) => {
    const item = items.find((i) => i.id === id);
    if (!item) return false;
    const oldLocationId = item.location_id;
    const destLocation = targetLocationId || targetSpaceRootId; // fall back to the space root
    if (oldLocationId === destLocation) return true; // no-op
    // OWNERSHIP GATE (2026-07-07): moving an item into a location owned by someone else
    // TRANSFERS the item to that owner. You may only do that if you own the item or own
    // the tree it currently sits in — otherwise an editor could pull another owner's item
    // out into their own tree (theft). Moving WITHIN the same owner's trees is always fine.
    const uid = session?.user?.id;
    const destOwner = getLocationById(locations, destLocation)?.owner_id || null;
    const changesOwner = destOwner && destOwner !== item.owner_id;
    if (changesOwner) {
      const ownsItem = item.owner_id === uid;
      const ownsSource = item.location_id && ownsNodeOrAncestor(locations, item.location_id, uid);
      if (!ownsItem && !ownsSource) {
        alert("You can't move items out of a space you don't own.");
        return false;
      }
    }
    const nowIso = new Date().toISOString();
    const patch = { parent_id: destLocation, updated_at: nowIso };
    if (changesOwner) patch.owner_id = destOwner;
    const { error: upErr } = await supabase.from("nodes").update(patch).eq("id", id);
    if (upErr) { alert(friendlyError(upErr, "move item")); return false; }
    supabase.from("events").insert({
      user_id: session.user.id, node_id: id,
      event_type: "node_moved",
      payload: { from_parent_id: oldLocationId, to_parent_id: destLocation },
      app_version: APP_VERSION,
    }).then(({ error }) => { if (error) console.warn("event log skipped:", error.message); });
    refreshItems();
    if (activeItem?.id === id) {
      const newLocName = getLocationById(locations, destLocation)?.name || "";
      setActiveItem({ ...activeItem, location_id: destLocation, location: newLocName });
    }
    return true;
  };

  // Move multiple items to the same destination at once.
  const moveItemsToDestination = async (ids, targetSpaceRootId, targetLocationId) => {
    if (!ids || !ids.length) return false;
    const destLocation = targetLocationId || targetSpaceRootId; // space root if no specific location
    const nowIso = new Date().toISOString();
    // Ownership follows the destination tree (see moveItemToDestination).
    const uid = session?.user?.id;
    const destOwner = getLocationById(locations, destLocation)?.owner_id || null;
    // GATE: if this destination would change owners, only items you own (or that sit in a
    // tree you own) may make the trip. Block the whole action if any selected item can't —
    // clearer than silently moving some and not others.
    if (destOwner) {
      const blocked = ids.filter((id) => {
        const it = items.find((i) => i.id === id);
        if (!it || destOwner === it.owner_id) return false; // no owner change → fine
        const ownsItem = it.owner_id === uid;
        const ownsSource = it.location_id && ownsNodeOrAncestor(locations, it.location_id, uid);
        return !ownsItem && !ownsSource;
      });
      if (blocked.length > 0) {
        alert(`You can't move items out of a space you don't own. ${blocked.length} of the selected item${blocked.length === 1 ? " is" : "s are"} in a space that isn't yours.`);
        return false;
      }
    }
    for (const id of ids) {
      const item = items.find((i) => i.id === id);
      if (!item) continue;
      const oldLocationId = item.location_id;
      if (oldLocationId === destLocation) continue;
      const patch = { parent_id: destLocation, updated_at: nowIso };
      if (destOwner && destOwner !== item.owner_id) patch.owner_id = destOwner;
      const { error: upErr } = await supabase.from("nodes").update(patch).eq("id", id);
      if (upErr) { console.error("move failed for", id, upErr); continue; }
      supabase.from("events").insert({
        user_id: session.user.id, node_id: id,
        event_type: "node_moved",
        payload: { from_parent_id: oldLocationId, to_parent_id: destLocation, batch: true },
        app_version: APP_VERSION,
      }).then(({ error }) => { if (error) console.warn("event log skipped:", error.message); });
    }
    refreshItems();
    return true;
  };

  // Pure creation: insert a location (or return existing of same name+parent). No navigation.
  // A location's space is implied by its parent chain (root = a space). parentLocationId null
  // means this is a new ROOT location (a space). Callers that create within a space pass the
  // space root (or a deeper location) as the parent.
  // Single-name get-or-create. (Chain parsing "A > B > C" was tried and REMOVED by user
  // decision 2026-07-05 — names may legitimately contain ">" and nested creation happens
  // through the picker menu instead, which works level by level.)
  const createLocationPure = async (name, parentLocationId) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return null;
    const existing = locations.find((l) =>
      l.name === trimmed &&
      (l.parent_location_id || null) === (parentLocationId || null)
    );
    if (existing) return existing;
    // OWNERSHIP MODEL (2026-07-06): locations belong to the tree they're in — a location
    // created under a parent is owned by the parent's owner; a new top-level location is
    // owned by its creator. added_by keeps attribution either way.
    const parentLoc = parentLocationId ? getLocationById(locations, parentLocationId) : null;
    const { data: nl, error } = await supabase.from("nodes").insert({
      owner_id: (parentLoc && parentLoc.owner_id) || session.user.id, name: trimmed, kind: "location",
      parent_id: parentLocationId || null, added_by: session.user.id,
    }).select("id, name, parent_id, last_used_at, owner_id").single();
    if (error || !nl) { alert("Couldn't create location: " + (error?.message || "")); return null; }
    embedNodeAfterWrite(nl.id);
    const newLoc = { ...nl, parent_location_id: nl.parent_id };
    setLocations((p) => [newLoc, ...p]);
    // If this is a new root (space), surface it in spaces too.
    if (!newLoc.parent_location_id) setSpaces((p) => [newLoc, ...p]);
    return newLoc;
  };

  // An item typed rather than photographed, through the same handler a scan uses.
  //
  // `save_item` with no image: the ownership rule (an item belongs to its parent location's owner),
  // the can_edit_node check on that parent, the vocabulary filter on the categories, the approve
  // event and the embedding all live there, and a second create path on the client would have to
  // reproduce every one of them — see the note above handleSaveItem.
  const createItemManually = async ({ name, parentId, quantity, tags, image }) => {
    try {
      await saveItem({
        image: image || undefined,
        name,
        parent_id: parentId || undefined,
        quantity,
        tags,
        label_source: "manual",
        app_version: APP_VERSION,
      });
      await refreshItems();
      return true;
    } catch (e) {
      alert("Couldn't add that item: " + (e?.message || "unknown error"));
      return false;
    }
  };

  // Used by SpaceView/LocationView to create + navigate to. parentLocationId is the location
  // (or space root) the new location sits under.
  const createLocation = async (name, parentLocationId, opts = {}) => {
    const loc = await createLocationPure(name, parentLocationId);
    if (loc && !opts.noNavigate) {
      setActiveLocationId(loc.id);
      setView("location_view");
    }
    return loc;
  };

  // Add existing items to a space = move them into that space (set location_id = root location).
  const addExistingItemsToSpace = async (itemIds) => {
    if (!activeSpaceId || !itemIds.length) return;
    // Ownership follows the tree: items added into a space take that space's owner.
    const destOwner = getLocationById(locations, activeSpaceId)?.owner_id || null;
    const patch = { parent_id: activeSpaceId };
    if (destOwner) patch.owner_id = destOwner;
    const { error } = await supabase.from("nodes").update(patch).in("id", itemIds);
    if (error) { alert(friendlyError(error, "add items")); return; }
    supabase.from("events").insert(itemIds.map((iid) => ({
      user_id: session.user.id, node_id: iid,
      event_type: "node_moved",
      payload: { to_parent_id: activeSpaceId },
      app_version: APP_VERSION,
    }))).then(({ error }) => { if (error) console.warn("event log skipped:", error.message); });
    refreshItems();
  };

  // If locationId is given, use it directly. Else look up by name (top-level), create if missing.
  const moveExistingItemsToLocation = async (itemIds, locationName, alsoAddToSpace, locationId) => {
    if (!itemIds.length) return;
    let locId = locationId;
    let locName = locationName;
    if (!locId) {
      if (!locationName) return;
      const trimmed = locationName.trim();
      locName = trimmed;
      const existing = locations.find((l) => l.name === trimmed && !l.parent_location_id);
      if (existing) {
        locId = existing.id;
        await supabase.from("nodes").update({ last_used_at: new Date().toISOString() }).eq("id", locId);
      } else {
        const { data: nl, error } = await supabase.from("nodes").insert({
          owner_id: session.user.id, name: trimmed, kind: "location", parent_id: null, added_by: session.user.id,
        }).select("id, name, parent_id, last_used_at").single();
        if (error || !nl) { alert("Couldn't create location: " + (error?.message || "")); return; }
        embedNodeAfterWrite(nl.id);
        const newLoc = { ...nl, parent_location_id: nl.parent_id };
        locId = newLoc.id;
        setLocations((p) => [newLoc, ...p]);
      }
    } else {
      await supabase.from("nodes").update({ last_used_at: new Date().toISOString() }).eq("id", locId);
    }
    // Update each item's parent — and its owner, since items belong to their tree.
    const nowIso = new Date().toISOString();
    const destOwner = getLocationById(locations, locId)?.owner_id || null;
    const patch = { parent_id: locId, updated_at: nowIso };
    if (destOwner) patch.owner_id = destOwner;
    const { error: upErr } = await supabase.from("nodes")
      .update(patch)
      .in("id", itemIds);
    if (upErr) { alert(friendlyError(upErr, "move items")); return; }

    // Write node_moved events
    supabase.from("events").insert(itemIds.map((iid) => ({
      user_id: session.user.id, node_id: iid,
      event_type: "node_moved",
      payload: { to_parent_id: locId, to_location_name: locName },
      app_version: APP_VERSION,
    }))).then(({ error }) => { if (error) console.warn("event log skipped:", error.message); });

    // (In the new model, moving an item to a location automatically places it in that
    //  location's space — no separate membership needed.)
    refreshItems();
  };

  const renameLocation = async (locationId, newName) => {
    const trimmed = (newName || "").trim();
    if (!trimmed) return false;
    const cur = getLocationById(locations, locationId);
    if (!cur || cur.name === trimmed) return true;
    // Check duplicate sibling
    const dup = locations.find((l) => l.id !== locationId
      && (l.parent_location_id || null) === (cur.parent_location_id || null)
      && l.name === trimmed);
    if (dup) { alert(`Another location named "${trimmed}" already exists at that level.`); return false; }
    const { error } = await supabase.from("nodes").update({ name: trimmed }).eq("id", locationId);
    if (error) { alert(friendlyError(error, "rename")); return false; }
    embedNodeAfterWrite(locationId);
    setLocations((p) => p.map((l) => l.id === locationId ? { ...l, name: trimmed } : l));
    // Refresh items in current space so item.location strings update
    refreshItems();
    return true;
  };

  const reparentLocation = async (locationId, newParentId) => {
    const cur = getLocationById(locations, locationId);
    if (!cur) return false;
    // OWNERSHIP GATE (2026-07-07): you can move a location only if you OWN it (or an
    // ancestor you own). Membership (editor in someone's space) lets you restructure
    // WITHIN that tree, but not pull a subtree out into your own. This blocks the theft
    // path: an editor invited into B's space can't move B's locations into their own tree.
    if (!ownsNodeOrAncestor(locations, locationId, session?.user?.id)) {
      alert("You can only move locations you own.");
      return false;
    }
    // Explicit self-guard (audit A-5): a location can never be its own parent. The subtree
    // check below also covers this, but this survives any future refactor of that check.
    // (The matching DATA check for pre-existing bad rows is TEST_PLAN §12.1.)
    if (newParentId === locationId) { alert("Can't move a location into itself."); return false; }
    if ((cur.parent_location_id || null) === (newParentId || null)) return true; // no change
    // Cycle guard: newParentId must not be in the subtree of locationId.
    if (newParentId) {
      const subtree = locationSubtree(locations, locationId);
      if (subtree.has(newParentId)) {
        alert("Can't move a location underneath itself.");
        return false;
      }
    }
    // Check duplicate sibling at new level
    const dup = locations.find((l) => l.id !== locationId
      && (l.parent_location_id || null) === (newParentId || null)
      && l.name === cur.name);
    if (dup) { alert(`Another location named "${cur.name}" already exists at the destination.`); return false; }
    // GUARD-AND-INFORM: access flows down, so nesting under a parent with more members
    // silently WIDENS who can see this location and everything inside. Diff the access
    // lists and confirm before proceeding. (Moving to top level narrows instead — that's
    // the intended manage-perms escape hatch, so it stays silent. If the RPC is
    // unavailable, skip the guard rather than blocking moves.)
    if (newParentId) {
      const dest = getLocationById(locations, newParentId);
      const uid = session?.user?.id;
      const crossOwner = dest && cur.owner_id && dest.owner_id && cur.owner_id !== dest.owner_id;
      const parts = [];
      // CROSS-OWNER NOTICE (decided 2026-07-06: allowed, informed): ownership stays put,
      // but the destination tree's owner gains full control incl. member management
      // ("your house, your rules" — server rule in migration 20260706000000).
      // CEDE MODEL (decided 2026-07-06): moving a location into someone else's tree
      // TRANSFERS ownership of it (and every location inside) to that tree's owner.
      // Items keep their scanners. This confirm is the consent moment.
      if (crossOwner) {
        parts.push(cur.owner_id === uid
          ? `"${cur.name}" and everything in it will belong to the owner of "${dest.name}". If they remove you from their space, you lose access to it.`
          : `"${cur.name}" and everything in it will transfer to the owner of "${dest.name}".`);
      }
      const [curAccess, destAccess] = await Promise.all([loadNodeAccess(locationId), loadNodeAccess(newParentId)]);
      if (curAccess !== null && destAccess !== null) {
        const curIds = new Set(curAccess.map((a) => a.user_id));
        const gainers = destAccess.filter((a) => !curIds.has(a.user_id));
        if (gainers.length > 0) {
          const names = gainers.slice(0, 3).map((g) => (g.display_name && g.display_name.trim()) || g.email || "someone");
          const more = gainers.length > 3 ? ` and ${gainers.length - 3} more` : "";
          parts.push(`${names.join(", ")}${more} will gain access to it and everything inside.`);
        }
      }
      if (parts.length > 0) {
        const destName = dest?.name || "the destination";
        const ok = window.confirm(`Move "${cur.name}" into "${destName}"?\n\n` + parts.join("\n\n"));
        if (!ok) return false;
      }
    }
    const destLoc = newParentId ? getLocationById(locations, newParentId) : null;
    const newOwner = (destLoc && destLoc.owner_id && destLoc.owner_id !== cur.owner_id) ? destLoc.owner_id : null;
    const { error } = await supabase.from("nodes")
      .update(newOwner ? { parent_id: newParentId || null, owner_id: newOwner } : { parent_id: newParentId || null })
      .eq("id", locationId);
    if (error) { alert(friendlyError(error, "reparent")); return false; }
    if (newOwner) {
      // Transfer EVERYTHING in the moved subtree (locations AND items) to the new tree's
      // owner — items belong to their tree just like locations. added_by preserves credit.
      const subIds = Array.from(locationSubtree(locations, locationId)).filter((id) => id !== locationId);
      if (subIds.length) {
        const { error: tErr } = await supabase.from("nodes").update({ owner_id: newOwner }).in("id", subIds).eq("kind", "location");
        if (tErr) console.error("Subtree location transfer failed:", tErr);
      }
      // Items are parented to any location in the subtree (including the moved node itself).
      const allSub = Array.from(locationSubtree(locations, locationId));
      const { error: iErr } = await supabase.from("nodes").update({ owner_id: newOwner }).in("parent_id", allSub).eq("kind", "item");
      if (iErr) console.error("Subtree item transfer failed:", iErr);
    }
    const movedSub = newOwner ? locationSubtree(locations, locationId) : null;
    setLocations((p) => p.map((l) => {
      if (l.id === locationId) return { ...l, parent_location_id: newParentId || null, ...(newOwner ? { owner_id: newOwner } : {}) };
      if (movedSub && movedSub.has(l.id)) return { ...l, owner_id: newOwner };
      return l;
    }));
    // Parent changes can move a location in/out of top-level (spaces list), so resync.
    await reloadAll();
    return true;
  };

  // Bulk reparent: move several locations under a new parent. null = make top-level.
  const moveLocations = async (locationIds, newParentId) => {
    if (!locationIds || !locationIds.length) return;
    // OWNERSHIP GATE (2026-07-07): every moved location must be one you OWN (or an ancestor
    // you own). Membership doesn't grant the right to pull a subtree out of someone's tree.
    const gateUid = session?.user?.id;
    const unowned = locationIds.filter((id) => !ownsNodeOrAncestor(locations, id, gateUid));
    if (unowned.length > 0) {
      alert(`You can only move locations you own. ${unowned.length} of the selected location${unowned.length === 1 ? " isn't" : "s aren't"} yours.`);
      return;
    }
    // Cycle guard only matters when nesting under a real parent.
    if (newParentId) {
      const allMovingSubtrees = new Set();
      for (const id of locationIds) for (const x of locationSubtree(locations, id)) allMovingSubtrees.add(x);
      if (allMovingSubtrees.has(newParentId)) { alert("Can't move locations into one of themselves."); return; }
    }
    // GUARD-AND-INFORM (aggregated): if the destination has members that any moved
    // location doesn't, this move widens who can see those locations. One combined
    // confirm. Skipped gracefully if the access RPC is unavailable.
    if (newParentId) {
      const destAccess = await loadNodeAccess(newParentId);
      if (destAccess !== null && destAccess.length > 0) {
        const gainerById = new Map();
        for (const id of locationIds) {
          const curAccess = await loadNodeAccess(id);
          if (curAccess === null) { gainerById.clear(); break; } // RPC unavailable — skip guard
          const curIds = new Set(curAccess.map((a) => a.user_id));
          for (const a of destAccess) if (!curIds.has(a.user_id)) gainerById.set(a.user_id, a);
        }
        const dest = getLocationById(locations, newParentId);
        const anyCrossOwner = locationIds.some((id) => {
          const l = getLocationById(locations, id);
          return l && dest && l.owner_id && dest.owner_id && l.owner_id !== dest.owner_id;
        });
        const parts = [];
        if (anyCrossOwner) parts.push(`What you're moving will belong to the owner of "${dest?.name || "the destination"}". If they remove you from their space, you lose access to it.`);
        if (gainerById.size > 0) {
          const gainers = Array.from(gainerById.values());
          const names = gainers.slice(0, 3).map((g) => (g.display_name && g.display_name.trim()) || g.email || "someone");
          const more = gainers.length > 3 ? ` and ${gainers.length - 3} more` : "";
          parts.push(`${names.join(", ")}${more} will gain access to what you're moving.`);
        }
        if (parts.length > 0) {
          const ok = window.confirm(`Move ${locationIds.length} location${locationIds.length === 1 ? "" : "s"}?\n\n` + parts.join("\n\n"));
          if (!ok) return;
        }
      }
    }
    const { error } = await supabase.from("nodes")
      .update({ parent_id: newParentId })
      .in("id", locationIds);
    if (error) { alert(friendlyError(error, "move locations")); return; }
    // CEDE MODEL: any moved location (plus everything in its subtree — locations AND items)
    // now belongs to the destination tree's owner. added_by preserves credit. reloadAll refreshes.
    const destLoc2 = newParentId ? getLocationById(locations, newParentId) : null;
    if (destLoc2 && destLoc2.owner_id) {
      const transferIds = [];
      for (const id of locationIds) {
        const l = getLocationById(locations, id);
        if (l && l.owner_id !== destLoc2.owner_id) transferIds.push(...Array.from(locationSubtree(locations, id)));
      }
      if (transferIds.length) {
        const { error: tErr } = await supabase.from("nodes").update({ owner_id: destLoc2.owner_id }).in("id", transferIds).eq("kind", "location");
        if (tErr) console.error("Subtree location transfer failed:", tErr);
        const { error: iErr } = await supabase.from("nodes").update({ owner_id: destLoc2.owner_id }).in("parent_id", transferIds).eq("kind", "item");
        if (iErr) console.error("Subtree item transfer failed:", iErr);
      }
    }
    setLocations((p) => p.map((l) => locationIds.includes(l.id) ? { ...l, parent_location_id: newParentId } : l));
    await reloadAll();
  };

  const deleteLocationCascade = async (locationId) => {
    const subtree = locationSubtree(locations, locationId);
    const subtreeIds = Array.from(subtree);

    // Items pointing into the subtree (this location + all sub-locations).
    const { data: countRows } = await supabase
      .from("nodes")
      .select("id, parent_id")
      .eq("kind", "item")
      .in("parent_id", subtreeIds);
    const itemsAffected = countRows || [];

    const nowIso = new Date().toISOString();

    // Always delete the contents: soft-delete every item in the subtree along with it.
    if (itemsAffected.length > 0) {
      const { error: delItemsErr } = await supabase
        .from("nodes")
        .update({ deleted_at: nowIso })
        .in("id", itemsAffected.map((it) => it.id));
      if (delItemsErr) { alert(friendlyError(delItemsErr, "delete items")); return false; }
      // Best-effort audit log (RLS may block; non-fatal to the delete).
      supabase.from("events").insert(itemsAffected.map((it) => ({
        user_id: session.user.id, node_id: it.id,
        event_type: "node_deleted",
        payload: { reason: "location_deleted_with_contents", from_parent_id: it.parent_id },
        app_version: APP_VERSION,
      }))).then(({ error }) => { if (error) console.warn("delete-event log skipped:", error.message); });
    }

    // Soft-delete the location nodes (subtree)
    const { data: deletedRows, error: delErr } = await supabase
      .from("nodes")
      .update({ deleted_at: nowIso })
      .in("id", subtreeIds)
      .select("id");
    if (delErr) { alert(friendlyError(delErr, "delete location")); return false; }
    if (!deletedRows || deletedRows.length === 0) {
      alert("Delete didn't remove anything — you may not have permission, or the location was already gone. Try refreshing.");
      return false;
    }

    // Refresh app state (awaited so the grid reflects the delete before we navigate).
    await loadLocations().then(setLocations);
    await loadItems().then(setItems);
    return true;
  };

  // ── Scan ops ─────────────────────────────────────────────────────
  // Optimistic approve: remove the tap from the queue immediately so the UI can
  // advance to the next item without waiting on the network. The save runs in the
  // background. If it fails, the tap is re-inserted into the queue as an error so
  // nothing is lost and the user can retry.
  // Undo and redo for the approve queue, one step deep each way.
  //
  // Approve pops the tap from the queue before the save comes back, so the review flow can advance
  // without waiting on the network. That is also why the scan you had just got wrong was the one you
  // could not reach: the queue arrows stepped through what was still *unapproved*, so pressing back
  // from a mistake skipped over it. Hence a real undo rather than better navigation.
  //
  // Every scan handled this session, in the order it was handled, and which one the approve screen is
  // currently showing (null = a scan that has not been handled yet). This replaced a one-deep
  // undo/redo pair; src/scan-history.js says why, and the short version is that the undo entry was
  // assigned in a network callback and so pointed at whichever save finished last.
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(null);

  // Open the history at a position, which is what the arrows and the camera's count both do. The scan
  // shown is the entry's own snapshot, so the screen is the scan as it was — same photo, same tap
  // coordinates — rather than a summary of it.
  const openHistoryAt = (index) => {
    setHistoryIndex(index);
    if (index === null) {
      // Back to the present: whatever is still waiting, or the camera if nothing is.
      const next = queue.find((t) => t.status === "done" || t.status === "pending" || t.status === "error");
      if (next) { setVerifyingId(next.id); setView("verify"); } else { setVerifyingId(null); setView("scan"); }
      return;
    }
    setVerifyingId(null);
    setView("verify");
  };

  const stepHistoryBy = (delta) => openHistoryAt(stepHistory(history, historyIndex, delta));

  // Take an approved scan back out: soft-delete the row and mark the entry declined. This is what the
  // old undo did, with the difference that it acts on the entry you are looking at rather than on a
  // guess about which one you meant.
  //
  // The tap is NOT put back in the queue. It is already in the history and the arrows reach it, so
  // refilling the queue would make one scan appear in two places with two different meanings.
  const unapproveEntry = async (entry) => {
    if (!entry?.nodeId) return;
    const { error } = await supabase.from("nodes")
      .update({ deleted_at: new Date().toISOString() }).eq("id", entry.nodeId);
    if (error) {
      // The row is still live, so calling it declined here would let the user save a second copy.
      alert(friendlyError(error, "take this back"));
      return;
    }
    supabase.from("events").insert({
      user_id: session.user.id, node_id: entry.nodeId,
      event_type: "node_deleted",
      payload: { name_at_deletion: entry.label, undo_of_approve: true },
      app_version: APP_VERSION,
    }).then(({ error: evErr }) => { if (evErr) console.warn("event log skipped:", evErr.message); });
    setItems((p) => p.filter((i) => i.id !== entry.nodeId));
    setSessionSaved((p) => p.filter((i) => i.id !== entry.nodeId));
    // Stays where it is, in both senses: the entry keeps its position in the list, and the screen keeps
    // showing it. The reason for pressing this was to change this scan, so being thrown somewhere else
    // would hide whether it worked.
    setHistory((h) => appendHandled(h, { tapId: entry.tapId, state: "declined", nodeId: null }));
  };

  // Approve a scan that is currently declined, with whatever the form now says. Routed through the
  // same approveTap the queue uses, which is why that function looks the tap up in the history as well
  // as the queue: ownership, tags, the embedding and the approve event are all decided there and must
  // not get a second implementation.
  const reapproveEntry = (entry, finalLabel, opts) => {
    if (!entry?.snapshot) return;
    approveTap(entry.tapId, finalLabel, opts || entry.opts || {});
  };

  // Write the form through to an item that already exists.
  //
  // Every one of these is the same operation the item's own screen performs, reused rather than
  // reimplemented — a rename here has to log the same `label_edited` event and re-embed the same way, and
  // a move has to go through the ownership gate that stops an item being pulled out of a tree you do not
  // own. The alternative was making the fields read-only once a scan is approved, which is worse for an
  // obvious reason: a field you can type into and whose contents then vanish is not a field.
  const saveEntryEdits = async (entry, draft) => {
    if (!entry?.nodeId || !draft) return;
    // `entry.stored`, not the snapshot's draft: the snapshot's draft is where leaving the screen stashes
    // the form, so an edit that had been arrowed away from and returned to compared equal to itself and
    // every write was skipped. What the database has is the only thing to diff against.
    const saved = entry.stored || verifyDraft(entry.snapshot);
    let ok = true;
    if (String(saved.label ?? "").trim() !== String(draft.label ?? "").trim()) {
      ok = (await renameItem(entry.nodeId, draft.label)) && ok;
    }
    if ((Number(saved.quantity) || 1) !== (Number(draft.quantity) || 1)) {
      ok = (await setItemQuantity(entry.nodeId, draft.quantity)) && ok;
    }
    const nextLocId = draft.locOverride?.id ?? null;
    if ((saved.locOverride?.id ?? null) !== nextLocId && nextLocId) {
      ok = (await moveItemToDestination(entry.nodeId, rootLocationIdOf(locations, nextLocId), nextLocId)) && ok;
    }
    const tagKey = (t) => (Array.isArray(t) ? [...t].sort().join("\u0000") : "");
    if (tagKey(saved.tags) !== tagKey(draft.tags)) {
      try {
        const res = await setNodeTags(entry.nodeId, draft.tags || []);
        const stored = Array.isArray(res?.tags) ? res.tags : (draft.tags || []);
        setItems((p) => p.map((i) => (i.id === entry.nodeId ? { ...i, tags: stored } : i)));
      } catch (e) {
        alert(friendlyError(e, "save the tags"));
        ok = false;
      }
    }
    // Only record the new form as the stored one if it actually stored. Patching regardless would leave
    // the screen claiming a value the database refused, and the next comparison would see no difference.
    if (!ok) return;
    setHistory((h) => patchHandled(h, entry.tapId, {
      label: draft.label,
      snapshot: { ...entry.snapshot, label: draft.label, draft },
      // What the database now has, which is the only thing "is there anything to save" may be measured
      // against.
      stored: draft,
      locName: nextLocId ? (getLocationById(locations, nextLocId)?.name || entry.locName) : entry.locName,
    }));
    setSessionSaved((p) => p.map((i) => (i.id === entry.nodeId ? { ...i, label: draft.label } : i)));
  };

  // Point the camera at a different location mid-session. Shared by the full-tree picker and by
  // the < / > arrows on the scan header, which are the same operation reached two ways — one
  // deliberate jump, one step sideways — and must not drift apart.
  const retargetScan = (targetId, { nested } = {}) => {
    if (!targetId) return;
    const targetLoc = getLocationById(locations, targetId);
    const rootId = rootLocationIdOf(locations, targetId);
    const targetName = targetLoc?.name || (spaces.find((s) => s.id === targetId)?.name) || "";
    // `nested` says whether this is a location inside a tree or the root of one. The picker knows
    // because it has separate rows for the two; the arrows work it out from the tree.
    const isNested = nested === undefined ? rootId !== targetId : nested;
    setScanSwitching(true);
    setActiveSpaceId(rootId);
    setActiveLocationId(isNested ? targetId : null);
    setSessionLocation(targetName);
    setSessionLocationId(targetId);
    // Back-fill: any already-queued taps that were captured with NO location (add-from-home
    // before a location was chosen) adopt this new destination, so the user doesn't have to set
    // each one at approve. Taps captured with a real stamp keep theirs — retargeting must not
    // move items that were already scanned somewhere.
    setQueue((prev) => prev.map((t) =>
      !t.capturedLocationId
        ? { ...t, capturedLocationId: targetId, capturedLocationName: targetName }
        : t
    ));
    setTimeout(() => setScanSwitching(false), 350);
  };

  const approveTap = (id, finalLabel, opts = {}) => {
    // The queue holds scans not yet handled; the history holds the ones that have been. Approving
    // something already declined comes through here with the same arguments, so both are looked up —
    // otherwise re-approving would need its own copy of the ownership, tag, embedding and event rules.
    const tap = queue.find((t) => t.id === id) || findHandled(history, id)?.snapshot;
    if (!tap) return;
    const asLocation = !!opts.isLocation;
    // Extra photos of the way to the thing. They ride along with the item and change nothing about
    // where it goes — a location scan carries none, since "how to find this shelf" is what the
    // shelf's own place in the tree already says.
    const trail = (!asLocation && Array.isArray(opts.trail)) ? opts.trail.filter(Boolean) : [];
    // Destination resolution: per-item override > the tap's CAPTURE-TIME stamp > current
    // session (legacy taps without stamps). The stamp means retargeting the session never
    // silently changes where already-scanned items get filed.
    const overrideLocId = opts.overrideLocationId || null;
    const hasStamp = "capturedLocationId" in tap;
    const locId = overrideLocId || (hasStamp ? (tap.capturedLocationId || null) : (sessionLocationId || null));
    const fallbackLocName = hasStamp ? (tap.capturedLocationName || "") : sessionLocation;
    // Display name for the saved item's location: an approve-time override (picked/created
    // in the verify picker) wins — fallbackLocName is empty in a no-location session.
    const displayLocName = opts.overrideLocationName || fallbackLocName;
    // Normally a scan must attach to a location. EXCEPTION: marking a scan as a LOCATION
    // with no target is valid — it becomes a TOP-LEVEL location (parent_id null).
    // Only block the "plain item with nowhere to go" case.
    //
    // A homeless item is not a mildly untidy one: every list in the app is built by walking down
    // from a space, so the row would exist in the database and no screen could reach it. Both
    // callers collect a destination before getting here (Verify's Approve and the queue pill both
    // open the picker), so reaching this is a bug — say so rather than returning quietly, which is
    // what let a scan look approved and then vanish with the session.
    if (!asLocation && !locId) {
      console.warn("Approve blocked: no destination for tap", id);
      alert("Choose a location for this first — nothing is saved without one.");
      return;
    }

    // Snapshot the tap (with the approved label) so we can restore it on failure.
    const snapshot = { ...tap, label: finalLabel, status: "done" };

    // If this approve will jump us into scanning the new location, block captures until
    // the retarget lands (the "Switching location…" pill).
    if (opts.scanMoreHere) setScanSwitching(true);

    // Remove from the queue right away — this lets the verify flow advance instantly.
    queueHandledRef.current.add(id); // explicit handling — queue-loss guard must ignore this
    setQueue((p) => p.filter((t) => t.id !== id));
    // Recorded HERE, not in the save's completion below. That is the whole fix: the old undo entry was
    // assigned when the upload finished, so two concurrent saves landing out of order left it pointing
    // at the wrong scan. `nodeId` and the stored photo are unknown at this moment and get patched in
    // when the save returns; the position in the list is fixed now and never moves.
    // The snapshot carries the form as it was when Approve was pressed, not as the scan arrived. A draft
    // is only stashed on the way out of the approve screen, so approving straight after editing sent the
    // edits to the save and left the snapshot showing the model's original answer — which is what made
    // reopening one from the history look like it had lost the label, the count and the destination.
    // `stored` is what the database has; `snapshot.draft` is what the form shows. They start equal and
    // must stay separate, because leaving the screen stashes the form so an edit survives being arrowed
    // away from — and if the dirty check read that stash, an unsaved edit would look committed and the
    // Save button would vanish while the item still had the old name.
    const approvedDraft = draftFromApprove(tap, finalLabel, opts);
    setHistory((h) => appendHandled(h, {
      tapId: id, label: finalLabel, state: "approved", opts,
      snapshot: { ...tap, label: finalLabel, draft: approvedDraft },
      stored: approvedDraft,
      isLocation: asLocation, locName: displayLocName || null,
    }));
    // Mark this id as saving-in-flight so the scan view shows a spinner and "Done"
    // waits until all background saves drain.
    setApprovingIds((p) => { const n = new Set(p); n.add(id); return n; });

    // Fire the save in the background.
    (async () => {
      try {
        const { item } = await saveItem({
          image: tap.cleanImage || tap.image,
          name: finalLabel,
          session_id: scanSessionId || undefined,
          proposed_label: tap.label || null,        // VLM's original proposal (evidence)
          confidence_score: null,
          parent_id: locId || undefined,
          name_for_new_location: locId ? undefined : (fallbackLocName || undefined),
          extra_images: trail.length ? trail : undefined,
          tap_x: tap.x, tap_y: tap.y,
          image_width: tap.imageWidth, image_height: tap.imageHeight,
          quantity: asLocation ? 1 : cleanQuantity(opts.quantity),
          // Whatever the user settled on, which may be fewer than the model proposed. The server
          // drops anything outside the vocabulary; a location gets none, since "what kind of
          // object is this shelf" has no answer.
          tags: asLocation ? [] : (Array.isArray(opts.tags) ? opts.tags : (tap.tags || [])),
          // Distinguishes "the first answer was right" from "the user took one of the
          // alternatives" from "they typed their own" — which is the only way to tell whether
          // offering choices is actually earning its extra tokens.
          label_source: labelSourceFor(tap, finalLabel),
          app_version: APP_VERSION,
        });
        // If the user marked this scan as a LOCATION, flip the freshly-saved node's
        // kind. It's saved as an item first (that's what detect.js does), then converted.
        if (asLocation) {
          // The flipped node becomes a LOCATION, so it takes the tree's owner (items are
          // creator-owned; locations are tree-owned). Top-level flips stay the scanner's.
          const parentForOwner = getLocationById(locations, item.parent_id || locId);
          const treeOwner = (parentForOwner && parentForOwner.owner_id) || session.user.id;
          const { error: flipErr } = await supabase.from("nodes").update({ kind: "location", owner_id: treeOwner }).eq("id", item.id);
          if (flipErr) { console.error("kind flip failed:", flipErr); alert(friendlyError(flipErr, "save this as a location")); }
          // Keep the node_images row — the location shows the photo it was scanned from.
          // Event log is best-effort: a blocked events RLS must NOT fail the approval.
          supabase.from("events").insert({
            user_id: session.user.id, node_id: item.id,
            event_type: "kind_changed",
            payload: { from: "item", to: "location", name: finalLabel, at_scan: true },
            app_version: APP_VERSION,
          }).then(({ error }) => { if (error) console.warn("kind_changed event skipped:", error.message); });
          // Sub-location prediction: create the checked predicted compartments as child
          // LOCATIONS inside this new location (one scan → place + its sub-locations).
          const predSubs = Array.isArray(opts.predictedSublocations) ? opts.predictedSublocations.filter((s) => (s || "").trim()) : [];
          if (predSubs.length) {
            const rows = predSubs.map((name) => ({
              owner_id: treeOwner, name: name.trim(), kind: "location",
              parent_id: item.id, added_by: session.user.id,
            }));
            const { data: created, error: pErr } = await supabase.from("nodes").insert(rows).select("id, name, parent_id");
            if (pErr) { console.error("Predicted sub-locations insert failed:", pErr); }
            else if (created?.length) {
              created.forEach((node) => embedNodeAfterWrite(node.id));
              supabase.from("events").insert({
                user_id: session.user.id, node_id: item.id,
                event_type: "predicted_sublocations_added",
                payload: { count: created.length, labels: created.map((n) => n.name), from_scan: true },
                app_version: APP_VERSION,
              }).then(({ error }) => { if (error) console.warn("predicted_sublocations event skipped:", error.message); });
            }
          }
          // Refresh locations so the new location (and its sub-locations) show in the tree.
          const freshLocs = await loadLocations();
          setLocations(freshLocs);
          // Session-saved accounting: a scan approved AS A LOCATION is still something this
          // session produced — show it in the saved strip/summary (was items-only).
          setSessionSaved((p) => [{
            id: item.id, label: finalLabel, location: "New location",
            image: item.image_url, raw_image_path: item.image_path, isLocation: true,
          }, ...p]);
          // A location approve is in the history too — it is something this session did, and the
          // arrows are navigation rather than a promise that everything can be reversed. Taking one
          // back is refused further down, for the reasons in the comment there.
          setHistory((h) => patchHandled(h, id, {
            nodeId: item.id, label: item.name || finalLabel,
            image: item.image_url || null, rawImagePath: item.image_path || null,
            locName: "New location",
          }));
          // If this became a TOP-LEVEL location (no parent), it's a new "space" — the home
          // page renders from the spaces list, which loadLocations doesn't populate. Refresh
          // spaces too so it appears without a manual page refresh.
          if (!locId) { const sp = await loadSpaces(); setSpaces(sp); }
          if (opts.scanMoreHere) {
            // Pre-declared: retarget the session at the new location. CRITICAL: do NOT
            // clear the queue — that was the disappearing-queue bug (it wiped still-
            // unapproved scans). Remaining taps carry their capture-time stamp, so they
            // still file where they were scanned. No view yank either: if you're mid-
            // review of the next tap, you finish, and the review flow drops you onto the
            // scan screen — which now points at the new location.
            const newLoc = { id: item.id, name: item.name || finalLabel };
            const rootId = rootLocationIdOf(freshLocs, newLoc.id);
            setActiveSpaceId(rootId);
            setActiveLocationId(newLoc.id);
            setSessionLocation(newLoc.name);
            setSessionLocationId(newLoc.id);
            setScanSwitching(false);
          }
          // (No offer popup when scanMoreHere is off — the user just continues.)
        } else {
          const local = {
            id: item.id, label: item.name || finalLabel,
            quantity: item.quantity ?? cleanQuantity(opts.quantity),
            location_id: item.parent_id,
            location: item.parent_name || displayLocName,
            image: item.image_url, raw_image_path: item.image_path,
            // Ownership follows the tree; attribution (added_by) is always the scanner. Stamp
            // both so the "added by" badge renders immediately, not only after a reload.
            owner_id: item.owner_id || session.user.id,
            added_by: session.user.id,
            // Carry the tap coords onto the optimistic item so MarkedImg can draw the
            // crosshair right away — without this, the marker only appeared after a reload
            // (which is when hydrateItemNodes fetches tap from node_images).
            tap: (tap.x != null && tap.y != null) ? { x: tap.x, y: tap.y } : null,
          };
          setItems((p) => [local, ...p]);
          setSessionSaved((p) => [local, ...p]);
          // Fill in what only the server could tell us. The entry's place in the history was fixed
          // when Approve was pressed, so this cannot reorder anything however late it arrives — which
          // is precisely what went wrong when the undo entry was created here instead.
          setHistory((h) => patchHandled(h, id, {
            nodeId: item.id,
            label: item.name || finalLabel,
            image: item.image_url || null,
            rawImagePath: item.image_path || null,
            locName: item.parent_name || displayLocName || null,
          }));
          loadLocations().then(setLocations);
          // A save with no target location can create a NEW top-level location server-side
          // (name_for_new_location, no parent) — refresh spaces so home shows it.
          if (!locId) loadSpaces().then(setSpaces);
        }
      } catch (e) {
        console.error("Background save failed:", e);
        if (opts.scanMoreHere) setScanSwitching(false);
        // Re-insert the tap as an error so it resurfaces with retry/decline controls.
        // Un-mark it as handled so the queue-loss guard protects it again.
        queueHandledRef.current.delete(id);
        const msg = (e && e.code === "timeout") ? "Weak signal — tap to retry save"
          : (typeof navigator !== "undefined" && navigator.onLine === false) ? "You're offline — tap to retry save"
          : "Save failed — tap to retry";
        setQueue((p) => {
          if (p.some((t) => t.id === id)) return p; // already back somehow
          return [...p, { ...snapshot, status: "error", error: msg }];
        });
        // And it is no longer something this session handled: the queue owns it again, waiting on a
        // retry. Leaving the entry would claim an approval with no row behind it, in a list whose whole
        // job is to be a truthful account of what happened.
        setHistory((h) => removeHandled(h, id));
      } finally {
        setApprovingIds((p) => { const n = new Set(p); n.delete(id); return n; });
      }
    })();
  };

  // Retry a failed *save* (item that errored during background save).
  const retrySave = (id) => {
    const tap = queue.find((t) => t.id === id);
    if (!tap) return;
    // Re-run with the tap's own flags so a failed LOCATION save retries as a location.
    // (Per-approve edits made in the Verify card aren't stored on the tap, so retry
    // replays the VLM guess with all predictions — strictly better than dropping them.)
    const asLocation = tap.capturedAsLocation ?? !!tap.is_location_guess;
    approveTap(id, tap.label || "", {
      isLocation: asLocation,
      predictedSublocations: (asLocation && Array.isArray(tap.predicted_sublocations)) ? tap.predicted_sublocations : [],
      // The trail rides on the tap, so a retry keeps it — a failed save must not quietly turn a
      // reference-carrying item into an ordinary one filed wherever the camera happened to be.
      trail: Array.isArray(tap.trail) ? tap.trail : [],
    });
  };

  // Approving straight from the pill on the camera screen, without opening the Verify card. The
  // tap's own flags are all there is to go on: the camera's location | item toggle beats the
  // model's guess, because the user said which this was before they took the picture.
  //
  // A tap captured with no destination (the camera opened from home) gets the picker instead of the
  // save, and the approve finishes once a location comes back. This tapped a check mark and did
  // nothing at all before — the scan stayed queued, looking approved, and went with the session.
  const pendingPillApproveRef = useRef(null);
  const approveFromQueue = (id, override) => {
    const tap = queue.find((t) => t.id === id);
    if (!tap || tap.status !== "done" || !(tap.label || "").trim()) return;
    const asLocation = tap.capturedAsLocation ?? !!tap.is_location_guess;
    const dest = override ? override.id : (tap.capturedLocationId || sessionLocationId || null);
    if (!asLocation && !dest) {
      pendingPillApproveRef.current = id;
      setScanRetargetOpen(true);
      return;
    }
    approveTap(id, tap.label, {
      isLocation: asLocation,
      predictedSublocations: (asLocation && Array.isArray(tap.predicted_sublocations)) ? tap.predicted_sublocations : [],
      trail: Array.isArray(tap.trail) ? tap.trail : [],
      // Passed explicitly rather than read back off the retargeted tap: the back-fill that
      // retargeting does is a state update this call cannot see yet.
      overrideLocationId: override ? override.id : undefined,
      overrideLocationName: override ? override.name : undefined,
    });
  };

  // `draft` is the form as it was when Decline was pressed, handed over the same way `opts` is on an
  // approve. Without it, editing a label and then declining left the history showing the model's original
  // answer — and re-approving from there would save the name the user had already replaced.
  const declineTap = (id, draft = null) => {
    const tap = queue.find((t) => t.id === id);
    queueHandledRef.current.add(id);
    setQueue((p) => p.filter((t) => t.id !== id));
    // A decline is something you did, so it belongs in the history beside the approvals. It used to be
    // recorded nowhere at all, which meant changing your mind about one was impossible: the scan was
    // gone from the queue and no control could reach it. Recorded synchronously, like an approve, and
    // holding the snapshot — approving it later needs the photo, and nothing was uploaded for it that
    // the app could read back.
    if (tap) {
      const kept = draft || tap.draft || null;
      setHistory((h) => appendHandled(h, {
        tapId: id, label: kept?.label || tap.label || "", state: "declined",
        snapshot: { ...tap, label: kept?.label || tap.label, draft: kept },
        locName: tap.capturedLocationName || null,
      }));
    }
    // Persist the rejection for training data — but only if the VLM actually returned a
    // label (status "done"). Taps that errored before labeling have no useful signal and
    // no clean frame worth storing. Fire-and-forget; a failed reject-log must not disrupt UX.
    if (tap && tap.status === "done" && (tap.cleanImage || tap.image)) {
      rejectScan({
        image: tap.cleanImage || tap.image,
        proposed_label: tap.label || null,
        tap_x: tap.x, tap_y: tap.y,
        image_width: tap.imageWidth, image_height: tap.imageHeight,
        retry_count: tap.retryCount || 0,
        session_id: scanSessionId || undefined,
        app_version: APP_VERSION,
      }).catch((e) => console.error("reject_scan failed (non-fatal):", e));
    }
  };
  const retryTap = async (id) => {
    const tap = queue.find((t) => t.id === id);
    if (!tap) return;
    setQueue((p) => p.map((t) => t.id === id ? { ...t, status: "pending", error: null } : t));
    try {
      // Rebuild the context from the tap's CAPTURE-TIME stamp (the session may have
      // moved since), so a retried label sees the same context the original would have.
      const retryCtx = buildScanContext(locations, items, tap.capturedLocationId || null, tap.capturedLocationName || "", { tree: scanConfig.tree });
      // The mode the tap was CAPTURED in, not whatever the camera is set to now: a retry has to ask
      // the same question the first attempt did, or the label comes back about a different subject.
      const retryConfig = scanConfigForMode(scanConfig, tap.capturedAsLocation ? "location" : "item");
      const result = await labelMarkedWithRetry(tap.image, retryConfig, retryCtx, scanSessionId);
      // A retry re-asks the model, so its fresh answer has to reach any draft already stashed for this
      // scan — otherwise reopening it shows the label the user just asked to have replaced.
      setQueue((p) => p.map((t) => {
        if (t.id !== id) return t;
        const refreshed = { ...t, ...tapFieldsFromScan(result) };
        return { ...refreshed, draft: draftAfterRetry(refreshed, t.draft) };
      }));
    } catch (err) {
      setQueue((p) => p.map((t) => t.id === id ? { ...t, status: "error", error: err.message } : t));
    }
  };

  // The approve screen shows either a queued scan or one from the history, never both, and
  // `historyIndex` is which. A history entry supplies its own snapshot, so the screen renders from the
  // same shape either way and needs no second code path for the fields.
  const historyEntry = historyIndex === null ? null : (history[historyIndex] || null);
  const verifyingTap = historyEntry ? historyEntry.snapshot : queue.find((t) => t.id === verifyingId);

  // Approving or declining anything leaves the history and returns to the present, because the action
  // just taken IS the newest entry — staying put would show it in the middle of a list it now ends.
  const advanceVerify = (handledId) => {
    if (historyIndex !== null) { setHistoryIndex(null); }
    return advanceQueue(handledId);
  };

  // After approving/declining in verify, jump to the next pending tap (if any),
  // otherwise drop back to the scan screen so the user can capture more.
  const advanceQueue = (handledId) => {
    setQueue((prev) => {
      // prev still includes the handled tap only on decline-before-state-settle;
      // compute the next from the freshest queue we can see.
      const remaining = prev.filter((t) => t.id !== handledId);
      const next = remaining.find((t) => t.status === "done" || t.status === "pending" || t.status === "error");
      if (next) {
        setVerifyingId(next.id);
      } else {
        setVerifyingId(null);
        setView("scan");
      }
      return prev; // don't mutate here; approve/decline already update the queue
    });
  };

  // Safety: the entry being looked at can go out from under the screen — a save failing takes its scan
  // back out of the history while it is open. Without this the screen renders nothing at all: there is no
  // entry and no `verifyingId` either, so the recovery below cannot fire and the result is a blank view.
  useEffect(() => {
    if (view === "verify" && historyIndex !== null && !history[historyIndex]) {
      openHistoryAt(history.length ? Math.min(historyIndex, history.length - 1) : null);
    }
  }, [view, historyIndex, history]);

  // Safety: if we're in verify but the current tap vanished and nothing advanced us, recover to scan.
  useEffect(() => {
    if (view === "verify" && verifyingId && !verifyingTap) {
      const next = queue.find((t) => t.status === "done" || t.status === "pending" || t.status === "error");
      if (next) setVerifyingId(next.id);
      else { setVerifyingId(null); setView("scan"); }
    }
  }, [view, verifyingId, verifyingTap, queue]);

  // Guard against silent queue loss. Taps should only leave the queue via approveTap or
  // declineTap (which record the id in queueHandledRef). If the queue shrinks while we're
  // in scan/verify by taps that were NOT explicitly handled, restore them — a location or
  // view change should never drop unapproved scans.
  const queueRef = useRef(queue);
  useEffect(() => {
    const prev = queueRef.current;
    if ((view === "scan" || view === "verify") && prev.length > queue.length) {
      const currentIds = new Set(queue.map((t) => t.id));
      const dropped = prev.filter((t) => !currentIds.has(t.id) && !queueHandledRef.current.has(t.id));
      if (dropped.length) {
        console.warn("Queue guard: restoring", dropped.length, "unapproved tap(s)");
        setQueue((cur) => {
          const have = new Set(cur.map((t) => t.id));
          return [...cur, ...dropped.filter((t) => !have.has(t.id))];
        });
      }
    }
    queueRef.current = queue;
    if (queueHandledRef.current.size > 200) queueHandledRef.current = new Set();
  }, [queue, view]);

  if (dataLoading) return <div style={S.bootScreen}><Loader size={28} /></div>;

  return (
    <div>
      {view === "spaces" && <SpacesHome
        spaces={spacesWithPhotos} locations={locations}
        spaceStats={spaceStats}
        items={visibleItems}
        stableOrder={searchConfig.stableOrder}
        userId={session.user.id}
        onOpenItem={(it) => { setActiveItem(it); setView("detail"); }}
        onOpenSpace={(id) => { setActiveSpaceId(id); setView("space"); }}
        unsortedCount={items.filter((it) => !it.location_id && !it.deleted_at).length}
        onAddItem={() => {
          // The camera from home: straight in, with no location set.
          // The tappable "Adding to" header lets them choose or create one in-scan
          // (or per-item at approve). No location picker preamble, no tutorial.
          setActiveSpaceId(null);
          setActiveLocationId(null);
          scanFromScreen(null, "");
        }}
        onOpenAllItems={() => setItemsScope({ id: null, name: null })}
        onAddManually={() => manualFromScreen(null, null)}
        onRenameSpace={renameSpace}
        onDeleteSpace={deleteSpace}
        onSetSpacePhoto={setLocationPhoto}
        onSetSpaceIcon={setLocationIcon}
        onMoveLocations={moveLocations}
        onCreateSpacePure={createSpace}
        onCreateLocationPure={createLocationPure}
        invitations={invitations}
        onAcceptInvite={acceptInvite}
        onDeclineInvite={declineInvite}
        myName={myName}
        onSaveName={async (name) => { const res = await setDisplayName(name); setMyName(res.display_name || ""); }}
        scanConfig={scanConfig}
        onSetScanConfig={setScanConfig}
        searchConfig={searchConfig}
        onSetSearchConfig={setSearchConfig}
        viewMode={viewMode}
        onSetViewMode={setViewMode}
        itemCounts={locationItemCounts}
        treeOps={treeOps}
        onSignOut={signOut}
        userEmail={session.user.email}
      />}
      {view === "space" && activeSpace && <SpaceView
        onAddManually={(id, name) => manualFromScreen(id, name)}
        canEdit={canEditLocationId(activeSpace.id)}
        space={activeSpace}
        items={items}
        locations={locations}
        spaces={spaces}
        userId={session.user.id}
        stableOrder={searchConfig.stableOrder}
        viewMode={viewMode}
        itemCounts={locationItemCounts}
        treeOps={treeOps}
        onBack={() => setView("spaces")}
        onOpenItem={(it) => { setActiveItem(it); setView("detail"); }}
        onOpenItemsHere={() => setItemsScope({ id: activeSpace.id, name: activeSpace.name })}
        onOpenLocation={(id) => { setActiveLocationId(id); setView("location_view"); }}
        onStartScan={() => { setSessionSaved([]); resetQueue(); setView("location"); }}
        onScanAtLocation={scanFromScreen}
        onRenameSpace={(newName) => renameSpace(activeSpace.id, newName)}
        onDeleteSpace={() => deleteSpace(activeSpace.id)}
        onSetSpacePhoto={async (file) => { await setLocationPhoto(activeSpace.id, file); }}
        onSetSpaceIcon={async (icon) => { await setLocationIcon(activeSpace.id, icon); }}
        onReparentLocation={async (newParentId) => { await reparentLocation(activeSpace.id, newParentId); }}
        onReparentChildLocation={async (id, newParentId) => { await reparentLocation(id, newParentId); }}
        onCreateLocation={createLocation}
        onCreateLocationPure={createLocationPure}
        onCreateSpace={createSpace}
        onMoveItemsToDestination={moveItemsToDestination}
        onDeleteItems={deleteItems}
        onInvite={inviteToSpace}
        onMembersChanged={reloadAll}
        onAddExistingItems={addExistingItemsToSpace}
      />}
      {view === "location_view" && activeSpace && activeLocationId && (() => {
        const loc = getLocationById(locations, activeLocationId);
        if (!loc) {
          // Location was deleted or not loaded; recover by going back to space view
          setTimeout(() => { setActiveLocationId(null); setView("space"); }, 0);
          return null;
        }
        const subtree = locationSubtree(locations, activeLocationId);
        const subtreeItems = items.filter((it) => it.location_id && subtree.has(it.location_id));
        const path = locationPath(locations, activeLocationId);
        const childLocs = locationChildren(locations, activeLocationId);
        return (
          <LocationView
            onAddManually={(id, name) => manualFromScreen(id, name)}
            space={activeSpace}
            location={loc}
            canEdit={canEditLocationId(loc.id)}
            path={path}
            childLocations={childLocs}
            allLocations={locations}
            stableOrder={searchConfig.stableOrder}
            viewMode={viewMode}
            itemCounts={locationItemCounts}
            treeOps={treeOps}
            items={subtreeItems}
                userId={session.user.id}
            onBack={() => {
              // In the node model the space root is itself a location node. When backing
              // out, if the parent IS the space root, jump straight to the space view —
              // don't open the root as a location_view (that caused a double "visit").
              const parentId = loc?.parent_location_id;
              if (parentId && parentId !== activeSpaceId) {
                setActiveLocationId(parentId);
              } else {
                setActiveLocationId(null);
                setView("space");
              }
            }}
            onOpenItem={(it) => { setActiveItem(it); setView("detail"); }}
            onOpenItemsHere={() => setItemsScope({ id: loc.id, name: loc.name })}
            onOpenChildLocation={(id) => { setActiveLocationId(id); }}
            onCreateChildLocation={async (name) => {
              await createLocation(name, activeLocationId, { noNavigate: true });
            }}
            onStartScan={() => scanFromScreen(loc?.id, loc?.name)}
            onMoveItemsHere={async (itemIds, alsoAddToSpace) => {
              await moveExistingItemsToLocation(itemIds, loc?.name || "", alsoAddToSpace, activeLocationId);
            }}
            onDeleteLocation={async () => {
              // Navigate BEFORE deleting, not after. deleteLocationCascade awaits a locations
              // reload, and the instant that lands this view re-renders with activeLocationId
              // pointing at a node that no longer exists — which trips the recovery guard
              // above and dumps the user at the space root however deep they were. Moving
              // first means the active id is always a location that still exists.
              const deletingId = activeLocationId;
              const parentId = loc?.parent_location_id;
              const toParent = parentId && parentId !== activeSpaceId;
              if (toParent) setActiveLocationId(parentId);
              else { setActiveLocationId(null); setView(activeSpaceId ? "space" : "spaces"); }

              const ok = await deleteLocationCascade(deletingId);
              if (!ok) {
                // Refused for permissions or already gone; cascade has alerted. Put the user
                // back where they were rather than leaving them somewhere they didn't pick.
                setActiveLocationId(deletingId);
                setView("location_view");
              }
            }}
            onRenameLocation={async (newName) => {
              await renameLocation(activeLocationId, newName);
            }}
            onConvertToItem={async () => {
              // Navigation into the item detail happens inside convertLocationToItem,
              // batched with the data update so there's no flash to top level.
              await convertLocationToItem(activeLocationId);
            }}
            onReparentChildLocation={async (id, newParentId) => { await reparentLocation(id, newParentId); }}
            onSetLocationPhoto={async (file) => { await setLocationPhoto(activeLocationId, file); }}
            onSetLocationIcon={async (icon) => { await setLocationIcon(activeLocationId, icon); }}
            onReparentLocation={async (newParentId) => {
              const ok = await reparentLocation(activeLocationId, newParentId);
              if (ok) {
                // The location moved. Re-sync navigation to its NEW position so backing
                // out doesn't return to the old parent.
                const freshLocs = await loadLocations();
                const moved = getLocationById(freshLocs, activeLocationId);
                if (moved && !moved.parent_location_id) {
                  // Now top-level → show it as a top-level location (space view).
                  setActiveSpaceId(activeLocationId);
                  setActiveLocationId(null);
                  setView("space");
                } else {
                  // Now nested under a different parent → keep the nested view, but point
                  // the space context at its new root so back-nav is correct.
                  setActiveSpaceId(rootLocationIdOf(freshLocs, activeLocationId));
                  setActiveLocationId(activeLocationId);
                }
              }
            }}
            spaces={spaces}
            onMoveItemsToDestination={async (ids, spaceRootId, locationId) => {
              await moveItemsToDestination(ids, spaceRootId, locationId);
            }}
            onCreateSpace={createSpace}
            onCreateLocationPure={createLocationPure}
            onDeleteItems={deleteItems}
            onInvite={inviteToSpace}
            onMembersChanged={reloadAll}
          />
        );
      })()}
      {view === "location" && activeSpace && <LocationPicker existingLocations={[...new Set(items.map((it) => it.location).filter(Boolean))]}
        spaceName={activeSpace.name}
        initial={sessionLocation}
        onCancel={() => setView("space")}
        onContinue={(loc) => { setSessionLocation(loc); setSessionLocationId(null); resetQueue(); setSessionSaved([]); setView("scan"); }} />}
      {(view === "scan" || view === "verify") && <ScanLive
        active={view === "scan"}
        spaceName={activeSpaceStable?.name || ""}
        location={sessionLocation} queue={queue} setQueue={setQueue}
        locations={locations}
        sessionLocationId={sessionLocationId}
        scanConfig={scanConfig}
        scanContext={scanContext}
        scanSessionId={scanSessionId}
        savedCount={sessionSaved.length}
        approvingIds={approvingIds}
        switching={scanSwitching}
        handledCount={history.length}
        onOpenHistory={() => openHistoryAt(history.length - 1)}
        // Closing the camera is the only way out, since the bottom bar's "Done — N saved" button
        // was removed. When the session actually produced something, that exit goes through the
        // summary rather than dropping the user back into the tree with no account of what happened.
        onCancel={() => {
          const produced = sessionSaved.length > 0;
          const back = afterScanView();
          // The history is the session's, so closing the camera ends it. Carrying it into the next
          // session would offer arrows onto scans whose destination no longer exists.
          setHistory([]);
          setHistoryIndex(null);
          resetQueue();
          setSessionLocation(""); setSessionLocationId(null);
          if (produced) { setView("review"); return; }
          setSessionSaved([]);
          setView(back);
        }}
        onVerify={(id) => { setVerifyingId(id); setView("verify"); }}
        onSwipeApprove={(id) => approveFromQueue(id)}
        onSwipeDecline={(id) => declineTap(id)}
        onChangeLocation={() => setScanRetargetOpen(true)}
        onStepLocation={(targetId) => retargetScan(targetId)} />}
      {scanRetargetOpen && (
        <LocationTreePicker
          items={[{ id: "__scan_target__", label: "new scans" }]}
          spaces={spaces}
          locations={locations}
          userId={session.user.id}
          currentSpaceRootId={sessionLocationId ? rootLocationIdOf(locations, sessionLocationId) : activeSpaceId}
          currentLocationId={sessionLocationId}
          moveLabel="Scan"
          onCancel={() => { setScanRetargetOpen(false); pendingPillApproveRef.current = null; }}
          onConfirm={({ spaceRootId, locationId }) => {
            setScanRetargetOpen(false);
            const targetId = locationId || spaceRootId;
            retargetScan(targetId, { nested: !!locationId });
            // Opened by a pill approve that had nowhere to file the thing: the destination was the
            // only missing piece, so finish that approve rather than making them tap the check again.
            const waiting = pendingPillApproveRef.current;
            pendingPillApproveRef.current = null;
            if (waiting && targetId) {
              const target = getLocationById(locations, targetId);
              const name = target?.name || spaces.find((s) => s.id === targetId)?.name || "";
              approveFromQueue(waiting, { id: targetId, name });
            }
          }}
          onCreateSpace={createSpace}
          onCreateLocation={createLocationPure}
        />
      )}
      {view === "verify" && verifyingTap && <Verify tap={verifyingTap} saving={saving}
        queueCount={queue.length}
        pendingCount={queue.filter((t) => t.status === "done" || t.status === "pending" || t.status === "error").length}
        // Newest first, matching the pill strip on the camera screen, so "previous" means the
        // one that was sitting to the right of this one rather than some other order.
        stepIndex={queue.length - 1 - queue.findIndex((t) => t.id === verifyingId)}
        stepTotal={queue.length}
        // The label also goes on the tap itself, not just into the draft: the queue strip along the
        // bottom of the camera draws `tap.label`, so the pill has to read as the name the user typed
        // rather than the one the model proposed and they replaced.
        onStashDraft={(draft) => {
          if (historyEntry) {
            // Edits to a handled scan live on its own snapshot, so walking away with the arrows and
            // coming back does not lose them — the same promise the queue makes for a pending scan.
            setHistory((h) => patchHandled(h, historyEntry.tapId, {
              label: draft.label, snapshot: { ...historyEntry.snapshot, label: draft.label, draft },
            }));
            return;
          }
          setQueue((prev) => prev.map((t) => (t.id === verifyingId ? { ...t, label: draft.label, draft } : t)));
        }}
        entryState={historyEntry ? historyEntry.state : null}
        entryIsLocation={!!historyEntry?.isLocation}
        // What the DATABASE has for this scan, which is neither what the fields opened with nor what they
        // currently say. Two wrong answers were tried: `seed` inside Verify is frozen on the tap id so it
        // goes stale the moment an edit lands, leaving "Save changes" showing for ever; and the snapshot's
        // own draft is where leaving the screen stashes the form, so reading that made an unsaved edit
        // look committed the moment you arrowed away and back.
        savedDraft={historyEntry ? historyEntry.stored || null : null}
        onSaveEdits={(draft) => historyEntry && saveEntryEdits(historyEntry, draft)}
        historyPos={historyPosition(history, historyIndex)}
        canBack={canStepBack(history, historyIndex)}
        canForward={canStepForward(history, historyIndex)}
        onStepHistory={stepHistoryBy}
        sessionLocationName={sessionLocation}
        sessionLocationId={sessionLocationId}
        locations={locations}
        spaces={spaces}
        userId={session.user.id}
        onCreateSpace={createSpace}
        onCreateLocationPure={createLocationPure}
        onRefreshLocations={async () => {
          const l = await loadLocations(); setLocations(l);
          const sp = await loadSpaces(); setSpaces(sp);
        }}
        onBack={() => { setHistoryIndex(null); setVerifyingId(null); setView("scan"); }}
        onApprove={(finalLabel, opts) => {
          // Approving a scan that is currently declined goes through the same approveTap, so the
          // ownership rule, the tag normalisation, the approve event and the embedding are decided in
          // one place whichever direction the user came from.
          // Stays on the scan. Approving from the history is a change to the thing in front of you, the
          // same as declining one is, so leaving the screen would hide whether it worked — and the entry
          // keeps its position, so there is somewhere to stay.
          if (historyEntry) { reapproveEntry(historyEntry, finalLabel, opts); return; }
          const cur = verifyingId;
          approveTap(cur, finalLabel, opts); // fire-and-forget; saves in background
          advanceVerify(cur);          // advance immediately
        }}
        onDecline={(draft) => {
          if (historyEntry) { unapproveEntry(historyEntry); return; }
          const cur = verifyingId;
          declineTap(cur, draft);
          advanceVerify(cur);
        }}
        onRetry={() => {
          const t = queue.find((q) => q.id === verifyingId);
          // If it already has a label, this was a failed save → retry the save.
          // Otherwise it was a failed label → retry labeling.
          if (t && t.label) retrySave(verifyingId);
          else retryTap(verifyingId);
        }} />}
      {view === "review" && <ReviewSummary location={sessionLocation} saved={sessionSaved}
        onDone={() => { const back = afterScanView(); resetQueue(); setSessionSaved([]); setSessionLocation(""); setSessionLocationId(null); setView(back); }} />}
      {view === "detail" && activeItem && <Detail canEdit={canEditItemNode(activeItem)} item={activeItem}
        locations={locations}
        spaces={spaces}
        existingLocations={[...new Set(locations.map((l) => l.name))]}
        currentSpaceRootId={activeSpaceId}
        userId={session.user.id}
        onBack={() => setView(activeLocationId ? "location_view" : (activeSpaceId ? "space" : "spaces"))}
        onDelete={deleteItem}
        onRenameItem={renameItem}
        onSetQuantity={setItemQuantity}
        onSetPhoto={setItemPhoto}
        onTagsChanged={async (id, tags) => {
          setItems((p) => p.map((it) => (it.id === id ? { ...it, tags } : it)));
          setActiveItem((cur) => (cur && cur.id === id ? { ...cur, tags } : cur));
        }}
        onMoveToDestination={moveItemToDestination}
        onConvertToLocation={async (id) => {
          const rootId = await convertItemToLocation(id);
          if (rootId !== null) {
            // The item is now a location, in place. Open it as a location.
            setActiveSpaceId(rootId);
            setActiveLocationId(id);
            setActiveItem(null);
            setView("location_view");
          }
        }}
        onCreateSpace={createSpace}
        onCreateLocation={createLocationPure}
      />}
      {/* Typed entry, over whatever screen asked for it — so cancelling puts the user back exactly
          where they were, which a view swap would not. */}
      {itemsScope && (
        <ScopedItemsView
          scopeId={itemsScope.id}
          scopeName={itemsScope.name}
          items={visibleItems}
          locations={locations}
          stableOrder={searchConfig.stableOrder}
          onClose={() => setItemsScope(null)}
          // From the camera there is nowhere to send them: leaving mid-session would throw away
          // whatever is queued, so there the list is a list. Everywhere else a card opens the item.
          onOpenItem={(view === "scan" || view === "verify")
            ? undefined
            : (it) => { setItemsScope(null); setActiveItem(it); setView("detail"); }}
        />
      )}
      {manualAdd && (
        <ManualAdd
          parentId={manualAdd.parentId}
          parentName={manualAdd.parentName}
          locations={locations}
          spaces={spaces}
          userId={session.user.id}
          onCancel={() => setManualAdd(null)}
          onCreateLocation={async (name, parentId, image) => {
            const loc = await createLocationPure(name, parentId);
            if (!loc) return false;
            // The photo is attached after the fact because it needs a node id. A failure here is
            // reported by setLocationPhoto and leaves the location, which is the part they typed.
            if (image) await setLocationPhoto(loc.id, image);
            return true;
          }}
          onCreateItem={createItemManually}
          onCreateSpace={createSpace}
          onCreateLocationPure={createLocationPure}
          onRefreshLocations={async () => setLocations(await loadLocations())}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Spaces home (grid of space cards)
// ─────────────────────────────────────────────────────────────────────

// One row of the scan settings: name, explanation, switch. Shared by the sub-location setting every
// user sees and the dev-only prompt flags, because two copies of a switch drift apart.
function ScanFeatureToggle({ feature, on, onToggle }) {
  return (
    <button type="button" onClick={onToggle} aria-pressed={on}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
        padding: "12px 14px", borderRadius: 14, fontFamily: "inherit", textAlign: "left", cursor: "pointer",
        background: on ? "var(--accent-soft)" : "var(--surface)",
        border: on ? "1px solid var(--accent-line)" : "1px solid var(--line)",
      }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)", display: "flex", alignItems: "center", gap: 7 }}>
          {feature.name}
          {feature.beta && <span style={S.betaPill}>beta</span>}
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1, lineHeight: 1.4 }}>{feature.desc}</div>
      </div>
      <div style={{
        flexShrink: 0, width: 44, height: 26, borderRadius: 13, position: "relative",
        transition: "background .15s", background: on ? "var(--accent)" : "var(--line-strong)",
      }}>
        <div style={{
          position: "absolute", top: 3, left: on ? 21 : 3, width: 20, height: 20,
          borderRadius: 10, background: "#fff", transition: "left .15s",
        }} />
      </div>
    </button>
  );
}

function ProfileSheet({ currentName, userEmail, isOwner, scanConfig, onSetScanConfig, searchConfig, onSetSearchConfig, viewMode, onSetViewMode, onCancel, onSave }) {
  const [name, setName] = useState(currentName || "");
  // Read once per open rather than per section, so the three panels cannot disagree about it.
  const showDevOptions = devOptions();
  const [saving, setSaving] = useState(false);
  const changed = (name || "").trim() !== (currentName || "").trim();
  // Owner-only tester invite.
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { ok, msg } | null
  const sendInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setInviteResult({ ok: false, msg: "Enter a valid email." });
      return;
    }
    setInviting(true); setInviteResult(null);
    try {
      const res = await adminInviteTester(email);
      const parts = [];
      parts.push(res.already_listed ? "Already on allowlist" : "Added to allowlist");
      parts.push(res.emailed ? "invite sent" : "email FAILED (check Resend)");
      setInviteResult({ ok: res.emailed, msg: parts.join(" · ") });
      if (res.emailed) setInviteEmail("");
    } catch (e) {
      setInviteResult({ ok: false, msg: e.message || "Failed" });
    } finally {
      setInviting(false);
    }
  };
  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>Your profile</h3>
          <button style={S.iconBtn} onClick={onCancel} aria-label="Close"><X size={20} /></button>
        </div>
        <p style={{ fontSize: 13, color: "var(--ink-soft)", margin: "0 0 16px", lineHeight: 1.5 }}>
          Your name shows to people you share spaces with. Leave it blank to show your email ({userEmail}).
        </p>
        <input style={{ ...S.input, fontSize: 16, padding: "14px 16px" }}
          placeholder="Your name (optional)" value={name} maxLength={60}
          onChange={(e) => setName(e.target.value)} />
        <button style={{ ...S.primaryBtn, marginTop: 16, opacity: changed && !saving ? 1 : 0.4 }}
          disabled={!changed || saving}
          onClick={async () => { setSaving(true); await onSave(name.trim()); setSaving(false); }}>
          {saving ? <Loader size={18} /> : <><Check size={18} /> Save</>}
        </button>

        {/* Outside the dev panel: this one changes what a scan writes, so it is the user's call, not
            a lab switch. Everything else about how scans are labelled is still hidden. */}
        {onSetScanConfig && scanConfig && (
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
            <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--ink-soft)", marginBottom: 8 }}>
              Scanning
            </div>
            <ScanFeatureToggle feature={SUBLOCATION_FEATURE} on={!!scanConfig.sublocation}
              onToggle={() => onSetScanConfig({ ...scanConfig, sublocation: !scanConfig.sublocation })} />
          </div>
        )}

        {showDevOptions && onSetScanConfig && scanConfig && (
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
            <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--ink-soft)", marginBottom: 4 }}>
              Scan config
            </div>
            <p style={{ fontSize: 12.5, color: "var(--ink-soft)", margin: "0 0 12px", lineHeight: 1.5 }}>
              Choose how scans are labeled. Basic is fast and works for most things; Max uses the strongest vision for tricky items.
            </p>

            {/* Model picker */}
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>Model</div>
            {SCAN_MODELS.map((m) => {
              const active = m.id === scanConfig.model;
              return (
                <button key={m.id} type="button"
                  onClick={() => onSetScanConfig({ ...scanConfig, model: m.id })}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
                    padding: "11px 14px", borderRadius: 14, fontFamily: "inherit", textAlign: "left", cursor: "pointer",
                    background: active ? "var(--accent-soft)" : "var(--surface)",
                    border: active ? "1px solid var(--accent-line)" : "1px solid var(--line)",
                  }}>
                  <div style={{
                    flexShrink: 0, width: 18, height: 18, borderRadius: 9,
                    border: active ? "5px solid var(--accent)" : "2px solid var(--line-strong)",
                    background: active ? "var(--bg)" : "transparent",
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>{m.name}</div>
                    <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1, lineHeight: 1.4 }}>{m.note}</div>
                  </div>
                </button>
              );
            })}

            {/* Weak-signal fallback picker (for testing scan resilience on/off) */}
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)", margin: "14px 0 6px" }}>Weak-signal handling</div>
            {SCAN_CHAIN_OPTIONS.map((opt) => {
              const active = opt.id === (scanConfig.scanChain || "resilient");
              return (
                <button key={opt.id} type="button"
                  onClick={() => onSetScanConfig({ ...scanConfig, scanChain: opt.id })}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
                    padding: "11px 14px", borderRadius: 14, fontFamily: "inherit", textAlign: "left", cursor: "pointer",
                    background: active ? "var(--accent-soft)" : "var(--surface)",
                    border: active ? "1px solid var(--accent-line)" : "1px solid var(--line)",
                  }}>
                  <div style={{
                    flexShrink: 0, width: 18, height: 18, borderRadius: 9,
                    border: active ? "5px solid var(--accent)" : "2px solid var(--line-strong)",
                    background: active ? "var(--bg)" : "transparent",
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>{opt.name}</div>
                    <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1, lineHeight: 1.4 }}>{opt.desc}</div>
                  </div>
                </button>
              );
            })}

            {/* Feature toggles (composable) */}
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)", margin: "14px 0 6px" }}>Prompt features</div>
            {SCAN_FEATURES.map((f) => (
              <ScanFeatureToggle key={f.id} feature={f} on={!!scanConfig[f.id]}
                onToggle={() => onSetScanConfig({ ...scanConfig, [f.id]: !scanConfig[f.id] })} />
            ))}
          </div>
        )}

        {/* How the browse screens draw a place. One radio group rather than a control on each screen:
            it is a statement about how the user reads their belongings, not about this shelf. */}
        {showDevOptions && (
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--ink-soft)", marginBottom: 8 }}>
            How things are shown
          </div>
          {VIEW_MODES.map((m) => {
            const on = (viewMode || DEFAULT_VIEW_MODE) === m.id;
            const Glyph = VIEW_MODE_ICONS[m.id];
            return (
              <button key={m.id} type="button" onClick={() => onSetViewMode(m.id)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
                  padding: "12px 14px", borderRadius: 14, fontFamily: "inherit", textAlign: "left", cursor: "pointer",
                  background: on ? "var(--accent-soft)" : "var(--surface)",
                  border: on ? "1px solid var(--accent-line)" : "1px solid var(--line)",
                }}>
                <div style={{ flexShrink: 0, color: on ? "var(--accent-ink)" : "var(--ink-soft)", display: "flex" }}>
                  <Glyph size={20} sw={2} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1, lineHeight: 1.4 }}>{m.desc}</div>
                </div>
                {on && <Check size={18} sw={2.6} extra={{ color: "var(--accent-ink)", flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
        )}

        {/* Search behaviour. Outside the scan-config block on purpose: it matters to anyone who
            searches, including people who never open the camera. */}
        {showDevOptions && (
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--ink-soft)", marginBottom: 8 }}>
            Search
          </div>
          {SEARCH_FEATURES.map((f) => {
            const on = !!(searchConfig || {})[f.id];
            return (
              <button key={f.id} type="button"
                onClick={() => onSetSearchConfig({ ...(searchConfig || {}), [f.id]: !on })}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
                  padding: "12px 14px", borderRadius: 14, fontFamily: "inherit", textAlign: "left", cursor: "pointer",
                  background: on ? "var(--accent-soft)" : "var(--surface)",
                  border: on ? "1px solid var(--accent-line)" : "1px solid var(--line)",
                }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>{f.name}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1, lineHeight: 1.4 }}>{f.desc}</div>
                </div>
                <div style={{
                  flexShrink: 0, width: 44, height: 26, borderRadius: 13, position: "relative",
                  transition: "background .15s", background: on ? "var(--accent)" : "var(--line-strong)",
                }}>
                  <div style={{
                    position: "absolute", top: 3, left: on ? 21 : 3, width: 20, height: 20,
                    borderRadius: 10, background: "#fff", transition: "left .15s",
                  }} />
                </div>
              </button>
            );
          })}
        </div>
        )}

        {/* Not behind the dev flag, unlike the panels above it. Handing out access is something the
            founders do on purpose and repeatedly, and it was sharing a switch with three half-finished
            experiments — so turning those off took the invite with it, and adding a tester meant the
            Supabase dashboard. `isOwner` is the gate that belongs here: for everyone else this section
            does not exist, which is why it can sit in a shipped settings sheet. */}
        {isOwner && (
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
            <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--ink-soft)", marginBottom: 4 }}>
              Invite a tester
            </div>
            <p style={{ fontSize: 12.5, color: "var(--ink-soft)", margin: "0 0 10px", lineHeight: 1.5 }}>
              Adds the email to the allowlist and sends them an invite from noreply@magicshelf.app.
            </p>
            <input style={{ ...S.input, fontSize: 16, padding: "13px 15px" }}
              placeholder="tester@example.com" value={inviteEmail} inputMode="email"
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
              onChange={(e) => { setInviteEmail(e.target.value); setInviteResult(null); }} />
            <button style={{ ...S.primaryBtn, marginTop: 10, opacity: inviteEmail.trim() && !inviting ? 1 : 0.4 }}
              disabled={!inviteEmail.trim() || inviting}
              onClick={sendInvite}>
              {inviting ? <Loader size={18} /> : <><Plus size={18} /> Add &amp; send invite</>}
            </button>
            {inviteResult && (
              <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, lineHeight: 1.4,
                            color: inviteResult.ok ? "var(--success)" : "var(--danger)" }}>
                {inviteResult.msg}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// One search field, four screens.
//
// A <form> around a single input, on purpose. It is what makes the phone keyboard's return key say
// "search" and gives Enter something to mean — the results are already on screen as the user types, so
// the only useful thing left for that key is to fold the keyboard away from them. Without the form the
// key is a newline in a text field and iOS shows its field-stepping accessory instead.
function SearchField({ value, onChange, placeholder, style, inputRef }) {
  const own = useRef(null);
  const ref = inputRef || own;
  const dismiss = () => ref.current?.blur();
  return (
    <form style={{ ...S.searchWrap, ...style }} onClick={() => ref.current?.focus()}
      onSubmit={(e) => { e.preventDefault(); dismiss(); }}>
      <Search size={18} sw={2.4} extra={{ color: "var(--ink-soft)", flexShrink: 0 }} />
      <input ref={ref} style={S.searchInput} placeholder={placeholder} value={value}
        type="search" inputMode="search" enterKeyHint="search"
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); dismiss(); } }}
        onChange={(e) => onChange(e.target.value)} />
      {value && (
        <button type="button" style={S.clearBtn} aria-label="Clear the search"
          onClick={(e) => { e.stopPropagation(); onChange(""); ref.current?.focus(); }}>
          <X size={16} />
        </button>
      )}
    </form>
  );
}

function SpacesHome({ spaces, locations, spaceStats, items = [], stableOrder = false, userId, onOpenSpace, onOpenAllItems, onOpenItem, onAddItem, onAddManually, unsortedCount = 0, onRenameSpace, onDeleteSpace, onSetSpacePhoto, onSetSpaceIcon, onMoveLocations, onCreateSpacePure, onCreateLocationPure, invitations = [], onAcceptInvite, onDeclineInvite, myName, onSaveName, onSignOut, userEmail, scanConfig, onSetScanConfig, searchConfig, onSetSearchConfig, viewMode = DEFAULT_VIEW_MODE, onSetViewMode, itemCounts = new Map(), treeOps }) {
  const [editingSpace, setEditingSpace] = useState(null);
  const [confirmDeleteSpace, setConfirmDeleteSpace] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [movePickerOpen, setMovePickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);

  // Search from the first screen, over everything the user can see.
  //
  // This was a card that navigated to an "All items" screen carrying its own search field, which put
  // a screen between opening the app and typing — and finding a thing you own is the reason the app
  // exists, so it belongs on the front screen. The card is now the field itself; the count that used
  // to be its subtitle is the chip beside it, still the way to the plain grid of everything.
  const semanticMatches = useSemanticMatches(query);
  const parentOf = useMemo(() => parentIndex(locations, items), [locations, items]);
  const itemResults = useMemo(
    () => (query.trim()
      ? fuseItemResults({ lexical: searchItems(items, query), matches: semanticMatches, pool: items, parentOf, stableOrder })
      : []),
    [items, query, semanticMatches, parentOf, stableOrder],
  );
  // Items only, deliberately. This field briefly returned matching locations above the items, on the
  // grounds that "workbench" should find the workbench — but from the front screen a search is a
  // question about a thing you own, and rooms in front of the answer are the shape of the old detour
  // this replaced. Locations are still how you navigate: they are the list underneath.

  const toggleSelect = (id) => setSelectedIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const enterSelectMode = (id) => { setSelectMode(true); setSelectedIds(new Set([id])); };
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const totalItems = Object.values(spaceStats).reduce((sum, n) => sum + n, 0);

  const spaceRows = spaces;   // root locations

  // Re-read the location being edited from the live list on every render. Holding the snapshot
  // taken when the sheet opened meant a photo saved from inside the sheet was stored and shown
  // in the list behind it, while the sheet itself still offered to "Add a photo".
  const editingLive = editingSpace
    ? (spaceRows.find((s) => s.id === editingSpace.id) || editingSpace)
    : null;

  return (
    <div className="screen">
      {/* Three things: a menu, the name, the count. Settings, feedback and sign out were three loose
          icons in the corner, which is three taps' worth of chrome given the same weight as the one
          number a user opens the app to see. They are rare, so they fold into the menu; the count is
          the door to everything owned, so it takes the corner they were in. */}
      <header style={{ ...S.header, alignItems: "center" }}>
        <button onClick={() => setMenuOpen(true)} style={S.purgeBtn} aria-label="Menu" title="Menu">
          <MoreVertical size={16} sw={2.4} extra={{ color: "var(--ink-soft)" }} />
        </button>
        <h1 style={{ ...S.wordmark, flex: 1, minWidth: 0, textAlign: "center" }}>MagicShelf</h1>
        <button onClick={onOpenAllItems} style={{ ...S.count, minWidth: 32, height: 32, borderRadius: 16, border: "none", cursor: "pointer", fontFamily: "inherit" }}
          aria-label={`All ${totalItems} items`} title="Everything you have">
          {totalItems}
        </button>
      </header>

      {menuOpen && (
        <div style={S.menuBackdrop} onClick={() => setMenuOpen(false)}>
          <div style={S.menuCard} onClick={(e) => e.stopPropagation()}>
            <button style={S.menuItem} onClick={() => { setMenuOpen(false); setProfileOpen(true); }}>
              <Settings size={16} sw={2} extra={{ color: "var(--ink-soft)" }} /> Settings
            </button>
            <button style={S.menuItem} onClick={() => { setMenuOpen(false); setFeedbackOpen(true); }}>
              <Info size={16} sw={2} extra={{ color: "var(--ink-soft)" }} /> Send feedback
            </button>
            <button style={{ ...S.menuItem, borderTop: "1px solid var(--line)" }} onClick={() => { setMenuOpen(false); onSignOut(); }} title={userEmail}>
              <LogOut size={16} sw={2} extra={{ color: "var(--ink-soft)" }} /> Sign out
            </button>
          </div>
        </div>
      )}

      <div className="grid-scroll" style={{ paddingBottom: 130 }}>
        {/* Pending invitations */}
        {invitations.length > 0 && (
          <div style={{ padding: "8px 18px 4px" }}>
            {invitations.map((inv) => (
              <div key={inv.id} style={S.inviteCard}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--accent-soft)", display: "grid", placeItems: "center", color: "var(--accent)", flexShrink: 0 }}>
                    <Package size={17} sw={1.8} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14.5, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      You're invited to "{inv.space_name}"
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1 }}>
                      Join as {inv.role}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={{ ...S.secondaryBtn, flex: "0 0 auto", width: "auto", padding: "10px 16px" }} onClick={() => onDeclineInvite(inv.id)}>Decline</button>
                  <button style={{ ...S.primaryBtn, flex: 1, padding: "10px 16px" }} onClick={() => onAcceptInvite(inv.id)}>Accept</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* The search field, in the same shape it has on every other screen. The count is in the
            placeholder and nowhere else: it was briefly a chip beside the field, opening a separate
            grid of everything, which is a second screen for a question this field already answers. */}
        <div style={{ padding: "0 18px" }}>
          <SearchField value={query} onChange={setQuery} inputRef={searchInputRef}
            style={{ margin: "8px 0 10px" }}
            placeholder={`Search across all ${totalItems} ${totalItems === 1 ? "item" : "items"}`} />
        </div>

        {query ? (
          // Searching replaces the locations list rather than sitting above it: the answer to "where
          // is my drill" is the drill, and a list of rooms in the way of it is the thing that made the
          // old screen feel like a detour.
          itemResults.length === 0 ? (
            <div style={S.noResults}>
              <Package size={40} sw={1.3} extra={{ color: "var(--ink-faint)" }} />
              <p style={{ margin: "14px 0 4px", fontWeight: 600 }}>No matches for "{query}"</p>
            </div>
          ) : (
            <div style={{ ...S.grid, paddingBottom: 30 }}>
              {itemResults.map((item, idx) => (
                <ItemCard key={item.id} item={item} idx={idx}
                  subPath={item.location_path || null}
                  onClick={() => onOpenItem(item)} />
              ))}
            </div>
          )
        ) : viewMode === "tree" ? (
          <div style={{ padding: "10px 16px 120px" }}>
            <LocationTreeBrowser
              rootIds={spaceRows.map((sp) => sp.id)}
              locations={locations} spaces={spaces}
              itemCounts={itemCounts}
              canEdit canAddRoot
              ops={treeOps}
            />
          </div>
        ) : (
        <>
        {/* Locations section (top-level locations). No heading in icons mode: the cards say what they
            are on their own badges, and a section head over a grid that is the whole screen labels
            nothing. */}
        {viewMode !== "icons" && (
          <div style={{ ...S.sectionHead, padding: "10px 22px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Locations</span>
          </div>
        )}
        {/* A stacked list rather than a grid of cards, and it matches the sub-location lists
            one level down — the same things were being drawn two different ways depending on
            depth. "Add location" is the first row: as the last one it went off-screen as soon
            as the list got long. Icons mode swaps the rows for photo cards; the add row stays a
            row above the grid, because it has no photo and a dashed square pretending to be a
            card is the widest thing on the screen saying the least. */}
        <div style={S.spacesList}>
          {viewMode !== "icons" && <AddLocationRow onClick={onAddManually} />}
          {spaceRows.length === 0 && (
            <div style={{ ...S.emptySectionRow, marginBottom: 8 }}>
              <p style={{ margin: 0, color: "var(--ink-soft)", fontSize: 13.5, lineHeight: 1.5 }}>
                Locations are physical places that contain items — a house, a closet, a shelf — and can nest inside each other.
              </p>
            </div>
          )}
          {viewMode === "icons" ? (
            <div style={S.gridNested}>
              <AddThingCard onClick={onAddManually} />
              {spaceRows.map((sp) => (
                <LocationCard
                  key={sp.id} location={sp} count={spaceStats[sp.id] || 0}
                  selectMode={selectMode}
                  selected={selectMode && selectedIds.has(sp.id)}
                  onClick={() => { if (selectMode) toggleSelect(sp.id); else onOpenSpace(sp.id); }}
                  onLongPress={() => { if (!selectMode) enterSelectMode(sp.id); else toggleSelect(sp.id); }}
                />
              ))}
            </div>
          ) : spaceRows.map((sp) => (
            <LocationRow
              key={sp.id} location={sp} count={spaceStats[sp.id] || 0}
              selectMode={selectMode}
              selected={selectMode && selectedIds.has(sp.id)}
              onOpen={() => { if (selectMode) toggleSelect(sp.id); else onOpenSpace(sp.id); }}
              onLongPress={() => { if (!selectMode) enterSelectMode(sp.id); else toggleSelect(sp.id); }}
            />
          ))}
        </div>
        </>
        )}
      </div>

      {/* The primary action: the camera. Named for the method rather than the kind, against "Add
          manually" in the list — what it produces is decided by the toggle on the camera, so calling
          it "Add item" named the common case and made the location case look like a different
          feature. Location is chosen in-scan (tappable "Adding to" header) or per-item at approve. In
          tree mode it aims at the highlighted node, which is why the tree grew no button of its own.

          Not while searching. Someone typing a word is reading results, and the button sits on top of
          the last row of them — adding a thing is the opposite of finding one you already have. */}
      {!query && (
        <button style={S.fab} onClick={() => onAddItem()}>
          <Camera size={20} sw={2.2} /> <span>Add from camera</span>
        </button>
      )}


      {editingSpace && (
        <EditSpaceSheet
          space={editingLive}
          onCancel={() => setEditingSpace(null)}
          onRename={async (newName) => { await onRenameSpace(editingLive.id, newName); }}
          onSetPhoto={onSetSpacePhoto ? (file) => onSetSpacePhoto(editingLive.id, file) : undefined}
          onSetIcon={onSetSpaceIcon ? (icon) => onSetSpaceIcon(editingLive.id, icon) : undefined}
          onRequestDelete={() => setConfirmDeleteSpace(editingLive)}
        />
      )}

      {confirmDeleteSpace && (
        <ConfirmDeleteLocationSheet
          location={confirmDeleteSpace}
          itemCount={spaceStats[confirmDeleteSpace.id] || 0}
          subLocationCount={locationSubtree(locations, confirmDeleteSpace.id).size - 1}
          onCancel={() => setConfirmDeleteSpace(null)}
          onConfirm={async () => {
            const target = confirmDeleteSpace;
            setConfirmDeleteSpace(null);
            setEditingSpace(null);
            await onDeleteSpace(target.id);
          }}
        />
      )}

      {/* Outside the menu, and controlled by it: the sheet is the same bottom sheet Settings uses, and
          opening it from a dropdown that stayed on screen made it look like part of the dropdown. */}
      <FeedbackButton screen="spaces" context={null} open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {profileOpen && (
        <ProfileSheet
          currentName={myName || ""}
          userEmail={userEmail}
          isOwner={isAppOwner({ id: userId, email: userEmail })}
          scanConfig={scanConfig}
          onSetScanConfig={onSetScanConfig}
          searchConfig={searchConfig}
          onSetSearchConfig={onSetSearchConfig}
          viewMode={viewMode}
          onSetViewMode={onSetViewMode}
          onCancel={() => setProfileOpen(false)}
          onSave={async (name) => { await onSaveName(name); setProfileOpen(false); }}
        />
      )}

      {selectMode && (
        <div style={S.selectionBar}>
          <button style={{ ...S.secondaryBtn, width: "auto", flex: "0 0 auto", padding: "13px 16px" }} onClick={exitSelectMode}>Cancel</button>
          {selectedIds.size === 1 && (
            <button style={{ ...S.secondaryBtn, width: "auto", flex: "0 0 auto", padding: "13px 16px" }}
              onClick={() => { const sp = spaceRows.find((s) => selectedIds.has(s.id)); if (sp) { exitSelectMode(); setEditingSpace(sp); } }}>
              Edit
            </button>
          )}
          <button style={{ ...S.primaryBtn, width: "auto", flex: 1, opacity: selectedIds.size ? 1 : 0.4 }}
            disabled={!selectedIds.size} onClick={() => setMovePickerOpen(true)}>
            Move {selectedIds.size}
          </button>
        </div>
      )}

      {movePickerOpen && (
        <LocationTreePicker
          items={spaceRows.filter((s) => selectedIds.has(s.id)).map((s) => ({ id: s.id, label: s.name }))}
          spaces={spaces}
          locations={locations}
          userId={userId}
          currentSpaceRootId={null}
          currentLocationId={null}
          excludeSubtreeOf={Array.from(selectedIds)}
          onCancel={() => setMovePickerOpen(false)}
          onConfirm={async ({ spaceRootId, locationId, topLevel }) => {
            // topLevel → make each a root (null parent). Else parent = picked location/space root.
            const newParentId = topLevel ? null : (locationId || spaceRootId);
            if (!topLevel && !newParentId) { setMovePickerOpen(false); return; }
            await onMoveLocations(Array.from(selectedIds), newParentId);
            setMovePickerOpen(false);
            exitSelectMode();
          }}
          onCreateSpace={onCreateSpacePure}
          onCreateLocation={onCreateLocationPure}
        />
      )}
    </div>
  );
}

function EditSpaceSheet({ space, onCancel, onRename, onRequestDelete, onSetPhoto, onSetIcon, label = "location", isOwner, onInvite, onManagePeople, onMove }) {
  const commitRename = (text) => {
    const trimmed = (text || "").trim();
    if (trimmed && trimmed !== space.name) onRename(trimmed);
  };
  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>Edit {label}</h3>
          <button style={S.iconBtn} onClick={onCancel} aria-label="Close"><X size={20} /></button>
        </div>
        <LocationPhotoField location={space} onSetPhoto={onSetPhoto} onSetIcon={onSetIcon} />
        <EditableTitle value={space.name} canEdit={!!onRename} editLabel={`Rename ${label}`}
          onCommit={commitRename} />
        {onMove && (
          <button style={{ ...S.secondaryBtn, marginTop: 14 }} onClick={onMove}>
            <MapPin size={18} /> Move to another location
          </button>
        )}
        {onManagePeople && (
          <button style={{ ...S.secondaryBtn, marginTop: 10 }} onClick={onManagePeople}>
            <Users size={18} /> People
          </button>
        )}
        {/* Delete: shown when there's no isOwner concept passed, and for spaces you own.
            Non-owner members manage their exit via "Leave" in the People sheet. */}
        {(isOwner || onManagePeople === undefined) && (
          <button style={{ ...S.dangerBtn, marginTop: 10 }} onClick={onRequestDelete}>
            <Trash2 size={18} /> Delete {label}
          </button>
        )}
      </div>
    </div>
  );
}

function InviteSheet({ space, onCancel, onInvite }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [status, setStatus] = useState(null); // null | 'sending' | {ok|error message}
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  const submit = async () => {
    if (!valid) return;
    setStatus("sending");
    try {
      const res = await onInvite(space.id, email.trim().toLowerCase(), role);
      if (res && res.ok) {
        setStatus({ kind: "ok", msg: res.alreadyInvited
          ? "They were already invited — no problem."
          : res.emailed
            ? "Invitation sent. They'll see it when they sign in."
            : "Invitation created. They'll see it when they sign in (email couldn't be sent)." });
        setEmail("");
      } else {
        setStatus({ kind: "error", msg: (res && res.message) || "Couldn't send the invitation." });
      }
    } catch (e) {
      // A thrown error here must never leave the button spinning forever.
      setStatus({ kind: "error", msg: (e && e.message) || "Couldn't send the invitation." });
    }
  };

  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>Invite to {space.name}</h3>
          <button style={S.iconBtn} onClick={onCancel}><X size={20} /></button>
        </div>
        {/* No longer "they must already be approved": inviting someone approves them, recorded
            against you. Said plainly, because sharing with a stranger and letting a stranger into a
            still-invite-only app are the same action now. */}
        <p style={{ fontSize: 13, color: "var(--ink-soft)", margin: "0 0 16px", lineHeight: 1.5 }}>
          They don't need a MagicShelf account yet — inviting them gives them access. They'll get an
          email, and the invite is waiting when they sign in with this address.
        </p>
        <input autoFocus type="email" inputMode="email" autoCapitalize="none" autoCorrect="off"
          style={{ ...S.input, fontSize: 16, padding: "14px 16px" }}
          placeholder="their@email.com" value={email}
          onChange={(e) => { setEmail(e.target.value); setStatus(null); }} />
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button style={{ ...(role === "editor" ? S.segActive : S.seg), flex: 1 }} onClick={() => setRole("editor")}>Editor</button>
          <button style={{ ...(role === "viewer" ? S.segActive : S.seg), flex: 1 }} onClick={() => setRole("viewer")}>Viewer</button>
        </div>
        <p style={{ fontSize: 11.5, color: "var(--ink-faint)", margin: "8px 0 0", lineHeight: 1.4 }}>
          {role === "editor" ? "Editors can add, move, and remove items." : "Viewers can see items but not change them."}
        </p>
        {status && status !== "sending" && (
          <p style={{ fontSize: 13, marginTop: 14, color: status.kind === "ok" ? "var(--accent-ink)" : "var(--danger)", lineHeight: 1.5 }}>
            {status.msg}
          </p>
        )}
        <button style={{ ...S.primaryBtn, marginTop: 16, opacity: valid && status !== "sending" ? 1 : 0.4 }}
          disabled={!valid || status === "sending"} onClick={submit}>
          {status === "sending" ? <Loader size={18} /> : <><Check size={18} /> Send invitation</>}
        </button>
      </div>
    </div>
  );
}

// Members management: list members + pending invites, change roles, remove, revoke,
// invite, leave, and (owner) toggle whether editors can manage members.
function MembersSheet({ space, userId, canManage: canManageProp, onCancel, onChanged, onInvite }) {
  const [members, setMembers] = useState(null);   // DIRECT rows on this node (owner + direct members)
  const [inherited, setInherited] = useState([]); // read-only, from ancestors
  const [invites, setInvites] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);
  const isOwner = space.owner_id === userId;

  const load = async () => {
    setErr(null);
    // Single source of truth: node_access_list returns EVERYONE with access, tagged
    // direct-vs-inherited, with identity — including the node's OWNER (who has no
    // node_members row on non-root nodes, so querying node_members alone would omit
    // them). Direct non-owner rows are exactly the manageable ones (they have real
    // node_members rows on this node).
    const access = await loadNodeAccess(space.id);
    if (access === null) {
      setMembers([]); setInherited([]);
      setErr("Couldn't load members. (If this persists, the member migration may not be applied.)");
      return;
    }
    const withOwnerFlag = (r) => ({ ...r, is_owner: r.role === "owner" });
    setMembers(access.filter((r) => r.is_direct).map(withOwnerFlag));
    setInherited(access.filter((r) => !r.is_direct));
    // Management = node owner OR ancestor owner (caller computes via ownsNodeOrAncestor;
    // fallback = node-owner check for callers that don't pass it, e.g. the root/space flow
    // where the two rules are identical).
    const allowed = canManageProp !== undefined ? canManageProp : (space.owner_id === userId);
    setCanManage(allowed);
    if (allowed) setInvites(await loadSpacePendingInvites(space.id));
  };
  useEffect(() => { load(); }, [space.id]);

  const run = async (key, fn, friendly) => {
    setBusyId(key); setErr(null);
    try { await fn(); await load(); if (onChanged) onChanged(); }
    catch (e) { setErr(e.message || friendly || "Something went wrong"); }
    finally { setBusyId(null); }
  };

  // Role changes are OPTIMISTIC: flip the label instantly, confirm with the server in
  // the background, and revert to server truth on failure. (The awaited path made a
  // simple toggle wait through a function call + reload before responding.)
  const toggleRole = (m) => {
    const next = m.role === "editor" ? "viewer" : "editor";
    setMembers((p) => p.map((x) => x.user_id === m.user_id ? { ...x, role: next } : x));
    setErr(null);
    setMemberRole(space.id, m.user_id, next).catch((e) => {
      setErr(e.message || "Couldn't change role");
      load(); // revert to server truth
    });
  };

  const labelFor = (m) => (m.display_name && m.display_name.trim()) || m.email || "member";

  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={{ ...S.sheet, maxHeight: "84%", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, flexShrink: 0 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>People in {space.name}</h3>
          <button style={S.iconBtn} onClick={onCancel}><X size={20} /></button>
        </div>

        {err && <p style={{ color: "var(--danger)", fontSize: 13, margin: "6px 0 0" }}>{err}</p>}

        <div style={{ flex: 1, overflowY: "auto", marginTop: 10 }}>
          {members === null ? (
            <div style={{ padding: 24, textAlign: "center" }}><Loader size={22} /></div>
          ) : (
            <>
              {members.map((m) => (
                <div key={m.user_id} style={S.memberRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14.5, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {labelFor(m)}{m.user_id === userId && <span style={{ color: "var(--ink-faint)", fontWeight: 500 }}> (you)</span>}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1 }}>
                      {m.is_owner ? "Owner" : m.role === "editor" ? "Editor" : "Viewer"}
                    </div>
                  </div>
                  {canManage && !m.is_owner && (
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                      <button style={S.miniBtn}
                        onClick={() => toggleRole(m)}>
                        {m.role === "editor" ? "Make viewer" : "Make editor"}
                      </button>
                      <button style={{ ...S.miniBtn, color: "var(--danger)", borderColor: "var(--danger-line)" }} disabled={busyId === m.user_id}
                        onClick={() => { if (window.confirm(`Remove ${labelFor(m)} from ${space.name}?`)) run(m.user_id, () => removeMember(space.id, m.user_id)); }}>
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {inherited.length > 0 && (
                <>
                  <div style={{ ...S.sectionHead, margin: "16px 0 6px" }}>Inherited access</div>
                  {inherited.map((m) => (
                    <div key={m.user_id} style={S.memberRow}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14.5, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {(m.display_name && m.display_name.trim()) || m.email || "member"}
                          {m.user_id === userId && <span style={{ color: "var(--ink-faint)", fontWeight: 500 }}> (you)</span>}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1 }}>
                          {m.role === "owner" ? "Owner" : m.role === "editor" ? "Editor" : "Viewer"}{m.source_name ? ` · via ${m.source_name}` : ""}
                        </div>
                      </div>
                      {m.role === "owner" ? null : (
                        <span style={{ fontSize: 11.5, color: "var(--ink-faint)", flexShrink: 0 }}>Manage in {m.source_name || "parent"}</span>
                      )}
                    </div>
                  ))}
                </>
              )}

              {canManage && invites.length > 0 && (
                <>
                  <div style={{ ...S.sectionHead, margin: "16px 0 6px" }}>Pending invites</div>
                  {invites.map((inv) => (
                    <div key={inv.id} style={S.memberRow}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.email}</div>
                        <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1 }}>Invited · {inv.role}</div>
                      </div>
                      <button style={{ ...S.miniBtn, color: "var(--danger)", borderColor: "var(--danger-line)" }} disabled={busyId === inv.id}
                        onClick={() => run(inv.id, () => revokeInvitation(inv.id))}>
                        Revoke
                      </button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <div style={{ flexShrink: 0, borderTop: "1px solid var(--line)", paddingTop: 12, marginTop: 6 }}>
          {canManage && (
            <button style={{ ...S.secondaryBtn, marginBottom: 8 }} onClick={onInvite}>
              <Plus size={18} /> Invite someone
            </button>
          )}
          {!isOwner && (
            <button style={{ ...S.dangerBtn }} disabled={busyId === "leave"}
              onClick={() => { if (window.confirm(`Leave ${space.name}? You'll lose access until you're invited again.`)) run("leave", () => leaveSpace(space.id), "Couldn't leave"); }}>
              <LogOut size={18} /> Leave location
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Space view (inside a single space)
// ─────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────
// Drag a location onto another location to reparent it.
//
// Moving a location used to mean Edit → its parent field → tree picker → confirm: four taps
// and two modals to say "this shelf is inside that cabinet", and reorganising a tree is
// mostly a long run of exactly that. Dragging one row onto another is one gesture.
//
// Pointer maths by hand rather than HTML5 drag-and-drop, which never fires on touch — and
// touch is the whole point. Press and hold a row for the same 450ms the item cards use for
// their long-press, so the gesture vocabulary stays consistent, and it lifts; release over
// another row and that row becomes the new parent. Two details are load-bearing:
//   • touchmove is swallowed with a NON-PASSIVE listener while lifted. Without it the
//     browser decides the gesture is a scroll, takes it over, and cancels the pointer
//     stream mid-drag. A passive listener cannot preventDefault, so it must be explicit.
//   • the floating ghost is pointer-events:none, or elementFromPoint below the finger only
//     ever finds the ghost and there is never a drop target.
// ─────────────────────────────────────────────────────────────────────

const DRAG_HOLD_MS = 450;
const DRAG_SLOP_PX = 12; // movement before the hold completes means the user meant to scroll

function useReparentDrag(allLocations, onDrop) {
  const [drag, setDrag] = useState(null); // { id, name, x, y, overId } while lifted
  const st = useRef({});
  const swallowedClick = useRef(false);

  const reset = useCallback(() => {
    if (st.current.timer) clearTimeout(st.current.timer);
    st.current = {};
  }, []);

  useEffect(() => {
    if (!drag) return undefined;
    const swallow = (e) => e.preventDefault();
    document.addEventListener("touchmove", swallow, { passive: false });
    return () => document.removeEventListener("touchmove", swallow);
  }, [!!drag]);

  const pointOf = (e) => (e.touches && e.touches[0]) || e.changedTouches?.[0] || e;

  const targetUnder = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const host = el && el.closest ? el.closest("[data-reparent-target]") : null;
    return host ? host.getAttribute("data-reparent-target") : null;
  };

  const start = (e, location) => {
    if (!onDrop) return;
    const p = pointOf(e);
    reset();
    st.current = {
      id: location.id,
      name: location.name,
      origin: { x: p.clientX, y: p.clientY },
      // A location cannot become its own descendant's child, so its whole subtree is
      // off-limits as a target. Computed once here rather than per row per render.
      blocked: locationSubtree(allLocations, location.id),
      timer: setTimeout(() => {
        st.current.active = true;
        setDrag({ id: location.id, name: location.name, x: p.clientX, y: p.clientY, overId: null });
        if (navigator.vibrate) { try { navigator.vibrate(15); } catch {} }
      }, DRAG_HOLD_MS),
    };
  };

  const move = (e) => {
    if (!st.current.origin) return;
    const p = pointOf(e);
    if (!st.current.active) {
      const { x, y } = st.current.origin;
      if (Math.hypot(p.clientX - x, p.clientY - y) > DRAG_SLOP_PX) reset();
      return;
    }
    const raw = targetUnder(p.clientX, p.clientY);
    const overId = raw && !st.current.blocked.has(raw) ? raw : null;
    st.current.overId = overId; // pointerup reads this, which a state closure may not have yet
    setDrag((d) => (d ? { ...d, x: p.clientX, y: p.clientY, overId } : d));
  };

  const end = async () => {
    const { active, id, overId } = st.current;
    reset();
    setDrag(null);
    if (!active) return;
    swallowedClick.current = true; // the tap that ends the drag must not also open the row
    if (overId && overId !== id) await onDrop(id, overId);
  };

  // Spread onto a row to make it draggable AND a drop target. Rows are both, which is what
  // makes "put this one in that one" work without a separate drop zone.
  const bind = (location) => (onDrop ? {
    "data-reparent-target": location.id,
    onTouchStart: (e) => start(e, location),
    onTouchMove: move,
    onTouchEnd: end,
    onTouchCancel: reset,
    onMouseDown: (e) => start(e, location),
    onMouseMove: move,
    onMouseUp: end,
    onMouseLeave: () => { if (!st.current.active) reset(); },
    onContextMenu: (e) => e.preventDefault(),
  } : {});

  // Spread onto anything that should accept a drop without being draggable itself — the
  // breadcrumb crumbs, which is how a location gets moved back OUT of where it is.
  const bindTarget = (id) => (onDrop && id ? { "data-reparent-target": id } : {});

  const clickGuard = (fn) => (...args) => {
    if (swallowedClick.current) { swallowedClick.current = false; return undefined; }
    return fn ? fn(...args) : undefined;
  };

  const overlay = drag ? (
    <>
      <div style={{
        position: "fixed", left: drag.x, top: drag.y, transform: "translate(-50%, -140%)",
        zIndex: 9000, pointerEvents: "none",
        display: "flex", alignItems: "center", gap: 8,
        padding: "10px 14px", borderRadius: 12,
        background: "var(--surface)", border: "1.5px solid var(--accent)",
        boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
        fontSize: 14, fontWeight: 700, color: "var(--ink)", maxWidth: "70vw",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        <MapPin size={14} extra={{ color: "var(--accent-ink)" }} /> {drag.name}
      </div>
      <div style={{
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 8999, pointerEvents: "none",
        padding: "12px 18px calc(14px + env(safe-area-inset-bottom))",
        background: "linear-gradient(to top, rgba(13,11,7,0.96), rgba(13,11,7,0))",
        textAlign: "center", fontSize: 13, fontWeight: 600,
        color: drag.overId ? "var(--accent-ink)" : "var(--ink-soft)",
      }}>
        {drag.overId ? "Release to move it here" : "Drag onto a location to move it there"}
      </div>
    </>
  ) : null;

  return { bind, bindTarget, clickGuard, overlay, draggingId: drag?.id || null, overId: drag?.overId || null };
}

// One location row. The space view and the location view had two copies of this markup that
// had already drifted (font sizes, icon sizes), and search results needed a third — so it is
// one component that all three use, and the drag wiring only has to be correct once.
// Press-and-hold to enter multi-select. A timer on the pointer events rather than
// `contextmenu`, which iOS Safari does not fire — and the press has to release focus, or the
// row keeps a highlight after the selection bar appears.
//
// `clickGuard` is the part that is not optional. Releasing a long press still fires a click,
// and the click lands after the state change, so the row that the hold just selected was
// immediately toggled back off: holding a location put you in select mode with nothing
// selected. Same shape as the reparent drag's guard, for the same reason.
function useLongPress(onLongPress, ms = 600) {
  const timer = useRef(null);
  const fired = useRef(false);
  const cancel = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  }, []);
  useEffect(() => cancel, [cancel]);
  if (!onLongPress) return { bind: {}, clickGuard: (fn) => fn };
  const start = (e) => {
    const el = e.currentTarget;
    fired.current = false;
    cancel();
    timer.current = setTimeout(() => {
      timer.current = null;
      fired.current = true;
      onLongPress();
      el?.blur();
    }, ms);
  };
  return {
    bind: {
      onTouchStart: start, onTouchEnd: cancel, onTouchMove: cancel,
      onMouseDown: start, onMouseUp: cancel, onMouseLeave: cancel,
    },
    clickGuard: (fn) => (e) => {
      if (fired.current) { fired.current = false; return; }
      fn(e);
    },
  };
}

// One row of a location list.
//
// Its photo if it has one, otherwise an icon guessed from the name. Every row used to show
// the same pin, which meant a list of eight places was eight identical marks and a column of
// text — the icon is the only part you can navigate by at a glance.
// The count on the right already says whether there is anything inside, so a "•••" hint for
// sub-locations was a second, vaguer answer to the same question.
// "Add from text", in the one shape it has everywhere: a row the width of the list, first in it.
//
// It used to be an inline pill in two views and a full-width row in a third, and in the main view
// it sat at the bottom — so in a long list of locations the way to add one was off-screen. First
// and full-width means it is reachable without scrolling and is the same target in every view.
//
// The two ways of adding something are now named by *method* rather than by kind — this one and
// "Add from camera" — because that is the choice being made at this point. It was "Add location"
// against "Add item", which named the kind and left the method implicit, so the difference between
// them looked like a difference of what you got rather than of how you said it.
//
// The plus sits in a box the size of a row's thumbnail so this row is exactly as tall as the location
// rows under it. Sized to its own 15px glyph, it was several pixels shorter than everything it sat
// above, which is visible in a stack of otherwise identical rows.
function AddLocationRow({ onClick }) {
  return (
    <button style={{ ...S.addListRow, marginBottom: 8 }} onClick={onClick}>
      <span style={{ ...S.locationRowThumb, background: "transparent", border: "1px dashed var(--line-strong)", display: "grid", placeItems: "center", color: "var(--accent-ink)" }}>
        <Plus size={15} sw={2.6} />
      </span>
      <span>Add from text</span>
    </button>
  );
}

// The photo of the place you are in, next to its name in the header.
//
// Same rule as the rows in the list below it: the photo if there is one, otherwise the icon the
// name implies, both in a box of one size so the title does not shift when a location gets
// photographed. The two header screens disagreed before — one showed a generic map pin for every
// location no matter what it looked like, and the top-level screen showed nothing at all, so the
// row you tapped and the screen it opened had no visual answer in common.
function HeaderMark({ location, size = 26 }) {
  const box = { width: size, height: size, borderRadius: 8, flexShrink: 0, objectFit: "cover", background: "var(--bg-deep)" };
  if (!locationDrawsIcon(location)) return <img src={location.image} alt="" style={box} />;
  const Icon = locationIcon(location);
  return (
    <span style={{ ...box, display: "grid", placeItems: "center", border: "1px solid var(--line)", color: "var(--accent-ink)" }}>
      <Icon size={Math.round(size * 0.62)} sw={1.9} extra={{ pointerEvents: "none" }} />
    </span>
  );
}

function LocationRow({ location, count, subPath, onOpen, reparent, selectMode, selected, onLongPress }) {
  const isOver = reparent?.overId === location.id;
  const isDragging = reparent?.draggingId === location.id;
  const Icon = locationIcon(location);
  const press = useLongPress(onLongPress);
  const openRow = () => onOpen(location.id);
  return (
    <button
      style={{
        ...S.locationHeader,
        marginBottom: 8,
        WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
        ...(isOver ? { borderColor: "var(--accent)", background: "var(--accent-soft)", transform: "scale(1.015)" } : null),
        ...(isDragging ? { opacity: 0.4 } : null),
        ...(selected ? { borderColor: "var(--accent)", boxShadow: "0 0 0 2px var(--accent-line)" } : null),
      }}
      onClick={press.clickGuard(reparent ? reparent.clickGuard(openRow) : openRow)}
      onContextMenu={onLongPress ? (e) => e.preventDefault() : undefined}
      {...press.bind}
      {...(reparent ? reparent.bind(location) : {})}
    >
      {selectMode && (
        <span style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center",
          border: selected ? "none" : "2px solid var(--line-strong)",
          background: selected ? "var(--accent)" : "transparent", pointerEvents: "none" }}>
          {selected && <Check size={13} extra={{ color: "#1a1205" }} />}
        </span>
      )}
      {/* Photo and fallback icon occupy the same box, so the names in a list line up whether
          or not a location has been photographed yet. A bare stroke icon next to a 28px
          thumbnail made the same list look like two lists. */}
      {locationDrawsIcon(location)
        ? <span style={{ ...S.locationRowThumb, display: "grid", placeItems: "center", color: "var(--accent-ink)" }}>
            <Icon size={17} sw={1.9} extra={{ pointerEvents: "none" }} />
          </span>
        : <img src={location.image} alt="" style={S.locationRowThumb} />}
      <span style={{ fontWeight: 700, fontSize: 15, color: "var(--ink)", flex: 1, textAlign: "left", minWidth: 0, pointerEvents: "none" }}>
        {location.name}
        {subPath && (
          <span style={{ display: "block", fontSize: 11.5, fontWeight: 500, color: "var(--ink-soft)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {subPath}
          </span>
        )}
      </span>
      {count != null && (
        <span style={{ fontSize: 13, color: count === 0 ? "var(--ink-faint)" : "var(--ink-soft)", pointerEvents: "none" }}>
          {count === 0 ? "empty" : count}
        </span>
      )}
      <ChevronLeft size={16} sw={2.4} extra={{ color: "var(--ink-soft)", transform: "rotate(180deg)", pointerEvents: "none" }} />
    </button>
  );
}

// A location as a photo card, for the icons view mode.
//
// Deliberately the same card as an item, down to the pixel: same border weight, same square photo,
// same two-line body, same grid — so the two can sit next to each other in one flowing list without
// one of them being visibly the odd size. The first version had a heavier border and a body with
// only a name in it, which made every location an inch shorter than the items beside it.
//
// What separates them is therefore *not* the shape. It is the badge, and the badge is load-bearing:
// without it a photographed drawer and a photographed hairbrush are the same object on screen, while
// tapping one navigates and tapping the other opens a form.
function LocationCard({ location, count, subPath, selectMode, selected, onClick, onLongPress, reparent }) {
  const press = useLongPress(onLongPress);
  const Icon = locationIcon(location);
  const isOver = reparent?.overId === location.id;
  const isDragging = reparent?.draggingId === location.id;
  return (
    <button className="card-pop" data-location-card
      style={{ ...S.card, borderColor: "var(--accent-line)",
               ...(selected ? S.cardSelected : null),
               ...(isOver ? { borderColor: "var(--accent)", boxShadow: "0 0 0 2px var(--accent)" } : null),
               ...(isDragging ? { opacity: 0.4 } : null) }}
      onClick={press.clickGuard(reparent ? reparent.clickGuard(onClick) : onClick)}
      onContextMenu={onLongPress ? (e) => e.preventDefault() : undefined}
      {...press.bind}
      {...(reparent ? reparent.bind(location) : {})}>
      <div style={S.cardImgWrap}>
        {locationDrawsIcon(location)
          ? <div style={S.cardImgPlaceholder}><Icon size={60} sw={1.2} extra={{ color: "var(--accent-ink)", opacity: 0.7 }} /></div>
          : <img src={location.image} alt="" style={S.cardImg} />}
        {selectMode && (
          <div style={{ ...S.cardCheckbox, ...(selected ? S.cardCheckboxOn : null) }}>
            {selected && <Check size={14} sw={3} />}
          </div>
        )}
        <div style={S.cardKindBadge}><MapPin size={11} sw={2.4} /> Location</div>
      </div>
      {/* Two lines, like an item's name and its location, because a card one line shorter than the
          card beside it is the whole of what looked wrong here. */}
      <div style={S.cardBody}>
        <span style={S.cardLabel}>{location.name}</span>
        <span style={S.cardLoc}>
          {subPath
            ? <><MapPin size={12} /> {subPath}</>
            : <><Package size={12} /> {count === 0 ? "empty" : `${count} item${count === 1 ? "" : "s"}`}</>}
        </span>
      </div>
    </button>
  );
}

// `AddLocationRow`'s twin for icons mode: a card in the grid rather than a row above it, and exactly
// the same size as the cards around it — same border, same square top, same two-line body — so the
// grid has no odd tile in it.
function AddThingCard({ onClick }) {
  return (
    <button className="card-pop" data-add-card style={{ ...S.card, borderStyle: "dashed", borderColor: "var(--line-strong)" }}
      onClick={onClick} aria-label="Add from text">
      <div style={{ ...S.cardImgWrap, display: "grid", placeItems: "center", background: "transparent" }}>
        <Plus size={54} sw={1.6} extra={{ color: "var(--accent-ink)" }} />
      </div>
      <div style={S.cardBody}>
        <span style={S.cardLabel}>Add from text</span>
        <span style={S.cardLoc}><Edit2 size={11} /> type a name</span>
      </div>
    </button>
  );
}

// The tree view mode: the whole subtree of wherever you are, on one screen.
//
// It is the scan picker's tree, and "like the picker" is a requirement rather than a resemblance, so
// it uses the picker's own row styles — `S.treeRow`, `S.treeChevron`, `S.treeLabel`, `S.treeAddRow`.
// The first version was a list of cards with a background each and a strip of buttons under whichever
// one was selected, and it read as a different control that happened to be nested. Three specific
// things came back from that:
//
//   • **Collapsed on arrival.** Expanding every root by default meant a tree of any size opened as a
//     wall of rows, which is the one thing a tree is for avoiding.
//   • **No background on a row.** A row is text, an icon and its indent. Selection is the only state
//     that colours anything, so it is the only thing that draws the eye.
//   • **No buttons under the selected row.** Selecting a location is not a menu. What can be done
//     with the selection lives in one bar at the bottom, exactly as the picker's confirm does, so the
//     tree stays a tree while you move around it.
//
// The gestures, which do not overlap:
//   • tap selects — that is all it does, and the bottom bar acts on it.
//   • the `>` at the end of a row opens that location's own screen.
//   • double tap opens its full edit sheet, the same one its screen's pencil opens. The earlier
//     version opened a cut-down sheet with a name and a photo in it, reported as not being the real
//     edit view, which it was not.
//   • press and hold lifts it to be dragged onto another location.
// Long press was asked for as the route to detail and cannot be it: this is the one view where
// press-and-hold already means "pick this up".
//
// Reordering siblings is not here. `public.nodes` has no column for it — siblings sort by name
// everywhere in the app — so it is a schema change plus a decision about what a manual order means
// when the same tree is shared with someone else, and neither belongs in a view-mode change.
function LocationTreeBrowser({ rootIds, locations, spaces, itemCounts, canEdit, canAddRoot, ops }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [detailId, setDetailId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const lastTap = useRef({ id: null, at: 0 });
  const reparent = useReparentDrag(locations, canEdit ? ops?.onReparent : null);

  // The selection lives above this component, because the thing that acts on it is the screen's own
  // floating camera button. A bar of the tree's own — with a Location/Item toggle on it — was tried
  // and is a worse answer to the same requirement: it put a second primary action on screen beside
  // the one that was already there, and exposed a choice that belongs on the approve screen.
  const selectedId = ops?.selectedId || null;
  const selected = selectedId ? locations.find((l) => l.id === selectedId) : null;
  // Cleared on the way in and on the way out, so a selection exists only while a tree is on screen.
  // Left behind, it would quietly aim the camera button on a screen with no tree on it at all.
  useEffect(() => {
    ops?.onSelect?.(null);
    return () => ops?.onSelect?.(null);
  }, []);

  // Re-read the node being edited from the live list on every render rather than holding the object
  // that was tapped: a photo taken from inside the sheet lands in `locations`, and a snapshot would
  // keep showing the empty frame it was opened with.
  const detail = detailId ? locations.find((l) => l.id === detailId) : null;
  const confirmDelete = confirmDeleteId ? locations.find((l) => l.id === confirmDeleteId) : null;

  const childrenOf = useMemo(() => {
    const map = new Map();
    for (const l of locations) {
      const key = l.parent_location_id || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    }
    for (const list of map.values()) list.sort((a, b) => a.name.localeCompare(b.name));
    return map;
  }, [locations]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // The first tap of a double tap still selects, which is harmless — selection colours the row and
  // aims the camera button. What matters is that the second tap is not read as a deselect.
  const tapNode = (loc) => {
    const now = Date.now();
    const isSecond = lastTap.current.id === loc.id && now - lastTap.current.at < 320;
    lastTap.current = { id: loc.id, at: now };
    if (isSecond) { setDetailId(loc.id); return; }
    ops?.onSelect?.(loc);
  };

  const renderNode = (loc, depth) => {
    const kids = childrenOf.get(loc.id) || [];
    const open = expanded.has(loc.id);
    const sel = selectedId === loc.id;
    const isOver = reparent.overId === loc.id;
    const isDragging = reparent.draggingId === loc.id;
    const count = itemCounts.get(loc.id) || 0;
    return (
      <div key={loc.id}>
        {/* The picker's row, one size up. The picker is a sheet you are passing through, so it packs
            rows in; this is a screen you browse and read, and at the picker's 14px it looked like a
            sub-list of something rather than the content. */}
        <div style={{ ...S.treeRow, paddingLeft: 12 + depth * 20, minHeight: 44,
                      ...(sel ? S.treeRowSelected : null),
                      ...(isOver ? S.treeRowSelected : null),
                      ...(isDragging ? { opacity: 0.4 } : null) }}
          {...reparent.bind(loc)}>
          <button style={S.treeChevron} onClick={() => toggle(loc.id)}
            aria-label={`${open ? "Collapse" : "Expand"} ${loc.name}`} aria-expanded={open}>
            <ChevronLeft size={17} sw={2.6} extra={{ color: kids.length ? "var(--ink-soft)" : "var(--ink-faint)", transform: open ? "rotate(-90deg)" : "rotate(180deg)", transition: "transform 0.15s" }} />
          </button>
          <button style={S.treeLabel} onClick={reparent.clickGuard(() => tapNode(loc))}>
            {(() => { const Icon = locationIcon(loc); return <Icon size={18} sw={1.9} extra={{ color: sel ? "var(--accent-ink)" : "var(--ink-soft)", flexShrink: 0 }} />; })()}
            <span style={{ fontWeight: sel ? 700 : 500, fontSize: 16, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {loc.name}
            </span>
            <span style={{ fontSize: 13.5, color: count === 0 ? "var(--ink-faint)" : "var(--ink-soft)", flexShrink: 0, marginLeft: "auto" }}>
              {count === 0 ? "empty" : count}
            </span>
          </button>
          {/* Into the location's own screen, where its items are. The tree draws places only, so
              without this there is no route from the tree to a thing on a shelf. */}
          <button style={S.treeChevron} onClick={() => ops.onOpenLocation(loc.id)}
            aria-label={`Open ${loc.name}`}>
            <ChevronLeft size={18} sw={2.6} extra={{ color: "var(--ink-soft)", transform: "rotate(180deg)" }} />
          </button>
        </div>
        {open && (
          <>
            {kids.map((k) => renderNode(k, depth + 1))}
            {canEdit && (
              <button style={{ ...S.treeAddRow, paddingLeft: 12 + (depth + 1) * 20, fontSize: 13.5 }}
                onClick={() => ops.onAddManually(loc.id, loc.name)}>
                <Plus size={13} /> Add from text
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  const roots = rootIds.map((id) => locations.find((l) => l.id === id)).filter(Boolean);

  return (
    <div>
      {roots.length === 0 && (
        <div style={{ fontSize: 13.5, color: "var(--ink-soft)", padding: "4px 12px" }}>Nothing here yet.</div>
      )}
      {roots.map((r) => renderNode(r, 0))}
      {/* The picker's own bottom row, doing the picker's job: something with no parent. Opens the same
          form the other two modes' Add from text opens, aimed at the top level — one label, one screen,
          wherever it is tapped. */}
      {canAddRoot && canEdit && (
        <button style={{ ...S.treeAddRow, paddingLeft: 12, marginTop: 6, fontSize: 13.5, fontWeight: 600 }}
          onClick={() => ops.onAddManually(null, null)}>
          <Plus size={13} /> Add from text
        </button>
      )}
      {/* What the selection is for, said once, because the screen's camera button is the thing that
          uses it and nothing on the row hints at that. */}
      {selected && canEdit && (
        <div style={{ fontSize: 13, color: "var(--ink-soft)", padding: "10px 12px 0" }}>
          The camera adds into <strong style={{ color: "var(--ink)" }}>{selected.name}</strong>.
        </div>
      )}
      {reparent.overlay}

      {detail && (
        <EditLocationSheet
          location={detail}
          allLocations={locations}
          spaces={spaces}
          userId={ops.userId}
          onCancel={() => setDetailId(null)}
          onRename={async (newName) => { await ops.onRename(detail.id, newName); }}
          onReparent={async (newParentId) => { await ops.onReparent(detail.id, newParentId); }}
          onSetPhoto={(file) => ops.onSetPhoto(detail.id, file)}
          onSetIcon={(icon) => ops.onSetIcon(detail.id, icon)}
          onRequestDelete={() => setConfirmDeleteId(detail.id)}
          onCreateSpace={ops.onCreateSpace}
          onCreateLocation={ops.onCreateChild}
          onMembersChanged={ops.onMembersChanged}
          onInvite={ops.onInvite}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteLocationSheet
          location={confirmDelete}
          itemCount={itemCounts.get(confirmDelete.id) || 0}
          subLocationCount={locationSubtree(locations, confirmDelete.id).size - 1}
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={async () => {
            const id = confirmDelete.id;
            setConfirmDeleteId(null);
            setDetailId(null);
            if (selectedId === id) ops?.onSelect?.(null);
            await ops.onDelete(id);
          }}
        />
      )}
    </div>
  );
}

function SpaceView({ canEdit = true, space, onAddManually, items, locations, spaces, userId, stableOrder = false, viewMode = DEFAULT_VIEW_MODE, itemCounts = new Map(), treeOps, onBack, onOpenItem, onOpenItemsHere, onOpenLocation, onStartScan, onScanAtLocation, onRenameSpace, onDeleteSpace, onSetSpacePhoto, onSetSpaceIcon, onReparentLocation, onReparentChildLocation, onCreateLocation, onAddExistingItems, onMoveItemsToDestination, onDeleteItems, onCreateSpace, onCreateLocationPure, onInvite, onMembersChanged }) {
  const [query, setQuery] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [createLocForScan, setCreateLocForScan] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [treePickerOpen, setTreePickerOpen] = useState(false);
  const [reparentOpen, setReparentOpen] = useState(false);
  const [confirmDeleteItemsOpen, setConfirmDeleteItemsOpen] = useState(false);
  const searchInputRef = useRef(null);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const enterSelectMode = (id) => {
    setSelectMode(true);
    setSelectedIds(new Set([id]));
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  // Grid shows ONLY items sitting directly in this space (location_id === space root).
  // Items nested inside child locations appear when you open those locations.
  const spaceItems = useMemo(
    () => items.filter((it) => it.location_id === space.id),
    [items, space.id]
  );

  // Every item anywhere in this space's subtree — search spans the whole place,
  // not just items sitting directly at the root (otherwise searching the top level
  // misses everything nested in sub-locations).
  const subtreeItems = useMemo(
    () => items.filter((it) => it.location_id && rootLocationIdOf(locations, it.location_id) === space.id),
    [items, locations, space.id]
  );

  // All items anywhere in this space's subtree — used only for location card counts.
  const subtreeItemLocIds = useMemo(
    () => subtreeItems.map((it) => it.location_id),
    [subtreeItems]
  );

  const semanticMatches = useSemanticMatches(query);
  const parentOf = useMemo(() => parentIndex(locations, items), [locations, items]);

  const results = useMemo(
    () => fuseItemResults({
      lexical: searchItems(subtreeItems, query),
      matches: semanticMatches,
      pool: subtreeItems,
      parentOf,
      stableOrder,
    }),
    [subtreeItems, query, semanticMatches, parentOf, stableOrder],
  );

  // Items only, here as on every other screen. Matching locations used to be listed above them, so
  // that "workbench" found the workbench and not only the things on it. A search is a question about a
  // thing you own; rooms in front of the answer are a list of places to go on from, which is what the
  // screen underneath the field already is. The query is still matched against location names — that is
  // how "camping utensils" finds the spork in Camping gear — it just does not return them as rows.

  // Hierarchy-aware location list for this space: the space's direct child locations.
  // Counts include items anywhere in each child's subtree.
  const locationList = useMemo(() => {
    const topLevel = locations.filter((l) => l.parent_location_id === space.id);
    return topLevel.map((loc) => {
      const subtree = locationSubtree(locations, loc.id);
      const count = subtreeItemLocIds.filter((iid) => subtree.has(iid)).length;
      return { location: loc, count };
    }).sort((a, b) => a.location.name.localeCompare(b.location.name));
  }, [subtreeItemLocIds, locations, space.id]);

  const reparent = useReparentDrag(locations, canEdit ? onReparentChildLocation : null);

  return (
    <div className="screen">
      {/* The header doubles as a drop target: dragging a location found deeper in the tree
          onto the space name is how it gets moved back up to the top of the space. */}
      <header style={{ ...S.subHeader, ...(reparent.overId === space.id ? { background: "var(--accent-soft)" } : null) }} {...reparent.bindTarget(space.id)}>
        <button style={S.iconBtn} onClick={onBack}><ChevronLeft size={24} /></button>
        <div style={{ flex: 1, textAlign: "center", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, minWidth: 0 }}>
          <HeaderMark location={space} />
          <h2 style={{ ...S.subTitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{space.name}</h2>
          {canEdit && (
            <button onClick={() => setEditOpen(true)} aria-label="Edit location"
              style={{ ...S.iconBtn, width: 28, height: 28, flexShrink: 0 }}>
              <Edit2 size={14} extra={{ color: "var(--ink-soft)" }} />
            </button>
          )}
        </div>
        <button style={S.countBtn} onClick={() => onOpenItemsHere?.()}
          aria-label={`All ${subtreeItems.length} items in ${space.name}`} title={`Everything in ${space.name}`}>
          {subtreeItems.length}
        </button>
      </header>

      <SearchField value={query} onChange={setQuery} inputRef={searchInputRef} placeholder={`Search in ${space.name}`} />

      <div className="grid-scroll">
        {query ? (
          results.length === 0 ? (
            <div style={S.noResults}>
              <Package size={40} sw={1.3} extra={{ color: "var(--ink-faint)" }} />
              <p style={{ margin: "14px 0 4px", fontWeight: 600 }}>No matches for "{query}"</p>
            </div>
          ) : (
            <>
              {results.length > 0 && (
                <>
                  <div style={S.grid}>
                    {results.map((item, idx) => {
                      // Show where the item lives if it's nested below the space root.
                      let subPath = null;
                      if (item.location_id && item.location_id !== space.id) {
                        const p = locationPath(locations, item.location_id);
                        if (p.length > 1) subPath = p.slice(1).map((x) => x.name).join(" > ");
                      }
                      return <ItemCard key={item.id} item={item} onClick={() => onOpenItem(item)} idx={idx} subPath={subPath} />;
                    })}
                  </div>
                </>
              )}
            </>
          )
        ) : (
          // Browse mode: child locations (for navigation) + all items in the space.
          (locationList.length === 0 && spaceItems.length === 0) ? (
            <div style={S.empty}>
              <div style={S.emptyMark}><Package size={44} sw={1.2} /></div>
              <h2 style={{ margin: "18px 0 6px", fontSize: 20 }}>This location is empty</h2>
              <p style={{ color: "var(--ink-soft)", fontSize: 14, maxWidth: 290, lineHeight: 1.5 }}>
                Keep your space organized with locations (like a shelf or a drawer),
                or add items directly with the camera button below.
              </p>
              <button style={{ ...S.secondaryBtn, marginTop: 22, width: "auto" }}
                onClick={() => onAddManually(space.id, space.name)}>
                <Plus size={16} /> Add from text
              </button>
            </div>
          ) : (
            <div style={{ padding: "4px 18px 130px" }}>
              {/* The tree mode replaces the list of sub-locations and nothing else. Replacing the
                  whole screen was tried and is a dead end: the tree draws locations only, so
                  opening one showed a tree of what was inside it and never the items, and there
                  was no mode in which a thing on a shelf could be seen. */}
              {viewMode === "tree" ? (
                <LocationTreeBrowser
                  rootIds={[space.id]}
                  locations={locations} spaces={spaces}
                  itemCounts={itemCounts}
                  canEdit={canEdit}
                  ops={treeOps}
                />
              ) : viewMode === "icons" ? (
                /* One grid, add tile first, then locations, then items — all the same size, so the
                   two kinds flow into each other and fill the row. Two grids stacked left a ragged
                   half-row of dead space between them whenever the number of locations was odd. */
                <div style={S.gridNested}>
                  <AddThingCard onClick={() => onAddManually(space.id, space.name)} />
                  {locationList.map(({ location, count }) => (
                    <LocationCard key={location.id} location={location} count={count}
                      reparent={reparent} onClick={() => onOpenLocation(location.id)} />
                  ))}
                  {spaceItems.map((item, idx) => (
                    <ItemCard key={item.id} item={item} idx={idx}
                      selectMode={selectMode}
                      selected={selectMode && selectedIds.has(item.id)}
                      onClick={() => { if (selectMode) toggleSelect(item.id); else onOpenItem(item); }}
                      onLongPress={() => { if (!selectMode) enterSelectMode(item.id); }} />
                  ))}
                </div>
              ) : (
                <>
              {/* The same heading home and the deeper levels both have. This screen was the one
                  rung of the ladder without it, so the list of rooms in a house arrived unlabelled
                  and the items below it looked like part of the same list. Held to the same rule as
                  the level below: no heading when there is nothing under it but the Add button. */}
              {locationList.length > 0 && (
                <div style={{ ...S.sectionHead, marginBottom: 8 }}>
                  Locations in {space.name}
                </div>
              )}
              <AddLocationRow onClick={() => onAddManually(space.id, space.name)} />
              {locationList.map(({ location, count }) => (
                <LocationRow key={location.id} location={location} count={count}
                  onOpen={onOpenLocation} reparent={reparent} />
              ))}
                </>
              )}
              {/* Not in icons mode, where they are already in the grid above. `gridNested`, not
                  `grid`: this whole block already sits inside a padded wrapper, and `S.grid` carries
                  18px of its own — so the cards on this one screen were inset 36px while the location
                  rows above them were inset 18. Same item card, 36px narrower here than on every
                  other screen, reported as items being different sizes. */}
              {viewMode !== "icons" && spaceItems.length > 0 && (
                <div style={{ ...S.gridNested, marginTop: locationList.length ? 14 : 4 }}>
                  {spaceItems.map((item, idx) => (
                    <ItemCard key={item.id} item={item} idx={idx}
                      selectMode={selectMode}
                      selected={selectMode && selectedIds.has(item.id)}
                      onClick={() => { if (selectMode) toggleSelect(item.id); else onOpenItem(item); }}
                      onLongPress={() => { if (!selectMode) enterSelectMode(item.id); }} />
                  ))}
                </div>
              )}
            </div>
          )
        )}
      </div>

      {selectMode ? (
        <div style={S.selectionBar}>
          <button style={{ ...S.secondaryBtn, width: "auto", flex: "0 0 auto", padding: "13px 16px" }} onClick={exitSelectMode}>Cancel</button>
          <button style={{ ...S.secondaryBtn, width: "auto", flex: "0 0 auto", padding: "13px 16px", borderColor: "var(--danger)", color: "var(--danger)", opacity: selectedIds.size ? 1 : 0.4 }}
            disabled={!selectedIds.size} onClick={() => setConfirmDeleteItemsOpen(true)} aria-label="Delete selected">
            <Trash2 size={18} />
          </button>
          <button style={{ ...S.primaryBtn, width: "auto", flex: 1, opacity: selectedIds.size ? 1 : 0.4 }}
            disabled={!selectedIds.size} onClick={() => setTreePickerOpen(true)}>
            Move {selectedIds.size}
          </button>
        </div>
      ) : canEdit ? (
        <button style={S.fab} onClick={() => onScanAtLocation(space.id, space.name)}>
          <Camera size={20} sw={2.2} /> <span>Add from camera</span>
        </button>
      ) : null}

      {treePickerOpen && (
        <LocationTreePicker
          items={spaceItems.filter((it) => selectedIds.has(it.id))}
          spaces={spaces}
          locations={locations}
          currentSpaceRootId={space.id}
          currentLocationId={null}
          userId={userId}
          onCancel={() => setTreePickerOpen(false)}
          onConfirm={async ({ spaceRootId, locationId }) => {
            await onMoveItemsToDestination(Array.from(selectedIds), spaceRootId, locationId);
            setTreePickerOpen(false);
            exitSelectMode();
          }}
          onCreateSpace={onCreateSpace}
          onCreateLocation={onCreateLocationPure}
        />
      )}

      {confirmDeleteItemsOpen && (
        <ConfirmDeleteItemsSheet
          count={selectedIds.size}
          onCancel={() => setConfirmDeleteItemsOpen(false)}
          onConfirm={async () => {
            await onDeleteItems(Array.from(selectedIds));
            setConfirmDeleteItemsOpen(false);
            exitSelectMode();
          }}
        />
      )}

      {createLocForScan && (
        <CreateLocationSheet
          parentName={null}
          onCancel={() => setCreateLocForScan(false)}
          onConfirm={async (name) => {
            setCreateLocForScan(false);
            const loc = await onCreateLocation(name, space.id, { noNavigate: true });
            if (loc) onScanAtLocation(loc.id, loc.name);
          }}
        />
      )}

      {editOpen && (
        <EditSpaceSheet
          space={space}
          isOwner={space.owner_id === userId}
          onInvite={() => { setEditOpen(false); setInviteOpen(true); }}
          onManagePeople={() => { setEditOpen(false); setMembersOpen(true); }}
          onMove={onReparentLocation ? () => { setEditOpen(false); setReparentOpen(true); } : undefined}
          onCancel={() => setEditOpen(false)}
          onRename={async (newName) => { await onRenameSpace(newName); }}
          onSetPhoto={canEdit ? onSetSpacePhoto : undefined}
          onSetIcon={canEdit ? onSetSpaceIcon : undefined}
          onRequestDelete={() => { setEditOpen(false); setConfirmDeleteOpen(true); }}
        />
      )}

      {confirmDeleteOpen && (
        <ConfirmDeleteLocationSheet
          location={space}
          itemCount={subtreeItems.length}
          subLocationCount={locationSubtree(locations, space.id).size - 1}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={async () => {
            setConfirmDeleteOpen(false);
            await onDeleteSpace();
          }}
        />
      )}

      {reparentOpen && (
        <LocationTreePicker
          items={[{ id: space.id, label: space.name }]}
          spaces={spaces}
          locations={locations}
          userId={userId}
          currentSpaceRootId={space.id}
          currentLocationId={space.id}
          excludeSubtreeOf={space.id}
          onCancel={() => setReparentOpen(false)}
          onConfirm={async ({ spaceRootId, locationId, topLevel }) => {
            const newParentId = topLevel ? null : (locationId || spaceRootId);
            await onReparentLocation(newParentId);
            setReparentOpen(false);
          }}
          onCreateSpace={onCreateSpace}
          onCreateLocation={onCreateLocationPure}
        />
      )}

      {inviteOpen && (
        <InviteSheet
          space={space}
          onCancel={() => setInviteOpen(false)}
          onInvite={async (nodeId, email, role) => {
            const res = await onInvite(nodeId, email, role);
            if (onMembersChanged) onMembersChanged();
            return res;
          }}
        />
      )}

      {membersOpen && (
        <MembersSheet
          space={space}
          userId={userId}
          onCancel={() => setMembersOpen(false)}
          onChanged={onMembersChanged}
          onInvite={() => { setMembersOpen(false); setInviteOpen(true); }}
        />
      )}

      {reparent.overlay}
    </div>
  );
}

// Adding something by typing, which is the approve screen with the camera's half removed.
//
// This began as a one-field sheet asking for a location's name, and the complaint against it was
// exact: "it should be a form to create an item or location, basically what is shown in the approve
// view during scan". So it is assembled from that screen's own parts — `S.labelInput` for the name,
// `TagEditor`, the `S.qtyRow` stepper, `LocationDisplay` over `LocationTreePicker`,
// `IsLocationToggle` last, `S.verifyFooter` under it — rather than styled to resemble them. Anything
// that only makes sense for a photograph is absent: no image, no alternative names the model
// offered, no sub-location predictions, no confidence.
//
// The toggle defaults ON because every route here is an "Add from text" beside a list of locations,
// so a place is what the user is nearly always after; an item is one tap away rather than a
// different screen. That default is the only thing this knows that Verify does not.
function ManualAdd({ parentId, parentName, locations, spaces, userId, onCancel, onCreateLocation, onCreateItem, onCreateSpace, onCreateLocationPure, onRefreshLocations }) {
  const [name, setName] = useState("");
  const [isLocation, setIsLocation] = useState(true);
  const [quantity, setQuantity] = useState("1");
  const [tags, setTags] = useState([]);
  const [locOverride, setLocOverride] = useState(null);
  const [locPickerOpen, setLocPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vocabulary, setVocabulary] = useState(() => tagVocabularyCache || []);
  // Held as a data URL rather than a File, because that is what both save paths take and because
  // it is also what the preview above the name field draws.
  const [photo, setPhoto] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const nameRef = useRef(null);
  useEffect(() => {
    if (tagVocabularyCache) return;
    let cancelled = false;
    loadTagVocabulary().then((rows) => { if (!cancelled) setVocabulary(rows); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (locPickerOpen && onRefreshLocations) onRefreshLocations();
  }, [locPickerOpen]);

  const effLocId = locOverride ? locOverride.id : parentId;
  const effLocName = locOverride ? locOverride.name : parentName;
  const effLocPath = (effLocId && locations) ? (() => {
    const p = locationPath(locations, effLocId);
    return p.length > 1 ? pathDisplay(p) : null;
  })() : null;

  const canSave = name.trim().length > 0 && !saving && !photoBusy;
  // Set when Add was pressed with nowhere to put the thing: the picker is standing in for the rest
  // of that tap, and finishing it finishes the save the user already asked for.
  const pendingSubmitRef = useRef(false);
  const submit = async (override) => {
    if (!canSave) return;
    const locId = override ? override.id : (effLocId || null);
    // A location with no parent is a new top level, which is a real thing to want. An ITEM with no
    // parent is not: every list in the app is built by walking down from a space, so the row lands
    // in the database and no screen can reach it again. Collect a destination instead of writing it.
    if (!isLocation && !locId) {
      pendingSubmitRef.current = true;
      setLocPickerOpen(true);
      return;
    }
    setSaving(true);
    const ok = isLocation
      ? await onCreateLocation(name.trim(), locId, photo)
      : await onCreateItem({ name: name.trim(), parentId: locId, quantity: cleanQuantity(quantity), tags, image: photo });
    if (ok) onCancel();
    else setSaving(false);
  };

  const pickPhoto = async (file) => {
    setPhotoBusy(true);
    try {
      setPhoto(await fileToUploadableDataUrl(file));
      setPhotoOpen(false);
    } catch (e) {
      alert(friendlyError(e, "read that photo"));
    } finally {
      setPhotoBusy(false);
    }
  };

  return (
    <div className="screen" style={{ background: "var(--bg)", zIndex: 12 }}>
      <header style={S.subHeader}>
        <button style={S.iconBtn} onClick={onCancel} aria-label="Cancel"><ChevronLeft size={24} /></button>
        <h2 style={{ ...S.subTitle, margin: 0, flex: 1, textAlign: "center" }}>Add from text</h2>
        <div style={{ width: 40 }} />
      </header>
      <div className="form-scroll">
        {/* Typing a name used to be the one way to get a thing with no picture at all. The photo sits
            where a scan's photograph sits, above the name, and is optional — nothing here waits on it. */}
        <button type="button" disabled={photoBusy} onClick={() => setPhotoOpen(true)}
          aria-label={photo ? "Replace the photo" : "Add a photo"}
          style={{ display: "block", width: "100%", padding: 0, marginTop: 4, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit" }}>
          <div style={S.locationHeroWrap}>
            {photo
              ? <img src={photo} alt="" style={S.locationHeroImg} />
              : <div style={S.detailImgPlaceholder}><ImageIcon size={44} sw={1.2} extra={{ color: "var(--ink-faint)" }} /></div>}
            <span style={S.locationHeroHint}>
              {photoBusy
                ? <><Loader size={13} /> Reading…</>
                : <><Camera size={13} sw={2.2} /> {photo ? "Tap to replace" : "Tap to add a photo"}</>}
            </span>
          </div>
        </button>
        <div style={{ ...S.labelInputWrap, marginTop: 12 }}>
          {/* No autofocus. The form opens with a photo field above this and a location below it, and
              a keyboard covering both of them decides for the user that the name is the first thing. */}
          <textarea ref={nameRef} style={S.labelInput} value={name} rows={1}
            onChange={(e) => setName(e.target.value)}
            placeholder="What is it?"
            onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }} />
          {name.length > 0 && (
            <button onClick={() => { setName(""); nameRef.current?.focus(); }} style={S.labelClearBtn} type="button"><X size={16} /></button>
          )}
        </div>
        {!isLocation && (
          <div style={{ marginTop: 14 }}>
            <TagEditor tags={tags} vocabulary={vocabulary} onChange={setTags} />
          </div>
        )}
        {!isLocation && (
          <div style={S.qtyRow}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>Quantity</div>
              <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1 }}>One entry, counted</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <button type="button" style={S.qtyStepBtn} aria-label="One fewer"
                onClick={() => setQuantity(String(Math.max(1, cleanQuantity(quantity) - 1)))}>−</button>
              <input style={S.qtyInput} inputMode="numeric" pattern="[0-9]*" value={quantity}
                aria-label="Quantity"
                onChange={(e) => setQuantity(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                onBlur={() => setQuantity(String(cleanQuantity(quantity)))} />
              <button type="button" style={S.qtyStepBtn} aria-label="One more"
                onClick={() => setQuantity(String(cleanQuantity(quantity) + 1))}>+</button>
            </div>
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <LocationDisplay
            name={effLocName || null}
            path={effLocPath}
            emptyLabel={isLocation ? "Top level" : "Choose a location"}
            onTap={() => setLocPickerOpen(true)}
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <IsLocationToggle on={isLocation} onToggle={setIsLocation} />
        </div>
        <div style={{ height: 110 }} />
      </div>
      {photoOpen && (
        <PhotoSourceSheet
          title={photo ? "Replace the photo" : "Add a photo"}
          busy={photoBusy}
          onPick={pickPhoto}
          onRemove={photo ? () => { setPhoto(null); setPhotoOpen(false); } : undefined}
          onCancel={() => setPhotoOpen(false)}
        />
      )}
      {locPickerOpen && (
        <LocationTreePicker
          items={[{ id: "manual", label: name.trim() || (isLocation ? "this place" : "this item") }]}
          spaces={spaces}
          locations={locations}
          userId={userId}
          currentSpaceRootId={effLocId ? rootLocationIdOf(locations, effLocId) : null}
          currentLocationId={effLocId}
          moveLabel="Add"
          onCancel={() => { setLocPickerOpen(false); pendingSubmitRef.current = false; }}
          onConfirm={({ spaceRootId, locationId }) => {
            const targetId = locationId || spaceRootId;
            const targetLoc = targetId ? getLocationById(locations, targetId) : null;
            const override = targetId ? { id: targetId, name: targetLoc?.name || effLocName } : null;
            setLocOverride(override);
            setLocPickerOpen(false);
            const wasSaving = pendingSubmitRef.current;
            pendingSubmitRef.current = false;
            if (wasSaving && override) submit(override);
          }}
          onCreateSpace={onCreateSpace}
          onCreateLocation={onCreateLocationPure}
        />
      )}
      <div style={S.verifyFooter}>
        <button style={S.declineBtn} onClick={onCancel} disabled={saving}><X size={18} /> Cancel</button>
        <button style={{ ...S.approveBtn, opacity: canSave ? 1 : 0.4 }} disabled={!canSave} onClick={() => submit()}>
          {saving ? <><Loader size={18} /> Saving…</> : <><Check size={18} /> Add</>}
        </button>
      </div>
    </div>
  );
}

// The one sheet for creating a location, at any depth.
//
// Still used by the scan picker, which creates a destination without leaving the sheet it is in.
// Everywhere else, typing a name goes through `ManualAdd` above.
function CreateLocationSheet({ onCancel, onConfirm, parentName }) {
  const [name, setName] = useState("");
  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>
            Add location
          </h3>
          <button style={S.iconBtn} onClick={onCancel}><X size={20} /></button>
        </div>
        <p style={{ fontSize: 13, color: "var(--ink-soft)", margin: "0 0 16px", lineHeight: 1.5 }}>
          {parentName
            ? `A place inside ${parentName} — a shelf, a drawer, a box. You can move or rename it later.`
            : "A physical place — your house, a closet, a shelf, a box. Locations nest inside each other, and you can rename them later."}
        </p>
        <input autoFocus style={{ ...S.input, fontSize: 17, padding: "14px 16px" }}
          placeholder={parentName ? "e.g. Top shelf, Bottom drawer" : "e.g. House, Car, Backpack"}
          value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) onConfirm(name); }} />
        <button style={{ ...S.primaryBtn, marginTop: 18, opacity: name.trim() ? 1 : 0.4 }}
          disabled={!name.trim()} onClick={() => onConfirm(name)}>
          <Plus size={18} /> Create
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Location view (one location inside a space)
// ─────────────────────────────────────────────────────────────────────

function LocationView({ space, location, onAddManually, path, childLocations, allLocations, items, userId, stableOrder = false, viewMode = DEFAULT_VIEW_MODE, itemCounts = new Map(), treeOps, onBack, onOpenItem, onOpenItemsHere, onOpenChildLocation, onCreateChildLocation, onStartScan, onMoveItemsHere, onDeleteLocation, onRenameLocation, onReparentLocation, onReparentChildLocation, onSetLocationPhoto, onSetLocationIcon, onConvertToItem, spaces, onMoveItemsToDestination, onCreateSpace, onCreateLocationPure, onDeleteItems, onInvite, onMembersChanged, canEdit = true }) {
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // Multi-select
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [treePickerOpen, setTreePickerOpen] = useState(false);
  const [confirmDeleteItemsOpen, setConfirmDeleteItemsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const enterSelectMode = (id) => {
    setSelectMode(true);
    setSelectedIds(new Set([id]));
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  // Subtree count includes everything under this node, including direct
  const subtreeCount = items.length;
  // Sub-locations in the subtree (excluding the location itself) — for the delete confirmation
  const subtreeLocationCount = locationSubtree(allLocations, location.id).size - 1;

  // Breadcrumb path (excluding the current location itself; that's in the title)
  // path starts at the space root (now a root location) and ends at the current location.
  // The header already shows space.name, so drop BOTH the root crumb and the current one.
  const breadcrumb = path.slice(1, -1);
  const sortedChildren = useMemo(() => [...childLocations].sort((a, b) => a.name.localeCompare(b.name)), [childLocations]);

  // Items selected for batch move (resolve full item objects)
  // Grid shows ONLY items directly in this location (not nested sub-locations).
  // `items` (the full subtree) is still used for sub-location counts.
  const directItems = useMemo(
    () => items.filter((it) => it.location_id === location.id),
    [items, location.id]
  );
  const selectedItems = directItems.filter((it) => selectedIds.has(it.id));

  // Search across this location's whole subtree (direct items AND everything in its
  // sub-locations), so searching a location finds nested items too. The grid with no query
  // still shows only directItems + the sub-locations section; search widens to the full
  // subtree, and each nested result is tagged with its relative path in the render below.
  const semanticMatches = useSemanticMatches(query);
  const parentOf = useMemo(() => parentIndex(allLocations, items), [allLocations, items]);

  const results = useMemo(
    () => fuseItemResults({
      lexical: searchItems(items, query),
      matches: semanticMatches,
      pool: items,
      parentOf,
      stableOrder,
    }),
    [items, query, semanticMatches, parentOf, stableOrder],
  );

  // Items only — see the note on the same decision in SpaceView. Sub-locations were listed above them.

  const reparent = useReparentDrag(allLocations, canEdit ? onReparentChildLocation : null);

  return (
    <div className="screen">
      <header style={S.subHeader}>
        <button style={S.iconBtn} onClick={onBack}><ChevronLeft size={24} /></button>
        <div style={{ flex: 1, textAlign: "center", overflow: "hidden", minWidth: 0 }}>
          {/* Each crumb is a drop target. Dragging a sub-location onto an ancestor's name is
              how it moves back OUT of here — the reverse of dropping it onto a sibling row,
              and without it the gesture could only ever nest things deeper. */}
          <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: 0.4, color: "var(--ink-soft)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <span {...reparent.bindTarget(space.id)}
              style={reparent.overId === space.id ? { color: "var(--accent-ink)", fontWeight: 700 } : null}>
              {space.name}
            </span>
            {breadcrumb.map((p) => (
              <span key={p.id}> · <span {...reparent.bindTarget(p.id)}
                style={reparent.overId === p.id ? { color: "var(--accent-ink)", fontWeight: 700 } : null}>{p.name}</span></span>
            ))}
          </div>
          <h2 style={{ ...S.subTitle, display: "flex", alignItems: "center", gap: 7, justifyContent: "center", minWidth: 0 }}>
            <HeaderMark location={location} size={24} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{location.name}</span>
            {canEdit && (
              <button onClick={() => setEditOpen(true)} style={{ ...S.iconBtn, width: 28, height: 28, flexShrink: 0 }} aria-label="Edit location">
                <Edit2 size={14} extra={{ color: "var(--ink-soft)" }} />
              </button>
            )}
          </h2>
        </div>
        <button style={S.countBtn} onClick={() => onOpenItemsHere?.()}
          aria-label={`All ${subtreeCount} items in ${location.name}`} title={`Everything in ${location.name}`}>
          {subtreeCount}
        </button>
      </header>

      {!selectMode && (
        <SearchField value={query} onChange={setQuery} inputRef={searchInputRef} placeholder={`Search in ${location.name}`} />
      )}

      <div className="grid-scroll">
        {query.trim() ? (
          results.length === 0 ? (
            <div style={{ ...S.empty, paddingTop: 40 }}>
              <p style={{ color: "var(--ink-soft)", fontSize: 14, margin: 0 }}>Nothing matches "{query}" here.</p>
            </div>
          ) : (
            <>
              {results.length > 0 && (
                <>
                  <div style={{ ...S.grid, paddingBottom: selectMode ? 90 : 110 }}>
              {results.map((item, idx) => {
                let relPath = null;
                if (item.location_id && item.location_id !== location.id) {
                  const fullPath = locationPath(allLocations, item.location_id);
                  const currentIdx = fullPath.findIndex((p) => p.id === location.id);
                  if (currentIdx >= 0) relPath = fullPath.slice(currentIdx + 1).map((p) => p.name).join(" > ");
                }
                return (
                  <ItemCard key={item.id} item={item} idx={idx} subPath={relPath}
                    selectMode={selectMode}
                    selected={selectMode && selectedIds.has(item.id)}
                    onClick={() => { if (selectMode) toggleSelect(item.id); else onOpenItem(item); }}
                    onLongPress={() => { if (!selectMode) enterSelectMode(item.id); }}
                  />
                );
              })}
                  </div>
                </>
              )}
            </>
          )
        ) : (
          <>
            {/* Sub-locations. The heading only earns its line when there is a list under it to
                head; with none it was labelling a single Add button. Tree mode replaces this
                section and leaves the items below it alone — see the note in the space view. */}
            {viewMode === "tree" ? (
              <div style={{ padding: "10px 18px 4px" }}>
                <LocationTreeBrowser
                  rootIds={[location.id]}
                  locations={allLocations} spaces={spaces}
                  itemCounts={itemCounts}
                  canEdit={canEdit}
                  ops={treeOps}
                />
              </div>
            ) : viewMode === "icons" ? (
              /* One grid for both kinds — see the note in the space view. */
              <div style={{ ...S.grid, paddingBottom: selectMode ? 90 : 110 }}>
                <AddThingCard onClick={() => onAddManually(location.id, location.name)} />
                {sortedChildren.map((child) => (
                  <LocationCard key={child.id} location={child} reparent={reparent}
                    count={itemCounts.get(child.id) || 0}
                    onClick={() => onOpenChildLocation(child.id)} />
                ))}
                {directItems.map((item, idx) => (
                  <ItemCard key={item.id} item={item} idx={idx}
                    selectMode={selectMode}
                    selected={selectMode && selectedIds.has(item.id)}
                    onClick={() => { if (selectMode) toggleSelect(item.id); else onOpenItem(item); }}
                    onLongPress={() => { if (!selectMode) enterSelectMode(item.id); }} />
                ))}
              </div>
            ) : (
            <div style={{ padding: "4px 18px 4px" }}>
              {sortedChildren.length > 0 && (
                <div style={{ ...S.sectionHead, marginBottom: 8 }}>
                  Sub-locations in {location.name}
                </div>
              )}
              <AddLocationRow onClick={() => onAddManually(location.id, location.name)} />
              {sortedChildren.map((child) => (
                <LocationRow key={child.id} location={child}
                  count={items.filter((it) => it.location_id && locationSubtree(allLocations, child.id).has(it.location_id)).length}
                  onOpen={onOpenChildLocation} reparent={reparent} />
              ))}
            </div>
            )}

            {viewMode !== "icons" && (
            <>
            {/* Items grid — only items directly in this location. Don't show the
                "no items yet" message if sub-locations contain items (the cards above
                already show where things are). */}
            {directItems.length === 0 ? (
              items.length === 0 ? (
                <div style={{ ...S.empty, paddingTop: 30, paddingBottom: 110 }}>
                  <p style={{ color: "var(--ink-soft)", fontSize: 13.5, maxWidth: 280, lineHeight: 1.5, margin: 0 }}>
                    No items in <strong style={{ color: "var(--ink)" }}>{location.name}</strong> yet.
                    {sortedChildren.length === 0 && " Add items, or set up sub-locations first."}
                  </p>
                </div>
              ) : <div style={{ height: 110 }} />
            ) : (
              <div style={{ ...S.grid, paddingBottom: selectMode ? 90 : 110 }}>
                {directItems.map((item, idx) => (
                  <ItemCard key={item.id} item={item} idx={idx}
                    selectMode={selectMode}
                    selected={selectMode && selectedIds.has(item.id)}
                    onClick={() => {
                      if (selectMode) toggleSelect(item.id);
                      else onOpenItem(item);
                    }}
                    onLongPress={() => { if (!selectMode) enterSelectMode(item.id); }}
                  />
                ))}
              </div>
            )}
            </>
            )}
          </>
        )}
      </div>

      {selectMode ? (
        <div style={S.selectionBar}>
          <button style={{ ...S.secondaryBtn, width: "auto", flex: "0 0 auto", padding: "13px 16px" }} onClick={exitSelectMode}>Cancel</button>
          <button style={{ ...S.secondaryBtn, width: "auto", flex: "0 0 auto", padding: "13px 16px", borderColor: "var(--danger)", color: "var(--danger)", opacity: selectedIds.size ? 1 : 0.4 }}
            disabled={!selectedIds.size} onClick={() => setConfirmDeleteItemsOpen(true)} aria-label="Delete selected">
            <Trash2 size={18} />
          </button>
          <button style={{ ...S.primaryBtn, width: "auto", flex: 1, opacity: selectedIds.size ? 1 : 0.4 }}
            disabled={!selectedIds.size} onClick={() => setTreePickerOpen(true)}>
            Move {selectedIds.size}
          </button>
        </div>
      ) : canEdit ? (
        // The same floating pill as the home and top-level views. This was a full-width bar
        // pinned to the bottom edge, left over from when it held two buttons, so the camera button
        // was visibly a different and much larger control one level down the tree.
        <button style={S.fab} onClick={() => onStartScan()}>
          <Camera size={20} sw={2.2} /> <span>Add from camera</span>
        </button>
      ) : null}

      {treePickerOpen && (
        <LocationTreePicker
          items={selectedItems}
          spaces={spaces}
          locations={allLocations}
          currentSpaceRootId={space.id}
          currentLocationId={location.id}
          userId={userId}
          onCancel={() => setTreePickerOpen(false)}
          onConfirm={async ({ spaceRootId, locationId }) => {
            await onMoveItemsToDestination(Array.from(selectedIds), spaceRootId, locationId);
            setTreePickerOpen(false);
            exitSelectMode();
          }}
          onCreateSpace={onCreateSpace}
          onCreateLocation={onCreateLocationPure}
        />
      )}

      {confirmDeleteItemsOpen && (
        <ConfirmDeleteItemsSheet
          count={selectedIds.size}
          onCancel={() => setConfirmDeleteItemsOpen(false)}
          onConfirm={async () => {
            await onDeleteItems(Array.from(selectedIds));
            setConfirmDeleteItemsOpen(false);
            exitSelectMode();
          }}
        />
      )}


      {editOpen && (
        <EditLocationSheet
          location={location}
          allLocations={allLocations}
          spaces={spaces}
          userId={userId}
          onCreateSpace={onCreateSpace}
          onCreateLocation={onCreateLocationPure}
          onCancel={() => setEditOpen(false)}
          onRename={async (newName) => { await onRenameLocation(newName); }}
          onReparent={async (newParentId) => { await onReparentLocation(newParentId); setEditOpen(false); }}
          onSetPhoto={canEdit ? onSetLocationPhoto : undefined}
          onSetIcon={canEdit ? onSetLocationIcon : undefined}
          // `items` is the whole subtree, so an empty one means every sub-location below is
          // empty too — which is exactly the case the conversion is allowed to clean up.
          onConvertToItem={(items.length === 0 && onConvertToItem)
            ? () => { setEditOpen(false); onConvertToItem(); }
            : undefined}
          convertRemovesCount={items.length === 0 ? subtreeLocationCount : 0}
          onRequestDelete={() => { setEditOpen(false); setConfirmDeleteOpen(true); }}
          onInvite={onInvite}
          onMembersChanged={onMembersChanged}
        />
      )}

      {confirmDeleteOpen && (
        <ConfirmDeleteLocationSheet
          location={location}
          itemCount={subtreeCount}
          subLocationCount={subtreeLocationCount}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={async () => {
            setConfirmDeleteOpen(false);
            await onDeleteLocation();
          }}
        />
      )}
      {reparent.overlay}
    </div>
  );
}

// A location's photo, with the control to set or replace it.
//
// The photo is what makes a location recognisable in a list of six drawers, and items inside
// a photoless sub-location inherit the nearest one up the chain. It lives here rather than
// inline in one edit sheet because top-level locations had no way to get a photo at all —
// their sheet simply never grew the control, and the only route to one was scanning an item
// and converting it into a location.
//
// Read-only viewers get the picture without the button.
// A location's picture, in the same shape an item's picture has on its detail screen: an image
// area that is always there.
//
// It used to appear only once a photo existed, so a location with no photo gave no sign that it
// could have one — the way to find out was to open the edit sheet and read the buttons. Now the
// area is always drawn, holding the location's default icon until there is something better, and
// tapping anywhere in it opens the camera.
//
// Three ways to answer "what does this place look like?", and an icon is one of them. A photo is the
// best answer when there is one worth taking, and for a great many locations there is not: a loft, a
// cupboard under the stairs, a plastic tub of cables all photograph as a dim rectangle, and the
// guessed icon is better than that. So the same sheet that offers the camera and the library offers
// the icon set — which is also the only way to correct a guess, and the guesser is a keyword list that
// cannot recognise "Christmas stuff".
// Camera or library, for a photo that is being added rather than looked at.
//
// Pulled out of LocationPhotoField rather than copied: an item has no icon to fall back to and no
// existing photo to view at full size, so it needs this half of that component and none of the rest.
function PhotoSourceSheet({ title, busy, onPick, onRemove, onCancel }) {
  // Two inputs, not one with a toggled attribute — see the note in LocationPhotoField.
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const take = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // so picking the same file twice still fires onChange
    if (!file) return;
    await onPick(file);
  };
  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={{ ...S.sheet, display: "flex", flexDirection: "column", gap: 12 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <h3 style={S.sheetTitle}>{title}</h3>
          <button style={S.iconBtn} onClick={onCancel} aria-label="Close"><X size={20} /></button>
        </div>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={take} />
        <input ref={libraryRef} type="file" accept="image/*" style={{ display: "none" }} onChange={take} />
        <button style={S.primaryBtn} disabled={busy} onClick={() => cameraRef.current?.click()}>
          <Camera size={17} sw={2.2} /> {busy ? "Working…" : "Take a photo"}
        </button>
        <button style={S.secondaryBtn} disabled={busy} onClick={() => libraryRef.current?.click()}>
          <ImageIcon size={17} sw={2.2} /> Choose from library
        </button>
        {onRemove && (
          <button style={S.secondaryBtn} disabled={busy} onClick={onRemove}>Remove the photo</button>
        )}
      </div>
    </div>
  );
}

function LocationPhotoField({ location, onSetPhoto, onSetIcon }) {
  // Two inputs, not one with a toggled attribute. `capture` is read when the picker opens and
  // React reuses the same DOM node, so flipping it on one input gave whichever value the previous
  // tap had left behind.
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [iconsOpen, setIconsOpen] = useState(false);
  const Icon = locationIcon(location);
  const editable = !!onSetPhoto;

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // so picking the same file twice still fires onChange
    if (!file) return;
    setBusy(true);
    await onSetPhoto(file);
    setBusy(false);
    setViewerOpen(false);
  };

  const chooseIcon = async (key) => {
    setBusy(true);
    await onSetIcon(key);
    setBusy(false);
    setIconsOpen(false);
    setViewerOpen(false);
  };

  const hero = (
    <div style={S.locationHeroWrap}>
      {locationDrawsIcon(location)
        ? <div style={S.detailImgPlaceholder}><Icon size={54} sw={1.2} extra={{ color: "var(--ink-faint)" }} /></div>
        : <img src={location.image} alt={location.name} style={S.locationHeroImg} />}
      {editable && (
        <span style={S.locationHeroHint}>
          {busy
            ? <><Loader size={13} /> Saving…</>
            : <><Camera size={13} sw={2.2} /> {location.image ? "Tap to view or replace" : "Tap to add a photo"}</>}
        </span>
      )}
    </div>
  );

  if (!editable) return <div style={{ marginBottom: 14 }}>{hero}</div>;

  return (
    <div style={{ marginBottom: 14 }}>
      {/* Tapping the photo used to open the camera directly, which meant there was no way to look
          at the existing photo at full size and no way to choose one already on the phone — and the
          only route to "what does this location look like?" was to start replacing it. */}
      <button type="button" disabled={busy} onClick={() => setViewerOpen(true)}
        aria-label={location.image ? "View or replace photo" : "Add a photo"}
        style={{ display: "block", width: "100%", padding: 0, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit" }}>
        {hero}
      </button>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={pick} />
      <input ref={libraryRef} type="file" accept="image/*" style={{ display: "none" }} onChange={pick} />
      {viewerOpen && (
        <div style={S.sheetBackdrop} onClick={() => setViewerOpen(false)}>
          <div style={{ ...S.sheet, display: "flex", flexDirection: "column", gap: 12 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <h3 style={S.sheetTitle}>{location.name}</h3>
              <button style={S.iconBtn} onClick={() => setViewerOpen(false)} aria-label="Close"><X size={20} /></button>
            </div>
            <div style={{ borderRadius: 14, overflow: "hidden", background: "var(--bg-deep)", maxHeight: "52vh", display: "grid", placeItems: "center" }}>
              {locationDrawsIcon(location)
                ? <div style={{ padding: "44px 0", color: "var(--ink-faint)" }}><Icon size={64} sw={1.2} /></div>
                : <img src={location.image} alt={location.name}
                    style={{ width: "100%", maxHeight: "52vh", objectFit: "contain", display: "block" }} />}
            </div>
            {iconsOpen ? (
              <>
                {/* A grid, not a list: an icon is recognised at a glance and read by nobody, so names
                    beside them would be a column of words to skim past. The current one is ringed. */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(58px, 1fr))", gap: 8, maxHeight: "38vh", overflowY: "auto" }}>
                  {Object.entries(LOCATION_ICONS).map(([key, Glyph]) => {
                    const on = location.icon === key;
                    return (
                      <button key={key} type="button" disabled={busy} onClick={() => chooseIcon(key)}
                        aria-label={`Use the ${key} icon`} aria-pressed={on}
                        style={{
                          aspectRatio: "1", display: "grid", placeItems: "center", cursor: "pointer",
                          borderRadius: 14, fontFamily: "inherit",
                          border: on ? "2px solid var(--accent)" : "1px solid var(--line)",
                          background: on ? "var(--accent-soft)" : "var(--surface-2)",
                          color: on ? "var(--accent-ink)" : "var(--ink-soft)",
                        }}>
                        <Glyph size={26} sw={1.7} />
                      </button>
                    );
                  })}
                </div>
                {/* Clearing goes back to the guess rather than to nothing, because "no icon" is not a
                    state a location can be in — something has to be drawn in the list. */}
                {location.icon && (
                  <button style={S.secondaryBtn} disabled={busy} onClick={() => chooseIcon(null)}>
                    {location.image ? "Use the photo instead" : "Use the suggested icon"}
                  </button>
                )}
                <button style={S.secondaryBtn} disabled={busy} onClick={() => setIconsOpen(false)}>Back</button>
              </>
            ) : (
              <>
                <button style={S.primaryBtn} disabled={busy} onClick={() => cameraRef.current?.click()}>
                  <Camera size={17} sw={2.2} /> {busy ? "Saving…" : "Take a photo"}
                </button>
                <button style={S.secondaryBtn} disabled={busy} onClick={() => libraryRef.current?.click()}>
                  <ImageIcon size={17} sw={2.2} /> Choose from library
                </button>
                {onSetIcon && (
                  <button style={S.secondaryBtn} disabled={busy} onClick={() => setIconsOpen(true)}>
                    <Layers size={17} sw={2.2} /> Choose an icon
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// EditLocationSheet: rename, change parent, set a photo, delete.
function EditLocationSheet({ location, allLocations, spaces, userId, onCreateSpace, onCreateLocation, onCancel, onRename, onReparent, onSetPhoto, onSetIcon, onConvertToItem, convertRemovesCount = 0, onRequestDelete, onMembersChanged, onInvite }) {
  const [reparentOpen, setReparentOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  // People visibility/management follows the server rule (migration 20260706000000):
  // owner of this location OR of any ancestor — so the tree owner manages nested
  // foreign-owned locations too ("your house, your rules").
  const canManageMembers = ownsNodeOrAncestor(allLocations, location.id, userId);

  // Current parent (where this location lives). null parent = top-level.
  const parent = location.parent_location_id ? getLocationById(allLocations, location.parent_location_id) : null;
  const parentName = parent ? parent.name : null;
  const parentPath = (parent && allLocations) ? (() => {
    const p = locationPath(allLocations, parent.id);
    return p.length > 1 ? pathDisplay(p) : null;
  })() : null;

  const commitRename = (text) => {
    const trimmed = (text || "").trim();
    if (trimmed && trimmed !== location.name) onRename(trimmed);
  };

  if (reparentOpen) {
    // Reuse the same tree picker as moving items. Picking a location sets it as the new
    // parent; picking a space root makes this a top-level location in that space. The
    // location's own subtree is excluded to prevent cycles.
    return (
      <LocationTreePicker
        items={[{ id: location.id, label: location.name }]}
        spaces={spaces}
        locations={allLocations}
        userId={userId}
        currentSpaceRootId={rootLocationIdOf(allLocations, location.id)}
        currentLocationId={location.id}
        excludeSubtreeOf={location.id}
        onCancel={() => setReparentOpen(false)}
        onConfirm={async ({ spaceRootId, locationId, topLevel }) => {
          const newParentId = topLevel ? null : (locationId || spaceRootId);
          await onReparent(newParentId);
        }}
        onCreateSpace={onCreateSpace}
        onCreateLocation={onCreateLocation}
      />
    );
  }

  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>Edit location</h3>
          <button style={S.iconBtn} onClick={onCancel} aria-label="Close"><X size={20} /></button>
        </div>
        <LocationPhotoField location={location} onSetPhoto={onSetPhoto} onSetIcon={onSetIcon} />
        <EditableTitle value={location.name} canEdit={!!onRename} editLabel="Rename location"
          onCommit={commitRename} />
        <div style={{ marginTop: 12 }}>
          <LocationDisplay
            name={parentName}
            path={parentPath}
            emptyLabel="Top level (its own location)"
            onTap={() => setReparentOpen(true)}
          />
        </div>
        <div style={{ marginTop: 10 }}>
          {onConvertToItem
            ? <IsLocationToggle on={true} onToggle={(v) => {
                if (v) return;
                // The empty sub-locations disappear with the conversion, so say so before
                // doing it rather than leaving the user to notice they're gone.
                if (convertRemovesCount > 0 && !window.confirm(
                  `${location.name} has ${convertRemovesCount} empty ${convertRemovesCount === 1 ? "sub-location" : "sub-locations"}. ` +
                  `Turning it into an item removes ${convertRemovesCount === 1 ? "it" : "them"} too.`,
                )) return;
                onConvertToItem();
              }} />
            : <IsLocationToggle on={true} disabled
                disabledNote="Has things inside — move or remove them to change this." />}
        </div>
        {/* Directly above Delete, matching the top-level sheet. It sat further up between the
            parent picker and the is-location toggle, so the same two buttons appeared in a
            different order depending on which depth of location you were editing. */}
        {canManageMembers && (
          <button style={{ ...S.secondaryBtn, marginTop: 10 }} onClick={() => setMembersOpen(true)}>
            <Users size={18} /> People
          </button>
        )}
        <button style={{ ...S.dangerBtn, marginTop: 10 }} onClick={onRequestDelete}>
          <Trash2 size={18} /> Delete location
        </button>
      </div>

      {membersOpen && (
        <MembersSheet
          space={location}
          userId={userId}
          canManage={canManageMembers}
          onCancel={() => setMembersOpen(false)}
          onChanged={onMembersChanged}
          onInvite={() => { setMembersOpen(false); setInviteOpen(true); }}
        />
      )}
      {inviteOpen && (
        <InviteSheet
          space={location}
          onCancel={() => { setInviteOpen(false); setMembersOpen(true); }}
          onInvite={async (nodeId, email, role) => {
            const res = await onInvite(nodeId, email, role);
            if (onMembersChanged) onMembersChanged();
            return res;
          }}
        />
      )}
    </div>
  );
}

function ConfirmDeleteLocationSheet({ location, itemCount, subLocationCount, onCancel, onConfirm }) {
  const [working, setWorking] = useState(false);
  const summary = [];
  if (subLocationCount > 0) summary.push(`${subLocationCount} sub-location${subLocationCount === 1 ? "" : "s"}`);
  if (itemCount > 0) summary.push(`${itemCount} item${itemCount === 1 ? "" : "s"}`);
  const run = async () => { setWorking(true); await onConfirm(); };
  const isEmpty = itemCount === 0 && subLocationCount === 0;
  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: "0 0 12px" }}>
          Delete {location.name}?
        </h3>
        <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.55, margin: "0 0 18px" }}>
          {isEmpty
            ? <>This location is empty. It will be deleted permanently.</>
            : <>This will permanently delete this location and everything inside it — {summary.join(" and ")}. This can't be undone.</>}
        </p>
        <button style={{ ...S.primaryBtn, background: "var(--danger)", color: "#fff", marginBottom: 10 }}
          disabled={working} onClick={run}>
          {working ? <><Loader size={18} /> Deleting…</> : <><Trash2 size={18} /> {isEmpty ? "Delete" : `Delete everything`}</>}
        </button>
        <button style={S.secondaryBtn} disabled={working} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// The one confirmation for deleting items, wherever it happens.
//
// There used to be two: this sheet in the multi-select bars, and a bare window.confirm() on
// the item detail screen. Same destructive action, two different levels of ceremony, and the
// native dialog is the one that looks like the browser rather than the app. Deleting one item
// from its own screen is not a lesser act than deleting it from a list.
function ConfirmDeleteItemsSheet({ count, label, onCancel, onConfirm }) {
  const [working, setWorking] = useState(false);
  const noun = count === 1 ? "item" : "items";
  const run = async () => { setWorking(true); await onConfirm(); };
  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <Trash2 size={22} extra={{ color: "var(--danger)" }} />
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>
            Delete {count} {noun}?
          </h3>
        </div>
        <p style={{ fontSize: 13.5, color: "var(--ink-soft)", margin: "0 0 18px", lineHeight: 1.5 }}>
          {count === 1 && label ? `"${label}"` : count === 1 ? "This item" : "These items"} will
          be deleted permanently. This can't be undone.
        </p>
        <button style={{ ...S.primaryBtn, background: "var(--danger)", color: "#fff", marginBottom: 10 }}
          disabled={working} onClick={run}>
          {working
            ? <><Loader size={18} /> Deleting…</>
            : <><Trash2 size={18} /> Delete {count} {noun}</>}
        </button>
        <button style={S.secondaryBtn} disabled={working} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// Every item under one location, or every item there is.
//
// An overlay rather than a screen in the `view` switch, because the count that opens it is on the
// scan bar as well as on the browse headers, and a camera session must survive being asked "what is
// already in here?". That is also why tapping a result is optional: from the camera there is nowhere
// to go that would not throw away the queue, so there the cards are a list and not links.
//
// A separate `AllItemsView` existed for the unscoped case, reached by a chip beside the home search.
// It was deleted for answering the same question as that field; this is the scoped version the counts
// were already implying, and the unscoped case is the count on the home header.
function ScopedItemsView({ scopeId, scopeName, items, locations, stableOrder = false, onClose, onOpenItem }) {
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);
  const scoped = useMemo(() => {
    if (!scopeId) return items;
    const ids = locationSubtree(locations, scopeId);
    return items.filter((it) => it.location_id && ids.has(it.location_id));
  }, [items, locations, scopeId]);
  const semanticMatches = useSemanticMatches(query);
  // Arriving here from a screen whose own search field had focus leaves the phone keyboard up over the
  // grid, which is the one thing this screen exists to show.
  useEffect(() => { document.activeElement?.blur?.(); }, []);
  const parentOf = useMemo(() => parentIndex(locations, scoped), [locations, scoped]);
  const results = useMemo(
    () => fuseItemResults({ lexical: searchItems(scoped, query), matches: semanticMatches, pool: scoped, parentOf, stableOrder }),
    [scoped, query, semanticMatches, parentOf, stableOrder],
  );

  return (
    <div className="screen" data-scoped-items style={{ background: "var(--bg)", zIndex: 30 }}>
      <header style={S.subHeader}>
        <button style={S.iconBtn} onClick={onClose} aria-label="Back"><ChevronLeft size={24} /></button>
        {/* "All items" over the place they are in, in the shape a nested location's header already
            uses: the scope belongs above the name, not folded into a sentence in place of it. */}
        <div style={{ flex: 1, textAlign: "center", overflow: "hidden", minWidth: 0 }}>
          {scopeName && (
            <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: 0.4, color: "var(--ink-soft)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              All items in
            </div>
          )}
          <h2 style={{ ...S.subTitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {scopeName || "All items"}
          </h2>
        </div>
        <span style={S.count}>{scoped.length}</span>
      </header>
      <SearchField value={query} onChange={setQuery} inputRef={searchInputRef}
        placeholder={scopeName ? `Search in ${scopeName}` : "Search all items"} />
      <div className="grid-scroll">
        {scoped.length === 0 ? (
          <div style={S.empty}>
            <div style={S.emptyMark}><Package size={44} sw={1.2} /></div>
            <h2 style={{ margin: "18px 0 6px", fontSize: 20 }}>Nothing here yet</h2>
            <p style={{ color: "var(--ink-soft)", fontSize: 14 }}>
              {scopeName ? `Nothing has been added to ${scopeName} or anywhere inside it.` : "Scan something into a location to see it here."}
            </p>
          </div>
        ) : results.length === 0 ? (
          <div style={S.noResults}>
            <Package size={40} sw={1.3} extra={{ color: "var(--ink-faint)" }} />
            <p style={{ margin: "14px 0 4px", fontWeight: 600 }}>No matches for "{query}"</p>
          </div>
        ) : (
          <div style={{ ...S.grid, paddingBottom: 30 }}>
            {results.map((item, idx) => (
              <ItemCard key={item.id} item={item} idx={idx} subPath={item.location_path || null}
                onClick={onOpenItem ? () => onOpenItem(item) : undefined} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ItemCard({ item, onClick, idx, subPath, selectMode, selected, onLongPress }) {
  const pressTimer = useRef(null);
  const longFired = useRef(false);
  const btnRef = useRef(null);

  const startPress = () => {
    if (!onLongPress) return;
    longFired.current = false;
    pressTimer.current = setTimeout(() => {
      longFired.current = true;
      onLongPress();
      if (btnRef.current) btnRef.current.blur();
      if (navigator.vibrate) { try { navigator.vibrate(15); } catch {} }
    }, 450);
  };
  const cancelPress = () => {
    if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
  };
  const handleClick = () => {
    if (longFired.current) { longFired.current = false; return; }
    onClick && onClick();
  };

  return (
    <button className="card-pop"
      ref={btnRef}
      style={{
        ...S.card,
        cursor: (onClick || onLongPress) ? "pointer" : "default",
        animationDelay: `${Math.min(idx, 8) * 40}ms`,
        WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none",
        outline: "none", WebkitTapHighlightColor: "transparent",
        ...(selected ? S.cardSelected : null),
      }}
      onClick={handleClick}
      onContextMenu={(e) => e.preventDefault()}
      onTouchStart={startPress}
      onTouchEnd={cancelPress}
      onTouchMove={cancelPress}
      onMouseDown={startPress}
      onMouseUp={cancelPress}
      onMouseLeave={cancelPress}
    >
      <div style={S.cardImgWrap}>
        {item.image ? <MarkedImg src={item.image} tap={item.tap} alt={item.label} draggable={false} style={{ ...S.cardImg, WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none", pointerEvents: "none" }} />
          : <div style={S.cardImgPlaceholder}><Package size={56} sw={1.2} extra={{ color: "var(--ink-faint)" }} /></div>}
        {selectMode && (
          <div style={{ ...S.cardCheckbox, ...(selected ? S.cardCheckboxOn : null) }}>
            {selected && <Check size={14} sw={3} />}
          </div>
        )}
        {subPath && (
          <div style={S.cardSubPathBadge}>
            <MapPin size={10} sw={2.8} /> {subPath}
          </div>
        )}
        {item.quantity > 1 && <div style={S.cardQtyBadge}>&times;{item.quantity}</div>}
      </div>
      <div style={S.cardBody}>
        <span style={S.cardLabel}>{item.label}</span>
        <span style={S.cardLoc}>
          <MapPin size={12} sw={2.6} /> {item.location || "No location"}
        </span>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Scan flow (reused from previous version)
// ─────────────────────────────────────────────────────────────────────

function LocationPicker({ existingLocations, spaceName, initial, onCancel, onContinue }) {
  const [location, setLocation] = useState(initial || "");
  const canContinue = location.trim().length > 0;
  return (
    <div className="screen">
      <header style={S.subHeader}>
        <button style={S.iconBtn} onClick={onCancel}><ChevronLeft size={24} /></button>
        <h2 style={S.subTitle}>Where are you scanning?</h2>
        <div style={{ width: 40 }} />
      </header>
      <div className="form-scroll">
        <p style={{ color: "var(--ink-soft)", fontSize: 14, marginTop: 6, marginBottom: 22, lineHeight: 1.5 }}>
          Items will be added to <strong style={{ color: "var(--accent-ink)" }}>{spaceName}</strong>. Pick or type a location.
        </p>
        <input autoFocus style={{ ...S.input, fontSize: 18, padding: "16px 18px" }}
          placeholder="e.g. Office cabinet, top drawer"
          value={location} onChange={(e) => setLocation(e.target.value)} />
        {existingLocations.length > 0 && (
          <>
            <div style={{ ...S.sectionHead, marginTop: 24 }}><MapPin size={14} /> <span>Recent places</span></div>
            <div style={S.chipRow}>
              {existingLocations.slice(0, 12).map((loc) => (
                <span key={loc} style={S.chipSuggest} onClick={() => setLocation(loc)}>
                  <MapPin size={12} /> {loc}
                </span>
              ))}
            </div>
          </>
        )}
        <div style={{ height: 90 }} />
      </div>
      <div style={S.footer}>
        <button style={{ ...S.primaryBtn, opacity: canContinue ? 1 : 0.4 }} disabled={!canContinue} onClick={() => onContinue(location.trim())}>
          <Camera size={18} /> Start scanning
        </button>
      </div>
    </div>
  );
}

// The prompt over the shutter, and what the i beside it opens.
//
// All three modes tap the same viewfinder and do entirely different things with the tap, and the
// mode names alone do not say which. The prompt is the one line that has to be readable at a glance
// over a live camera, so the explanation of when to reach for a mode sits behind the i instead of
// under the prompt, where it was permanent small print across the picture.
//
// `label` is per mode rather than generated, because "What items are for" is not a question anybody
// has; what they want to know is what a tap does.
// `body` is a list of paragraphs: what the thing is, then how to do it, and for locations a third
// about the setting that changes what a tap produces. One block of four sentences was a wall.
const SCAN_MODE_HELP = {
  location: {
    hint: "Tap a place to add it as a permanent location",
    title: "Locations",
    label: "What locations are for",
    Icon: MapPin,
    body: [
      "Locations contain items and other locations. Things like \u2018hall closet\u2019, \u2018camp gear box\u2019, or \u2018filing cabinet\u2019 are usually locations. Use them to organize your space like folders on a computer.",
      "Simply tap on a location in the view and approve the returned label to start adding items to it.",
      "Toggle sub-location prediction in the settings to automatically predict sub-locations. For example: Drawer 1, Drawer 2, Drawer 3 for a 3 drawer filing cabinet.",
    ],
  },
  item: {
    hint: "Tap items in view to add them",
    title: "Items",
    label: "What tapping an item does",
    Icon: Package,
    body: [
      "Items are things you want to catalog and find again through search. Things like \u2018ski jacket\u2019, \u2018staple gun\u2019, and \u2018spare AA batteries\u2019 are usually items. You can organize items like files that go in folders (locations).",
      "Simply tap on an item in the view and approve the returned label to add it to the current location.",
    ],
  },
  reference: {
    hint: "Add reference images for your next item",
    title: "Reference images",
    label: "What reference images are for",
    Icon: ImageIcon,
    body: [
      "Use reference images for quick or temporary locations, or for capturing other information you want stored with the item.",
      "A couple of quick pictures of where you put an item when you don\u2019t want to make a new location, or a picture of an item\u2019s packaging that shows usage instructions, are usually reference images.",
    ],
  },
};

function ScanLive({ active, spaceName, location, locations, queue, setQueue, savedCount, handledCount = 0, approvingIds, switching, sessionLocationId, scanConfig, scanContext, scanSessionId, onOpenHistory, onCancel, onVerify, onSwipeApprove, onSwipeDecline, onChangeLocation, onStepLocation }) {
  const savingCount = approvingIds?.size || 0;
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const streamRef = useRef(null);
  const fileFallbackRef = useRef(null);
  const fallbackImgRef = useRef(null);
  const [streamReady, setStreamReady] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [fallbackImage, setFallbackImage] = useState(null);
  const [dots, setDots] = useState([]);
  const [scanMode, setScanMode] = useState(DEFAULT_SCAN_MODE);
  // Which mode's help sheet is open, not a boolean: one sheet, and the mode it explains is the mode
  // it was opened from, so switching modes underneath it cannot leave the wrong copy on screen.
  const [helpMode, setHelpMode] = useState(null);

  // Everything that floats under the top bar is positioned from its measured height rather than a
  // number matching it. The bar grew a second row and the queue of pending scans, offset by a
  // constant, went underneath it — reachable by neither finger nor test. Measuring costs one
  // observer and cannot go stale.
  const topBarRef = useRef(null);
  const [topBarH, setTopBarH] = useState(96);
  useEffect(() => {
    const el = topBarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setTopBarH(el.getBoundingClientRect().height));
    ro.observe(el);
    setTopBarH(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [active]);
  // And the same for the queue itself, because the nudge below it points up at it. That one was offset
  // by a constant tuned against a single pill, so a second scan put the bubble on top of the strip it
  // was pointing at — which is what `queue.length < 2` was really hiding. The strip grows with every
  // scan and then stops at its own maxHeight, so there is no arithmetic that gets this right.
  const queueStripRef = useRef(null);
  const [queueStripH, setQueueStripH] = useState(0);
  useEffect(() => {
    const el = queueStripRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setQueueStripH(el.getBoundingClientRect().height));
    ro.observe(el);
    setQueueStripH(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [active]);
  // The reference frames photographed so far: whatever will help find the thing again, held here
  // until an item is captured and takes them with it. Nothing here has been near the network —
  // these never go to the VLM, so a trail costs nothing until it is saved.
  const [trail, setTrail] = useState([]);

  // Where the < and > arrows would land. Computed rather than stored so they follow the tree as it
  // changes under a session — approving a scan as a location adds children to the current node.
  const stepOutId = useMemo(() => outwardTarget(locations, sessionLocationId), [locations, sessionLocationId]);
  const stepInId = useMemo(() => inwardTarget(locations, sessionLocationId), [locations, sessionLocationId]);

  // ORIENTATION: scanning is a one-purpose, two-handed screen, and rotating it mid-session
  // re-crops the camera and reflows the queue for no benefit. Pin it to portrait where the
  // platform allows. iOS Safari implements no part of ScreenOrientation.lock — it rejects —
  // so this is best-effort and the layout still has to survive landscape; a real lock there
  // has to come from the Capacitor wrapper's native orientation setting.
  useEffect(() => {
    const orientation = window.screen?.orientation;
    if (!orientation || typeof orientation.lock !== "function") return undefined;
    let unmounted = false;
    const release = () => { try { orientation.unlock(); } catch {} };
    orientation.lock("portrait").then(() => { if (unmounted) release(); }).catch(() => {});
    return () => { unmounted = true; release(); };
  }, []);

  useEffect(() => {
    if (fallbackImage) return;
    let cancelled = false;
    let revealTimer = null;
    async function start() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera API not supported");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          // Reveal only once the video has real frame dimensions. This used to flip straight
          // after play(), while videoWidth/videoHeight were still 0 — so the element faded in
          // empty and then visibly re-cropped the instant metadata arrived and object-fit:cover
          // finally had an aspect ratio to work with. That pop is the "camera frame resizing on
          // startup, looks janky" report. The timer is a floor, not a feature: if neither event
          // fires on some engine, a slightly early reveal beats being stuck on "Opening camera…".
          const reveal = () => { if (!cancelled) setStreamReady(true); };
          if (video.videoWidth > 0) reveal();
          else {
            video.addEventListener("loadedmetadata", reveal, { once: true });
            video.addEventListener("canplay", reveal, { once: true });
            revealTimer = setTimeout(reveal, 1500);
          }
          video.play().catch(() => {});
        }
      } catch (e) { setStreamError(e.message || "Couldn't open camera"); }
    }
    start();
    return () => {
      cancelled = true;
      if (revealTimer) clearTimeout(revealTimer);
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    };
  }, [fallbackImage]);

  const onSurfaceTap = useCallback(async (e) => {
    if (switching) return; // location switch settling — don't capture against a half-applied target
    if (streamError && !fallbackImage) return;
    if (e.target !== e.currentTarget && !e.currentTarget.contains(e.target)) return;
    const t = e.target;
    if (t.closest && t.closest("[data-no-camera-tap]")) return;
    const rect = containerRef.current.getBoundingClientRect();
    const touch = e.touches?.[0] || e.changedTouches?.[0] || e;
    const x = (touch.clientX - rect.left) / rect.width;
    const y = (touch.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    let annotated;
    try {
      const source = fallbackImage ? fallbackImgRef.current : videoRef.current;
      annotated = await annotateImage(source, x, y);
    } catch (err) { console.error("annotate failed", err); return; }
    const id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

    // A reference frame is a photograph of a place, so it keeps the clean frame and stops there:
    // no crosshair (nothing was aimed at), no queue entry (it is not a thing to approve), and no
    // VLM call (a corridor has no label worth paying for). It waits for an item to attach to.
    if (scanMode === "reference") {
      if (trail.length >= MAX_TRAIL_FRAMES) return;
      setTrail((prev) => (prev.length >= MAX_TRAIL_FRAMES
        ? prev
        : [...prev, { id, url: annotated.cleanDataUrl }]));
      setDots((d) => [...d, { id, x, y }]);
      setTimeout(() => setDots((d) => d.filter((p) => p.id !== id)), 2200);
      return;
    }

    // The trail rides along with an ITEM only. Switching to `location` mid-trail leaves the frames
    // in the buffer rather than consuming them: the trail was walked to record where a thing is,
    // and a shelf is not that thing, so silently spending it there would be a loss.
    //
    // Carried as bare data URLs, not as the {id, url} the strip renders: this is what eventually
    // reaches extra_images, and the server drops anything that is not a string — silently, because
    // one malformed frame must not cost the user the item.
    //
    // Capturing an item COPIES the frames rather than spending them, so a set of reference photos
    // stays until the user clears it. One corner of a loft usually covers everything you put in that
    // corner, and the version that consumed them meant re-photographing the same shelf for the second
    // thing off the same shelf. The risk of the other direction — a stale trail quietly attached to
    // something somewhere else — is answered by the strip: it is on screen with a count for as long as
    // it is live, and `Clear` is right there on it.
    const carriedTrail = scanMode === "item" ? trail.map((f) => f.url) : [];

    const newTap = {
      id, x, y, image: annotated.dataUrl, cleanImage: annotated.cleanDataUrl,
      imageWidth: annotated.width, imageHeight: annotated.height,
      // What the user said this is, which beats asking the model to guess. Seeds the Verify
      // screen's is-a-location toggle; they can still flip it there.
      capturedAsLocation: scanMode === "location",
      trail: carriedTrail,
      label: "", confidence: null, status: "pending", error: null,
      // Capture-time destination stamp. Approval files the tap HERE (unless the user
      // overrides per-item) — so retargeting the session later never silently changes
      // where already-scanned items go, and the queue never needs clearing on retarget.
      capturedLocationId: sessionLocationId || null,
      capturedLocationName: location || null,
    };
    setQueue((prev) => [...prev, newTap]);
    setDots((d) => [...d, { id, x, y }]);
    setTimeout(() => setDots((d) => d.filter((p) => p.id !== id)), 2200);
    try {
      const result = await labelMarkedWithRetry(annotated.dataUrl, scanConfigForMode(scanConfig, scanMode), scanContext, scanSessionId);
      setQueue((prev) => prev.map((t) => t.id === id ? { ...t, ...tapFieldsFromScan(result) } : t));
    } catch (err) {
      let msg = err.message;
      if (err.status === 402) msg = "Monthly scan limit reached";
      else if (err.code === "timeout") msg = "Weak signal — tap to retry";
      else if (typeof navigator !== "undefined" && navigator.onLine === false) msg = "You're offline — tap to retry";
      setQueue((prev) => prev.map((t) => t.id === id ? { ...t, status: "error", error: msg } : t));
    }
  }, [fallbackImage, setQueue, streamError, switching, sessionLocationId, location, scanConfig, scanContext, scanSessionId, scanMode, trail]);

  const onFallbackFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setFallbackImage(reader.result); setStreamError(null); };
    reader.readAsDataURL(f);
  };

  const pendingCount = queue.filter((t) => t.status === "pending").length;
  const readyCount = queue.length - pendingCount;

  // First-run nudge: show once the first item is ready, then dismiss it politely — after a few
  // seconds, or once they approve one (savedCount > 0). Fades out rather than nagging.
  //
  // It used to vanish the moment a second scan joined the queue, on the reasoning that anyone who has
  // marked two things clearly gets it. They do not. Marking is the gesture they have just performed;
  // this bubble is about the next one — that the thumbnail is tappable, and that saving is a separate
  // step. Someone who marks five things and approves none is exactly who it is for, and was the only
  // person guaranteed never to see it. It also pulled the signpost off screen mid-sentence, since
  // tapping another thing is a normal thing to do while reading.
  //
  // `nudgeRelevant` is why the bubble exists; `nudgeVisible` is whether it is shown right now. It stays
  // mounted after dismissal so it can fade instead of blinking out, and having one flag each is what
  // stops the render gate and the opacity from spelling the same condition twice — which is how the
  // queue-length rule came to live in two places, needing deletion from both.

  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const nudgeRelevant = active && savedCount === 0 && readyCount >= 1;
  const nudgeVisible = nudgeRelevant && !nudgeDismissed;
  // `readyCount` is in the deps so each newly labelled scan restarts the countdown: the bubble lasts as
  // long as someone is still marking things and has approved none of them. Without it the fade is the
  // queue-length rule with a delay on it — mark four things over a minute and the signpost is gone for
  // the last three. It cannot turn into nagging, since it never returns once faded and never appears at
  // all after the first approve.
  useEffect(() => {
    if (!nudgeVisible) return;
    const t = setTimeout(() => setNudgeDismissed(true), 4500);
    return () => clearTimeout(t);
  }, [nudgeVisible, readyCount]);

  return (
    <div className="screen" style={{ background: "#000" }}>
      <div ref={containerRef} onClick={onSurfaceTap} style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
        {fallbackImage ? <img ref={fallbackImgRef} src={fallbackImage} alt="" style={S.scanSurface} />
          : <video ref={videoRef} playsInline muted autoPlay
              style={{ ...S.scanSurface, opacity: streamReady ? 1 : 0, transition: "opacity .2s ease-out" }} />}
        {dots.map((d) => (
          <div key={d.id} style={{
            position: "absolute", left: `${d.x * 100}%`, top: `${d.y * 100}%`,
            width: 30, height: 30, borderRadius: 15, background: "var(--accent)",
            transform: "translate(-50%,-50%)",
            animation: "dotFade 2.2s ease-out forwards, ringPulse 1.4s ease-out 1",
            zIndex: 4, pointerEvents: "none", boxShadow: "0 2px 12px rgba(0,0,0,.5)",
          }} />
        ))}
        {!fallbackImage && !streamReady && !streamError && <div style={S.overlayMsg}>Opening camera…</div>}
        {streamError && !fallbackImage && (
          <div style={S.overlayCard}>
            <AlertCircle size={26} extra={{ color: "var(--danger)" }} />
            <div style={{ fontWeight: 700, marginTop: 10 }}>Couldn't open the live camera</div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 4, marginBottom: 14 }}>{streamError}</div>
            <button style={S.primaryBtn} onClick={() => fileFallbackRef.current?.click()}>
              <Camera size={16} /> Take a photo instead
            </button>
            <input ref={fileFallbackRef} type="file" accept="image/*" capture="environment" onChange={onFallbackFile} style={{ display: "none" }} />
          </div>
        )}
      </div>
      {/* A solid bar rather than a gradient over the viewfinder. The controls are the point of
          this strip, and floating them on a fade meant their contrast depended on whatever the
          camera happened to be looking at. */}
      <div data-no-camera-tap ref={topBarRef} style={S.scanTopBar}
        onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
        {/* Two rows, and the split is the point. The chrome — close, undo, the session count — is a
            row of controls whose widths change as things happen, and the destination was sharing
            that row: centred in the space they left over rather than on the screen, so it read as
            off-centre with nothing wrong, and moved whenever a control appeared. On its own row it
            is centred on the bar, and nothing above it can push it. */}
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <button style={S.scanBackBtn} onClick={onCancel} aria-label="Close the camera"><X size={20} /></button>
          <div style={{ flex: 1 }} />
          {/* The count is the way back to what this session has already done, and there is no undo
              button beside it any more.
              
              An undo here had to guess which scan you meant, and got it wrong — its entry was claimed
              by whichever background save finished last. The count does not guess: it opens the newest
              handled scan on the approve screen, where the arrows walk the rest.
              
              It counts what it opens — every scan approved or declined this session — which is also
              what makes it a legitimate door. The earlier version of this idea opened a list of the
              destination's contents, a different subject entirely, and was removed for making one
              number mean two things. */}
          {handledCount > 0 ? (
            <button data-no-camera-tap style={{ ...S.scanCount, marginLeft: 6, cursor: "pointer" }}
              onClick={(e) => { e.stopPropagation(); onOpenHistory?.(); }}
              aria-label={`Review ${handledCount} handled ${handledCount === 1 ? "scan" : "scans"}`}>
              {handledCount}
            </button>
          ) : (
            <div style={{ ...S.scanCount, marginLeft: 6 }}>{handledCount}</div>
          )}
        </div>
        {/* The destination and the two arrows that move it are one control, centred together. The
            arrows were pinned to the outer edges of the bar with the name floating between them,
            so the thing they acted on was an inch away from them and they read as unrelated
            navigation. The picker is still a tap on the name, for going somewhere else entirely. */}
        <div style={{ width: "100%", minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
          {/* An arrow with nowhere to go opens the picker rather than doing nothing. Both cases are
              the same intent — the user wants to be somewhere else — and a dead control answers it
              by leaving them to work out that the tree is behind the name in the middle. */}
          <button style={S.scanStepBtn} aria-label={stepOutId ? "Out to the location above" : "Choose where to add items"}
            onClick={() => (stepOutId ? onStepLocation(stepOutId) : onChangeLocation?.())}>
            <ChevronLeft size={19} />
          </button>
          <button
            onClick={onChangeLocation ? () => onChangeLocation() : undefined}
            style={{ minWidth: 0, background: "none", border: "none", padding: "0 4px", color: "inherit",
                     font: "inherit", cursor: onChangeLocation ? "pointer" : "default", overflow: "hidden" }}>
            <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: 0.6, textTransform: "uppercase" }}>Adding to</div>
            <div style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, overflow: "hidden" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", opacity: location ? 1 : 0.85 }}>
                {location || "Choose a location"}
              </span>
              {onChangeLocation && <Edit2 size={12} extra={{ color: "var(--accent-ink)", flexShrink: 0 }} />}
            </div>
          </button>
          {/* Ambiguous below — two drawers, or none — so this hands the choice to the picker rather
              than making it silently. See inwardTarget. */}
          <button style={S.scanStepBtn} aria-label={stepInId ? "In to the sub-location" : "Choose where to add items"}
            onClick={() => (stepInId ? onStepLocation(stepInId) : onChangeLocation?.())}>
            <ChevronRight size={19} />
          </button>
        </div>
      </div>
      {switching && (
        <div style={{ position: "absolute", top: topBarH + 10, left: 0, right: 0, display: "flex", justifyContent: "center", zIndex: 20, pointerEvents: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 999,
                        background: "rgba(20,17,12,0.9)", border: "1px solid var(--accent-line)", color: "var(--accent-ink)", fontSize: 13, fontWeight: 600 }}>
            <Loader size={15} /> Switching location…
          </div>
        </div>
      )}
      {/* First-time signpost: the moment an item is labeled and ready, point at the
          queue so a new user knows the thumbnail is tappable and approval is next.
          In-context, appears only until they've approved their first item this session
          (savedCount === 0), then never shown again. Not a tutorial — a signpost at the
          one fork where a first-timer could stall, which is why it survives a growing queue:
          several scans with nothing approved out of them is that stall, happening. */}
      {nudgeRelevant && (
        <div data-no-camera-tap style={{
          position: "absolute", left: 0, right: 0,
          // Under the strip, wherever the strip currently ends. Its arrow points up at the pills, so a
          // constant here is a guess about how many there are.
          top: topBarH + 8 + queueStripH + 10,
          display: "flex", flexDirection: "column", alignItems: "center",
          zIndex: 15, pointerEvents: "none",
          opacity: nudgeVisible ? 1 : 0,
          transition: "opacity .5s ease",
        }}>
          <div style={{
            width: 0, height: 0, borderLeft: "7px solid transparent", borderRight: "7px solid transparent",
            borderBottom: "8px solid var(--accent)", marginBottom: -1,
          }} />
          <div style={{
            display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 999,
            background: "var(--accent)", color: "#1a1205", fontSize: 13.5, fontWeight: 700,
            boxShadow: "0 4px 16px rgba(0,0,0,.35)",
            animation: nudgeVisible ? "nudgeBreathe 1.4s ease-in-out infinite" : "none",
          }}>
            Tap item to edit and save
          </div>
        </div>
      )}
      <div ref={queueStripRef} data-no-camera-tap style={{ ...S.queueWrap, top: topBarH + 8 }}
        onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
        {[...queue].reverse().map((t) => (
          <QueueItem key={t.id} tap={t} saving={approvingIds?.has(t.id)}
            onTap={() => onVerify(t.id)}
            onSwipeApprove={() => onSwipeApprove(t.id)}
            onSwipeDecline={() => onSwipeDecline(t.id)} />
        ))}
      </div>
      <div data-no-camera-tap style={S.scanBottomBar}
        onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
        {/* The trail so far, when there is one. Shown above the toggle because it is the thing
            that makes the next item behave differently, and it should not be possible to forget
            it is there. */}
        {trail.length > 0 && (
          // The label, then the photos, then one line under them. All three were a single scrolling
          // line, which pushed the words off the right edge of a phone at about the sixth photo — so
          // the caption is its own row now, and only the photos scroll.
          <div style={S.trailStrip}>
            <div style={S.trailStripFrames}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: "rgba(255,255,255,.75)", flexShrink: 0, paddingRight: 2 }}>
                REF {trail.length}
              </span>
              {trail.map((frame) => (
                <span key={frame.id} style={{ position: "relative", flexShrink: 0, padding: "5px 5px 0 0" }}>
                  <img src={frame.url} alt="" style={{ width: 38, height: 38, borderRadius: 8, objectFit: "cover", display: "block" }} />
                  <button type="button" aria-label="Drop this reference photo" style={S.trailStripX}
                    onClick={() => setTrail((p) => p.filter((f) => f.id !== frame.id))}>
                    <X size={11} sw={3} />
                  </button>
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.6)", width: "100%", textAlign: "center" }}>
              Rides along with the next item
            </div>
          </div>
        )}
        <div style={S.scanModeRow}>
          {SCAN_MODES.map((m) => {
            const on = scanMode === m.id;
            return (
              <button key={m.id} type="button" onClick={() => setScanMode(m.id)}
                aria-pressed={on}
                style={{ ...S.scanModeBtn, ...(on ? S.scanModeBtnOn : null) }}>
                {m.name}
              </button>
            );
          })}
        </div>
        {savingCount > 0 ? (
          <div style={S.scanHint}>
            <Loader size={15} /> <span style={{ paddingLeft: 6 }}>Saving {savingCount} {savingCount === 1 ? "item" : "items"}…</span>
          </div>
        ) : queue.length > 0 ? (
          <div style={S.scanHint}>
            {pendingCount > 0 && `${pendingCount} labeling… `}
            {readyCount > 0 && `${readyCount} to verify`}
          </div>
        ) : (
          <div style={S.scanHint}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
              {SCAN_MODE_HELP[scanMode].hint}
              <button type="button" onClick={() => setHelpMode(scanMode)}
                aria-label={SCAN_MODE_HELP[scanMode].label}
                style={S.scanHintInfoBtn}>
                <Info size={15} sw={2.2} />
              </button>
            </span>
          </div>
        )}
      </div>
      {/* Lowered from the bottom like every other sheet in the app, and marked so the tap that opens
          or dismisses it is not also a tap on the viewfinder behind it. */}
      {helpMode && (() => {
        const help = SCAN_MODE_HELP[helpMode];
        const HelpIcon = help.Icon;
        return (
          <div data-no-camera-tap style={S.sheetBackdrop} onClick={() => setHelpMode(null)}
            onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
            <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <HelpIcon size={20} sw={2} extra={{ color: "var(--accent-ink)" }} />
                <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>
                  {help.title}
                </h3>
              </div>
              {help.body.map((paragraph, i) => (
                <p key={i} style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.55, margin: i === help.body.length - 1 ? "0 0 18px" : "0 0 12px" }}>
                  {paragraph}
                </p>
              ))}
              <button style={S.primaryBtn} onClick={() => setHelpMode(null)}>Got it</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function QueueItem({ tap, saving, onTap, onSwipeApprove, onSwipeDecline }) {
  const pending = tap.status === "pending";
  const error = tap.status === "error";
  const canAct = !pending && !error && !saving && (tap.label || "").trim();
  const bg = error ? "rgba(217,105,79,.85)" : "rgba(20,17,12,.85)";
  const borderColor = error ? "rgba(217,105,79,.6)" : "rgba(255,255,255,.12)";
  const stop = (fn) => (e) => { e.stopPropagation(); e.preventDefault(); fn?.(); };
  return (
    <div style={{
      ...S.queuePill, background: bg, borderColor,
      color: error ? "#fff" : "var(--ink)",
      animation: "pillIn .25s ease-out both",
      cursor: pending ? "default" : "pointer",
      padding: "4px 5px 4px 9px", gap: 6,
      opacity: saving ? 0.7 : 1,
    }} onClick={(pending || saving) ? undefined : onTap}>
      {pending ? (
        <><Loader size={14} /><span style={{ fontSize: 13.5, fontStyle: "italic", color: "var(--ink-soft)", flex: 1, paddingLeft: 4 }}>Labeling…</span></>
      ) : saving ? (
        <><Loader size={14} /><span style={{ fontSize: 13.5, fontWeight: 600, flex: 1, paddingLeft: 4 }}>Saving…</span></>
      ) : error ? (
        <>
          <button onClick={stop(onSwipeDecline)} style={S.pillDeclineBtn} aria-label="Discard this scan"><X size={16} sw={2.6} /></button>
          <AlertCircle size={14} />
          <span style={{ fontSize: 13.5, fontWeight: 600, flex: 1 }}>{tap.error || "Couldn't label — tap to retry"}</span>
        </>
      ) : (
        <>
          <button onClick={stop(onSwipeDecline)} style={S.pillDeclineBtn} aria-label={`Discard ${tap.label}`}><X size={16} sw={2.6} /></button>
          <span style={{ fontSize: 14, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingLeft: 2 }}>{tap.label}</span>
          <button onClick={canAct ? stop(onSwipeApprove) : undefined} disabled={!canAct}
            aria-label={`Save ${tap.label}`}
            style={{ ...S.pillApproveBtn, opacity: canAct ? 1 : 0.4 }}>
            <Check size={16} sw={2.8} />
          </button>
        </>
      )}
    </div>
  );
}

function Verify({ tap, saving, queueCount, pendingCount, stepIndex = 0, stepTotal = 1, onStashDraft, entryState = null, entryIsLocation = false, savedDraft = null, onSaveEdits, historyPos = null, canBack = false, canForward = false, onStepHistory, sessionLocationName, sessionLocationId, locations, spaces, userId, onCreateSpace, onCreateLocationPure, onRefreshLocations, onBack, onApprove, onDecline, onRetry }) {
  // What the fields say when this screen opens: the user's own edits if they have been on this scan
  // before, otherwise what the scan proposed. Keyed on the tap id so it cannot recompute while they are
  // typing, only when a different scan opens. src/scan-draft.js says why the draft exists at all.
  const seed = useMemo(() => verifyDraft(tap), [tap.id]);
  const [label, setLabel] = useState(seed.label);
  // Whether this scan should be saved as a LOCATION (container) instead of an item.
  // Defaults from the VLM's guess (tap.is_location_guess) when that's wired up; until
  // then it defaults off and the user can flip it.
  const [isLocation, setIsLocation] = useState(seed.isLocation);
  // Per-item location override. null = use the session's target location. When the user
  // taps the location display and picks another, we store {id, name} here and it applies
  // to THIS item only (resets when a new tap comes in).
  const [locOverride, setLocOverride] = useState(seed.locOverride);
  const [locPickerOpen, setLocPickerOpen] = useState(false);
  // Locations created by BACKGROUND saves earlier in this session (approve-as-location,
  // new-named locations) race the user to the next approve screen. Refreshing when the
  // picker opens closes that gap — fresh props flow into the already-open picker.
  useEffect(() => {
    if (locPickerOpen && onRefreshLocations) onRefreshLocations();
  }, [locPickerOpen]);
  // When marking as a location, whether to jump into scanning it after approve.
  const [scanMoreHere, setScanMoreHere] = useState(seed.scanMoreHere);
  // Sub-location prediction: compartments the VLM saw in this place (drawers, shelves).
  // Each starts checked; on approve the checked ones are created as child LOCATIONS
  // inside the new location.
  const [predictedSublocations, setPredictedSublocations] = useState(seed.predictedSublocations);
  // Six identical bolts are one scan, not six. Kept as a string so the field can be
  // cleared while typing without snapping back to 1 under the user's cursor.
  const [quantity, setQuantity] = useState(seed.quantity);
  const [justApproved, setJustApproved] = useState(false);
  const labelRef = useRef(null);
  const pending = tap.status === "pending";
  const error = tap.status === "error";
  // Alternatives never include the label itself (vlm.js dedupes), but the user may have
  // typed their way onto one, so the chip for whatever is currently in the box is shown as
  // selected rather than hidden — a chip vanishing as you type reads as a glitch.
  const alternatives = Array.isArray(tap.alternative_labels) ? tap.alternative_labels : [];
  // The same name at three lengths, when the styles flag asked for them. Shown alongside the
  // alternatives rather than merged into them: these are not competing guesses, so presenting
  // them as if they were would misrepresent what the model said.
  const labelVariants = Array.isArray(tap.label_variants) ? tap.label_variants : [];
  // Tags the model proposed, which the user owns from here on. Held in local state like
  // every other field on this screen, so removing one is instant and only commits on approve.
  const [tags, setTags] = useState(seed.tags);
  const [vocabulary, setVocabulary] = useState(() => tagVocabularyCache || []);
  useEffect(() => {
    if (tagVocabularyCache) return;
    let cancelled = false;
    loadTagVocabulary().then((rows) => { if (!cancelled) setVocabulary(rows); });
    return () => { cancelled = true; };
  }, []);
  // The reference frames this scan arrived with, as the flipper wants it. Local state because a
  // frame can be dropped here — this screen is where the trail gets approved — and because
  // nothing is committed until Approve.
  const [trail, setTrail] = useState(seed.trail);
  // The scan's own photo first, then the trail — the same shape the item screen builds from
  // node_images, so one ImageFlipper serves both. A location scan carries no trail, so it is always
  // a single frame here.
  const carousel = useMemo(() => {
    // No tap coords: tap.image is the annotated capture, with the crosshair already burned in by
    // the camera. Handing them to the flipper as well would draw a second circle over the first.
    const primary = { id: "primary", url: tap.image, tap: null, is_primary: true };
    return isLocation ? [primary] : [primary, ...trail];
  }, [tap.image, isLocation, trail]);
  useEffect(() => { setLabel(tap.label || ""); }, [tap.label]);
  // A different scan opened in the same mounted screen — approve advances to the next one without
  // unmounting, so the fields have to be re-seeded rather than left showing the last scan's edits.
  useEffect(() => {
    setIsLocation(seed.isLocation);
    setLocOverride(seed.locOverride);
    setScanMoreHere(seed.scanMoreHere);
    setQuantity(seed.quantity);
    setTags(seed.tags);
    setTrail(seed.trail);
    setPredictedSublocations(seed.predictedSublocations);
  }, [tap.id]);
  // Effective target for this item: the override if set, else the tap's CAPTURE-TIME
  // stamp (where it was scanned), else the session location (legacy taps).
  const effLocId = locOverride ? locOverride.id : (tap.capturedLocationId ?? sessionLocationId);
  const effLocName = locOverride ? locOverride.name : (tap.capturedLocationName ?? sessionLocationName);
  const effLocPath = (effLocId && locations) ? (() => {
    const p = locationPath(locations, effLocId);
    return p.length > 1 ? pathDisplay(p) : null;
  })() : null;
  // Auto-grow the label textarea so long labels are fully visible (not cut off).
  useEffect(() => {
    const el = labelRef.current;
    if (el && el.tagName === "TEXTAREA") { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; }
  }, [label, tap.id, pending]);
  // Reset the approved flash whenever a new tap comes in — and also when the scan in front of you
  // changes state, because approving from the history stays on the screen. Without `entryState` here the
  // footer would sit on "Saving…" for as long as you kept looking at the thing you had just approved.
  useEffect(() => { setJustApproved(false); }, [tap.id, entryState]);
  const clearLabel = () => {
    if (labelRef.current) labelRef.current.focus();
    setLabel("");
  };
  const pendingApproveRef = useRef(false);
  const doApprove = (overrideLocId, overrideLocName) => {
    if (justApproved) return;
    setJustApproved(true);
    onApprove(label, {
      isLocation,
      // A location is a place, not a countable thing, so the count only rides along on items.
      quantity: isLocation ? 1 : cleanQuantity(quantity),
      scanMoreHere: isLocation && scanMoreHere,
      overrideLocationId: overrideLocId || (locOverride ? locOverride.id : undefined),
      overrideLocationName: overrideLocName || (locOverride ? locOverride.name : undefined),
      predictedSublocations: isLocation ? predictedSublocations.filter((p) => p.checked).map((p) => p.label) : [],
      // A location is a place; asking what kind of object it is has no answer, so tags only
      // ride along on items.
      tags: isLocation ? [] : tags,
      // Whatever survived review here, which may be fewer frames than were photographed. An
      // empty trail makes this an ordinary item, filed where the camera was pointed.
      trail: isLocation ? [] : trail.map((f) => f.url),
    });
  };
  const handleApprove = () => {
    if (justApproved) return;
    // Guard: if this item has no destination yet (e.g. "Add item" from home with no
    // location chosen, and it's not being saved as a location itself), open the picker
    // and finish the approve once a location is chosen (see picker onConfirm).
    if (!isLocation && !effLocId) {
      pendingApproveRef.current = true;
      setLocPickerOpen(true);
      return;
    }
    doApprove();
  };

  // Anything entered here lives in local state until approve, and this screen unmounts on the way back
  // to the camera — so whatever navigates away has to hand the whole form back to the queue first. It
  // used to stash the label alone, which meant a count, a tag, a chosen destination, a dropped reference
  // frame or an unchecked compartment was quietly thrown away by a trip to the camera and back. Nothing
  // said so: the label came back, which made the screen look like it had remembered everything.
  // The form as it stands, in the shape src/scan-draft.js reads back. One builder, because the three
  // things that need it — leaving, declining, and stepping through the history — must not each assemble
  // their own idea of what the form is.
  const currentDraft = () => ({ label, isLocation, locOverride, scanMoreHere, quantity, tags, trail, predictedSublocations });
  // An already-saved item whose form has been changed. The primary button becomes the save rather than a
  // third control appearing beside two others — one button that says what pressing it will do.
  const savedAlready = entryState === "approved";
  // Against `savedDraft`, not `seed`: seed is what the fields opened with and is frozen on the tap id, so
  // it goes stale the moment an edit is written and would leave this stuck on.
  const dirty = savedAlready && draftChanged(savedDraft, currentDraft());
  const leaving = (go) => () => {
    if (!pending && onStashDraft) onStashDraft(currentDraft());
    go();
  };

  return (
    <div className="screen" style={{ background: "var(--bg)", zIndex: 10 }}>
      <header style={S.subHeader}>
        {/* Left goes back to the camera; the right pair is undo/redo for the approve queue.
            That pair used to step through the scans still waiting — which is the wrong axis, and it
            is why "I can't go back once I approve something" was reported against a header that
            already had a back arrow in it. Stepping backwards through unapproved scans skips the one
            you just got wrong, because approving is what removed it. Reaching another queued scan is
            still one tap from the camera, on its pill in the queue strip. */}
        <button style={S.iconBtn} onClick={leaving(onBack)} aria-label="Back to camera"><ChevronLeft size={24} /></button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <h2 style={{ ...S.subTitle, margin: 0 }}>{historyPos ? (entryState === "declined" ? "Declined" : "Approved") : "Verify"}</h2>
          {historyPos ? (
            <div style={{ fontSize: 11.5, color: "var(--ink-soft)", marginTop: 1 }}>{historyPos.position} of {historyPos.total} done</div>
          ) : stepTotal > 1 ? (
            <div style={{ fontSize: 11.5, color: "var(--ink-soft)", marginTop: 1 }}>{stepIndex + 1} of {stepTotal} to review</div>
          ) : null}
        </div>
        {/* Two arrows where undo and redo used to be, and they are navigation rather than a pair of
            one-shot reversals. Undo had to decide which scan you meant and decided wrong: its entry was
            assigned when a background save completed, so two concurrent uploads finishing out of order
            left it aimed at the earlier one. Walking a list needs no such guess — you go to the scan you
            want and change it there, and a decline is reachable for the first time.
            
            They appear ONLY while reviewing handled scans. Arriving here from a pill in the queue strip
            is about the things still waiting, and offering to walk out of that into finished work made
            one screen answer two questions. The way into the history is the count on the camera; the way
            out is the forward arrow past the newest. The empty slot keeps "Verify" centred, since a
            heading that shifts sideways between the two routes reads as a different screen. */}
        {historyPos ? (
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            <button style={{ ...S.iconBtn, width: 34, opacity: canBack ? 1 : 0.25 }} disabled={!canBack}
              onClick={leaving(() => onStepHistory?.(-1))}
              aria-label={canBack ? "Back to the previous scan you handled" : "This is the oldest one"}>
              <ChevronLeft size={21} sw={2.4} />
            </button>
            <button style={{ ...S.iconBtn, width: 34, opacity: canForward ? 1 : 0.25 }} disabled={!canForward}
              onClick={leaving(() => onStepHistory?.(1))}
              aria-label={canForward ? "Forward to the next scan you handled" : "Already at the newest"}>
              <ChevronRight size={21} sw={2.4} />
            </button>
          </div>
        ) : (
          <div style={{ width: 68, flexShrink: 0 }} />
        )}
      </header>
      <div className="form-scroll">
        {/* One carousel: the thing, then the way to it. This screen kept the item photo as a fixed
            hero with the extra photos in their own strip below, which is the arrangement the item
            screen already abandoned — two places to swipe for one set of pictures. The trail frames
            are still removable here, and only here, since this is the last moment before they are
            stored. */}
        {carousel.length > 1 ? (
          <div style={{ marginBottom: 16 }}>
            {/* Removal is offered only before the frames are stored. On a scan that is already an item
                they are rows in node_images, and dropping one here would look like a delete and be
                nothing at all. */}
            <ImageFlipper images={carousel} alt={label || "this item"} height={230}
              onRemove={savedAlready ? undefined : (frameId) => setTrail((p) => p.filter((f) => f.id !== frameId))} />
          </div>
        ) : (
          <div style={S.verifyImgWrap}>
            <img src={tap.image} alt="" style={S.verifyImg} />
          </div>
        )}
        <p style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 12, marginBottom: 18, textAlign: "center" }}>
          The yellow circle shows what was identified.
        </p>
        {pending && <div style={S.detectStatus}><Loader size={18} /><div style={{ fontSize: 14 }}>Still labeling…</div></div>}
        {error && (
          <div style={S.errorBox}>
            <AlertCircle size={20} extra={{ color: "var(--danger)" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Couldn't label this</div>
              <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>{tap.error}</div>
              <button style={S.linkBtn} onClick={onRetry}>Try again</button>
            </div>
          </div>
        )}
        {!pending && (
          <>
            {/* No confidence badge. The model's self-reported certainty is not something the user
                asked to see or can act on — the name is in an editable field either way, and
                "low confidence" over a correct label just undermines it. It is still recorded on the
                scan, where it is useful for judging the model rather than the object. */}
            <div style={{ ...S.labelInputWrap, marginTop: error ? 22 : 0 }}>
              <textarea ref={labelRef} style={S.labelInput} value={label} rows={1}
                onChange={(e) => setLabel(e.target.value)} placeholder="What is this?"
                onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }} />
              {label.length > 0 && (
                <button onClick={clearLabel} style={S.labelClearBtn} type="button"><X size={16} /></button>
              )}
            </div>
            {/* Other names the model offered (the "Offer other names" scan option). Tapping
                one is a whole lot faster than typing a correction, and it also tells us —
                via the label_source on the save — how often the first answer was wrong. */}
            {alternatives.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 9 }}>
                {alternatives.map((alt) => (
                  <button key={alt} type="button" onClick={() => setLabel(alt)}
                    style={{
                      ...S.chipSuggest,
                      ...(normalizeText(label) === normalizeText(alt)
                        ? { borderColor: "var(--accent)", color: "var(--accent-ink)" }
                        : null),
                    }}>
                    {alt}
                  </button>
                ))}
              </div>
            )}
            {/* The same name at three lengths (the "shorter and longer names" scan option).
                Labelled with the style, because "spork" and "beige plastic camping spork" side
                by side with no explanation looks like the model contradicting itself. */}
            {labelVariants.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 9 }}>
                {labelVariants.map((variant) => (
                  <button key={variant.style} type="button" onClick={() => setLabel(variant.label)}
                    style={{
                      ...S.chipSuggest,
                      flexDirection: "column", alignItems: "flex-start", gap: 1, padding: "7px 12px",
                      ...(normalizeText(label) === normalizeText(variant.label)
                        ? { borderColor: "var(--accent)", color: "var(--accent-ink)" }
                        : null),
                    }}>
                    <span style={{ fontSize: 10, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-soft)" }}>
                      {VARIANT_STYLE_NAMES[variant.style] || variant.style}
                    </span>
                    <span>{variant.label}</span>
                  </button>
                ))}
              </div>
            )}
            {/* Tags, for search. The model proposes; the user disposes. Only on items:
                asking what kind of object a shelf is has no useful answer. */}
            {!isLocation && (
              <div style={{ marginTop: 14 }}>
                <TagEditor
                  tags={tags}
                  vocabulary={vocabulary}
                  onChange={setTags}
                />
              </div>
            )}
            {!isLocation && (
              <div style={S.qtyRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>Quantity</div>
                  <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1 }}>One entry, counted</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <button type="button" style={S.qtyStepBtn} aria-label="One fewer"
                    onClick={() => setQuantity(String(Math.max(1, cleanQuantity(quantity) - 1)))}>−</button>
                  <input style={S.qtyInput} inputMode="numeric" pattern="[0-9]*" value={quantity}
                    aria-label="Quantity"
                    onChange={(e) => setQuantity(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                    onBlur={() => setQuantity(String(cleanQuantity(quantity)))} />
                  <button type="button" style={S.qtyStepBtn} aria-label="One more"
                    onClick={() => setQuantity(String(cleanQuantity(quantity) + 1))}>+</button>
                </div>
              </div>
            )}
            {/* Where this item will be filed. Tap to retarget THIS item only. */}
            <div style={{ marginTop: 10 }}>
              <LocationDisplay
                name={effLocName || null}
                path={effLocPath}
                emptyLabel="Choose a location"
                onTap={() => setLocPickerOpen(true)}
              />
            </div>
            {/* Is-this-a-location toggle, last. When on, the scan is saved as a location (a place to
                put things) instead of an item. It sat above the quantity and the destination, which
                put the rarest decision on the screen — most scans are things — in front of the two
                fields that are read every time. Everything it reveals stays attached to it below. */}
            {/* Hidden once the scan is a stored item, because flipping it then is a kind conversion
                rather than an edit — the item's own screen has `onConvertToLocation` for that. A toggle
                here that silently did nothing would be the same fault as an editable field that
                discards what you type. */}
            {!savedAlready && (
              <div style={{ marginTop: 12 }}>
                <IsLocationToggle on={isLocation} onToggle={(v) => { setIsLocation(v); if (!v) setScanMoreHere(false); }} />
              </div>
            )}
            {/* When marking this as a location, offer to jump straight into scanning it
                after approving (pre-declared, so no post-hoc delay). */}
            {isLocation && (
              <button type="button" onClick={() => setScanMoreHere((v) => !v)}
                style={{
                  marginTop: 8, width: "100%", display: "flex", alignItems: "center", gap: 12,
                  padding: "13px 15px", borderRadius: 14, fontFamily: "inherit", textAlign: "left",
                  cursor: "pointer",
                  background: scanMoreHere ? "var(--accent-soft)" : "var(--surface)",
                  border: scanMoreHere ? "1px solid var(--accent-line)" : "1px solid var(--line)",
                }}>
                <div style={{ flexShrink: 0, color: scanMoreHere ? "var(--accent-ink)" : "var(--ink-soft)" }}>
                  <Camera size={20} sw={2.2} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>Scan more items here</div>
                  <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1 }}>
                    After approving, keep scanning into this new location
                  </div>
                </div>
                <div style={{
                  flexShrink: 0, width: 44, height: 26, borderRadius: 13, position: "relative",
                  transition: "background .15s", background: scanMoreHere ? "var(--accent)" : "var(--line-strong)",
                }}>
                  <div style={{
                    position: "absolute", top: 3, left: scanMoreHere ? 21 : 3, width: 20, height: 20,
                    borderRadius: 10, background: "#fff", transition: "left .15s",
                  }} />
                </div>
              </button>
            )}
            {/* Sub-location prediction: compartments the VLM saw in this place. Checked
                ones get created as child LOCATIONS inside the new location on approve. */}
            {isLocation && predictedSublocations.length > 0 && (
              <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 14, background: "var(--surface)", border: "1px solid var(--line)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
                    Add sections inside ({predictedSublocations.filter((p) => p.checked).length})
                  </div>
                  <button type="button"
                    onClick={() => {
                      const allOn = predictedSublocations.every((p) => p.checked);
                      setPredictedSublocations((prev) => prev.map((p) => ({ ...p, checked: !allOn })));
                    }}
                    style={{ background: "none", border: "none", color: "var(--accent-ink)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                    {predictedSublocations.every((p) => p.checked) ? "Clear all" : "Select all"}
                  </button>
                </div>
                <div style={{ fontSize: 11.5, color: "var(--ink-soft)", marginBottom: 10, lineHeight: 1.4 }}>
                  Compartments the AI spotted (drawers, shelves) — these become locations inside. Uncheck or edit any.
                </div>
                {predictedSublocations.map((p) => (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0" }}>
                    <button type="button"
                      onClick={() => setPredictedSublocations((prev) => prev.map((x) => x.id === p.id ? { ...x, checked: !x.checked } : x))}
                      style={{
                        flexShrink: 0, width: 22, height: 22, borderRadius: 6, cursor: "pointer",
                        display: "grid", placeItems: "center", padding: 0,
                        background: p.checked ? "var(--accent)" : "transparent",
                        border: p.checked ? "1px solid var(--accent)" : "1px solid var(--line-strong)",
                      }}>
                      {p.checked && <Check size={14} extra={{ color: "#1a1205" }} />}
                    </button>
                    <input value={p.label}
                      onChange={(e) => setPredictedSublocations((prev) => prev.map((x) => x.id === p.id ? { ...x, label: e.target.value } : x))}
                      style={{
                        flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none",
                        color: p.checked ? "var(--ink)" : "var(--ink-soft)", fontSize: 14.5, fontFamily: "inherit",
                        textDecoration: p.checked ? "none" : "line-through",
                      }} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <div style={{ height: 110 }} />
      </div>
      {locPickerOpen && (
        <LocationTreePicker
          items={[{ id: tap.id, label: label || "this item" }]}
          spaces={spaces}
          locations={locations}
          userId={userId}
          currentSpaceRootId={effLocId ? rootLocationIdOf(locations, effLocId) : null}
          currentLocationId={effLocId}
          moveLabel="Scan"
          onCancel={() => { setLocPickerOpen(false); pendingApproveRef.current = false; }}
          onConfirm={({ spaceRootId, locationId }) => {
            const targetId = locationId || spaceRootId;
            const targetLoc = getLocationById(locations, targetId);
            const override = targetId ? { id: targetId, name: targetLoc?.name || effLocName } : null;
            setLocOverride(override);
            setLocPickerOpen(false);
            // Choosing a destination changes the destination. It used to also approve and advance,
            // on the reasoning that picking a place IS the decision — but it collapsed two steps
            // that are not the same step, so a user who opened the picker to check where this was
            // going lost the chance to look at the label, the count and the categories before it
            // was saved. Changing your mind about one field should never commit the form.
            //
            // The one exception is the guard below: when a scan has nowhere to go at all, Approve
            // opens the picker to collect the missing destination, and finishing that IS finishing
            // the approve the user already asked for.
            const wasApproving = pendingApproveRef.current;
            pendingApproveRef.current = false;
            // Mirrors what disables the Approve button, so a scan whose label is still arriving
            // falls back to the explicit button rather than saving something blank.
            if (wasApproving && override && !pending && label.trim()) {
              doApprove(override.id, override.name);
            }
          }}
          onCreateSpace={onCreateSpace}
          onCreateLocation={onCreateLocationPure}
        />
      )}
      {/* The same two buttons whichever scan this is, because they mean the same thing: make it
          approved, or make it declined. On a handled scan the one it already is reads as the current
          state rather than an action, so the only thing left to press is the change — which is what
          "navigate them and change their state" asks for, without a third control to explain.
          
          A location approve is the exception: taking one back would have to unpick the kind flip, the
          ownership change and any sub-locations it created, and a half-undone location is worse than
          none. It stays visible and editable in place, on its own screen. */}
      <div style={S.verifyFooter}>
        <button style={{ ...S.declineBtn, opacity: (entryState === "declined" || (entryState === "approved" && entryIsLocation)) ? 0.4 : 1 }}
          onClick={() => onDecline(currentDraft())}
          disabled={saving || justApproved || entryState === "declined" || (entryState === "approved" && entryIsLocation)}>
          <X size={18} /> {entryState === "declined" ? "Declined" : "Decline"}
        </button>
        <button style={{ ...S.approveBtn, opacity: (!pending && label.trim() && (!savedAlready || dirty)) ? 1 : 0.4 }}
          disabled={pending || !label.trim() || justApproved || (savedAlready && !dirty)}
          onClick={savedAlready ? () => onSaveEdits?.(currentDraft()) : handleApprove}>
          {justApproved ? <><Loader size={18} /> Saving…</>
            : <><Check size={18} /> {savedAlready ? (dirty ? "Save changes" : "Approved") : "Approve"}</>}
        </button>
      </div>
    </div>
  );
}

function ReviewSummary({ saved, onDone }) {
  return (
    <div className="screen">
      <header style={S.subHeader}>
        <div style={{ width: 40 }} /><h2 style={S.subTitle}>Saved</h2><div style={{ width: 40 }} />
      </header>
      <div className="form-scroll">
        {/* No session-level "Added to" banner: items in one session can be routed to
            different locations at approve time, so the location is shown PER ITEM. */}
        <div style={{ ...S.sectionHead, marginTop: 22 }}>
          <span>{saved.length} {saved.length === 1 ? "item" : "items"} saved this session</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 26 }}>
          {saved.map((it) => (
            <div key={it.id} style={S.reviewCard}>
              <div style={S.reviewThumb}><MarkedImg src={it.image} tap={it.tap} style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15.5, lineHeight: 1.3, wordBreak: "break-word" }}>{it.label}</div>
                {it.location ? (
                  <div style={{ fontSize: 12.5, color: "var(--ink-soft)", marginTop: 2, display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                    <MapPin size={12} extra={{ flexShrink: 0 }} />
                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.location}</span>
                  </div>
                ) : null}
              </div>
              <Check size={18} extra={{ color: "var(--success)" }} />
            </div>
          ))}
        </div>
        <div style={{ height: 90 }} />
      </div>
      <div style={S.footer}>
        <button style={S.primaryBtn} onClick={onDone}><Check size={18} /> Back to location</button>
      </div>
    </div>
  );
}

// A name that reads as a heading until you tap the pencil.
//
// Extracted so the location sheet and the item screen cannot drift again: the location's name was a
// permanently-open text box while the item's was a serif title, and the two screens are otherwise
// the same screen. This is the small half of the unification the two views eventually need.
//
// onCommit may return false to reject the new name, which restores what was there before — renames
// go through the server, and a rejected one must not leave the UI showing a name that does not exist.
function EditableTitle({ value, canEdit, onCommit, editLabel = "Rename" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  if (editing) {
    return (
      <input
        autoFocus
        // Named, because the home screen's search field is also a textbox and "the textbox" stopped
        // being unambiguous the moment search moved onto the first screen.
        aria-label="Name"
        style={{ ...S.detailTitle, fontSize: 20, color: "var(--ink)", width: "100%", boxSizing: "border-box",
          background: "var(--surface)", border: "1px solid var(--accent-line)", borderRadius: 10,
          padding: "10px 12px", fontFamily: "inherit", marginBottom: 18 }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={async () => {
          const ok = await onCommit(draft);
          if (ok === false) setDraft(value);
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
          if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
      />
    );
  }
  return (
    <h1 style={{ ...S.detailTitle, display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ wordBreak: "break-word", minWidth: 0 }}>{value}</span>
      {canEdit && (
        <button onClick={() => { setDraft(value); setEditing(true); }} aria-label={editLabel}
          style={{ ...S.iconBtn, width: 28, height: 28, flexShrink: 0 }}>
          <Edit2 size={14} extra={{ color: "var(--ink-soft)" }} />
        </button>
      )}
    </h1>
  );
}

// Who added this, and who can reach it.
//
// The access half is deliberately asymmetric, because the useful answer differs by who is asking.
// The owner gets the list — they are the only one who can change it, and "who else can see my
// things" is a question only they can act on. Everyone else gets one sentence about themselves.
// Showing a guest the other guests would leak the owner's address book through an item screen, and
// telling them a name they cannot do anything about is noise either way.
function ItemInfoSheet({ item, userId, canEdit, addedByLabel, addedDate, onCancel }) {
  const [access, setAccess] = useState(null);
  const isOwner = item.owner_id === userId;

  useEffect(() => {
    let alive = true;
    // Only the owner's view needs the list, so only the owner's view pays for the query. A guest's
    // own role is already known from canEdit, the same ancestor walk that gates every control on
    // this screen — asking the server again would be a round trip to be told what the buttons say.
    if (!isOwner) return () => { alive = false; };
    loadNodeAccess(item.id).then((rows) => { if (alive) setAccess(rows || []); });
    return () => { alive = false; };
  }, [item.id, isOwner]);

  const others = (access || []).filter((r) => r.user_id !== userId);

  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>About this item</h3>
          <button style={S.iconBtn} onClick={onCancel} aria-label="Close"><X size={20} /></button>
        </div>

        {(addedByLabel || addedDate) && (
          <div style={S.metaRow}>
            <span style={{ fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--ink-faint)" }}>Added</span>
            <span style={{ fontSize: 13.5, color: "var(--ink-soft)" }}>
              {addedByLabel ? `by ${addedByLabel}` : ""}{addedByLabel && addedDate ? " · " : ""}{addedDate || ""}
            </span>
          </div>
        )}

        {isOwner ? (
          <div style={{ marginTop: 16 }}>
            <div style={S.fieldLabel}>Who can reach it</div>
            {access === null ? (
              <div style={{ ...S.metaRow, justifyContent: "flex-start" }}><Loader size={15} /> <span style={{ fontSize: 13.5, color: "var(--ink-soft)" }}>Checking…</span></div>
            ) : others.length === 0 ? (
              <p style={{ fontSize: 13.5, color: "var(--ink-soft)", lineHeight: 1.5, margin: "6px 0 0" }}>
                Only you. Share a location to give someone access to everything inside it.
              </p>
            ) : (
              others.map((r) => (
                <div key={r.user_id} style={S.metaRow}>
                  <span style={{ fontSize: 13.5, color: "var(--ink)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {identityLabel(r, "a member")}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--accent-ink)", flexShrink: 0 }}>
                    {r.role === "editor" ? "Editor" : "Viewer"}
                  </span>
                </div>
              ))
            )}
          </div>
        ) : (
          <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.55, margin: "16px 0 0" }}>
            {canEdit ? "You can edit this item." : "You can view this item."}
          </p>
        )}
      </div>
    </div>
  );
}

// Shared is-location toggle switch. Same look everywhere (approve view, item detail,
// location edit). `on` = currently a location. `onToggle` fires with the desired new
// state. `disabled` + `disabledNote` show a greyed switch with an explanation (e.g. a
// location with contents can't become an item).
function IsLocationToggle({ on, onToggle, disabled, disabledNote }) {
  return (
    <button type="button"
      onClick={() => { if (!disabled) onToggle(!on); }}
      disabled={disabled}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12,
        padding: "13px 15px", borderRadius: 14, fontFamily: "inherit", textAlign: "left",
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1,
        background: on ? "var(--accent-soft)" : "var(--surface)",
        border: on ? "1px solid var(--accent-line)" : "1px solid var(--line)",
      }}>
      <div style={{ flexShrink: 0, color: on ? "var(--accent-ink)" : "var(--ink-soft)" }}>
        <MapPin size={20} sw={2.2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>This is a location</div>
        <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1 }}>
          {disabled && disabledNote ? disabledNote : "A place you can put other things inside"}
        </div>
      </div>
      <div style={{
        flexShrink: 0, width: 44, height: 26, borderRadius: 13, position: "relative",
        transition: "background .15s", background: on ? "var(--accent)" : "var(--line-strong)",
      }}>
        <div style={{
          position: "absolute", top: 3, left: on ? 21 : 3, width: 20, height: 20,
          borderRadius: 10, background: "#fff", transition: "left .15s",
        }} />
      </div>
    </button>
  );
}

// Tappable current-location display. Shows where something lives (name + full path)
// and, when tapped, invokes onTap (which opens the location picker). The display IS
// the control — there's no separate "move" button. `name` is the immediate location
// name (or null), `path` the full "Space > A > B" string (or null when it's the same
// as name / top-level). `emptyLabel` shows when there's no location yet.
function LocationDisplay({ name, path, onTap, image = null, emptyLabel = "Choose a location" }) {
  const has = !!name;
  return (
    <button type="button" onClick={onTap}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12,
        padding: "13px 15px", borderRadius: 14, fontFamily: "inherit", textAlign: "left",
        cursor: "pointer", background: "var(--surface)", border: "1px solid var(--line)",
      }}>
      <div style={{ flexShrink: 0, color: has ? "var(--accent-ink)" : "var(--ink-soft)", display: "flex" }}>
        {has && image ? (
          <img src={image} alt=""
            style={{ width: 56, height: 56, borderRadius: 12, objectFit: "cover", border: "1px solid var(--line)", display: "block" }} />
        ) : (
          <MapPin size={20} sw={2.2} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--ink-soft)", marginBottom: 1 }}>
          Location
        </div>
        <div style={{ fontSize: 15.5, fontWeight: 600, color: has ? "var(--ink)" : "var(--ink-soft)", wordBreak: "break-word" }}>
          {has ? name : emptyLabel}
        </div>
        {has && path && (
          <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 2, wordBreak: "break-word" }}>{path}</div>
        )}
      </div>
      <div style={{ flexShrink: 0, fontSize: 12.5, fontWeight: 600, color: "var(--accent-ink)" }}>
        Change
      </div>
    </button>
  );
}

function Detail({ item, locations, spaces, existingLocations, currentSpaceRootId, userId, onBack, onDelete, onRenameItem, onSetQuantity, onSetPhoto, onTagsChanged, onMoveToDestination, onConvertToLocation, onCreateSpace, onCreateLocation, canEdit = true }) {
  const [moveSheetOpen, setMoveSheetOpen] = useState(false);
  const [adder, setAdder] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [tagsBusy, setTagsBusy] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [vocabulary, setVocabulary] = useState(() => tagVocabularyCache || []);
  useEffect(() => {
    if (tagVocabularyCache) return;
    let cancelled = false;
    loadTagVocabulary().then((rows) => { if (!cancelled) setVocabulary(rows); });
    return () => { cancelled = true; };
  }, []);
  // Every photo on this item, fetched on open rather than carried on the item row: most items have
  // one, and the list screens have no reason to pay for finding that out.
  //
  // Until it lands, the item's own photo is already in hand from the list load, so the carousel
  // starts as a single slide and grows rather than showing a gap where the picture goes.
  const [fetchedImages, setFetchedImages] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setFetchedImages(null);
    loadNodeImages(item.id).then((images) => { if (!cancelled) setFetchedImages(images); });
    return () => { cancelled = true; };
  }, [item.id, photoVersion]);
  const images = useMemo(() => {
    if (fetchedImages && fetchedImages.length) return fetchedImages;
    if (!item.image) return [];
    return [{ id: "primary", url: item.image, tap: item.tap || null, is_primary: true }];
  }, [fetchedImages, item.image, item.tap]);
  // Writes immediately, unlike the approve screen, because there is no Approve button here to
  // commit against. The server replies with what it actually kept.
  const saveTags = async (next) => {
    setTagsBusy(true);
    try {
      const res = await setNodeTags(item.id, next);
      await onTagsChanged(item.id, Array.isArray(res?.tags) ? res.tags : next);
    } catch (e) {
      alert(friendlyError(e, "save the tags"));
    } finally {
      setTagsBusy(false);
    }
  };
  const canSetPhoto = canEdit && !!onSetPhoto;
  const pickPhoto = async (file) => {
    setPhotoBusy(true);
    const ok = await onSetPhoto(item.id, file);
    setPhotoBusy(false);
    if (ok) {
      setPhotoOpen(false);
      setPhotoVersion((v) => v + 1); // so the carousel refetches and shows what was just uploaded
    }
  };
  const path = item.location_id && locations ? locationPath(locations, item.location_id) : [];
  const fullPath = path.length > 1 ? pathDisplay(path) : null;
  // The photo shown in the location section: the item's location's own image, or the
  // nearest ancestor's if that sub-location has none (e.g. a drawer inherits the dresser's
  // photo). Falls back to the default pin when nothing in the chain has a photo.
  const locImg = item.location_id && locations ? nearestLocationImage(locations, item.location_id) : null;

  // Resolve who added this item. "Added by" = who SCANNED it (added_by), not owner_id —
  // ownership now transfers to the tree owner, but attribution stays with the scanner.
  useEffect(() => {
    let alive = true;
    const adderId = item.added_by || item.owner_id;
    if (adderId) {
      loadIdentities([adderId]).then((map) => { if (alive) setAdder(map[adderId] || null); });
    }
    return () => { alive = false; };
  }, [item.added_by, item.owner_id]);

  const addedByLabel = (item.added_by || item.owner_id) === userId ? "You" : (adder ? identityLabel(adder, "a member") : null);
  const addedDate = item.created_at
    ? new Date(item.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : null;

  return (
    <div className="screen">
      <header style={S.subHeader}>
        <button style={S.iconBtn} onClick={onBack}><ChevronLeft size={24} /></button>
        <h2 style={S.subTitle}>Item</h2>
        {canEdit ? (
          <button style={S.iconBtn} onClick={() => setConfirmDeleteOpen(true)}>
            <Trash2 size={20} extra={{ color: "var(--danger)" }} />
          </button>
        ) : <div style={{ width: 40 }} />}
      </header>
      <div className="form-scroll">
        {images.length ? (
          <div style={{ marginBottom: 22 }}>
            <ImageFlipper images={images} alt={item.label} height={260} />
            {/* A button rather than a tap on the picture: the carousel already answers to a drag and a
                tap, and a photograph you can accidentally replace is worse than one more button. */}
            {canSetPhoto && (
              <button type="button" style={{ ...S.linkBtn, marginTop: 8 }} disabled={photoBusy}
                onClick={() => setPhotoOpen(true)}>
                {photoBusy ? "Saving…" : "Replace the photo"}
              </button>
            )}
          </div>
        ) : canSetPhoto ? (
          <button type="button" disabled={photoBusy} onClick={() => setPhotoOpen(true)} aria-label="Add a photo"
            style={{ display: "block", width: "100%", padding: 0, marginBottom: 22, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit" }}>
            <div style={S.locationHeroWrap}>
              <div style={S.detailImgPlaceholder}><ImageIcon size={44} sw={1.2} extra={{ color: "var(--ink-faint)" }} /></div>
              <span style={S.locationHeroHint}>
                {photoBusy
                  ? <><Loader size={13} /> Saving…</>
                  : <><Camera size={13} sw={2.2} /> Tap to add a photo</>}
              </span>
            </div>
          </button>
        ) : (
          <div style={S.detailImgWrap}>
            <div style={S.detailImgPlaceholder}><ImageIcon size={40} sw={1.2} extra={{ color: "var(--ink-faint)" }} /></div>
          </div>
        )}
        {photoOpen && (
          <PhotoSourceSheet
            title={images.length ? "Replace the photo" : "Add a photo"}
            busy={photoBusy}
            onPick={pickPhoto}
            onCancel={() => setPhotoOpen(false)}
          />
        )}
        <EditableTitle value={item.label} canEdit={canEdit} editLabel="Rename item"
          onCommit={(text) => onRenameItem(item.id, text)} />
        {/* Tags, directly under the name. They were below the quantity, at the bottom of the
            screen, which is a long way from the thing they describe. */}
        <div style={{ marginTop: 2, marginBottom: 14 }}>
          <TagEditor
            tags={item.tags || []}
            vocabulary={vocabulary}
            readOnly={!canEdit}
            busy={tagsBusy}
            onChange={saveTags}
          />
        </div>
        <div style={{ marginTop: 4 }}>
          <LocationDisplay
            name={item.location || null}
            path={fullPath}
            image={locImg?.image || null}
            emptyLabel="Set a location"
            onTap={canEdit ? () => setMoveSheetOpen(true) : undefined}
          />
        </div>
        {canEdit && onSetQuantity && (
          <div style={S.qtyRow}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>Quantity</div>
              <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 1 }}>Counted as one entry</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <button type="button" style={S.qtyStepBtn} aria-label="One fewer"
                onClick={() => onSetQuantity(item.id, cleanQuantity(item.quantity) - 1)}>&minus;</button>
              <div style={{ ...S.qtyInput, padding: "9px 4px" }}>{cleanQuantity(item.quantity)}</div>
              <button type="button" style={S.qtyStepBtn} aria-label="One more"
                onClick={() => onSetQuantity(item.id, cleanQuantity(item.quantity) + 1)}>+</button>
            </div>
          </div>
        )}
        {canEdit && onConvertToLocation && (
          <div style={{ marginTop: 10 }}>
            <IsLocationToggle on={false} onToggle={(v) => { if (v) setConvertConfirmOpen(true); }} />
          </div>
        )}
        {/* Provenance and access, behind one button at the bottom.
            This was a circled i beside the item's name that toggled a single "Added by" row. It put
            a permanent glyph next to the title for a fact almost nobody needs, and it advertised
            nothing about who else can see the thing — which is the question actually worth asking
            about a shared tree. */}
        <button type="button" style={{ ...S.secondaryBtn, marginTop: 14 }} onClick={() => setInfoOpen(true)}>
          <Info size={17} /> More info
        </button>
        <div style={{ height: 40 }} />
      </div>
      {moveSheetOpen && (
        <LocationTreePicker
          items={[item]} spaces={spaces} locations={locations}
          currentSpaceRootId={currentSpaceRootId} currentLocationId={item.location_id} userId={userId}
          onCancel={() => setMoveSheetOpen(false)}
          onConfirm={async ({ spaceRootId, locationId }) => {
            await onMoveToDestination(item.id, spaceRootId, locationId);
            setMoveSheetOpen(false);
          }}
          onCreateSpace={onCreateSpace}
          onCreateLocation={onCreateLocation}
        />
      )}
      {convertConfirmOpen && (
        <div style={S.sheetBackdrop} onClick={() => !converting && setConvertConfirmOpen(false)}>
          <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <MapPin size={22} extra={{ color: "var(--accent-ink)" }} />
              <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 }}>
                Turn "{item.label}" into a location?
              </h3>
            </div>
            <p style={{ fontSize: 13.5, color: "var(--ink-soft)", margin: "0 0 8px", lineHeight: 1.55 }}>
              This makes <strong style={{ color: "var(--ink)" }}>{item.label}</strong> a place you can put other
              items inside — like a box, shelf, or drawer. It stays right where it is.
            </p>
            <p style={{ fontSize: 12.5, color: "var(--ink-faint)", margin: "0 0 18px", lineHeight: 1.5 }}>
              Its photo is kept, and you'll be able to add items inside it.
            </p>
            <button style={{ ...S.primaryBtn, marginBottom: 10, opacity: converting ? 0.5 : 1 }}
              disabled={converting}
              onClick={async () => { setConverting(true); await onConvertToLocation(item.id); }}>
              {converting ? <><Loader size={18} /> Converting…</> : <><MapPin size={18} /> Make it a location</>}
            </button>
            <button style={S.secondaryBtn} disabled={converting} onClick={() => setConvertConfirmOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirmDeleteOpen && (
        <ConfirmDeleteItemsSheet
          count={1}
          label={item.label}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={async () => { await onDelete(item.id); }}
        />
      )}
      {infoOpen && (
        <ItemInfoSheet
          item={item}
          userId={userId}
          canEdit={canEdit}
          addedByLabel={addedByLabel}
          addedDate={addedDate}
          onCancel={() => setInfoOpen(false)}
        />
      )}
    </div>
  );
}
// One combined tree across all spaces. Current space + current location's
// ancestors expanded by default. Tap a node to select; confirm at the bottom.
// Supports moving one or many items at once.
// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// LocationTreePicker — file-explorer style destination picker.
// One combined tree across all spaces. Current space + current location's
// ancestors expanded by default. Tap a node to select; confirm at the bottom.
// Supports moving one or many items at once.
// ─────────────────────────────────────────────────────────────────────
function LocationTreePicker({ items, spaces, locations, userId, currentSpaceRootId, currentLocationId, excludeSubtreeOf, moveLabel, onCancel, onConfirm, onCreateSpace, onCreateLocation }) {
  // When used to retarget the scan destination (moveLabel="Scan"), the verbs change
  // from "Move" to "Add here" so it reads correctly in the scanning flow.
  const scanMode = moveLabel === "Scan";
  // items: array of items being moved (1 or more)
  // onConfirm({ spaceRootId, locationId })
  // excludeSubtreeOf: a location id whose subtree (incl. itself) can't be a destination
  //   (used when reparenting a location, to prevent cycles).
  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(null); // { spaceRootId, locationId }
  const [working, setWorking] = useState(false);
  const [createLocFor, setCreateLocFor] = useState(null); // { spaceId, parentLocId } | null
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false);

  // Locations that can't be chosen as a destination (the moving location + its descendants).
  const excludedIds = useMemo(() => {
    if (!excludeSubtreeOf) return new Set();
    const roots = Array.isArray(excludeSubtreeOf) ? excludeSubtreeOf : [excludeSubtreeOf];
    const out = new Set();
    for (const r of roots) {
      for (const id of locationSubtree(locations, r)) out.add(id);
    }
    return out;
  }, [excludeSubtreeOf, locations]);

  // Spaces are root locations (the picker receives them in `spaces`).
  const spaceRows = useMemo(() => spaces, [spaces]);

  // space id -> Set of location ids whose root is that space (the space's subtree, excl. the root itself).
  const spaceScope = useMemo(() => {
    const map = new Map();
    for (const sp of spaceRows) map.set(sp.id, new Set());
    for (const l of locations) {
      if (!l.parent_location_id) continue; // root itself, skip
      const root = rootLocationIdOf(locations, l.id);
      if (root && map.has(root)) map.get(root).add(l.id);
    }
    return map;
  }, [spaceRows, locations]);

  // Most-recent destinations, with sibling sub-locations rolled up into the location that
  // contains them so one chest of drawers cannot fill the list. See src/recents.js.
  //
  // Off behind SHOW_RECENT_DESTINATIONS while the scan view gets in/out arrows on its header,
  // which cover the same ground — hopping to a nearby location — without a second, differently
  // ordered copy of the tree above the real one. The code stays because whether recents beat
  // the arrows is a question to answer by using both, not by guessing now.
  const recents = useMemo(
    () => (SHOW_RECENT_DESTINATIONS ? recentDestinations(locations) : []),
    [locations],
  );

  // Expand current space + current location's ancestors by default on mount.
  useEffect(() => {
    const next = new Set();
    if (currentSpaceRootId) next.add("tree:space:" + currentSpaceRootId);
    if (currentLocationId) {
      const path = locationPath(locations, currentLocationId);
      for (const p of path) next.add("tree:loc:" + p.id);
    }
    setExpanded(next);
  }, []);

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  // Locally-created locations (added via this picker) keyed by space, so they show
  // immediately even before the locations list reloads.
  const [justCreated, setJustCreated] = useState({}); // { [spaceId]: Set(locId) }

  const childrenOf = (spaceId, parentLocId) => {
    // A space IS a root location. Its "top level" = locations whose parent is the space root.
    // Deeper levels = locations whose parent is parentLocId. The scope set already excludes
    // the root itself and limits to this space's subtree.
    const effectiveParent = parentLocId === null ? spaceId : parentLocId;
    const scope = spaceScope.get(spaceId) || new Set();
    const extra = justCreated[spaceId] || new Set();
    return locations
      .filter((l) => l.parent_location_id === effectiveParent)
      .filter((l) => scope.has(l.id) || extra.has(l.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const isSelected = (spaceRootId, locationId) =>
    selected && selected.spaceRootId === spaceRootId && selected.locationId === locationId;

  const confirm = async () => {
    if (!selected) return;
    setWorking(true);
    try { await onConfirm(selected); }
    finally { setWorking(false); }
  };

  // Recursive render of a location node within a space.
  //
  // `section` namespaces the open/closed state. A recent location also appears in the tree
  // below, and with one shared key, opening it in the tree silently expanded the copy under
  // "Recent" too — the list grew above the row being tapped and shoved it off screen, which
  // read as the picker scrolling itself back to the top.
  const renderLocNode = (loc, spaceId, depth, section = "tree") => {
    const kids = childrenOf(spaceId, loc.id);
    const hasKids = kids.length > 0;
    const key = section + ":loc:" + loc.id;
    const isOpen = expanded.has(key);
    const sel = isSelected(spaceId, loc.id);
    const isCurrent = loc.id === currentLocationId;
    const excluded = excludedIds.has(loc.id);
    return (
      <div key={loc.id}>
        <div style={{ ...S.treeRow, paddingLeft: 12 + depth * 18, ...(sel ? S.treeRowSelected : null), ...(excluded ? { opacity: 0.4 } : null) }}>
          <button style={S.treeChevron}
            onClick={() => toggleExpand(key)}
            aria-label={isOpen ? "Collapse" : "Expand"}>
            <ChevronLeft size={15} sw={2.6} extra={{ color: hasKids ? "var(--ink-soft)" : "var(--ink-faint)", transform: isOpen ? "rotate(-90deg)" : "rotate(180deg)", transition: "transform 0.15s" }} />
          </button>
          <button style={S.treeLabel} disabled={excluded}
            onClick={() => { if (!excluded) setSelected({ spaceRootId: spaceId, locationId: loc.id }); }}>
            {(() => { const Icon = locationIcon(loc); return <Icon size={15} sw={1.9} extra={{ color: sel ? "var(--accent-ink)" : "var(--ink-soft)", flexShrink: 0 }} />; })()}
            <span style={{ fontWeight: sel ? 700 : 500, fontSize: 14, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {loc.name}
            </span>
            {/* The one badge in the tree. "now" was too terse to mean anything on its own, and it
                competed with a second "current location" badge on the root row — two marks for one
                idea, in different words, often both on screen. */}
            {isCurrent && <span style={S.treeCurrentTag}>{excludeSubtreeOf ? "moving" : "adding here"}</span>}
          </button>
        </div>
        {isOpen && (
          <>
            {kids.map((k) => renderLocNode(k, spaceId, depth + 1, section))}
            <button style={{ ...S.treeAddRow, paddingLeft: 12 + (depth + 1) * 18 }}
              onClick={() => setCreateLocFor({ spaceId, parentLocId: loc.id })}>
              <Plus size={12} /> Add location
            </button>
          </>
        )}
      </div>
    );
  };

  const renderSpaceNode = (sp, section = "tree") => {
    const key = section + ":space:" + sp.id;
    const isOpen = expanded.has(key);
    const roots = childrenOf(sp.id, null);
    const sel = isSelected(sp.id, null);
    return (
      <div key={sp.id} style={{ marginBottom: 4 }}>
        <div style={{ ...S.treeRow, ...(sel ? S.treeRowSelected : null) }}>
          <button style={S.treeChevron} onClick={() => toggleExpand(key)}>
            <ChevronLeft size={15} sw={2.6} extra={{ color: "var(--ink-soft)", transform: isOpen ? "rotate(-90deg)" : "rotate(180deg)", transition: "transform 0.15s" }} />
          </button>
          <button style={S.treeLabel} onClick={() => setSelected({ spaceRootId: sp.id, locationId: null })}>
            {(() => { const Icon = locationIcon(sp); return <Icon size={16} sw={1.9} extra={{ color: sel ? "var(--accent-ink)" : "var(--accent)", flexShrink: 0 }} />; })()}
            <span style={{ fontWeight: 700, fontSize: 14.5, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {sp.name}
            </span>
            {/* Only when the root IS the destination. It used to mark the root of whichever tree
                the destination sat somewhere inside, so "current location" pointed at the house
                while "now" pointed at the drawer. */}
            {sp.id === currentSpaceRootId && !currentLocationId && (
              <span style={S.treeCurrentTag}>{excludeSubtreeOf ? "moving" : "adding here"}</span>
            )}
          </button>
        </div>
        {isOpen && (
          <>
            {roots.map((r) => renderLocNode(r, sp.id, 1, section))}
            <button style={{ ...S.treeAddRow, paddingLeft: 12 + 18 }}
              onClick={() => setCreateLocFor({ spaceId: sp.id, parentLocId: null })}>
              <Plus size={12} /> Add location
            </button>
          </>
        )}
      </div>
    );
  };

  const movingCount = items.length;
  const selLoc = selected && selected.locationId ? getLocationById(locations, selected.locationId) : null;
  const selSpace = selected ? spaces.find((s) => s.id === selected.spaceRootId) : null;
  const selLabel = selected
    ? (selected.topLevel ? "Top level (its own location)"
       : selLoc ? pathDisplay(locationPath(locations, selLoc.id))
       : (selSpace ? selSpace.name + " (no location)" : ""))
    : null;

  return (
    <div style={S.sheetBackdrop} onClick={onCancel}>
      <div style={{ ...S.sheet, height: "92%", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
        {/* One line. It was a stacked eyebrow and title — "SCAN INTO" over "Choose a location" —
            which is two headings saying one thing, and the eyebrow's shouting caps made the
            smaller of them the loudest. The move case still names what is being moved, because
            there the count and the item are the information. */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12, flexShrink: 0 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 600, margin: 0, minWidth: 0 }}>
            {scanMode
              ? "Choose where to add items"
              : `Move ${movingCount > 1 ? `${movingCount} items` : `"${items[0]?.label || "item"}"`} to`}
          </h3>
          <button style={S.iconBtn} onClick={onCancel} aria-label="Close"><X size={20} /></button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", margin: "0 -6px" }}>
            {/* Recents */}
            {recents.length > 0 && (
              <div data-section="recent" style={{ marginBottom: 10 }}>
                <div style={{ ...S.sectionHead, padding: "2px 12px 6px", marginBottom: 0 }}>Recent</div>
                {/* Rendered with the same row components as the tree below, so a rolled-up
                    parent opens to reach the exact drawer, and so selecting a recent records
                    the space it actually belongs to — the old bespoke row assumed the current
                    one, which quietly mis-filed anything recent from another space. Every
                    recent is a sub-location (see src/recents.js), so there is no root row here. */}
                {recents.map((loc) => (
                  <div key={"recent:" + loc.id}>
                    {renderLocNode(loc, rootLocationIdOf(locations, loc.id), 0, "recent")}
                  </div>
                ))}
                <div style={{ height: 1, background: "var(--line)", margin: "10px 12px 4px" }} />
              </div>
            )}

            {/* Spaces tree. The data-section hooks let a test tell this copy of a row from the
                one under "Recent", which is the whole subject of the expansion-key namespacing. */}
            <div data-section="tree">
              {spaceRows.map((sp) => renderSpaceNode(sp))}
            </div>

            <button style={{ ...S.treeAddRow, paddingLeft: 12, marginTop: 6, fontWeight: 600 }}
              onClick={() => setCreateSpaceOpen(true)}>
              <Plus size={13} /> Add location
            </button>

            {/* Top-level option at the very bottom — only when moving a LOCATION
                (excludeSubtreeOf is set). Picking it makes the location a root
                (parent_id = null). Not shown for items. */}
            {excludeSubtreeOf && (
              <div style={{ marginTop: 10 }}>
                <div style={{ height: 1, background: "var(--line)", margin: "0 12px 8px" }} />
                <div style={{ ...S.treeRow, paddingLeft: 12,
                  ...(selected && selected.topLevel ? S.treeRowSelected : null) }}>
                  <span style={{ width: 15, display: "inline-block" }} />
                  <button style={S.treeLabel}
                    onClick={() => setSelected({ spaceRootId: null, locationId: null, topLevel: true })}>
                    <Package size={15} extra={{ color: selected && selected.topLevel ? "var(--accent-ink)" : "var(--accent)", flexShrink: 0 }} />
                    <span style={{ fontWeight: 700, fontSize: 14, color: "var(--ink)", whiteSpace: "nowrap" }}>
                      Move to top level (its own location)
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>

        {/* Confirm bar */}
        <div style={{ flexShrink: 0, paddingTop: 12, borderTop: "1px solid var(--line)", marginTop: 8 }}>
          {selLabel && (
            <div style={{ fontSize: 12.5, color: "var(--ink-soft)", marginBottom: 8, textAlign: "center", wordBreak: "break-word" }}>
              {scanMode ? "Add here: " : "Move to "}<strong style={{ color: "var(--ink)" }}>{selLabel}</strong>
            </div>
          )}
          <button style={{ ...S.primaryBtn, opacity: selected && !working ? 1 : 0.4 }}
            disabled={!selected || working} onClick={confirm}>
            {working ? <><Loader size={18} /> {scanMode ? "Switching…" : "Moving…"}</>
                     : <><Check size={18} /> {scanMode ? "Add here" : `Move ${movingCount > 1 ? `${movingCount} items` : "here"}`}</>}
          </button>
        </div>

        {createLocFor && (
          <CreateLocationSheet
            parentName={createLocFor.parentLocId ? getLocationById(locations, createLocFor.parentLocId)?.name : null}
            onCancel={() => setCreateLocFor(null)}
            onConfirm={async (name) => {
              const sid = createLocFor.spaceId;
              // A "top-level location" in a space has the space root as its parent.
              const parent = createLocFor.parentLocId || sid;
              setCreateLocFor(null);
              const created = await onCreateLocation(name, parent);
              if (created) {
                // Show it immediately in this session, before the locations list reloads.
                // Chain-aware: created.chain holds every node along a typed "A > B > C" path.
                const chainNodes = created.chain || [created];
                const knownNow = [...locations, ...chainNodes];
                setJustCreated((prev) => {
                  const next = { ...prev };
                  const s = new Set(next[sid] || []);
                  for (const n of chainNodes) s.add(n.id);
                  let cur = getLocationById(knownNow, created.id);
                  while (cur && cur.parent_location_id) { s.add(cur.parent_location_id); cur = getLocationById(knownNow, cur.parent_location_id); }
                  next[sid] = s;
                  return next;
                });
                setExpanded((prev) => {
                  const n = new Set(prev);
                  n.add("tree:space:" + sid);
                  const path = locationPath(knownNow, created.id);
                  for (const p of path) n.add("tree:loc:" + p.id);
                  return n;
                });
                setSelected({ spaceRootId: sid, locationId: created.id });
              }
            }}
          />
        )}

        {createSpaceOpen && (
          <CreateLocationSheet
            parentName={null}
            onCancel={() => setCreateSpaceOpen(false)}
            onConfirm={async (name) => {
              setCreateSpaceOpen(false);
              const sp = await onCreateSpace(name);
              if (sp) {
                setExpanded((prev) => new Set([...prev, "tree:space:" + sp.id]));
                setSelected({ spaceRootId: sp.id, locationId: null });
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

const S = {
  bootScreen: { position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "var(--accent)" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "calc(24px + env(safe-area-inset-top)) 22px 14px" },
  wordmark: { fontFamily: "'Fraunces', serif", fontSize: 30, fontWeight: 600, margin: 0, letterSpacing: -0.5 },
  tagline: { margin: "2px 0 0", fontSize: 13, color: "var(--ink-soft)", letterSpacing: 0.2 },
  // The same chip as `count`, as a button. Every total in the app is a way into the flat list of what
  // it counts, so the plain span is now the exception rather than the rule.
  countBtn: { background: "var(--accent)", color: "#1a1205", fontWeight: 700, fontSize: 14, minWidth: 28, height: 28, borderRadius: 14, display: "grid", placeItems: "center", padding: "0 8px", border: "none", cursor: "pointer", fontFamily: "inherit", flexShrink: 0 },
  count: { background: "var(--accent)", color: "#1a1205", fontWeight: 700, fontSize: 14, minWidth: 28, height: 28, borderRadius: 14, display: "grid", placeItems: "center", padding: "0 8px" },
  menuBackdrop: { position: "absolute", inset: 0, background: "rgba(0,0,0,.28)", zIndex: 50 },
  menuCard: { position: "absolute", top: "calc(62px + env(safe-area-inset-top))", left: 18, width: 208, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 16, boxShadow: "0 18px 40px rgba(0,0,0,.35)", overflow: "hidden", display: "flex", flexDirection: "column" },
  menuItem: { display: "flex", alignItems: "center", gap: 10, padding: "13px 15px", background: "none", border: "none", color: "var(--ink)", fontSize: 14.5, fontWeight: 600, fontFamily: "inherit", textAlign: "left", cursor: "pointer" },
  purgeBtn: { width: 32, height: 32, borderRadius: 16, border: "1px solid var(--line)", background: "var(--surface)", display: "grid", placeItems: "center", cursor: "pointer" },
  searchWrap: { display: "flex", alignItems: "center", gap: 10, margin: "4px 18px 12px", padding: "13px 16px", background: "var(--surface)", borderRadius: 16, border: "1px solid var(--line)", cursor: "text" },
  searchInput: { flex: 1, border: "none", background: "transparent", outline: "none", fontSize: 16, color: "var(--ink)", fontFamily: "inherit", minWidth: 0 },
  clearBtn: { border: "none", background: "var(--line)", color: "var(--ink-soft)", width: 24, height: 24, borderRadius: 12, display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, padding: "4px 18px 130px" },
  gridNested: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
  card: { textAlign: "left", border: "1px solid var(--line)", background: "var(--surface)", borderRadius: 18, overflow: "hidden", cursor: "pointer", padding: 0, fontFamily: "inherit", color: "inherit" },
  cardImgWrap: { aspectRatio: "1", overflow: "hidden", background: "var(--bg-deep)", position: "relative" },
  cardSubPathBadge: { position: "absolute", bottom: 6, left: 6, right: 6, fontSize: 10, fontWeight: 600, color: "#fff", background: "rgba(0,0,0,0.7)", padding: "3px 7px", borderRadius: 8, display: "inline-flex", alignItems: "center", gap: 4, lineHeight: 1.3, maxWidth: "calc(100% - 12px)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  // Says "location" on a card that is otherwise, deliberately, an item card. Top-left as a tag
  // rather than a full-width strip: the strip covered the top of the photo, which is where a shelf
  // usually shows what it is.
  cardKindBadge: { position: "absolute", top: 6, left: 6, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "#1a1205", background: "var(--accent)", padding: "3px 7px", borderRadius: 7, display: "inline-flex", alignItems: "center", gap: 3, lineHeight: 1.3, whiteSpace: "nowrap" },
  listRow: { display: "flex", alignItems: "center", gap: 4, borderBottom: "1px solid var(--line)" },
  listCheckbox: { background: "transparent", border: "none", padding: "12px 6px 12px 2px", cursor: "pointer", display: "flex", alignItems: "center", flexShrink: 0 },
  listCheckboxBox: { width: 22, height: 22, borderRadius: 7, border: "2px solid var(--line-strong)", display: "grid", placeItems: "center", transition: "background .12s, border-color .12s" },
  listCheckboxBoxOn: { background: "var(--accent)", borderColor: "var(--accent)" },
  listRowBody: { flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 11, background: "transparent", border: "none", padding: "9px 2px", cursor: "pointer", fontFamily: "inherit", textAlign: "left" },
  listThumb: { width: 34, height: 34, borderRadius: 8, objectFit: "cover", flexShrink: 0 },
  listRowLabel: { fontSize: 15.5, fontWeight: 500, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  listRowSub: { fontSize: 12, color: "var(--ink-soft)", display: "flex", alignItems: "center", gap: 3, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  cardSelected: { borderColor: "var(--accent)", boxShadow: "0 0 0 2px var(--accent) inset" },
  cardCheckbox: { position: "absolute", top: 8, right: 8, width: 26, height: 26, borderRadius: 13, border: "2px solid #fff", background: "rgba(0,0,0,0.35)", display: "grid", placeItems: "center", color: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" },
  cardCheckboxOn: { background: "var(--accent)", borderColor: "#fff", color: "#1a1205" },
  treeRow: { display: "flex", alignItems: "center", gap: 2, borderRadius: 8, minHeight: 38 },
  treeRowSelected: { background: "var(--accent-soft)" },
  treeChevron: { background: "transparent", border: "none", padding: "8px 4px", cursor: "pointer", display: "flex", alignItems: "center", flexShrink: 0 },
  treeLabel: { flex: 1, display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "none", padding: "8px 8px 8px 2px", cursor: "pointer", fontFamily: "inherit", minWidth: 0, textAlign: "left" },
  treeCurrentTag: { fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--accent-ink)", background: "var(--accent-soft)", border: "1px solid var(--accent-line)", padding: "1px 5px", borderRadius: 6, flexShrink: 0, marginLeft: 2 },
  treeAddRow: { display: "flex", alignItems: "center", gap: 5, background: "transparent", border: "none", color: "var(--ink-faint)", padding: "6px 8px", cursor: "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 500 },
  selectionBar: { position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 18px calc(16px + env(safe-area-inset-bottom))", borderTop: "1px solid var(--line)", background: "var(--bg)", display: "flex", gap: 10, alignItems: "center", zIndex: 20 },
  cardImg: { width: "100%", height: "100%", objectFit: "cover" },
  cardImgPlaceholder: { width: "100%", height: "100%", display: "grid", placeItems: "center" },
  cardBody: { padding: "11px 13px 14px" },
  // Two lines, always, whether the name needs them or not.
  //
  // Grid rows stretch to their tallest card, so one long name made both cards in *its* row taller
  // than every card in every other row — and the card you had just added, being short and alone in
  // the last row, was the one that looked wrong ("the new items are smaller"). Reserving the second
  // line costs a few pixels of white space and makes every card in the grid one size. Clamped rather
  // than truncated to one line, because item names here run to "Beige plastic camping spork".
  cardLabel: { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", height: "2.5em", fontWeight: 600, fontSize: 15, lineHeight: 1.25, marginBottom: 6, wordBreak: "break-word" },
  cardLoc: { display: "flex", alignItems: "center", gap: 4, fontSize: 12.5, color: "var(--ink-soft)" },
  fab: { position: "absolute", bottom: "calc(26px + env(safe-area-inset-bottom))", left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "center", gap: 10, background: "var(--accent)", color: "#1a1205", border: "none", borderRadius: 30, padding: "16px 26px", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 10px 30px -8px rgba(232,168,56,.6)", whiteSpace: "nowrap" },
  empty: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "60px 30px" },
  emptyMark: { width: 92, height: 92, borderRadius: 26, background: "var(--surface)", border: "1px solid var(--line)", display: "grid", placeItems: "center", color: "var(--ink-faint)" },
  noResults: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "70px 30px" },
  primaryBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: "var(--accent)", color: "#1a1205", border: "none", borderRadius: 16, padding: "16px 22px", fontSize: 16, fontWeight: 700, cursor: "pointer", width: "100%", fontFamily: "inherit" },
  secondaryBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: "transparent", color: "var(--ink)", border: "1.5px solid var(--line-strong)", borderRadius: 16, padding: "14px 22px", fontSize: 15.5, fontWeight: 600, cursor: "pointer", width: "100%", fontFamily: "inherit" },
  dangerBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: "transparent", color: "var(--danger)", border: "1.5px solid var(--danger)", borderRadius: 16, padding: "14px 22px", fontSize: 15.5, fontWeight: 600, cursor: "pointer", width: "100%", fontFamily: "inherit" },
  subHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "calc(16px + env(safe-area-inset-top)) 14px 16px", borderBottom: "1px solid var(--line)", gap: 8 },
  subTitle: { fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, margin: 0 },
  iconBtn: { width: 40, height: 40, display: "grid", placeItems: "center", border: "none", background: "transparent", color: "var(--ink)", cursor: "pointer", borderRadius: 12 },
  sectionHead: { display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: "var(--ink-soft)", fontWeight: 600, marginBottom: 12, letterSpacing: 0.2 },
  emptySectionRow: { padding: "14px 16px", background: "var(--surface)", border: "1px dashed var(--line)", borderRadius: 12 },
  fieldHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 9 },
  fieldLabel: { fontWeight: 700, fontSize: 15 },
  input: { width: "100%", padding: "14px 16px", fontSize: 16, border: "1px solid var(--line)", background: "var(--surface)", borderRadius: 14, outline: "none", color: "var(--ink)", fontFamily: "inherit" },
  labelInputWrap: { position: "relative", display: "flex", alignItems: "center" },
  labelInput: { width: "100%", padding: "14px 44px 14px 16px", fontSize: 16, border: "1px solid var(--line)", background: "var(--surface)", borderRadius: 14, outline: "none", color: "var(--ink)", fontFamily: "inherit", resize: "none", lineHeight: 1.35, overflow: "hidden", display: "block", boxSizing: "border-box" },
  labelClearBtn: { position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", width: 30, height: 30, borderRadius: 15, border: "none", background: "var(--line)", color: "var(--ink-soft)", display: "grid", placeItems: "center", cursor: "pointer" },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4, alignItems: "center" },
  cardQtyBadge: { position: "absolute", top: 6, left: 6, fontSize: 11, fontWeight: 700, color: "#1a1205", background: "var(--accent)", padding: "2px 7px", borderRadius: 8, lineHeight: 1.4 },
  qtyRow: { marginTop: 10, display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 14, background: "var(--surface)", border: "1px solid var(--line)" },
  qtyStepBtn: { width: 36, height: 36, borderRadius: 10, border: "1px solid var(--line-strong)", background: "var(--bg)", color: "var(--ink)", fontSize: 20, lineHeight: 1, cursor: "pointer", fontFamily: "inherit" },
  qtyInput: { width: 54, textAlign: "center", padding: "8px 4px", borderRadius: 10, border: "1px solid var(--line-strong)", background: "var(--bg)", color: "var(--ink)", fontSize: 16, fontWeight: 600, fontFamily: "inherit", outline: "none" },
  // `outline: none` because a tapped chip otherwise keeps the browser's focus ring, which on this dark
  // surface reads as a stray white border sitting on the button after the tap — and it lingers, since
  // nothing takes focus away afterwards. Every other control here sets it for the same reason.
  chipSuggest: { display: "inline-flex", alignItems: "center", gap: 4, background: "var(--surface)", border: "1px solid var(--line)", color: "var(--ink)", padding: "9px 14px", borderRadius: 20, fontSize: 14, cursor: "pointer", outline: "none" },
  footer: { padding: "14px 18px calc(14px + env(safe-area-inset-bottom))", borderTop: "1px solid var(--line)", background: "var(--bg)" },
  locationBanner: { display: "flex", alignItems: "center", gap: 14, background: "var(--accent-soft)", border: "1px solid var(--accent-line)", color: "var(--accent-ink)", padding: "16px 18px", borderRadius: 18 },
  detailImgWrap: { width: "100%", aspectRatio: "4/3", borderRadius: 22, overflow: "hidden", background: "var(--bg-deep)", marginBottom: 22, position: "relative" },
  flipBtn: { position: "absolute", top: "50%", transform: "translateY(-50%)", width: 34, height: 34, borderRadius: 17, border: "none", background: "rgba(20,18,16,.62)", color: "#f4efe7", display: "grid", placeItems: "center", cursor: "pointer", backdropFilter: "blur(6px)" },
  flipCount: { position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", padding: "3px 9px", borderRadius: 20, background: "rgba(20,18,16,.72)", color: "#f4efe7", fontSize: 11.5, fontWeight: 700 },
  trailBadge: { position: "absolute", top: 8, left: 8, display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 9px", borderRadius: 20, background: "rgba(20,18,16,.72)", color: "#f4efe7", fontSize: 11.5, fontWeight: 600 },
  trailRemoveBtn: { position: "absolute", top: 8, right: 8, width: 28, height: 28, borderRadius: 14, border: "none", background: "rgba(20,18,16,.72)", color: "#f4efe7", display: "grid", placeItems: "center", cursor: "pointer" },
  // Same treatment as detailImgWrap, shorter because it sits inside a sheet rather than owning
  // the top of a screen: 4:3 of a phone's width leaves no room for the fields under it.
  locationHeroWrap: { width: "100%", aspectRatio: "16/9", maxHeight: 190, borderRadius: 18, overflow: "hidden", background: "var(--bg-deep)", border: "1px solid var(--line)", position: "relative", display: "grid", placeItems: "center" },
  locationHeroImg: { width: "100%", height: "100%", objectFit: "cover" },
  locationHeroHint: { position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 11px", borderRadius: 20, background: "rgba(20,18,16,.72)", color: "#f4efe7", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" },
  detailImg: { width: "100%", height: "100%", objectFit: "contain" },
  detailImgPlaceholder: { width: "100%", height: "100%", display: "grid", placeItems: "center" },
  detailTitle: { fontFamily: "'Fraunces', serif", fontSize: 28, fontWeight: 600, margin: "0 0 18px", lineHeight: 1.15 },
  detectStatus: { display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 16 },
  errorBox: { display: "flex", alignItems: "flex-start", gap: 14, padding: "16px 18px", background: "var(--surface)", border: "1px solid var(--line-strong)", borderRadius: 16 },
  linkBtn: { background: "none", border: "none", color: "var(--accent-ink)", padding: 0, marginTop: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  betaPill: { flexShrink: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--accent-ink)", background: "var(--accent-soft)", border: "1px solid var(--accent-line)", borderRadius: 6, padding: "1px 5px" },
  // Deliberately not full width: it is the other way in, offered under the main one, and a second
  // button of the same size reads as a second equal choice.
  smallBtn: { alignSelf: "center", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, background: "transparent", color: "var(--ink-soft)", border: "1px solid var(--line)", borderRadius: 12, padding: "9px 15px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  scanSurface: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", background: "#000" },
  scanTopBar: { position: "absolute", top: 0, left: 0, right: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "calc(10px + env(safe-area-inset-top)) 10px 8px", background: "rgba(0,0,0,.86)", color: "#fff", zIndex: 10 },
  scanStepBtn: { width: 34, height: 34, borderRadius: 17, background: "rgba(255,255,255,.12)", border: "none", color: "#fff", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0, fontFamily: "inherit" },
  scanBackBtn: { width: 40, height: 40, borderRadius: 20, background: "rgba(0,0,0,.5)", border: "none", color: "#fff", display: "grid", placeItems: "center", cursor: "pointer", backdropFilter: "blur(8px)", flexShrink: 0 },
  scanCount: { minWidth: 36, height: 36, borderRadius: 18, background: "var(--accent)", color: "#1a1205", fontWeight: 800, fontSize: 15, display: "grid", placeItems: "center", padding: "0 10px", flexShrink: 0 },
  // `top` is overridden from the top bar's measured height at the call site; this is the fallback for
  // a browser without ResizeObserver.
  queueWrap: { position: "absolute", top: "calc(104px + env(safe-area-inset-top))", left: 12, right: 12, display: "flex", flexDirection: "column", gap: 5, zIndex: 9, maxHeight: 210, overflowY: "auto", pointerEvents: "auto" },
  queuePill: { display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 11, border: "1px solid", backdropFilter: "blur(10px)", boxShadow: "0 3px 10px rgba(0,0,0,.3)", animation: "pillIn .25s ease-out both", flexShrink: 0 },
  pillDeclineBtn: { width: 26, height: 26, borderRadius: 13, border: "none", background: "rgba(217,105,79,.85)", color: "#fff", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0, padding: 0 },
  pillApproveBtn: { width: 26, height: 26, borderRadius: 13, border: "none", background: "var(--success)", color: "#0d1f12", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0, padding: 0 },
  scanBottomBar: { position: "absolute", bottom: 0, left: 0, right: 0, padding: "10px 12px calc(14px + env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, background: "rgba(0,0,0,.86)", zIndex: 10, pointerEvents: "auto" },
  scanModeRow: { display: "flex", gap: 4, padding: 3, borderRadius: 22, background: "rgba(255,255,255,.1)" },
  scanModeBtn: { padding: "8px 16px", borderRadius: 19, border: "none", background: "transparent", color: "rgba(255,255,255,.7)", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  scanModeBtnOn: { background: "var(--accent)", color: "#1a1205", fontWeight: 700 },
  trailStrip: { display: "flex", flexDirection: "column", alignItems: "center", gap: 1, width: "100%", padding: "2px 0" },
  trailStripFrames: { display: "flex", alignItems: "center", gap: 1, maxWidth: "100%", overflowX: "auto", justifyContent: "center", margin: "0 auto" },
  trailStripX: { position: "absolute", top: 0, right: 0, width: 17, height: 17, borderRadius: 9, border: "none", background: "rgba(20,18,16,.95)", color: "#f4efe7", display: "grid", placeItems: "center", cursor: "pointer", padding: 0 },
  // A fixed height, not padding around whatever is inside. This bubble carries three different
  // prompts, a spinner and a queue count, and the tallest thing in any of them decided the height of
  // the whole bar — so the bar jumped 4px when the reference prompt's info button appeared, and the
  // camera above it re-cropped, on nothing more than a mode toggle. 44 is what one line of it came to.
  scanHint: { pointerEvents: "auto", boxSizing: "border-box", height: 44, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,.9)", fontSize: 14, fontWeight: 600, padding: "0 18px", background: "rgba(0,0,0,.55)", borderRadius: 24, backdropFilter: "blur(8px)" },
  scanHintInfoBtn: { flexShrink: 0, display: "grid", placeItems: "center", width: 22, height: 22, padding: 0, borderRadius: 11, border: "none", background: "rgba(255,255,255,.14)", color: "rgba(255,255,255,.92)", cursor: "pointer" },
  scanDoneBtn: { pointerEvents: "auto", display: "inline-flex", alignItems: "center", gap: 10, background: "var(--accent)", color: "#1a1205", border: "none", borderRadius: 30, padding: "16px 24px", fontSize: 15.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 10px 30px -8px rgba(232,168,56,.6)" },
  overlayMsg: { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: "rgba(255,255,255,.85)", fontSize: 15, padding: "10px 18px", background: "rgba(0,0,0,.5)", borderRadius: 20, backdropFilter: "blur(8px)", zIndex: 6 },
  overlayCard: { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "85%", maxWidth: 320, background: "rgba(20,17,12,.95)", color: "var(--ink)", padding: "22px 22px", borderRadius: 20, backdropFilter: "blur(10px)", boxShadow: "0 16px 40px rgba(0,0,0,.5)", border: "1px solid var(--line)", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", zIndex: 8 },
  verifyImgWrap: { width: "100%", aspectRatio: "4/3", borderRadius: 22, overflow: "hidden", background: "var(--bg-deep)" },
  verifyImg: { width: "100%", height: "100%", objectFit: "contain" },
  verifyFooter: { padding: "14px 18px calc(14px + env(safe-area-inset-bottom))", borderTop: "1px solid var(--line)", background: "var(--bg)", display: "flex", gap: 10 },
  declineBtn: { flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, background: "transparent", color: "var(--danger)", border: "1.5px solid var(--danger)", borderRadius: 16, padding: "15px 16px", fontSize: 15.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  approveBtn: { flex: 2, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: "var(--accent)", color: "#1a1205", border: "none", borderRadius: 16, padding: "16px 18px", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  approvedFlash: { position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none", zIndex: 50 },
  approvedFlashCircle: { width: 88, height: 88, borderRadius: 44, background: "var(--success)", display: "grid", placeItems: "center", boxShadow: "0 8px 30px -6px rgba(0,0,0,.4)", animation: "approvedPop .34s ease-out both" },
  reviewCard: { display: "flex", gap: 12, padding: 12, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 14, alignItems: "center" },
  reviewThumb: { position: "relative", width: 64, height: 64, borderRadius: 10, overflow: "hidden", background: "var(--bg-deep)", flexShrink: 0 },
  sheetBackdrop: { position: "absolute", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "flex-end", zIndex: 20, backdropFilter: "blur(4px)" },
  sheet: { width: "100%", background: "var(--bg)", borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: "22px 22px calc(28px + env(safe-area-inset-bottom))", borderTop: "1px solid var(--line)", maxHeight: "85%", overflowY: "auto" },

  // Spaces home
  allItemsCard: { width: "100%", display: "flex", alignItems: "center", border: "1px solid var(--line)", background: "var(--surface)", borderRadius: 18, padding: "14px 16px", cursor: "pointer", fontFamily: "inherit", color: "inherit", textAlign: "left" },
  inviteCard: { border: "1px solid var(--accent-line)", background: "var(--accent-soft)", borderRadius: 16, padding: "14px 14px", marginBottom: 8 },
  memberRow: { display: "flex", alignItems: "center", gap: 10, padding: "11px 4px", borderBottom: "1px solid var(--line)" },
  miniBtn: { padding: "7px 10px", borderRadius: 9, border: "1px solid var(--line)", background: "var(--surface)", color: "var(--ink-soft)", fontWeight: 600, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  metaRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 14, padding: "10px 14px", borderRadius: 12, background: "var(--surface)", border: "1px solid var(--line)" },
  seg: { padding: "11px 14px", borderRadius: 12, border: "1px solid var(--line)", background: "var(--surface)", color: "var(--ink-soft)", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" },
  segActive: { padding: "11px 14px", borderRadius: 12, border: "1px solid var(--accent)", background: "var(--accent-soft)", color: "var(--ink)", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" },
  spacesList: { padding: "0 18px 130px" },
  // Reads as the next row of the list rather than a control bolted to the heading: same
  // height and inset as a location row, dashed so it is clearly the empty one at the end.
  addListRow: { width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "13px 14px", background: "transparent", border: "1px dashed var(--line-strong)", borderRadius: 12, cursor: "pointer", fontFamily: "inherit", color: "var(--ink-soft)", fontSize: 14.5, fontWeight: 600, textAlign: "left" },
  addListRowPlus: { width: 18, display: "grid", placeItems: "center", flexShrink: 0, color: "var(--accent-ink)" },
  locationRowThumb: { width: 28, height: 28, borderRadius: 8, objectFit: "cover", flexShrink: 0, pointerEvents: "none", background: "var(--accent-soft)" },

  // Locations list (in-space view)
  addLocationRow: { display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px dashed var(--line-strong)", color: "var(--ink-soft)", padding: "10px 14px", borderRadius: 12, fontSize: 13.5, fontWeight: 600, cursor: "pointer", marginBottom: 14, fontFamily: "inherit" },
  locationHeader: { width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "13px 14px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 12, cursor: "pointer", fontFamily: "inherit", color: "inherit" },

  // Add menu (Scan / from shelf)
  bigActionBtn: { width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "16px 16px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 16, cursor: "pointer", fontFamily: "inherit", color: "inherit" },
  bigActionIcon: { width: 46, height: 46, borderRadius: 12, display: "grid", placeItems: "center", flexShrink: 0 },

  // Location view actions (two buttons fixed at bottom)
  locationActionMove: { flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: "transparent", color: "var(--ink)", border: "1.5px solid var(--line-strong)", borderRadius: 16, padding: "14px 18px", fontSize: 15.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },

  // Add existing items picker rows
  pickerRow: { width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 14, cursor: "pointer", fontFamily: "inherit", color: "inherit", marginBottom: 8 },
  pickerThumb: { position: "relative", width: 48, height: 48, borderRadius: 10, overflow: "hidden", background: "var(--bg-deep)", flexShrink: 0, display: "grid", placeItems: "center" },
  pickerCheckBox: { width: 24, height: 24, borderRadius: 12, border: "1.5px solid var(--line-strong)", display: "grid", placeItems: "center", flexShrink: 0, color: "transparent", transition: "all 0.15s" },
  pickerCheckBoxOn: { background: "var(--accent)", borderColor: "var(--accent)", color: "#1a1205" },
  inSpaceTag: { fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--accent-ink)", background: "var(--accent-soft)", border: "1px solid var(--accent-line)", padding: "2px 7px", borderRadius: 8, whiteSpace: "nowrap" },
};

ReactDOM.createRoot(document.getElementById("root")).render(<Root />);
